import { FormEvent, Fragment, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, BarChart3, Bell, Check, ClipboardList, Download, Lock, Package, RefreshCw, RotateCcw, Search, Send, ShieldCheck, SlidersHorizontal, Upload, Warehouse } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import type {
  AdminErpInventoryItem,
  AdminErpWarehouse,
  AdminErpOutboundOrder,
  AdminErpPurchaseOrder,
  AdminErpStoreOrderCandidate,
  AdminStoreSalesOrderOption,
  AdminUser,
  PaginationMeta,
} from "../admin/api";
import ErpLayout from "./ErpLayout";
import { ErpActionBar, ErpDocumentPrintMenu, ErpProductSearchPanel } from "./shared/components";
import {
  ErpLocationOption,
  ErpLogisticsCarrierOption,
  ErpPurchaseDemandGroup,
  ErpPurchaseReceiveItem,
  ErpStockAdjustmentPayload,
  ErpStockAdjustmentOrder,
  ErpStockAdjustmentUserOption,
  ErpStockMovement,
  ErpStockUnit,
  ErpStockTransferOrder,
  ErpSupplier,
  ErpSupplierOption,
  ErpSupplierPricePayload,
  ErpSupplierProductPrice,
  ErpSupplierPayload,
  ErpWarehouse,
  ErpWarehousePayload,
  ErpProductSearchItem,
  ErpInventoryDetail,
  ErpInventoryCountCandidate,
  ErpApiError,
  ErpPurchaseReceiptRecord,
  ErpAttachment,
  ErpAuditLog,
  ErpSupplierPayable,
  ErpQualityInspection,
  ErpAccountingPeriod,
  ErpImportBatch,
  ErpNotificationTask,
  ErpRmaOrder,
  ErpReportData,
  UploadProgressInfo,
  applyErpImportBatch,
  disableErpAttachment,
  downloadErpAttachment,
  approveErpPayable,
  approveErpStockAdjustment,
  arriveErpOutboundOrder,
  cancelErpStockAdjustment,
  closeErpAccountingPeriod,
  closeErpRmaOrder,
  completeErpTask,
  createErpAccountingPeriod,
  createErpAttachment,
  createErpPayable,
  createErpPayablePayment,
  createErpPaymentBatch,
  createErpStockAdjustment,
  createErpStockTransfer,
  createErpSupplierPrice,
  createErpSupplier,
  createErpWarehouse,
  createErpOutboundOrder,
  createErpPurchaseOrder,
  createErpRmaOrder,
  getCurrentErpUser,
  getErpPurchaseOrder,
  getErpStockAdjustmentOptions,
  getErpStockAdjustment,
  getErpInventoryDetail,
  listErpAccountingPeriods,
  listErpInventory,
  listErpAttachments,
  listErpAuditLogs,
  listErpPayables,
  listErpPaymentBatches,
  listErpQualityInspections,
  listErpImportBatches,
  listErpInventoryCountCandidates,
  listErpOutboundOrders,
  listErpPurchaseOrders,
  listErpPurchaseReceipts,
  listErpRmaOrders,
  listErpStockAdjustments,
  listErpStockMovements,
  listErpStockUnits,
  listErpStockTransfers,
  listErpSupplierPrices,
  listErpSuppliers,
  listErpWarehouses,
  listErpTasks,
  logoutErp,
  exportErpInventory,
  exportErpReports,
  getErpReports,
  receiveErpPurchaseOrder,
  reconcileErpPaymentBatch,
  reviseErpPurchaseOrder,
  approveErpPurchaseOrder,
  cancelErpPurchaseOrder,
  receiveErpStockTransfer,
  getErpPurchaseReceipt,
  releaseErpPurchaseReceipt,
  resolveErpPurchaseReceipt,
  rejectErpStockAdjustment,
  reopenErpAccountingPeriod,
  searchErpProducts,
  shipErpOutboundOrder,
  shipErpStockTransfer,
  startErpStockAdjustment,
  storeSignErpOutboundOrder,
  submitErpStockAdjustment,
  updateErpOutboundLogistics,
  updateErpRmaOrderStage,
  updateErpInventoryPolicy,
  updateErpSupplierPrice,
  updateErpSupplier,
  updateErpWarehouse,
  uploadErpAttachment,
  validateErpImportBatch,
  voidErpAccountingPeriod,
  voidErpPayablePayment,
} from "./api";

type ErpView = "inventory" | "inventory-detail" | "units" | "movements" | "inventory-counts" | "inventory-count-detail" | "inventory-count-create" | "adjustments" | "adjustment-detail" | "adjustment-create" | "transfers" | "transfer-create" | "purchase" | "purchase-suggestions" | "purchase-create" | "purchase-receipts" | "purchase-receipt-detail" | "outbound" | "outbound-create" | "quality-inspections" | "attachments" | "payables" | "reports" | "audit-logs" | "warehouses" | "suppliers" | "supplier-create" | "supplier-edit" | "accounting-periods" | "rma-orders" | "import-batches" | "tasks";

type OperationProgressState = {
  label: string;
  detail?: string;
  percent?: number | null;
};

const emptyPagination: PaginationMeta = { Page: 1, PerPage: 20, Total: 0, TotalPages: 1 };

function money(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`;
}

function plainMoney(value?: number | null) {
  return `NT$ ${Number(value || 0).toLocaleString("zh-TW")}`;
}

function count(value?: number | null) {
  return `${Number(value || 0).toLocaleString("zh-TW")} 筆`;
}

function csvCell(value: string | number | null | undefined) {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

function downloadCsvFile(filename: string, rows: Array<Array<string | number | null | undefined>>) {
  const csv = rows.map((row) => row.map(csvCell).join(",")).join("\r\n");
  const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}

function formatBytes(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "0 KB";
  if (value >= 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`;
  return `${Math.ceil(value / 1024).toLocaleString("zh-TW")} KB`;
}

function OperationProgress({ progress }: { progress: OperationProgressState | null }) {
  if (!progress) return null;
  const percent = typeof progress.percent === "number" ? Math.max(0, Math.min(100, progress.percent)) : null;
  return (
    <div className="rounded-md border border-[#bfd8ca] bg-[#f1faf4] p-3 text-sm font-bold text-[#203d32]" role="status" aria-live="polite">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span>{progress.label}</span>
        {percent !== null ? <span>{percent}%</span> : <span>處理中...</span>}
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-white">
        <div className={`h-full rounded-full bg-[#315746] ${percent === null ? "erp-progress-indeterminate" : ""}`} style={percent !== null ? { width: `${percent}%` } : undefined} />
      </div>
      {progress.detail ? <p className="mt-2 break-words text-xs leading-5 text-[#59665f]">{progress.detail}</p> : null}
    </div>
  );
}

function readFileAsTextWithProgress(file: File, onProgress: (progress: UploadProgressInfo) => void) {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onprogress = (event) => {
      if (!event.lengthComputable) return;
      onProgress({
        loaded: event.loaded,
        total: event.total,
        percent: Math.min(100, Math.round((event.loaded / event.total) * 100)),
      });
    };
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("檔案讀取失敗，請確認檔案是否可正常開啟。"));
    reader.readAsText(file, "utf-8");
  });
}

function ErpCheckbox({
  ariaLabel,
  checked,
  className = "",
  disabled = false,
  label,
  onChange,
}: {
  ariaLabel?: string;
  checked: boolean;
  className?: string;
  disabled?: boolean;
  label?: ReactNode;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className={`inline-flex min-w-0 cursor-pointer items-center gap-2 font-bold text-[#315746] ${disabled ? "cursor-not-allowed opacity-60" : ""} ${className}`}>
      <input aria-label={ariaLabel} checked={checked} className="sr-only" disabled={disabled} type="checkbox" onChange={(event) => onChange(event.target.checked)} />
      <span
        aria-hidden="true"
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition ${
          checked ? "border-[#315746] bg-[#315746] text-white" : "border-[#aebbb4] bg-white text-transparent"
        }`}
      >
        <Check size={14} strokeWidth={3} />
      </span>
      {label ? <span className="min-w-0 break-words">{label}</span> : null}
    </label>
  );
}

function ErpCheckboxCard({
  checked,
  disabled = false,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: ReactNode;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={`flex min-h-11 min-w-0 cursor-pointer items-center gap-3 rounded-md border px-3 py-2 text-sm font-bold transition ${
        checked ? "border-[#9ac8aa] bg-[#f1f8f3] text-[#315746]" : "border-[#dfe6e1] bg-white text-[#59665f] hover:border-[#b8c8bf] hover:bg-[#fbfcfb]"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <input checked={checked} className="sr-only" disabled={disabled} type="checkbox" onChange={(event) => onChange(event.target.checked)} />
      <span
        aria-hidden="true"
        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 transition ${
          checked ? "border-[#315746] bg-[#315746] text-white" : "border-[#aebbb4] bg-white text-transparent"
        }`}
      >
        <Check size={14} strokeWidth={3} />
      </span>
      <span className="min-w-0 break-words">{label}</span>
    </label>
  );
}

function ErpSwitchCard({
  checked,
  disabled = false,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: ReactNode;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label
      className={`grid min-h-11 min-w-0 cursor-pointer grid-cols-[minmax(0,1fr)_48px] items-center gap-3 rounded-md border px-3 py-2 font-bold transition ${
        checked ? "border-[#9ac8aa] bg-[#f1f8f3] text-[#315746]" : "border-[#dfe6e1] bg-white text-[#59665f] hover:border-[#b8c8bf] hover:bg-[#fbfcfb]"
      } ${disabled ? "cursor-not-allowed opacity-60" : ""}`}
    >
      <input checked={checked} className="sr-only" disabled={disabled} type="checkbox" onChange={(event) => onChange(event.target.checked)} />
      <span className="min-w-0 break-words">{label}</span>
      <span aria-hidden="true" className={`relative h-7 w-12 shrink-0 rounded-full transition ${checked ? "bg-[#315746]" : "bg-[#b8c1bc]"}`}>
        <span className={`absolute top-0.5 flex h-6 w-6 items-center justify-center rounded-full bg-white text-[#315746] shadow-sm transition ${checked ? "left-[22px]" : "left-0.5"}`}>
          {checked ? <Check size={15} strokeWidth={3} /> : null}
        </span>
      </span>
    </label>
  );
}

function escapeHtml(value: unknown) {
  return String(value ?? "-")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");
}

function todayText() {
  return new Date().toLocaleDateString("zh-TW", { year: "numeric", month: "2-digit", day: "2-digit" });
}

function printableText(value: ReactNode) {
  return String(value ?? "");
}

function splitPrintableCell(value: ReactNode, maxLines = 8) {
  const text = printableText(value).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
  const rawLines = text.split("\n");
  const lines = rawLines.flatMap((line) => {
    const trimmed = line.trim();
    if (!trimmed) return [""];
    const chunks: string[] = [];
    for (let index = 0; index < trimmed.length; index += 24) {
      chunks.push(trimmed.slice(index, index + 24));
    }
    return chunks.length ? chunks : [trimmed];
  });
  const groups: string[] = [];
  for (let index = 0; index < lines.length; index += maxLines) {
    groups.push(lines.slice(index, index + maxLines).join("\n"));
  }
  return groups.length ? groups : [""];
}

function normalizePrintRows(rows: ReactNode[][]) {
  return rows.flatMap((row) => {
    const cellGroups = row.map((cell) => splitPrintableCell(cell));
    const chunkCount = Math.max(...cellGroups.map((group) => group.length), 1);
    return Array.from({ length: chunkCount }, (_, chunkIndex) => row.map((cell, cellIndex) => {
      const value = cellGroups[cellIndex]?.[chunkIndex] || "";
      if (chunkIndex === 0 || value !== "") return value;
      return cellIndex === 0 ? "（續）" : "";
    }));
  });
}

function documentHtml({
  title,
  subtitle,
  number,
  status,
  meta,
  columns,
  rows,
  note,
  signatures,
}: {
  title: string;
  subtitle: string;
  number: string;
  status: string;
  meta: Array<[string, ReactNode]>;
  columns: string[];
  rows: ReactNode[][];
  note?: ReactNode;
  signatures: string[];
}) {
  const rowsPerPage = columns.length >= 8 ? 12 : 15;
  const sourceRows = rows.length > 0 ? normalizePrintRows(rows) : [columns.map((_, index) => (index === 0 ? "尚無品項明細" : "-"))];
  const pages: ReactNode[][][] = [];
  for (let index = 0; index < sourceRows.length; index += rowsPerPage) {
    pages.push(sourceRows.slice(index, index + rowsPerPage));
  }
  const metaRows = [];
  for (let index = 0; index < meta.length; index += 4) {
    metaRows.push(meta.slice(index, index + 4));
  }
  const documentHeader = (pageIndex: number, pageCount: number) => `
    <header class="doc-header">
      <table class="doc-title-table">
        <tbody>
          <tr>
            <td class="title-cell">
              <div class="brand">FULING ERP PORTAL</div>
              <h1>${escapeHtml(title)}</h1>
              <div class="subtitle">${escapeHtml(subtitle)}</div>
            </td>
            <td class="docno">
              <div>單據編號：${escapeHtml(number)}</div>
              <div>列印日期：${escapeHtml(todayText())}</div>
              <div>頁次：${pageIndex + 1} / ${pageCount}</div>
              <span class="status">${escapeHtml(status)}</span>
            </td>
          </tr>
        </tbody>
      </table>
      <table class="meta-table">
        <tbody>
          ${metaRows.map((metaRow) => `<tr>${Array.from({ length: 4 }, (_, cellIndex) => {
            const item = metaRow[cellIndex];
            return `<td>${item ? `<span class="label">${escapeHtml(item[0])}</span><span class="value">${escapeHtml(item[1])}</span>` : ""}</td>`;
          }).join("")}</tr>`).join("")}
        </tbody>
      </table>
    </header>`;
  const itemTable = (pageRows: ReactNode[][]) => `
    <h2>品項明細</h2>
    <table class="item-table">
      <thead>
        <tr>${columns.map((column, index) => `<th class="${index >= columns.length - 3 ? "num" : ""}">${escapeHtml(column)}</th>`).join("")}</tr>
      </thead>
      <tbody>
        ${pageRows.map((row) => `<tr>${row.map((cell, index) => `<td class="${index >= row.length - 3 ? "num" : ""}">${escapeHtml(cell)}</td>`).join("")}</tr>`).join("")}
      </tbody>
    </table>`;
  const closingSection = `
    <h2>備註</h2>
    <div class="note">${escapeHtml(note || "-")}</div>
    <section class="signatures">${signatures.map((signature) => `<div class="signature">${escapeHtml(signature)}：</div>`).join("")}</section>
    <div class="foot">本單據為 ERP 作業留存文件，請依實際收貨、出庫、調撥與交接結果簽核；若品項、數量、序號或金額異常，請於系統內建立異常備註並通知主管覆核。</div>`;
  const pageHtml = pages.map((pageRows, pageIndex) => `
  <main class="sheet">
    ${documentHeader(pageIndex, pages.length)}
    ${itemTable(pageRows)}
    ${pageIndex === pages.length - 1 ? closingSection : ""}
    <div class="page-count">第 ${pageIndex + 1} / ${pages.length} 頁</div>
  </main>`).join("");

  return `<!doctype html>
<html lang="zh-Hant">
<head>
  <meta charset="utf-8" />
  <title>${escapeHtml(title)} - ${escapeHtml(number)}</title>
  <style>
    * { box-sizing: border-box; }
    body { margin: 0; background: #f4f6f3; color: #203d32; font-family: "Noto Sans TC", "Microsoft JhengHei", Arial, sans-serif; }
    .sheet { width: 210mm; min-height: 297mm; margin: 0 auto 8mm; background: #fff; padding: 14mm 14mm; page-break-after: always; break-after: page; }
    .sheet:last-child { page-break-after: auto; break-after: auto; }
    .doc-header { margin-bottom: 10px; }
    .doc-title-table { border: 0; margin: 0 0 8px; }
    .doc-title-table td { border: 0; padding: 0; }
    .title-cell { width: 68%; }
    .brand { letter-spacing: .18em; font-size: 12px; font-weight: 900; color: #2d3f35; }
    h1 { margin: 5px 0 0; font-size: 27px; letter-spacing: 0; color: #203d32; }
    .subtitle { margin-top: 6px; font-size: 13px; font-weight: 700; color: #59665f; }
    .docno { text-align: right; font-size: 12px; line-height: 1.7; font-weight: 900; color: #203d32; }
    .status { display: inline-block; margin-top: 4px; border: 1px solid #acd7bb; background: #eff8f1; border-radius: 999px; padding: 2px 10px; color: #245438; }
    .meta-table { border-collapse: collapse; table-layout: fixed; border-top: 2px solid #315746; margin-top: 8px; }
    .meta-table td { width: 25%; height: 37px; border: 1px solid #9db1a7; padding: 5px 8px; vertical-align: middle; }
    .label { display: block; font-size: 11px; font-weight: 800; color: #59665f; }
    .value { display: block; margin-top: 3px; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 14px; font-weight: 800; color: #203d32; }
    h2 { margin: 12px 0 7px; font-size: 15px; color: #315746; }
    table { width: 100%; border-collapse: collapse; table-layout: fixed; }
    .item-table th, .item-table td { border: 1px solid #9db1a7; padding: 5px 7px; vertical-align: top; overflow-wrap: anywhere; white-space: pre-wrap; font-size: 11.5px; line-height: 1.42; }
    th { background: #f3f7f4; color: #315746; font-weight: 900; text-align: left; }
    td.num, th.num { text-align: right; }
    .note { min-height: 64px; border: 1px solid #9db1a7; padding: 9px; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 13px; line-height: 1.7; }
    .signatures { display: grid; grid-template-columns: repeat(${Math.max(1, signatures.length)}, 1fr); gap: 12px; margin-top: 22px; }
    .signature { border: 1px solid #9db1a7; height: 72px; padding: 8px; font-size: 12px; font-weight: 800; color: #59665f; }
    .foot { margin-top: 14px; border-top: 1px solid #dfe6e1; padding-top: 8px; font-size: 11px; line-height: 1.7; color: #59665f; }
    .page-count { margin-top: 8px; text-align: right; font-size: 11px; font-weight: 800; color: #7b877f; }
    @media print {
      body { background: #fff; }
      .sheet { width: auto; min-height: auto; margin: 0; padding: 0; }
      .doc-header { break-inside: avoid; page-break-inside: avoid; }
      .doc-title-table, .meta-table { break-inside: avoid; page-break-inside: avoid; }
      table { break-inside: auto; page-break-inside: auto; }
      thead { display: table-header-group; }
      tfoot { display: table-footer-group; }
      tr { break-inside: avoid; page-break-inside: avoid; }
      th { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { size: A4 portrait; margin: 13mm; }
    }
  </style>
</head>
<body>
  ${pageHtml}
</body>
</html>`;
}

function openPrintDocument(html: string) {
  const printWindow = window.open("", "_blank", "width=1040,height=900");
  if (!printWindow) {
    alert("瀏覽器阻擋了列印視窗，請允許彈出視窗後再試一次。");
    return;
  }
  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();
  printWindow.focus();
  let printed = false;
  const triggerPrint = () => {
    if (printed) return;
    printed = true;
    window.setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 150);
  };
  printWindow.onload = triggerPrint;
  window.setTimeout(triggerPrint, 450);
}

function pageTitle(view: ErpView) {
  if (view === "inventory") return "庫存總覽";
  if (view === "inventory-detail") return "庫存詳情";
  if (view === "units") return "庫存序號明細";
  if (view === "movements") return "庫存異動紀錄";
  if (view === "inventory-counts") return "正式盤點單";
  if (view === "inventory-count-detail") return "盤點單詳情";
  if (view === "inventory-count-create") return "建立盤點單";
  if (view === "adjustments") return "庫存調整盤點";
  if (view === "adjustment-detail") return "庫存調整單詳情";
  if (view === "adjustment-create") return "建立庫存調整";
  if (view === "transfers") return "庫存調撥單";
  if (view === "transfer-create") return "建立調撥單";
  if (view === "purchase") return "採購單";
  if (view === "purchase-suggestions") return "採購需求 / 建議";
  if (view === "purchase-create") return "建立採購單";
  if (view === "purchase-receipts") return "採購入庫驗收";
  if (view === "purchase-receipt-detail") return "驗收單詳情";
  if (view === "outbound") return "出庫作業";
  if (view === "outbound-create") return "建立出庫單";
  if (view === "quality-inspections") return "工程檢查 / QC";
  if (view === "attachments") return "附件中心";
  if (view === "payables") return "供應商請款付款";
  if (view === "reports") return "ERP 報表";
  if (view === "audit-logs") return "操作稽核紀錄";
  if (view === "accounting-periods") return "會計期間鎖定";
  if (view === "rma-orders") return "退貨換貨 / RMA";
  if (view === "import-batches") return "匯入批次管理";
  if (view === "tasks") return "ERP 通知待辦";
  if (view === "warehouses") return "倉庫管理";
  if (view === "supplier-create") return "新增供應商";
  if (view === "supplier-edit") return "編輯供應商";
  return "供應商管理";
}

function StatusBadge({ children, tone = "default" }: { children: ReactNode; tone?: "default" | "warning" | "danger" }) {
  const toneClass = tone === "danger" ? "bg-[#fdebea] text-[#8a1f1f]" : tone === "warning" ? "bg-[#fff5df] text-[#8a5b14]" : "bg-[#edf5f0] text-[#315746]";
  return <span className={`inline-flex min-h-7 items-center rounded-full px-3 py-1 text-xs font-extrabold ${toneClass}`}>{children}</span>;
}

type StoreOrderAuditView = {
  OrderPurpose?: string | null;
  OrderPurposeLabel?: string | null;
  ZeroPriceReason?: string | null;
  ZeroPriceReasonLabel?: string | null;
  ZeroPriceNote?: string | null;
  SourceOrderPurpose?: string | null;
  SourceOrderPurposeLabel?: string | null;
  SourceZeroPriceReason?: string | null;
  SourceZeroPriceReasonLabel?: string | null;
  SourceZeroPriceNote?: string | null;
};

function storeOrderAuditSummary(row?: StoreOrderAuditView | null) {
  if (!row) return "";
  const purpose = row.OrderPurposeLabel || row.OrderPurpose || row.SourceOrderPurposeLabel || row.SourceOrderPurpose || "";
  const reason = row.ZeroPriceReasonLabel || row.ZeroPriceReason || row.SourceZeroPriceReasonLabel || row.SourceZeroPriceReason || "";
  const note = row.ZeroPriceNote || row.SourceZeroPriceNote || "";
  const isGeneral = !purpose || purpose === "Sale" || purpose === "一般銷售";
  if (isGeneral && !reason && !note) return "";
  return [purpose || "特殊訂單", reason, note].filter(Boolean).join(" / ");
}

function StoreOrderAuditLine({ row }: { row?: StoreOrderAuditView | null }) {
  const summary = storeOrderAuditSummary(row);
  if (!summary) return null;
  return <p className="mt-2 break-words rounded-md bg-[#fff8e8] px-3 py-2 text-xs font-extrabold leading-5 text-[#8a5b14]">訂單性質：{summary}</p>;
}

function purchaseSourceAuditSummary(row: AdminErpPurchaseOrder) {
  const sourceSummaries = (row.SourceOrders || []).map((source) => storeOrderAuditSummary(source)).filter(Boolean);
  if (sourceSummaries.length > 0) return Array.from(new Set(sourceSummaries)).join("；");
  return storeOrderAuditSummary({
    SourceOrderPurpose: row.SourceOrderPurpose,
    SourceOrderPurposeLabel: row.SourceOrderPurposeLabel,
    SourceZeroPriceReason: row.SourceZeroPriceReason,
    SourceZeroPriceReasonLabel: row.SourceZeroPriceReasonLabel,
    SourceZeroPriceNote: row.SourceZeroPriceNote,
  });
}

function canReceivePurchase(row: AdminErpPurchaseOrder) {
  return ["Ordered", "PartiallyArrived", "ArrivedPendingInspection", "PartiallyReceived", "Exception"].includes(row.Status) && (row.Items || []).some((item) => Number(item.RemainingQuantity || 0) > 0);
}

const purchaseInspectionChecks = [
  ["ProductMatched", "商品型號 / 規格正確"],
  ["QuantityMatched", "到貨數量正確"],
  ["SerialChecked", "序號 / 耳側確認"],
  ["AccessoryChecked", "配件與包裝完整"],
  ["DocumentChecked", "保卡與文件齊全"],
  ["AppearanceChecked", "外觀與基本功能正常"],
] as const;

const purchaseExceptionOptions = [
  ["", "無異常"],
  ["ModelMismatch", "型號不符"],
  ["QuantityMismatch", "數量不符"],
  ["SerialMissing", "序號缺漏"],
  ["SerialMismatch", "序號不符"],
  ["Damaged", "外觀 / 功能異常"],
  ["AccessoryMissing", "配件缺漏"],
  ["DocumentMissing", "文件缺漏"],
  ["Other", "其他異常"],
] as const;

const purchaseResolutionOptions = [
  ["", "無需處理"],
  ["PendingReview", "待主管判定"],
  ["ReturnToSupplier", "退供應商"],
  ["ExchangeRequired", "要求換貨"],
  ["ReplenishRequired", "要求補件"],
  ["ConcessionAccepted", "讓步接收"],
  ["WriteOff", "報廢處理"],
] as const;

const purchaseRmaStatusOptions = [
  ["None", "無退換貨"],
  ["PendingSupplier", "待供應商回覆"],
  ["ReturnPending", "退貨待寄回"],
  ["ReturnShipped", "已退回供應商"],
  ["ExchangePending", "換貨待回"],
  ["SupplementPending", "補件待回"],
  ["CreditPending", "折讓待確認"],
  ["WriteOffPending", "報廢待確認"],
  ["Closed", "已結案"],
] as const;

function defaultPurchaseInspectionChecklist(): Record<string, boolean> {
  return Object.fromEntries(purchaseInspectionChecks.map(([key]) => [key, true]));
}

function canShipOutbound(row: AdminErpOutboundOrder) {
  return ["Draft", "Picked"].includes(row.Status);
}

function canArriveOutbound(row: AdminErpOutboundOrder) {
  return row.Status === "Shipped";
}

function checklistSummary(checklist?: Record<string, boolean> | null) {
  const values = Object.values(checklist || {});
  if (values.length === 0) return "尚未建立";
  return `${values.filter(Boolean).length} / ${values.length} 項完成`;
}

function canShipTransfer(row: ErpStockTransferOrder) {
  return row.Status === "Draft";
}

function canReceiveTransfer(row: ErpStockTransferOrder) {
  return row.Status === "Shipped";
}

function printPurchaseOrder(row: AdminErpPurchaseOrder) {
  const supplier = row.SupplierName || row.SupplierMasterName || "-";
  openPrintDocument(documentHtml({
    title: "採購單",
    subtitle: "供應商採購與內部留存單據",
    number: row.PurchaseNumber,
    status: row.StatusLabel,
    meta: [
      ["供應商", supplier],
      ["供應商代碼", row.SupplierCode || "-"],
      ["外部採購單號", row.ExternalOrderNumber || "-"],
      ["來源門市訂單", row.StoreOrderNumber || "一般採購"],
      ["來源訂單性質", purchaseSourceAuditSummary(row) || "-"],
      ["幣別", row.Currency || "TWD"],
      ["匯率", row.ExchangeRate || 1],
      ["預計到貨", row.ExpectedArrivalDate || "-"],
      ["建立人", row.CreatedByAdminName || "-"],
    ],
    columns: ["商品", "SKU / 供應商料號", "規格", "單位", "數量", "單價", "小計"],
    rows: (row.Items || []).map((item) => [
      item.ProductName,
      `${item.Sku || "-"}\n${item.SupplierSku || "-"}`,
      item.VariantLabel || "單一規格",
      item.PurchaseUnit || "台",
      item.Quantity,
      plainMoney(item.UnitCost),
      plainMoney(item.Quantity * item.UnitCost),
    ]),
    note: row.Note || "-",
    signatures: ["採購經辦", "主管覆核", "供應商確認"],
  }));
}

function printPurchaseReceipt(row: AdminErpPurchaseOrder) {
  openPrintDocument(documentHtml({
    title: "採購入庫驗收單",
    subtitle: "到貨暫收、品質驗收、合格入庫與異常處理單據",
    number: row.PurchaseNumber,
    status: row.StatusLabel,
    meta: [
      ["供應商", row.SupplierName || row.SupplierMasterName || "-"],
      ["來源採購單", row.PurchaseNumber],
      ["來源訂單性質", purchaseSourceAuditSummary(row) || "-"],
      ["最近驗收", row.ReceivedAt || row.Receipts?.[0]?.ReceivedAt || "-"],
      ["入庫倉庫", row.Receipts?.[0]?.WarehouseName || "-"],
      ["採購總數", row.QuantityTotal],
      ["合格入庫", row.ReceivedQuantityTotal || 0],
      ["最近異常", row.Receipts?.[0]?.RejectedQuantity || 0],
      ["預計到貨", row.ExpectedArrivalDate || "-"],
      ["建立人", row.CreatedByAdminName || "-"],
    ],
    columns: ["商品", "SKU / 供應商料號", "規格", "單位", "採購數量", "已收數量", "待收數量"],
    rows: (row.Items || []).map((item) => [
      item.ProductName,
      `${item.Sku || "-"}\n${item.SupplierSku || "-"}`,
      item.VariantLabel || "單一規格",
      item.PurchaseUnit || "台",
      item.Quantity,
      item.ReceivedQuantity,
      item.RemainingQuantity,
    ]),
    note: [
      row.Note ? `採購備註：${row.Note}` : "",
      ...(row.Receipts || []).map((receipt) => `${receipt.ReceiptNumber} / ${receipt.WarehouseName || "-"} / ${receipt.ReceivedAt} / 到貨 ${receipt.ArrivedQuantity ?? receipt.QuantityTotal} / 合格 ${receipt.AcceptedQuantity ?? receipt.QuantityTotal} / 異常 ${receipt.RejectedQuantity ?? 0}${receipt.InspectionStatusLabel ? ` / ${receipt.InspectionStatusLabel}` : ""}${receipt.ExceptionSummary ? ` / ${receipt.ExceptionSummary}` : ""}${receipt.Note ? ` / ${receipt.Note}` : ""}`),
    ].filter(Boolean).join("\n") || "-",
    signatures: ["收貨人員", "驗收人員", "主管覆核"],
  }));
}

function printOutboundOrder(row: AdminErpOutboundOrder) {
  openPrintDocument(documentHtml({
    title: "出庫單 / 撿貨單",
    subtitle: "倉庫撿貨、序號確認與門市交接單據",
    number: row.OutboundNumber,
    status: row.StatusLabel,
    meta: [
      ["來源門市訂單", row.StoreOrderNumber || "-"],
      ["來源訂單性質", storeOrderAuditSummary(row) || "-"],
      ["出庫倉庫", row.FromWarehouseName || "-"],
      ["目的門市", row.ToLocationName || "-"],
      ["建立人", row.CreatedByAdminName || "-"],
      ["出庫時間", row.ShippedAt || "-"],
      ["到店時間", row.ArrivedAt || "-"],
      ["品項數", row.ItemCount],
      ["總數量", row.QuantityTotal],
    ],
    columns: ["商品", "SKU", "規格", "序號", "耳側", "數量", "備註"],
    rows: (row.Items || []).map((item) => [
      item.ProductName,
      item.Sku || "-",
      item.VariantLabel || "單一規格",
      item.SerialNumber || "-",
      item.EarSideLabel || "-",
      item.Quantity,
      item.Note || "-",
    ]),
    note: row.Note || "-",
    signatures: ["撿貨人員", "出庫覆核", "門市簽收"],
  }));
}

function printOutboundProcessDocument(row: AdminErpOutboundOrder, kind: "picking" | "packing" | "logistics" | "store-sign") {
  const config = {
    picking: {
      title: "揀貨單",
      subtitle: "倉庫依門市訂單揀貨、序號挑選與數量核對",
      columns: ["商品", "SKU", "規格", "序號", "耳側", "需求數量", "揀貨檢核"],
      rows: (row.Items || []).map((item) => [item.ProductName, item.Sku || "-", item.VariantLabel || "單一規格", item.SerialNumber || "-", item.EarSideLabel || "-", item.Quantity, checklistSummary(row.PickingChecklist)]),
      signatures: ["揀貨人員", "覆核人員"],
      note: `揀貨檢核：${checklistSummary(row.PickingChecklist)}${row.Note ? `\n出庫備註：${row.Note}` : ""}`,
    },
    packing: {
      title: "包裝單",
      subtitle: "出庫前包裝、配件、文件與保固資料核對",
      columns: ["商品", "SKU", "規格", "序號", "耳側", "數量", "包裝檢核"],
      rows: (row.Items || []).map((item) => [item.ProductName, item.Sku || "-", item.VariantLabel || "單一規格", item.SerialNumber || "-", item.EarSideLabel || "-", item.Quantity, checklistSummary(row.PackingChecklist)]),
      signatures: ["包裝人員", "出庫覆核"],
      note: `包裝檢核：${checklistSummary(row.PackingChecklist)}${row.LogisticsNote ? `\n物流備註：${row.LogisticsNote}` : ""}`,
    },
    logistics: {
      title: "物流交接單",
      subtitle: "出庫交運、物流單號與門市到貨追蹤",
      columns: ["商品", "SKU", "規格", "序號", "數量", "物流公司", "物流單號"],
      rows: (row.Items || []).map((item) => [item.ProductName, item.Sku || "-", item.VariantLabel || "單一規格", item.SerialNumber || "-", item.Quantity, row.LogisticsCarrier || "-", row.TrackingNumber || "-"]),
      signatures: ["出庫交接", "物流收件", "門市收件"],
      note: row.LogisticsNote || "-",
    },
    "store-sign": {
      title: "門市簽收單",
      subtitle: "門市確認到貨、數量、序號與包裝狀態",
      columns: ["商品", "SKU", "規格", "序號", "耳側", "數量", "簽收備註"],
      rows: (row.Items || []).map((item) => [item.ProductName, item.Sku || "-", item.VariantLabel || "單一規格", item.SerialNumber || "-", item.EarSideLabel || "-", item.Quantity, row.StoreSignedNote || "-"]),
      signatures: ["門市簽收", "工程覆核", "主管確認"],
      note: row.StoreSignedAt ? `簽收時間：${row.StoreSignedAt}${row.StoreSignedNote ? `\n簽收備註：${row.StoreSignedNote}` : ""}` : "尚未簽收，請門市收貨後確認品項、序號、配件與包裝狀態。",
    },
  }[kind];

  openPrintDocument(documentHtml({
    title: config.title,
    subtitle: config.subtitle,
    number: row.OutboundNumber,
    status: row.StatusLabel,
    meta: [
      ["來源門市訂單", row.StoreOrderNumber || "-"],
      ["來源訂單性質", storeOrderAuditSummary(row) || "-"],
      ["出庫倉庫", row.FromWarehouseName || "-"],
      ["目的門市", row.ToLocationName || "-"],
      ["建立人", row.CreatedByAdminName || "-"],
      ["出庫時間", row.ShippedAt || "-"],
      ["到店時間", row.ArrivedAt || "-"],
      ["物流公司", row.LogisticsCarrier || "-"],
      ["物流單號", row.TrackingNumber || "-"],
    ],
    columns: config.columns,
    rows: config.rows,
    note: config.note,
    signatures: config.signatures,
  }));
}

function printReceiptExceptionDocument(row: ErpPurchaseReceiptRecord, mode: "exception" | "return-exchange") {
  const isReturnExchange = mode === "return-exchange";
  openPrintDocument(documentHtml({
    title: isReturnExchange ? "退貨 / 換貨處理單" : "採購驗收異常單",
    subtitle: isReturnExchange ? "供應商退貨、換貨、補件與異常結案追蹤" : "到貨驗收異常、隔離、主管判定與處理依據",
    number: row.ReceiptNumber,
    status: row.ExceptionStatusLabel || row.InspectionStatusLabel,
    meta: [
      ["採購單", row.PurchaseNumber || "-"],
      ["供應商", row.SupplierName || "-"],
      ["入庫倉庫", row.WarehouseName || "-"],
      ["驗收狀態", row.InspectionStatusLabel || "-"],
      ["異常狀態", row.ExceptionStatusLabel || "-"],
      ["到貨 / 合格 / 異常", `${row.ArrivedQuantity} / ${row.AcceptedQuantity} / ${row.RejectedQuantity}`],
      ["處理方式", row.ResolutionActionLabel || "-"],
      ["RMA 狀態", row.RmaStatusLabel || "-"],
      ["RMA / 退貨單號", row.RmaNumber || "-"],
      ["退貨物流單號", row.ReturnTrackingNumber || "-"],
      ["換貨 / 補件預計到貨", row.ReplacementExpectedDate || "-"],
      ["折讓 / 扣款", row.CreditAmount ? plainMoney(row.CreditAmount) : "-"],
      ["結案時間", row.ResolvedAt || "-"],
    ],
    columns: ["商品", "SKU", "規格", "序號", "到貨", "異常", "異常 / 處理"],
    rows: (row.Items || []).map((item) => [
      item.ProductName,
      item.Sku || "-",
      item.VariantLabel || "單一規格",
      item.SerialNumber || "-",
      item.ArrivedQuantity,
      item.RejectedQuantity,
      `${item.ExceptionTypeLabel || "-"} / ${item.ResolutionActionLabel || "-"}`,
    ]),
    note: [row.ExceptionSummary, row.ReleaseNote, row.SupplierContactNote, row.ResolutionNote].filter(Boolean).join("\n") || "-",
    signatures: isReturnExchange ? ["ERP 經辦", "供應商窗口", "主管覆核"] : ["驗收人員", "工程覆核", "主管判定"],
  }));
}

function printPayableStatement(items: ErpSupplierPayable[], counts: Record<string, number>, dateFrom: string, dateTo: string) {
  openPrintDocument(documentHtml({
    title: "供應商應付對帳單",
    subtitle: "依查詢條件彙整請款、付款、未付餘額與帳齡狀態",
    number: `AP-STMT-${dateFrom}_${dateTo}`,
    status: `未付 ${plainMoney(counts.BalanceAmount || 0)}`,
    meta: [
      ["對帳起日", dateFrom],
      ["對帳迄日", dateTo],
      ["請款總數", counts.Total || 0],
      ["待付款", counts.Approved || 0],
      ["逾期筆數", counts.Overdue || 0],
      ["7日內到期", counts.DueSoon || 0],
      ["未付餘額", plainMoney(counts.BalanceAmount || 0)],
      ["列印日期", todayText()],
    ],
    columns: ["請款單", "供應商", "採購 / 驗收", "發票", "到期日", "帳齡", "未付餘額"],
    rows: items.map((item) => [
      item.PayableNumber,
      item.SupplierName,
      `${item.PurchaseNumber || "-"}\n${item.ReceiptNumber || "-"}`,
      `${item.InvoiceNumber || "-"}\n${item.InvoiceDate || "-"}`,
      item.DueDate || "-",
      item.AgingBucketLabel || "未逾期",
      plainMoney(item.BalanceAmount),
    ]),
    note: `逾期區間：1-30 天 ${counts.Aging1To30 || 0} 筆；31-60 天 ${counts.Aging31To60 || 0} 筆；61-90 天 ${counts.Aging61To90 || 0} 筆；90 天以上 ${counts.AgingOver90 || 0} 筆。\n付款批次/交易編號請以付款紀錄為準，作廢付款需留存原因。`,
    signatures: ["財務製表", "採購覆核", "主管確認"],
  }));
}

function printTransferOrder(row: ErpStockTransferOrder) {
  openPrintDocument(documentHtml({
    title: "庫存調撥單",
    subtitle: "倉庫間庫存移轉、出庫與入庫簽收單據",
    number: row.TransferNumber,
    status: row.StatusLabel,
    meta: [
      ["調撥用途", row.TransferPurposeLabel || "一般倉庫調撥"],
      ["來源倉庫", row.FromWarehouseName],
      ["目的倉庫", row.ToWarehouseName],
      ["建立人", row.CreatedByAdminName || "-"],
      ["建立時間", row.CreatedAt || "-"],
      ["出庫時間", row.ShippedAt || "-"],
      ["入庫時間", row.ReceivedAt || "-"],
      ["品項數", row.ItemCount],
      ["總數量", row.QuantityTotal],
    ],
    columns: ["商品", "SKU", "規格", "序號清單", "數量", "單位成本", "備註"],
    rows: (row.Items || []).map((item) => [
      item.ProductName,
      item.Sku || "-",
      item.VariantLabel || "單一規格",
      item.SerialText || "-",
      item.Quantity,
      plainMoney(item.UnitCost),
      item.Note || "-",
    ]),
    note: row.Note || "-",
    signatures: ["調出經辦", "調出覆核", "調入簽收"],
  }));
}

function printStockAdjustmentOrder(row: ErpStockAdjustmentOrder) {
  const isInventoryCount = row.IsReviewRequired && ["AdjustmentIn", "AdjustmentOut"].includes(row.AdjustmentType);
  openPrintDocument(documentHtml({
    title: isInventoryCount ? "正式盤點單" : "庫存調整單",
    subtitle: isInventoryCount ? "盤點差異、主管覆核與庫存帳務異動留存單據" : "退貨入庫、損耗報廢與庫存修正留存單據",
    number: row.AdjustmentNumber,
    status: row.StatusLabel,
    meta: [
      ["調整倉庫", row.WarehouseName || "-"],
      ["調整類型", row.AdjustmentTypeLabel],
      ["調整日期", row.AdjustmentDate],
      ["調整原因", row.Reason || "-"],
      ["建立人", row.CreatedByAdminName || "-"],
      ["建立時間", row.CreatedAt || "-"],
      ["覆核人", row.ReviewedByAdminName || "-"],
      ["覆核時間", row.ReviewedAt || "-"],
      ["品項數", row.ItemCount],
      ["入庫數量", row.QuantityIn],
      ["扣庫數量", row.QuantityOut],
      ["覆核需求", row.IsReviewRequired ? "需要主管覆核" : "不需主管覆核"],
    ],
    columns: ["商品", "SKU", "規格", "耳側", "序號 / 現貨", "異動數量", "調整前 / 後", "單位成本", "備註"],
    rows: (row.Items || []).map((item) => [
      item.ProductName,
      item.Sku || "-",
      item.VariantLabel || "單一規格",
      item.EarSideLabel || "未指定",
      item.SerialText || "-",
      `${item.QuantityDelta > 0 ? "+" : ""}${item.QuantityDelta}`,
      `${item.BeforeQuantity} / ${item.AfterQuantity}`,
      plainMoney(item.UnitCost),
      item.Note || "-",
    ]),
    note: [
      row.Note ? `內部備註：${row.Note}` : "",
      row.ReviewNote ? `覆核備註：${row.ReviewNote}` : "",
    ].filter(Boolean).join("\n") || "-",
    signatures: ["建立經辦", "倉庫確認", "主管覆核"],
  }));
}

function EmptyState({ text }: { text: string }) {
  return <div className="rounded-md border border-dashed border-[#cfd8d2] bg-[#fbfcfb] px-4 py-10 text-center font-semibold text-[#7b877f]">{text}</div>;
}

function Message({ text, tone = "danger" }: { text: string; tone?: "danger" | "success" }) {
  const className = tone === "success" ? "border-[#acd7bb] bg-[#eff8f1] text-[#245438]" : "border-[#f2b8b5] bg-[#fdebea] text-[#8a1f1f]";
  return <div className={`rounded-md border px-4 py-3 text-sm font-bold ${className}`}>{text}</div>;
}

function ToastNotice({ text, tone = "danger", onClose }: { text: string; tone?: "danger" | "success"; onClose: () => void }) {
  useEffect(() => {
    if (!text) return undefined;
    const timer = window.setTimeout(onClose, tone === "success" ? 3200 : 5200);
    return () => window.clearTimeout(timer);
  }, [onClose, text, tone]);

  const toneClass = tone === "success" ? "border-[#acd7bb] bg-[#eff8f1] text-[#245438]" : "border-[#f2b8b5] bg-[#fdebea] text-[#8a1f1f]";
  const label = tone === "success" ? "成功" : "提醒";
  return (
    <div className={`pointer-events-auto flex w-full items-start justify-between gap-3 rounded-md border px-4 py-3 shadow-[0_18px_45px_rgba(32,61,50,.18)] ${toneClass}`} role="alert">
      <div className="min-w-0">
        <p className="text-xs font-extrabold">{label}</p>
        <p className="mt-1 break-words text-sm font-bold leading-6">{text}</p>
      </div>
      <button className="inline-flex h-8 min-h-8 w-8 shrink-0 items-center justify-center rounded-md border border-current/20 bg-white/60 px-0 text-lg leading-none" onClick={onClose} type="button" aria-label="關閉提醒">×</button>
    </div>
  );
}

function ToastAlerts({ message, success, onClearMessage, onClearSuccess }: { message?: string; success?: string; onClearMessage?: () => void; onClearSuccess?: () => void }) {
  if (!message && !success) return null;
  return (
    <div className="pointer-events-none fixed right-4 top-4 z-[120] grid w-[min(440px,calc(100vw-2rem))] gap-2">
      {success ? <ToastNotice text={success} tone="success" onClose={onClearSuccess || (() => undefined)} /> : null}
      {message ? <ToastNotice text={message} tone="danger" onClose={onClearMessage || (() => undefined)} /> : null}
    </div>
  );
}

function ErpTextActionDialog({
  open,
  title,
  description,
  label,
  placeholder,
  value,
  required = false,
  confirmText,
  tone = "default",
  loading = false,
  error = "",
  onChange,
  onClose,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  label: string;
  placeholder?: string;
  value: string;
  required?: boolean;
  confirmText: string;
  tone?: "default" | "danger";
  loading?: boolean;
  error?: string;
  onChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
}) {
  if (!open) return null;
  const confirmClass = tone === "danger" ? "bg-[#a6332f] text-white hover:bg-[#842622]" : "bg-[#315746] text-white hover:bg-[#203d32]";
  return (
    <div className="fixed inset-0 z-[140] flex items-end justify-center bg-[#10231b]/45 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="erp-action-dialog-title">
      <div className="max-h-[92vh] w-full overflow-hidden rounded-t-md border border-[#dfe6e1] bg-white shadow-[0_24px_80px_rgba(16,35,27,.28)] sm:max-w-[640px] sm:rounded-md">
        <div className="flex items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs font-extrabold uppercase tracking-[.22em] text-[#8a978f]">ERP ACTION</p>
            <h2 id="erp-action-dialog-title" className="mt-1 break-words text-xl font-extrabold text-[#203d32]">{title}</h2>
          </div>
          <button className="inline-flex h-10 min-h-10 w-10 shrink-0 items-center justify-center rounded-md border border-[#cfd8d2] bg-white text-xl font-bold text-[#315746]" disabled={loading} onClick={onClose} type="button" aria-label="關閉">×</button>
        </div>
        <div className="grid max-h-[calc(92vh-150px)] gap-4 overflow-y-auto px-5 py-4">
          <p className="break-words text-sm font-semibold leading-6 text-[#59665f]">{description}</p>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">{label}{required ? <span className="text-[#a6332f]"> *</span> : null}</span>
            <textarea
              className={`min-h-[128px] w-full resize-y rounded-md border px-3 py-3 text-base leading-7 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15 ${error ? "border-[#d65c57] bg-[#fff8f7]" : "border-[#cfd8d2]"}`}
              disabled={loading}
              placeholder={placeholder}
              value={value}
              onChange={(event) => onChange(event.target.value)}
            />
            {error ? <p className="mt-2 text-sm font-bold text-[#a6332f]">{error}</p> : null}
          </label>
        </div>
        <div className="grid gap-2 border-t border-[#edf0ee] bg-[#fbfcfb] px-5 py-4 sm:flex sm:justify-end">
          <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] disabled:opacity-60" disabled={loading} onClick={onClose} type="button">取消</button>
          <button className={`inline-flex min-h-11 items-center justify-center gap-2 rounded-md px-5 font-bold transition disabled:opacity-60 ${confirmClass}`} aria-busy={loading ? "true" : "false"} disabled={loading} onClick={onConfirm} type="button">{loading ? "處理中" : confirmText}</button>
        </div>
      </div>
    </div>
  );
}

type FormErrors = Record<string, string>;

const fieldBaseClass = "rounded-md border border-[#cfd8d2] outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";
const fieldErrorClass = "border-[#d65c57] bg-[#fff8f7] focus:border-[#a6332f] focus:ring-[#d65c57]/20";

function fieldClass(errors: FormErrors, key: string, className = "") {
  return `${fieldBaseClass} ${errors[key] ? fieldErrorClass : ""} ${className}`.trim();
}

function FieldError({ errors, name }: { errors: FormErrors; name: string }) {
  return errors[name] ? <p className="mt-1 text-xs font-bold leading-5 text-[#a6332f]">{errors[name]}</p> : null;
}

function firstErrorMessage(errors: FormErrors) {
  return Object.values(errors).find(Boolean) || "";
}

function scrollToFirstError(errors: FormErrors) {
  const firstKey = Object.keys(errors).find((key) => errors[key]);
  if (!firstKey) return;
  window.setTimeout(() => {
    const target = document.querySelector(`[data-field="${CSS.escape(firstKey)}"]`);
    target?.scrollIntoView({ behavior: "smooth", block: "center" });
    const input = target?.querySelector("input, select, textarea, button") as HTMLElement | null;
    input?.focus?.();
  }, 30);
}

function applySubmitErrors(errors: FormErrors, setErrors: (errors: FormErrors) => void, setMessage: (message: string) => void) {
  setErrors(errors);
  setMessage(firstErrorMessage(errors) || "請確認表單欄位。");
  scrollToFirstError(errors);
}

function apiErrors(error: unknown) {
  return error instanceof ErpApiError ? error.errors : {};
}

function errorText(error: unknown, fallback: string) {
  return error instanceof Error ? error.message : fallback;
}

function MetricCard({ icon, label, value, tone = "default" }: { icon: ReactNode; label: string; value: string; tone?: "default" | "warning" | "danger" }) {
  const iconClass = tone === "danger" ? "bg-[#fdebea] text-[#8a1f1f]" : tone === "warning" ? "bg-[#fff5df] text-[#8a5b14]" : "bg-[#edf5f0] text-[#315746]";
  return (
    <div className="min-h-[118px] rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-extrabold leading-6 text-[#69776f]">{label}</p>
          <p className="mt-3 whitespace-nowrap text-2xl font-extrabold leading-none text-[#203d32]">{value}</p>
        </div>
        <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${iconClass}`}>{icon}</span>
      </div>
    </div>
  );
}

function FilterForm({
  keyword,
  loading,
  onKeyword,
  onReset,
  onSearch,
  onStatus,
  status,
  statusLabel = "狀態",
  statusOptions,
}: {
  keyword: string;
  loading?: boolean;
  onKeyword: (value: string) => void;
  onReset: () => void;
  onSearch: () => void;
  onStatus?: (value: string) => void;
  status: string;
  statusLabel?: string;
  statusOptions?: AdminStoreSalesOrderOption[];
}) {
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    onSearch();
  }

  return (
    <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={submit}>
      <div className="grid gap-3 xl:grid-cols-[minmax(260px,1fr)_220px_300px] xl:items-end">
        <label className="min-w-0">
          <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
          <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="單號、商品、SKU" value={keyword} onChange={(event) => onKeyword(event.target.value)} />
        </label>
        {statusOptions && onStatus ? (
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">{statusLabel}</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => onStatus(event.target.value)}>
              <option value="">全部</option>
              {statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
        ) : null}
        <div className="rwd-filter-actions xl:col-span-3">
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:opacity-60" disabled={loading} type="submit">
            <Search size={18} />
            查詢
          </button>
          <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60" disabled={loading} onClick={onReset} type="button">
            <RefreshCw size={18} />
            重整
          </button>
        </div>
      </div>
    </form>
  );
}

function Pagination({ disabled, pagination, onChange }: { disabled?: boolean; pagination: PaginationMeta; onChange: (page: number) => void }) {
  return (
    <div className="flex flex-col gap-3 border-t border-[#edf0ee] px-4 py-4 sm:flex-row sm:items-center sm:justify-between">
      <p className="text-sm font-bold text-[#59665f]">共 {pagination.Total.toLocaleString("zh-TW")} 筆</p>
      <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:flex">
        <button className="h-10 rounded-md border border-[#dfe6e1] bg-white px-4 font-bold text-[#315746] disabled:opacity-45" disabled={disabled || pagination.Page <= 1} onClick={() => onChange(pagination.Page - 1)} type="button">上一頁</button>
        <span className="px-3 text-center font-extrabold text-[#203d32]">{pagination.Page} / {pagination.TotalPages}</span>
        <button className="h-10 rounded-md border border-[#dfe6e1] bg-white px-4 font-bold text-[#315746] disabled:opacity-45" disabled={disabled || pagination.Page >= pagination.TotalPages} onClick={() => onChange(pagination.Page + 1)} type="button">下一頁</button>
      </div>
    </div>
  );
}

function InventoryView({ mode = "list" }: { mode?: "list" | "detail" }) {
  const navigate = useNavigate();
  const { productId } = useParams();
  const [searchParams] = useSearchParams();
  const isDetailMode = mode === "detail";
  const [keyword, setKeyword] = useState("");
  const [stockLevel, setStockLevel] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [source, setSource] = useState("");
  const [alert, setAlert] = useState("");
  const [items, setItems] = useState<AdminErpInventoryItem[]>([]);
  const [counts, setCounts] = useState({ Total: 0, Available: 0, Low: 0, Out: 0, Reorder: 0, Incoming: 0, Reserved: 0, PendingInspection: 0, Quarantined: 0, StockValue: 0 });
  const [pagination, setPagination] = useState(emptyPagination);
  const [options, setOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [sourceOptions, setSourceOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [alertOptions, setAlertOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [warehouses, setWarehouses] = useState<AdminErpWarehouse[]>([]);
  const [detail, setDetail] = useState<ErpInventoryDetail | null>(null);
  const [policyForm, setPolicyForm] = useState({ SafetyStock: "5", ReorderPoint: "5", MaxStock: "0", LeadTimeDays: "0", Note: "" });
  const [policySaving, setPolicySaving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load(page = 1, nextKeyword = keyword, nextStockLevel = stockLevel, nextWarehouseId = warehouseId, nextSource = source, nextAlert = alert) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpInventory({ Keyword: nextKeyword.trim(), StockLevel: nextStockLevel, ErpWarehouseId: nextWarehouseId, Source: nextSource, Alert: nextAlert, Page: page, PerPage: 20 });
      setItems(data.Items);
      setCounts({
        Total: data.Counts.Total,
        Available: data.Counts.Available,
        Low: data.Counts.Low,
        Out: data.Counts.Out,
        Reorder: data.Counts.Reorder || 0,
        Incoming: data.Counts.Incoming || 0,
        Reserved: data.Counts.Reserved || 0,
        PendingInspection: data.Counts.PendingInspection || 0,
        Quarantined: data.Counts.Quarantined || 0,
        StockValue: data.Counts.StockValue || 0,
      });
      setPagination(data.Pagination);
      setOptions(data.StockLevelOptions);
      setSourceOptions(data.SourceOptions || []);
      setAlertOptions(data.AlertOptions || []);
      setWarehouses(data.Warehouses || []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "庫存總覽載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!isDetailMode) void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailMode]);

  useEffect(() => {
    if (!isDetailMode || !productId) return;
    setLoading(true);
    setMessage("");
    getErpInventoryDetail(productId, searchParams.get("VariantId"))
      .then((data) => {
        setDetail(data);
        setPolicyForm({
          SafetyStock: String(data.Policy?.SafetyStock ?? 5),
          ReorderPoint: String(data.Policy?.ReorderPoint ?? 5),
          MaxStock: String(data.Policy?.MaxStock ?? 0),
          LeadTimeDays: String(data.Policy?.LeadTimeDays ?? 0),
          Note: data.Policy?.Note || "",
        });
      })
      .catch((error) => setMessage(errorText(error, "庫存詳情載入失敗。")))
      .finally(() => setLoading(false));
  }, [isDetailMode, productId, searchParams]);

  function reset() {
    setKeyword("");
    setStockLevel("");
    setWarehouseId("");
    setSource("");
    setAlert("");
    void load(1, "", "", "", "", "");
  }

  async function handleExportInventory() {
    setMessage("");
    try {
      await exportErpInventory({ Keyword: keyword.trim(), StockLevel: stockLevel, ErpWarehouseId: warehouseId, Source: source, Alert: alert });
    } catch (error) {
      setMessage(errorText(error, "庫存總覽匯出失敗。"));
    }
  }

  function openDetail(item: AdminErpInventoryItem) {
    navigate(`/erp/inventory/${item.ProductId}${item.VariantId ? `?VariantId=${item.VariantId}` : ""}`);
  }

  async function savePolicy(event: FormEvent) {
    event.preventDefault();
    if (!productId || !detail?.Item) return;
    setPolicySaving(true);
    setMessage("");
    try {
      const data = await updateErpInventoryPolicy(productId, {
        VariantId: detail.Item.VariantId || null,
        SafetyStock: Number(policyForm.SafetyStock || 0),
        ReorderPoint: Number(policyForm.ReorderPoint || 0),
        MaxStock: Number(policyForm.MaxStock || 0),
        LeadTimeDays: Number(policyForm.LeadTimeDays || 0),
        Note: policyForm.Note,
        IsActive: true,
      });
      setDetail((previous) => previous ? { ...previous, Item: data.Item || previous.Item, Policy: data.Policy, WarehouseStocks: data.WarehouseStocks } : previous);
      setPolicyForm({
        SafetyStock: String(data.Policy.SafetyStock ?? 0),
        ReorderPoint: String(data.Policy.ReorderPoint ?? 0),
        MaxStock: String(data.Policy.MaxStock ?? 0),
        LeadTimeDays: String(data.Policy.LeadTimeDays ?? 0),
        Note: data.Policy.Note || "",
      });
      setMessage("庫存策略已儲存。");
    } catch (error) {
      setMessage(errorText(error, "庫存策略儲存失敗。"));
    } finally {
      setPolicySaving(false);
    }
  }

  if (isDetailMode) {
    const item = detail?.Item;
    return (
      <div className="grid gap-5">
        <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
        <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-extrabold tracking-[.18em] text-[#7b877f]">INVENTORY DETAIL</p>
              <h2 className="mt-1 break-words text-2xl font-extrabold text-[#203d32]">{item ? item.ProductName : loading ? "載入中..." : "庫存詳情"}</h2>
              <p className="mt-1 break-words text-sm font-bold text-[#59665f]">{item ? `${item.VariantLabel || "單一規格"} / ${item.Sku}` : "查看各倉庫、序號、異動與未結單據。"}</p>
            </div>
            <div className="rwd-action-grid lg:min-w-[420px]">
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/inventory")} type="button">返回庫存總覽</button>
              {item ? <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white" onClick={() => navigate(`/erp/stock-movements?Keyword=${encodeURIComponent(item.Sku)}`)} type="button">查看異動</button> : null}
              {item ? <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white" onClick={() => navigate(`/erp/purchase-orders/new?ProductId=${item.ProductId}${item.VariantId ? `&VariantId=${item.VariantId}` : ""}&Quantity=${Math.max(1, item.SuggestedPurchaseQuantity || 1)}`)} type="button">建立採購單</button> : null}
            </div>
          </div>
        </section>

        {item ? (
          <>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
              <MetricCard icon={<Warehouse size={22} />} label="可用庫存" value={count(item.AvailableStock)} tone={item.IsOutOfStock ? "danger" : item.IsLowStock ? "warning" : "default"} />
              <MetricCard icon={<Package size={22} />} label="現貨 / 保留" value={`${item.ErpInStock} / ${item.ErpReserved}`} tone={(item.ErpReserved || 0) > 0 ? "warning" : "default"} />
              <MetricCard icon={<ShieldCheck size={22} />} label="待驗 / 隔離" value={`${item.PendingInspectionStock || 0} / ${item.QuarantinedStock || 0}`} tone={(item.QuarantinedStock || 0) > 0 ? "danger" : (item.PendingInspectionStock || 0) > 0 ? "warning" : "default"} />
              <MetricCard icon={<ClipboardList size={22} />} label="採購在途 / 調撥在途" value={`${item.PurchaseIncoming || 0} / ${item.TransferIncoming || 0}`} />
              <MetricCard icon={<AlertTriangle size={22} />} label="建議採購" value={count(item.SuggestedPurchaseQuantity || 0)} tone={item.NeedsReorder ? "warning" : "default"} />
            </div>

            <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
              <div className="flex flex-col gap-2 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h2 className="text-base font-extrabold text-[#203d32]">庫存策略設定</h2>
                  <p className="mt-1 text-sm font-bold text-[#7b877f]">用來判斷低庫存、需補貨、建議採購量與庫存預警。</p>
                </div>
                <InfoBox label="庫存金額" value={money(item.StockValue || 0)} />
                <InfoBox label="補貨建議來源" value={item.ReorderSuggestionSource || "-"} />
              </div>
              <form className="mt-4 grid gap-3" onSubmit={savePolicy}>
                <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <label className="grid gap-1 text-sm font-extrabold text-[#59665f]">
                    安全庫存
                    <input className="min-h-12 rounded-md border border-[#cfd8d2] px-4 text-base font-bold text-[#203d32]" min="0" type="number" value={policyForm.SafetyStock} onChange={(event) => setPolicyForm((form) => ({ ...form, SafetyStock: event.target.value }))} />
                  </label>
                  <label className="grid gap-1 text-sm font-extrabold text-[#59665f]">
                    補貨點
                    <input className="min-h-12 rounded-md border border-[#cfd8d2] px-4 text-base font-bold text-[#203d32]" min="0" type="number" value={policyForm.ReorderPoint} onChange={(event) => setPolicyForm((form) => ({ ...form, ReorderPoint: event.target.value }))} />
                  </label>
                  <label className="grid gap-1 text-sm font-extrabold text-[#59665f]">
                    最高庫存
                    <input className="min-h-12 rounded-md border border-[#cfd8d2] px-4 text-base font-bold text-[#203d32]" min="0" type="number" value={policyForm.MaxStock} onChange={(event) => setPolicyForm((form) => ({ ...form, MaxStock: event.target.value }))} />
                  </label>
                  <label className="grid gap-1 text-sm font-extrabold text-[#59665f]">
                    採購前置天數
                    <input className="min-h-12 rounded-md border border-[#cfd8d2] px-4 text-base font-bold text-[#203d32]" min="0" type="number" value={policyForm.LeadTimeDays} onChange={(event) => setPolicyForm((form) => ({ ...form, LeadTimeDays: event.target.value }))} />
                  </label>
                </div>
                <label className="grid gap-1 text-sm font-extrabold text-[#59665f]">
                  策略備註
                  <textarea className="min-h-24 rounded-md border border-[#cfd8d2] px-4 py-3 text-base font-bold text-[#203d32]" placeholder="例如：此品項屬常用耗材，台北倉補貨點建議提高。" value={policyForm.Note} onChange={(event) => setPolicyForm((form) => ({ ...form, Note: event.target.value }))} />
                </label>
                <div className="rwd-action-grid lg:ml-auto lg:max-w-[220px]">
                  <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:bg-[#9aa8a0]" disabled={policySaving} type="submit">{policySaving ? "儲存中" : "儲存策略"}</button>
                </div>
              </form>
            </section>

            <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
              <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
                <h2 className="text-base font-extrabold text-[#203d32]">各倉庫庫存</h2>
                <span className="text-sm font-bold text-[#7b877f]">{count(detail?.WarehouseStocks.length || 0)}</span>
              </div>
              <div className="overflow-x-auto px-4 pb-4">
                <table className="w-full min-w-[1180px] table-fixed text-left text-sm">
                  <thead className="bg-[#f3f7f4] text-[#59665f]"><tr><th className="w-[180px] px-4 py-3">倉庫</th><th className="w-[90px] px-4 py-3 text-right">現貨</th><th className="w-[90px] px-4 py-3 text-right">保留</th><th className="w-[90px] px-4 py-3 text-right">待驗</th><th className="w-[90px] px-4 py-3 text-right">隔離</th><th className="w-[100px] px-4 py-3 text-right">可用</th><th className="w-[120px] px-4 py-3 text-right">採購在途</th><th className="w-[120px] px-4 py-3 text-right">調撥在途</th><th className="w-[110px] px-4 py-3 text-right">補貨點</th><th className="w-[130px] px-4 py-3 text-right">庫存金額</th><th className="w-[120px] px-4 py-3">狀態</th></tr></thead>
                  <tbody className="divide-y divide-[#edf0ee]">
                    {(detail?.WarehouseStocks || []).map((row) => (
                      <tr key={row.ErpWarehouseId}>
                        <td className="break-words px-4 py-4 font-extrabold text-[#203d32]">{row.WarehouseName}<p className="mt-1 text-xs text-[#7b877f]">{row.WarehouseTypeLabel}</p></td>
                        <td className="px-4 py-4 text-right font-bold">{row.InStock}</td>
                        <td className="px-4 py-4 text-right">{row.Reserved}</td>
                        <td className="px-4 py-4 text-right">{row.PendingInspectionStock || 0}</td>
                        <td className="px-4 py-4 text-right">{row.QuarantinedStock || 0}</td>
                        <td className="px-4 py-4 text-right font-extrabold text-[#203d32]">{row.AvailableStock}</td>
                        <td className="px-4 py-4 text-right">{row.PurchaseIncoming || 0}</td>
                        <td className="px-4 py-4 text-right">{row.TransferIncoming || 0}</td>
                        <td className="px-4 py-4 text-right">{row.ReorderPoint}</td>
                        <td className="px-4 py-4 text-right">{money(row.StockValue)}</td>
                        <td className="px-4 py-4"><StatusBadge tone={row.NeedsReorder ? "warning" : "default"}>{row.NeedsReorder ? "需補貨" : "正常"}</StatusBadge></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>

            <div className="grid gap-5 xl:grid-cols-2">
              <InventoryMiniTable title="現貨序號 / 批次" empty="目前沒有 ERP 序號或批次資料。" rows={detail?.StockUnits || []} columns={["序號", "倉庫", "狀態", "數量", "成本"]} render={(row) => [row.SerialNumber || "無序號", row.WarehouseName || "-", row.StatusLabel, row.Quantity, money(row.UnitCost)]} />
              <InventoryMiniTable title="最近庫存異動" empty="目前沒有庫存異動。" rows={detail?.Movements || []} columns={["時間", "類型", "倉庫", "異動", "單據"]} render={(row) => [row.CreatedAt, row.MovementTypeLabel, row.WarehouseName || "-", row.QuantityDelta, row.PurchaseNumber || row.OutboundNumber || row.StoreOrderNumber || "-"]} />
            </div>

            <div className="grid gap-5 xl:grid-cols-3">
              <InventoryMiniTable title="未結採購" empty="沒有未結採購。" rows={detail?.OpenPurchaseOrders || []} columns={["單號", "供應商", "待收", "預計到貨"]} render={(row) => [row.Number, row.SupplierName || "-", row.RemainingQuantity || 0, row.ExpectedArrivalDate || "-"]} />
              <InventoryMiniTable title="未結出庫" empty="沒有未結出庫。" rows={detail?.OpenOutboundOrders || []} columns={["單號", "門市", "數量", "狀態"]} render={(row) => [row.Number, row.LocationName || "-", row.Quantity, row.StatusLabel]} />
              <InventoryMiniTable title="在途調撥" empty="沒有在途調撥。" rows={detail?.OpenTransfers || []} columns={["單號", "方向", "數量", "狀態"]} render={(row) => [row.Number, `${row.FromWarehouseName || "-"} → ${row.ToWarehouseName || "-"}`, row.Quantity, row.StatusLabel]} />
            </div>
          </>
        ) : !loading ? <EmptyState text="找不到庫存資料。" /> : null}
      </div>
    );
  }

  return (
    <div className="erp-page-shell grid gap-5">
      <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-[minmax(240px,1.3fr)_170px_180px_160px_160px] 2xl:items-end">
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" placeholder="商品、商品編號、SKU、規格" value={keyword} onChange={(event) => setKeyword(event.target.value)} /></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">倉庫</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}><option value="">全部倉庫</option>{warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">庫存狀態</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={stockLevel} onChange={(event) => setStockLevel(event.target.value)}><option value="">全部</option>{options.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">資料來源</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={source} onChange={(event) => setSource(event.target.value)}><option value="">全部來源</option>{sourceOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">庫存警示</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={alert} onChange={(event) => setAlert(event.target.value)}><option value="">全部</option>{alertOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
        </div>
        <div className="rwd-filter-actions mt-3 justify-end">
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" disabled={loading} onClick={reset} type="button"><RefreshCw size={18} />重整</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" disabled={loading} onClick={handleExportInventory} type="button"><Download size={18} />匯出 Excel</button>
        </div>
      </form>
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <div className="erp-metric-grid">
        <MetricCard icon={<Package size={22} />} label="庫存項目" value={count(counts.Total)} />
        <MetricCard icon={<Warehouse size={22} />} label="有可用庫存" value={count(counts.Available)} />
        <MetricCard icon={<AlertTriangle size={22} />} label="低庫存" value={count(counts.Low)} tone={counts.Low > 0 ? "warning" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="無庫存" value={count(counts.Out)} tone={counts.Out > 0 ? "danger" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="需補貨" value={count(counts.Reorder)} tone={counts.Reorder > 0 ? "warning" : "default"} />
        <MetricCard icon={<ClipboardList size={22} />} label="有在途" value={count(counts.Incoming)} />
        <MetricCard icon={<Package size={22} />} label="有保留" value={count(counts.Reserved)} tone={counts.Reserved > 0 ? "warning" : "default"} />
        <MetricCard icon={<ShieldCheck size={22} />} label="待驗收" value={count(counts.PendingInspection)} tone={counts.PendingInspection > 0 ? "warning" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="驗收隔離" value={count(counts.Quarantined)} tone={counts.Quarantined > 0 ? "danger" : "default"} />
        <MetricCard icon={<Warehouse size={22} />} label="庫存金額" value={money(counts.StockValue)} />
      </div>
      <section className="erp-table-card rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <h2 className="text-base font-extrabold text-[#203d32]">庫存列表</h2>
          <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
        </div>
        {items.length === 0 && !loading ? <div className="px-4 pb-4"><EmptyState text="目前沒有符合條件的庫存項目。" /></div> : null}
        {items.length > 0 ? (
          <div className="erp-table-scroll border-t border-[#edf0ee]">
            <table className="w-full min-w-[1580px] table-fixed text-left text-sm">
              <thead className="sticky top-0 z-40 bg-[#f3f7f4] text-[#59665f]">
                <tr>
                  <th className="w-[300px] whitespace-nowrap px-4 py-3">商品資訊</th>
                  <th className="w-[120px] whitespace-nowrap px-4 py-3">狀態</th>
                  <th className="w-[150px] whitespace-nowrap px-4 py-3 text-right">可用 / 現貨</th>
                  <th className="w-[150px] whitespace-nowrap px-4 py-3 text-right">保留 / 出庫</th>
                  <th className="w-[150px] whitespace-nowrap px-4 py-3 text-right">待驗 / 隔離</th>
                  <th className="w-[150px] whitespace-nowrap px-4 py-3 text-right">在途</th>
                  <th className="w-[150px] whitespace-nowrap px-4 py-3 text-right">補貨建議</th>
                  <th className="w-[190px] whitespace-nowrap px-4 py-3">建議來源</th>
                  <th className="w-[150px] whitespace-nowrap px-4 py-3 text-right">庫存金額</th>
                  <th className="erp-sticky-action-head sticky right-0 z-50 w-[120px] whitespace-nowrap bg-[#f3f7f4] px-4 py-3 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {items.map((item) => (
                  <tr className="align-top" key={`${item.ProductId}-${item.VariantId || "single"}`}>
                    <td className="px-4 py-4">
                      <p className="whitespace-normal break-words text-base font-extrabold leading-6 text-[#203d32]">{item.ProductName}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5 text-xs font-bold">
                        <span className="rounded-md bg-[#f3f7f4] px-2 py-1 text-[#59665f]">{item.VariantLabel || "單一規格"}</span>
                        <span className="rounded-md bg-[#f3f7f4] px-2 py-1 text-[#59665f]">SKU {item.Sku || "-"}</span>
                        <span className="rounded-md bg-[#eef3f6] px-2 py-1 text-[#64727a]">{item.Source}</span>
                      </div>
                    </td>
                    <td className="px-4 py-4"><StatusBadge tone={item.IsOutOfStock ? "danger" : item.NeedsReorder ? "warning" : "default"}>{item.IsOutOfStock ? "無庫存" : item.NeedsReorder ? "需補貨" : "正常"}</StatusBadge></td>
                    <td className="px-4 py-4 text-right">
                      <p className="text-lg font-extrabold text-[#203d32]">{item.AvailableStock}</p>
                      <p className="mt-1 whitespace-nowrap text-xs font-bold text-[#7b877f]">現貨 {item.ErpInStock || item.LegacyStock}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p className="font-bold text-[#203d32]">{item.ErpReserved}</p>
                      <p className="mt-1 whitespace-nowrap text-xs font-bold text-[#7b877f]">出庫 {item.ErpOutbound}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p className="font-bold text-[#203d32]">{item.PendingInspectionStock || 0}</p>
                      <p className="mt-1 whitespace-nowrap text-xs font-bold text-[#7b877f]">隔離 {item.QuarantinedStock || 0}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p className="font-bold text-[#203d32]">{(item.PurchaseIncoming || 0) + (item.TransferIncoming || 0)}</p>
                      <p className="mt-1 whitespace-nowrap text-xs font-bold text-[#7b877f]">採購 {item.PurchaseIncoming || 0} / 調撥 {item.TransferIncoming || 0}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p className="font-bold text-[#203d32]">{item.SuggestedPurchaseQuantity || 0}</p>
                      <p className="mt-1 whitespace-nowrap text-xs font-bold text-[#7b877f]">補貨點 {item.ReorderPoint || 0}</p>
                    </td>
                    <td className="break-words px-4 py-4 text-xs font-bold text-[#7b877f]">{item.ReorderSuggestionSource || "-"}</td>
                    <td className="px-4 py-4 text-right">{money(item.StockValue || 0)}</td>
                    <td className="erp-sticky-action-cell sticky right-0 z-10 bg-white px-4 py-4 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">
                      <div className="grid gap-2">
                        <button className="inline-flex min-h-10 w-full items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" onClick={() => openDetail(item)} type="button">查看</button>
                        <button className="inline-flex min-h-10 w-full items-center justify-center rounded-md bg-[#315746] px-3 font-bold text-white" onClick={() => navigate(`/erp/purchase-orders/new?ProductId=${item.ProductId}${item.VariantId ? `&VariantId=${item.VariantId}` : ""}&Quantity=${Math.max(1, item.SuggestedPurchaseQuantity || 1)}`)} type="button">採購</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
      </section>
    </div>
  );
}

function InventoryMiniTable<T>({ columns, empty, render, rows, title }: { columns: string[]; empty: string; render: (row: T) => ReactNode[]; rows: T[]; title: string }) {
  return (
    <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
        <h2 className="text-base font-extrabold text-[#203d32]">{title}</h2>
        <span className="text-sm font-bold text-[#7b877f]">{count(rows.length)}</span>
      </div>
      {rows.length === 0 ? <div className="px-4 pb-4"><EmptyState text={empty} /></div> : (
        <div className="overflow-x-auto px-4 pb-4">
          <table className="w-full min-w-[620px] table-fixed text-left text-sm">
            <thead className="bg-[#f3f7f4] text-[#59665f]"><tr>{columns.map((column) => <th className="px-3 py-2" key={column}>{column}</th>)}</tr></thead>
            <tbody className="divide-y divide-[#edf0ee]">
              {rows.map((row, index) => (
                <tr key={index}>{render(row).map((cell, cellIndex) => <td className="break-words px-3 py-3 font-semibold text-[#203d32]" key={cellIndex}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function StockUnitsView() {
  const year = new Date().getFullYear();
  const defaultDateFrom = `${year}-01-01`;
  const defaultDateTo = `${year}-12-31`;
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [transferPurpose, setTransferPurpose] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [items, setItems] = useState<ErpStockUnit[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({ Total: 0 });
  const [pagination, setPagination] = useState(emptyPagination);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [warehouses, setWarehouses] = useState<AdminErpWarehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load(
    page = 1,
    nextKeyword = keyword,
    nextStatus = status,
    nextWarehouseId = warehouseId,
    nextDateFrom = dateFrom,
    nextDateTo = dateTo,
  ) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpStockUnits({
        Keyword: nextKeyword.trim(),
        Status: nextStatus,
        ErpWarehouseId: nextWarehouseId,
        DateFrom: nextDateFrom,
        DateTo: nextDateTo,
        Page: page,
        PerPage: 20,
      });
      setItems(data.Items);
      setCounts(data.Counts);
      setPagination(data.Pagination);
      setStatusOptions(data.StatusOptions);
      setWarehouses(data.Warehouses);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "庫存序號明細載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reset() {
    setKeyword("");
    setStatus("");
    setWarehouseId("");
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    void load(1, "", "", "", defaultDateFrom, defaultDateTo);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void load(1);
  }

  function relatedText(item: ErpStockUnit) {
    const order = item.StoreOrderNumber ? `門市訂單 ${item.StoreOrderNumber}` : "";
    const location = item.LocationName ? `門市 ${item.LocationName}` : "";
    return [order, location].filter(Boolean).join(" / ") || "-";
  }

  function statusTone(value: string): "default" | "warning" | "danger" {
    if (["AdjustmentOut", "DamageOut"].includes(value)) return "danger";
    if (["Outbound", "TransferOut", "Reserved"].includes(value)) return "warning";
    return "default";
  }

  return (
    <div className="grid gap-5">
      <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={submit}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(260px,1fr)_170px_210px_170px_170px_300px] xl:items-end">
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="商品、SKU、序號、門市訂單" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">庫存狀態</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">全部</option>
              {statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">倉庫</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}>
              <option value="">全部倉庫</option>
              {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <div className="rwd-filter-actions md:col-span-2 xl:col-span-6">
            <button className="inline-flex h-11 min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:opacity-60" disabled={loading} type="submit">
              <Search size={18} />
              查詢
            </button>
            <button className="inline-flex h-11 min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60" disabled={loading} onClick={reset} type="button">
              <RefreshCw size={18} />
              重整
            </button>
          </div>
        </div>
      </form>
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <MetricCard icon={<Package size={22} />} label="序號總數" value={count(counts.Total)} />
        <MetricCard icon={<Warehouse size={22} />} label="現貨" value={count(counts.InStock)} />
        <MetricCard icon={<ClipboardList size={22} />} label="已保留" value={count(counts.Reserved)} tone={Number(counts.Reserved || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<Send size={22} />} label="已出庫 / 交付" value={count(Number(counts.Outbound || 0) + Number(counts.Delivered || 0))} />
      </div>
      <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <div>
            <h2 className="text-base font-extrabold text-[#203d32]">序號明細</h2>
            <p className="mt-1 text-sm font-semibold text-[#7b877f]">追蹤每一台商品目前所在倉庫、序號、成本與關聯單據。</p>
          </div>
          <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
        </div>
        {items.length === 0 && !loading ? <div className="px-4 pb-4"><EmptyState text="目前沒有符合條件的庫存序號明細。" /></div> : null}
        {items.length > 0 ? (
          <div className="erp-table-scroll border-t border-[#edf0ee]">
            <table className="w-full min-w-[1180px] table-fixed text-left text-sm">
              <thead className="bg-[#f3f7f4] text-[#59665f]">
                <tr>
                  <th className="w-[120px] px-4 py-3">狀態</th>
                  <th className="w-[140px] px-4 py-3">倉庫</th>
                  <th className="w-[270px] px-4 py-3">商品 / SKU</th>
                  <th className="w-[210px] px-4 py-3">序號 / 耳側</th>
                  <th className="w-[130px] px-4 py-3 text-right">數量 / 成本</th>
                  <th className="w-[230px] px-4 py-3">關聯單據</th>
                  <th className="w-[210px] px-4 py-3">入庫 / 出庫時間</th>
                  <th className="px-4 py-3">備註</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {items.map((item) => (
                  <tr key={item.ErpStockUnitId}>
                    <td className="px-4 py-4"><StatusBadge tone={statusTone(item.Status)}>{item.StatusLabel}</StatusBadge></td>
                    <td className="break-words px-4 py-4 font-bold text-[#203d32]">{item.WarehouseName || "-"}</td>
                    <td className="px-4 py-4">
                      <p className="break-words font-extrabold text-[#203d32]">{item.ProductName}</p>
                      <p className="mt-1 break-words font-semibold text-[#7b877f]">{item.VariantLabel || "單一規格"} / {item.Sku || "-"}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="break-all font-extrabold text-[#203d32]">{item.SerialNumber || "-"}</p>
                      <p className="mt-1 font-semibold text-[#7b877f]">{item.EarSideLabel}</p>
                    </td>
                    <td className="px-4 py-4 text-right">
                      <p className="font-extrabold text-[#203d32]">{item.Quantity}</p>
                      <p className="mt-1 font-semibold text-[#7b877f]">{money(item.UnitCost)}</p>
                    </td>
                    <td className="break-words px-4 py-4 font-bold text-[#203d32]">{relatedText(item)}</td>
                    <td className="px-4 py-4">
                      <p className="font-semibold text-[#203d32]">{item.ReceivedAt || "-"}</p>
                      <p className="mt-1 font-semibold text-[#7b877f]">{item.OutboundAt || "-"}</p>
                    </td>
                    <td className="break-words px-4 py-4 text-[#59665f]">{item.Note || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
      </section>
    </div>
  );
}

function StockMovementsView() {
  const year = new Date().getFullYear();
  const defaultDateFrom = `${year}-01-01`;
  const defaultDateTo = `${year}-12-31`;
  const [keyword, setKeyword] = useState("");
  const [movementType, setMovementType] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [items, setItems] = useState<ErpStockMovement[]>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [movementOptions, setMovementOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [warehouses, setWarehouses] = useState<AdminErpWarehouse[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load(
    page = 1,
    nextKeyword = keyword,
    nextMovementType = movementType,
    nextWarehouseId = warehouseId,
    nextDateFrom = dateFrom,
    nextDateTo = dateTo,
  ) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpStockMovements({
        Keyword: nextKeyword.trim(),
        MovementType: nextMovementType,
        ErpWarehouseId: nextWarehouseId,
        DateFrom: nextDateFrom,
        DateTo: nextDateTo,
        Page: page,
        PerPage: 20,
      });
      setItems(data.Items);
      setPagination(data.Pagination);
      setMovementOptions(data.MovementTypeOptions);
      setWarehouses(data.Warehouses);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "庫存異動紀錄載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reset() {
    setKeyword("");
    setMovementType("");
    setWarehouseId("");
    setDateFrom(defaultDateFrom);
    setDateTo(defaultDateTo);
    void load(1, "", "", "", defaultDateFrom, defaultDateTo);
  }

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    void load(1);
  }

  function sourceText(item: ErpStockMovement) {
    return [item.PurchaseNumber, item.OutboundNumber, item.StoreOrderNumber].filter(Boolean).join(" / ") || "-";
  }

  return (
    <div className="grid gap-5">
      <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={submit}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(260px,1fr)_180px_220px_180px_180px_300px] xl:items-end">
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="商品、SKU、單號、備註" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">異動類型</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={movementType} onChange={(event) => setMovementType(event.target.value)}>
              <option value="">全部</option>
              {movementOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">倉庫</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}>
              <option value="">全部倉庫</option>
              {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
          <div className="rwd-filter-actions md:col-span-2 xl:col-span-6">
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:opacity-60" disabled={loading} type="submit">
              <Search size={18} />
              查詢
            </button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60" disabled={loading} onClick={reset} type="button">
              <RefreshCw size={18} />
              重整
            </button>
          </div>
        </div>
      </form>
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <div>
            <h2 className="text-base font-extrabold text-[#203d32]">異動明細</h2>
            <p className="mt-1 text-sm font-semibold text-[#7b877f]">入庫、出庫與未來盤點調整都會集中在這裡追溯。</p>
          </div>
          <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
        </div>
        {items.length === 0 && !loading ? <div className="px-4 pb-4"><EmptyState text="目前沒有符合條件的庫存異動紀錄。" /></div> : null}
        {items.length > 0 ? (
          <div className="erp-table-scroll border-t border-[#edf0ee]">
            <table className="w-full min-w-[1120px] table-fixed text-left text-sm">
              <thead className="bg-[#f3f7f4] text-[#59665f]">
                <tr>
                  <th className="w-[160px] px-4 py-3">異動時間</th>
                  <th className="w-[120px] px-4 py-3">類型</th>
                  <th className="w-[260px] px-4 py-3">商品 / SKU</th>
                  <th className="w-[140px] px-4 py-3">倉庫</th>
                  <th className="w-[110px] px-4 py-3 text-right">數量異動</th>
                  <th className="w-[130px] px-4 py-3 text-right">異動前 / 後</th>
                  <th className="w-[220px] px-4 py-3">來源單據</th>
                  <th className="px-4 py-3">操作人 / 備註</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {items.map((item) => (
                  <tr key={item.ErpStockMovementId}>
                    <td className="px-4 py-4 font-semibold text-[#59665f]">{item.CreatedAt}</td>
                    <td className="px-4 py-4"><StatusBadge tone={item.QuantityDelta < 0 ? "warning" : "default"}>{item.MovementTypeLabel}</StatusBadge></td>
                    <td className="px-4 py-4">
                      <p className="break-words font-extrabold text-[#203d32]">{item.ProductName}</p>
                      <p className="mt-1 break-words font-semibold text-[#7b877f]">{item.VariantLabel || "單一規格"} / {item.Sku || "-"}</p>
                    </td>
                    <td className="break-words px-4 py-4 font-bold text-[#203d32]">{item.WarehouseName || "-"}</td>
                    <td className={`px-4 py-4 text-right text-lg font-extrabold ${item.QuantityDelta < 0 ? "text-[#8a5b14]" : "text-[#245438]"}`}>{item.QuantityDelta > 0 ? "+" : ""}{item.QuantityDelta}</td>
                    <td className="px-4 py-4 text-right font-bold">{item.BeforeQuantity} / {item.AfterQuantity}</td>
                    <td className="break-words px-4 py-4 font-bold text-[#203d32]">{sourceText(item)}</td>
                    <td className="px-4 py-4">
                      <p className="break-words font-bold text-[#203d32]">{item.AdminUserName || "-"}</p>
                      <p className="mt-1 break-words text-[#7b877f]">{item.Note || "-"}</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
      </section>
    </div>
  );
}

type AdjustmentProduct = {
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string;
  AvailableStock: number;
  SalePrice: number;
};

type AdjustmentLine = {
  LineKey: string;
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string;
  AvailableStock: number;
  Quantity: number;
  UnitCost: number;
  EarSide: string;
  SerialText: string;
  StockUnitIds: string[];
  Note: string;
  SerialPrefix: string;
  SerialStart: string;
  SerialEnd: string;
};

type StockAdjustmentWorkflowStatus = "Draft" | "Counting" | "PendingReview";
type StockAdjustmentActionType = "reject" | "cancel" | "start" | "submit";
type InventoryCountScopeMode = "full" | "filtered" | "manual";

function StockAdjustmentsView({ mode = "list", inventoryCountMode = false }: { mode?: "list" | "create" | "detail"; inventoryCountMode?: boolean }) {
  const navigate = useNavigate();
  const { stockAdjustmentId } = useParams();
  const isCreateMode = mode === "create";
  const isDetailMode = mode === "detail";
  const basePath = inventoryCountMode ? "/erp/inventory-counts" : "/erp/stock-adjustments";
  const documentName = inventoryCountMode ? "盤點單" : "庫存調整單";
  const createButtonLabel = inventoryCountMode ? "新增盤點單" : "建立調整";
  const listTitle = inventoryCountMode ? "正式盤點單列表" : "庫存調整紀錄列表";
  const listDescription = inventoryCountMode ? "正式盤點單只收斂盤盈與盤虧差異，建立後必須主管覆核才會異動庫存。" : "只顯示盤盈入庫、盤虧扣庫、退貨入庫與損耗報廢相關異動。";
  const emptyText = inventoryCountMode ? "目前沒有符合條件的盤點單。" : "目前沒有符合條件的庫存調整單。";
  const [keyword, setKeyword] = useState("");
  const [movementType, setMovementType] = useState("");
  const [status, setStatus] = useState("");
  const [createdByAdminUserId, setCreatedByAdminUserId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [dateFrom, setDateFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(`${new Date().getFullYear()}-12-31`);
  const [orders, setOrders] = useState<ErpStockAdjustmentOrder[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState(emptyPagination);
  const [warehouses, setWarehouses] = useState<ErpWarehouse[]>([]);
  const [adjustmentOptions, setAdjustmentOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [createdByOptions, setCreatedByOptions] = useState<ErpStockAdjustmentUserOption[]>([]);
  const [selectedOrder, setSelectedOrder] = useState<ErpStockAdjustmentOrder | null>(null);
  const [productKeyword, setProductKeyword] = useState("");
  const [productResults, setProductResults] = useState<ErpProductSearchItem[]>([]);
  const [countScopeMode, setCountScopeMode] = useState<InventoryCountScopeMode>("filtered");
  const [countScopeKeyword, setCountScopeKeyword] = useState("");
  const [countScopeCategoryId, setCountScopeCategoryId] = useState("");
  const [countScopeStockStatus, setCountScopeStockStatus] = useState("available");
  const [countScopeIncludeZero, setCountScopeIncludeZero] = useState(false);
  const [countScopeLimit, setCountScopeLimit] = useState(300);
  const [countScopeCategories, setCountScopeCategories] = useState<Array<{ ProductCategoryId: string; Name: string }>>([]);
  const [countScopeStatusOptions, setCountScopeStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [countScopeCounts, setCountScopeCounts] = useState<Record<string, number>>({});
  const [stockUnitKeyword, setStockUnitKeyword] = useState("");
  const [stockUnitResults, setStockUnitResults] = useState<ErpStockUnit[]>([]);
  const [formWarehouseId, setFormWarehouseId] = useState("");
  const [formAdjustmentType, setFormAdjustmentType] = useState("AdjustmentIn");
  const [formAdjustmentDate, setFormAdjustmentDate] = useState(new Date().toISOString().slice(0, 10));
  const [formReason, setFormReason] = useState("");
  const [formNote, setFormNote] = useState("");
  const [requiresReview, setRequiresReview] = useState(false);
  const [reviewNote, setReviewNote] = useState("");
  const [stockAdjustmentAction, setStockAdjustmentAction] = useState<{ type: StockAdjustmentActionType; order: ErpStockAdjustmentOrder; note: string; error: string } | null>(null);
  const [lines, setLines] = useState<AdjustmentLine[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");

  async function loadOptions() {
    setLoading(true);
    setMessage("");
    try {
      const data = await getErpStockAdjustmentOptions();
      const nextAdjustmentOptions = inventoryCountMode ? data.AdjustmentTypeOptions.filter((option) => ["AdjustmentIn", "AdjustmentOut"].includes(option.Key)) : data.AdjustmentTypeOptions;
      setWarehouses(data.Warehouses);
      setAdjustmentOptions(nextAdjustmentOptions);
      setFormWarehouseId((current) => current || data.Warehouses[0]?.ErpWarehouseId || "");
      setFormAdjustmentType((current) => nextAdjustmentOptions.some((option) => option.Key === current) ? current : nextAdjustmentOptions[0]?.Key || "AdjustmentIn");
      if (inventoryCountMode) {
        setRequiresReview(true);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${documentName}選項載入失敗。`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isCreateMode && !isDetailMode) {
      void loadAdjustmentList(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, isDetailMode]);

  useEffect(() => {
    if (isDetailMode && stockAdjustmentId) {
      void loadAdjustmentDetail(stockAdjustmentId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailMode, stockAdjustmentId]);

  useEffect(() => {
    if (inventoryCountMode && isCreateMode && formWarehouseId) {
      void loadInventoryCountScopeMetadata();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryCountMode, isCreateMode, formWarehouseId]);

  async function loadAdjustmentList(page = 1, nextKeyword = keyword, nextMovementType = movementType, nextStatus = status, nextCreatedByAdminUserId = createdByAdminUserId, nextWarehouseId = warehouseId, nextDateFrom = dateFrom, nextDateTo = dateTo) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpStockAdjustments({ Keyword: nextKeyword.trim(), AdjustmentType: nextMovementType, Status: nextStatus, CreatedByAdminUserId: nextCreatedByAdminUserId, ErpWarehouseId: nextWarehouseId, DateFrom: nextDateFrom, DateTo: nextDateTo, AdjustmentGroup: inventoryCountMode ? "InventoryCount" : undefined, RequiresReview: inventoryCountMode ? "1" : undefined, Page: page, PerPage: 20 });
      setOrders(data.Orders);
      setCounts(data.Counts);
      setPagination(data.Pagination);
      if (data.Warehouses?.length) {
        setWarehouses(data.Warehouses);
      }
      if (data.AdjustmentTypeOptions?.length) {
        setAdjustmentOptions(inventoryCountMode ? data.AdjustmentTypeOptions.filter((option) => ["AdjustmentIn", "AdjustmentOut"].includes(option.Key)) : data.AdjustmentTypeOptions);
      }
      setStatusOptions(data.StatusOptions || []);
      setCreatedByOptions(data.CreatedByOptions || []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${documentName}紀錄載入失敗。`);
    } finally {
      setLoading(false);
    }
  }

  async function loadAdjustmentDetail(orderId: string) {
    setLoading(true);
    setMessage("");
    try {
      const data = await getErpStockAdjustment(orderId);
      setSelectedOrder(data.Order);
      setWarehouses(data.Warehouses || []);
      setAdjustmentOptions(inventoryCountMode ? (data.AdjustmentTypeOptions || []).filter((option) => ["AdjustmentIn", "AdjustmentOut"].includes(option.Key)) : data.AdjustmentTypeOptions || []);
      setStatusOptions(data.StatusOptions || []);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${documentName}詳情載入失敗。`);
    } finally {
      setLoading(false);
    }
  }

  function resetFilters() {
    const year = new Date().getFullYear();
    setKeyword("");
    setMovementType("");
    setStatus("");
    setCreatedByAdminUserId("");
    setWarehouseId("");
    setDateFrom(`${year}-01-01`);
    setDateTo(`${year}-12-31`);
    void loadAdjustmentList(1, "", "", "", "", "", `${year}-01-01`, `${year}-12-31`);
  }

  async function searchProducts() {
    if (productKeyword.trim().length < 2) {
      setMessage("商品搜尋至少輸入 2 個字。");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const data = await searchErpProducts({ Keyword: productKeyword.trim() });
      setProductResults(data.Items);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "商品搜尋失敗。");
    } finally {
      setLoading(false);
    }
  }

  function adjustmentLineFromProduct(item: ErpProductSearchItem | ErpInventoryCountCandidate, quantity = inventoryCountMode ? 0 : 1): AdjustmentLine {
    const lineKey = `${item.ProductId}-${item.VariantId || "single"}-${Date.now()}`;
    const serialPrefix = item.Sku ? `${item.Sku}-` : "";
    return {
      LineKey: lineKey,
      ProductId: item.ProductId,
      VariantId: item.VariantId,
      ProductName: item.ProductName,
      VariantLabel: item.VariantLabel,
      Sku: item.Sku,
      AvailableStock: item.AvailableStock,
      Quantity: quantity,
      UnitCost: item.SupplierPrice?.UnitCost || 0,
      EarSide: "",
      SerialText: "",
      StockUnitIds: [],
      Note: "StockStatusLabel" in item ? item.StockStatusLabel : "",
      SerialPrefix: serialPrefix,
      SerialStart: "1",
      SerialEnd: quantity > 0 ? String(quantity) : "",
    };
  }

  function addAdjustmentLine(item: ErpProductSearchItem) {
    setLines((current) => [...current, adjustmentLineFromProduct(item)]);
    setProductKeyword("");
    setProductResults([]);
  }

  async function loadInventoryCountScopeCandidates() {
    if (!formWarehouseId) {
      setFormErrors((current) => ({ ...current, ErpWarehouseId: "請先選擇盤點倉庫。" }));
      setMessage("請先選擇盤點倉庫。");
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const data = await listErpInventoryCountCandidates({
        Keyword: countScopeMode === "filtered" ? countScopeKeyword.trim() : "",
        ErpWarehouseId: formWarehouseId,
        ProductCategoryId: countScopeMode === "filtered" ? countScopeCategoryId : "",
        StockStatus: countScopeMode === "full" ? "all" : countScopeStockStatus,
        IncludeZeroStock: countScopeMode === "full" ? true : countScopeIncludeZero,
        Limit: countScopeLimit,
      });
      setCountScopeCategories(data.Categories || []);
      setCountScopeStatusOptions(data.StockStatusOptions || []);
      setCountScopeCounts(data.Counts || {});
      const existingKeys = new Set(lines.map((line) => `${line.ProductId}-${line.VariantId || ""}`));
      const nextItems = data.Items.filter((item) => !existingKeys.has(`${item.ProductId}-${item.VariantId || ""}`));
      setLines((current) => [...current, ...nextItems.map((item, index) => ({
        ...adjustmentLineFromProduct(item, 0),
        LineKey: `${item.ProductId}-${item.VariantId || "single"}-${Date.now()}-${index}`,
      }))]);
      setSuccess(`已載入 ${nextItems.length} 項盤點商品；差異數量預設 0，送審時只會送出有差異的品項。`);
    } catch (error) {
      setMessage(errorText(error, "盤點範圍載入失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function loadInventoryCountScopeMetadata() {
    try {
      const data = await listErpInventoryCountCandidates({
        ErpWarehouseId: formWarehouseId,
        StockStatus: "all",
        IncludeZeroStock: true,
        Limit: 1,
      });
      setCountScopeCategories(data.Categories || []);
      setCountScopeStatusOptions(data.StockStatusOptions || []);
    } catch {
      setCountScopeCategories([]);
      setCountScopeStatusOptions([]);
    }
  }

  function updateAdjustmentLine(lineKey: string, patch: Partial<AdjustmentLine>) {
    setLines((current) => current.map((line) => line.LineKey === lineKey ? { ...line, ...patch } : line));
    setFormErrors((current) => {
      const next = { ...current };
      Object.keys(patch).forEach((key) => { delete next[`Items.${lineKey}.${key}`]; });
      return next;
    });
  }

  function serialPrefixForLine(line: Pick<AdjustmentLine, "Sku" | "ProductId">) {
    const source = (line.Sku || line.ProductId || "").trim();
    return source ? `${source}-` : "";
  }

  function serialEndForLine(line: Pick<AdjustmentLine, "Quantity">) {
    return String(Math.max(1, Number(line.Quantity || 0)));
  }

  function applySerialSuggestion(line: AdjustmentLine) {
    updateAdjustmentLine(line.LineKey, {
      SerialPrefix: line.SerialPrefix.trim() || serialPrefixForLine(line),
      SerialStart: line.SerialStart.trim() || "1",
      SerialEnd: serialEndForLine(line),
    });
  }

  function serialRows(serialText: string, earSide: string) {
    return serialText
      .split(/\r?\n/)
      .map((value) => value.trim())
      .filter(Boolean)
      .map((SerialNumber) => ({ SerialNumber, EarSide: earSide || undefined }));
  }

  function generateSerialRange(line: AdjustmentLine) {
    const prefix = line.SerialPrefix.trim() || serialPrefixForLine(line);
    const startText = line.SerialStart.trim() || "1";
    const endText = line.SerialEnd.trim() || serialEndForLine(line);
    const start = Number(startText);
    const end = Number(endText);
    if (!prefix || !Number.isInteger(start) || !Number.isInteger(end) || end < start) {
      setMessage("請輸入有效的序號前綴、起號與迄號。");
      return;
    }
    const width = Math.max(startText.length, endText.length);
    const rows = Array.from({ length: end - start + 1 }, (_, index) => `${prefix}${String(start + index).padStart(width, "0")}`);
    updateAdjustmentLine(line.LineKey, { SerialPrefix: prefix, SerialStart: startText, SerialEnd: endText, SerialText: rows.join("\n"), Quantity: rows.length });
  }

  async function searchStockUnitsForLine(line: AdjustmentLine) {
    if (!formWarehouseId) {
      setMessage("請先選擇調整倉庫。");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpStockUnits({ Keyword: stockUnitKeyword.trim() || line.Sku || line.ProductName, ErpWarehouseId: formWarehouseId, Status: "InStock", Page: 1, PerPage: 50 });
      setStockUnitResults(data.Items.filter((unit) => unit.ProductName === line.ProductName && (unit.Sku || "") === (line.Sku || "")));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "現有庫存搜尋失敗。");
    } finally {
      setLoading(false);
    }
  }

  function resetForm() {
    setProductKeyword("");
    setProductResults([]);
    setCountScopeKeyword("");
    setCountScopeCategoryId("");
    setCountScopeStockStatus("available");
    setCountScopeIncludeZero(false);
    setCountScopeCounts({});
    setStockUnitKeyword("");
    setStockUnitResults([]);
    setFormAdjustmentType("AdjustmentIn");
    setFormAdjustmentDate(new Date().toISOString().slice(0, 10));
    setFormReason("");
    setFormNote("");
    setRequiresReview(inventoryCountMode);
    setReviewNote("");
    setLines([]);
    setFormErrors({});
  }

  function validateStockAdjustmentForm() {
    const errors: FormErrors = {};
    const effectiveLines = inventoryCountMode ? lines.filter((line) => Number(line.Quantity || 0) > 0) : lines;
    if (!formWarehouseId) errors.ErpWarehouseId = "請選擇調整倉庫。";
    if (!formAdjustmentType) errors.AdjustmentType = "請選擇調整類型。";
    if (!formAdjustmentDate) errors.AdjustmentDate = "請選擇調整日期。";
    if (!formReason.trim()) errors.Reason = "請輸入調整原因。";
    if (lines.length === 0) errors.Items = inventoryCountMode ? "請先載入或加入盤點商品。" : "請至少加入一項調整明細。";
    if (inventoryCountMode && lines.length > 0 && effectiveLines.length === 0) errors.Items = "請至少填寫一項盤點差異數量；無差異品項不會送審。";
    lines.forEach((line) => {
      if (!Number.isFinite(Number(line.Quantity)) || Number(line.Quantity) < (inventoryCountMode ? 0 : 1)) errors[`Items.${line.LineKey}.Quantity`] = inventoryCountMode ? "差異數量不可小於 0。" : "調整數量至少為 1。";
      if (inventoryCountMode && Number(line.Quantity || 0) === 0) return;
      if (!Number.isFinite(Number(line.UnitCost)) || Number(line.UnitCost) < 0) errors[`Items.${line.LineKey}.UnitCost`] = "單位成本不可小於 0。";
      if (isDecrease && line.StockUnitIds.length > 0 && Number(line.Quantity) !== line.StockUnitIds.length) errors[`Items.${line.LineKey}.StockUnitIds`] = "已挑選序號筆數需與數量一致。";
      const serialCount = serialRows(line.SerialText, line.EarSide).length;
      if (!isDecrease && serialCount > 0 && serialCount !== Number(line.Quantity)) errors[`Items.${line.LineKey}.SerialText`] = "序號筆數需與數量一致；若非序號品可留空。";
    });
    return errors;
  }

  async function submitStockAdjustmentForm(workflowStatus?: StockAdjustmentWorkflowStatus) {
    const validationErrors = validateStockAdjustmentForm();
    if (Object.keys(validationErrors).length > 0) {
      applySubmitErrors(validationErrors, setFormErrors, setMessage);
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    setFormErrors({});
    try {
      const submitLines = inventoryCountMode ? lines.filter((line) => Number(line.Quantity || 0) > 0) : lines;
      const payload: ErpStockAdjustmentPayload = {
        ErpWarehouseId: formWarehouseId,
        AdjustmentType: formAdjustmentType,
        AdjustmentDate: formAdjustmentDate,
        Reason: formReason,
        Note: formNote,
        RequiresReview: inventoryCountMode || requiresReview,
        WorkflowStatus: inventoryCountMode ? (workflowStatus || "PendingReview") : undefined,
        Items: submitLines.map((line) => ({
          ProductId: line.ProductId,
          VariantId: line.VariantId,
          Quantity: Number(line.Quantity || 0),
          UnitCost: Number(line.UnitCost || 0),
          EarSide: line.EarSide || undefined,
          Serials: serialRows(line.SerialText, line.EarSide),
          StockUnitIds: line.StockUnitIds,
          Note: line.Note,
        })),
      };
      const result = await createErpStockAdjustment(payload);
      setSuccess(result.message || `${documentName}已建立。`);
      resetForm();
      navigate(basePath);
    } catch (error) {
      const errors = apiErrors(error);
      if (Object.keys(errors).length > 0) applySubmitErrors(errors, setFormErrors, setMessage);
      else setMessage(errorText(error, `${documentName}建立失敗。`));
    } finally {
      setLoading(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await submitStockAdjustmentForm(inventoryCountMode ? "PendingReview" : undefined);
  }

  const isDecrease = ["AdjustmentOut", "DamageOut"].includes(formAdjustmentType);

  async function approveAdjustment(orderId: string) {
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = await approveErpStockAdjustment(orderId, { ReviewNote: reviewNote });
      setSuccess(result.message || `${documentName}已覆核通過。`);
      setReviewNote("");
      if (isDetailMode && stockAdjustmentId) {
        await loadAdjustmentDetail(stockAdjustmentId);
      } else {
        await loadAdjustmentList(pagination.Page);
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : `${documentName}主管覆核失敗。`);
    } finally {
      setLoading(false);
    }
  }

  async function closeStockAdjustmentAction() {
    if (!stockAdjustmentAction) return;
    const note = stockAdjustmentAction.note.trim();
    const needsReason = stockAdjustmentAction.type === "reject" || stockAdjustmentAction.type === "cancel";
    if (needsReason && !note) {
      setStockAdjustmentAction((current) => current ? { ...current, error: "請填寫正式原因。" } : current);
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const orderId = stockAdjustmentAction.order.ErpStockAdjustmentOrderId;
      const payload = { ReviewNote: note || undefined };
      const result = stockAdjustmentAction.type === "reject"
        ? await rejectErpStockAdjustment(orderId, payload)
        : stockAdjustmentAction.type === "cancel"
          ? await cancelErpStockAdjustment(orderId, payload)
          : stockAdjustmentAction.type === "start"
            ? await startErpStockAdjustment(orderId, payload)
            : await submitErpStockAdjustment(orderId, payload);
      const fallbackMessage = stockAdjustmentAction.type === "reject"
        ? `${documentName}已退回修正。`
        : stockAdjustmentAction.type === "cancel"
          ? `${documentName}已作廢。`
          : stockAdjustmentAction.type === "start"
            ? `${documentName}已開始盤點。`
            : `${documentName}已送主管覆核。`;
      setSuccess(result.message || fallbackMessage);
      setStockAdjustmentAction(null);
      if (isDetailMode && stockAdjustmentId) {
        await loadAdjustmentDetail(orderId);
      } else {
        await loadAdjustmentList(pagination.Page);
      }
    } catch (error) {
      const errors = apiErrors(error);
      setStockAdjustmentAction((current) => current ? { ...current, error: errors.ReviewNote || errorText(error, "單據狀態更新失敗。") } : current);
    } finally {
      setLoading(false);
    }
  }

  const stockAdjustmentActionCopy = stockAdjustmentAction ? {
    reject: {
      title: `退回${documentName}`,
      verb: "退回",
      label: "退回原因",
      placeholder: "例如：實盤數量或序號資料不完整，請重新確認。",
      confirmText: "確認退回",
      required: true,
      tone: "default" as const,
    },
    cancel: {
      title: `作廢${documentName}`,
      verb: "作廢",
      label: "作廢原因",
      placeholder: "例如：盤點資料建立錯誤，需作廢重建。",
      confirmText: "確認作廢",
      required: true,
      tone: "danger" as const,
    },
    start: {
      title: `開始${documentName}`,
      verb: "開始盤點",
      label: "盤點備註",
      placeholder: "例如：已完成盤點範圍確認，開始盤點。",
      confirmText: "開始盤點",
      required: false,
      tone: "default" as const,
    },
    submit: {
      title: `送主管覆核`,
      verb: "送主管覆核",
      label: "送審備註",
      placeholder: "例如：已完成實盤與序號確認，請主管覆核。",
      confirmText: "送主管覆核",
      required: false,
      tone: "default" as const,
    },
  }[stockAdjustmentAction.type] : null;

  return (
    <div className="grid gap-5">
      <ErpTextActionDialog
        open={Boolean(stockAdjustmentAction)}
        title={stockAdjustmentActionCopy?.title || ""}
        description={stockAdjustmentAction && stockAdjustmentActionCopy ? `${stockAdjustmentActionCopy.verb} ${stockAdjustmentAction.order.AdjustmentNumber}。此動作不會異動 ERP 庫存；覆核通過前都只更新盤點流程狀態。` : ""}
        label={stockAdjustmentActionCopy?.label || "備註"}
        placeholder={stockAdjustmentActionCopy?.placeholder || ""}
        value={stockAdjustmentAction?.note || ""}
        required={stockAdjustmentActionCopy?.required}
        confirmText={stockAdjustmentActionCopy?.confirmText || "確認"}
        tone={stockAdjustmentActionCopy?.tone || "default"}
        loading={loading}
        error={stockAdjustmentAction?.error || ""}
        onChange={(value) => setStockAdjustmentAction((current) => current ? { ...current, note: value, error: "" } : current)}
        onClose={() => setStockAdjustmentAction(null)}
        onConfirm={closeStockAdjustmentAction}
      />
      {!isCreateMode && !isDetailMode ? <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void loadAdjustmentList(1); }}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[minmax(260px,1fr)_180px_170px_190px_220px_170px_170px] 2xl:items-end">
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="商品、SKU、調整原因、備註" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">狀態</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">全部狀態</option>
              {statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">建立人</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={createdByAdminUserId} onChange={(event) => setCreatedByAdminUserId(event.target.value)}>
              <option value="">全部建立人</option>
              {createdByOptions.map((option) => <option key={option.AdminUserId} value={option.AdminUserId}>{option.DisplayName || option.Username}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">調整類型</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={movementType} onChange={(event) => setMovementType(event.target.value)}>
              <option value="">全部類型</option>
              {adjustmentOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">倉庫</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}>
              <option value="">全部倉庫</option>
              {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
        </div>
        <div className="rwd-filter-actions mt-3 justify-end">
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={() => navigate(`${basePath}/new`)} type="button"><SlidersHorizontal size={18} />{createButtonLabel}</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" disabled={loading} onClick={resetFilters} type="button"><RefreshCw size={18} />重整</button>
        </div>
      </form> : null}
      <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />

      {!isCreateMode && !isDetailMode ? <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <div>
            <h2 className="text-base font-extrabold text-[#203d32]">{listTitle}</h2>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">{listDescription}</p>
          </div>
          <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
        </div>
        <div className="grid gap-3 px-4 pb-4">
          <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
            <MetricCard icon={<SlidersHorizontal size={20} />} label={inventoryCountMode ? "盤點單總數" : "調整單總數"} value={count(pagination.Total)} />
            <MetricCard icon={<ClipboardList size={20} />} label="草稿" value={count(counts.Draft || 0)} />
            <MetricCard icon={<Package size={20} />} label="盤點中" value={count(counts.Counting || 0)} tone={(counts.Counting || 0) > 0 ? "warning" : "default"} />
            <MetricCard icon={<AlertTriangle size={20} />} label="待主管覆核" value={count(counts.PendingReview || 0)} tone={(counts.PendingReview || 0) > 0 ? "warning" : "default"} />
            <MetricCard icon={<Package size={20} />} label={inventoryCountMode ? "已覆核結案" : "已完成"} value={count(counts.Completed || 0)} />
            <MetricCard icon={<AlertTriangle size={20} />} label="退回 / 作廢" value={count((counts.Rejected || 0) + (counts.Cancelled || 0))} tone={(counts.Rejected || 0) > 0 ? "warning" : "default"} />
          </div>
          {orders.length === 0 ? <EmptyState text={emptyText} /> : null}
          {orders.map((order) => {
            const isCancelled = order.Status === "Cancelled";
            return (
            <article className={`rounded-md border p-4 ${isCancelled ? "border-[#f2b8b5] bg-[#fff8f7]" : "border-[#dfe6e1] bg-[#fbfcfb]"}`} key={order.ErpStockAdjustmentOrderId}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className={`break-all text-xl font-extrabold ${isCancelled ? "text-[#8a1f1f]" : "text-[#203d32]"}`}>{order.AdjustmentNumber}</p>
                  <p className="mt-1 break-words text-sm font-bold text-[#59665f]">{order.WarehouseName} / {order.Reason}</p>
                  <p className="mt-1 text-xs font-semibold text-[#7b877f]">{order.AdjustmentDate} 建立：{order.CreatedByAdminName || "-"}</p>
                </div>
                <div className="flex flex-wrap justify-end gap-2">
                  <StatusBadge tone={order.QuantityOut > 0 ? "warning" : "default"}>{order.AdjustmentTypeLabel}</StatusBadge>
                  <StatusBadge tone={isCancelled ? "danger" : order.Status === "Rejected" ? "warning" : "default"}>{order.StatusLabel}</StatusBadge>
                </div>
              </div>
              <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                <InfoBox label="品項" value={`${order.ItemCount} 項`} />
                <InfoBox label="入庫數量" value={`${order.QuantityIn} 筆`} />
                <InfoBox label="扣庫數量" value={`${order.QuantityOut} 筆`} />
                <InfoBox label="備註" value={order.Note || "-"} />
              </div>
              {isCancelled ? (
                <p className="mt-3 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-3 py-2 text-sm font-extrabold leading-6 text-[#8a1f1f]">此{documentName}已作廢，不會異動 ERP 庫存；請保留作廢原因與覆核紀錄供日後查帳。</p>
              ) : null}
              {order.Status === "PendingReview" ? (
                <p className="mt-3 rounded-md border border-[#f0d49d] bg-[#fffaf0] px-3 py-2 text-sm font-extrabold text-[#8a5b14]">此{documentName}尚未異動庫存，請進入詳情頁確認明細後再覆核。</p>
              ) : order.ReviewedAt ? (
                <p className="mt-3 text-xs font-bold text-[#7b877f]">覆核：{order.ReviewedByAdminName || "-"} / {order.ReviewedAt}{order.ReviewNote ? ` / ${order.ReviewNote}` : ""}</p>
              ) : null}
              <div className="mt-3 overflow-x-auto rounded-md border border-[#edf0ee] bg-white">
                <table className="w-full min-w-[1160px] table-fixed text-left text-sm">
                  <thead className="bg-[#f3f7f4] text-[#59665f]">
                    <tr><th className="w-[240px] px-3 py-2">商品</th><th className="w-[130px] px-3 py-2">SKU</th><th className="w-[90px] px-3 py-2 text-right">異動</th><th className="w-[130px] px-3 py-2 text-right">調整前 / 後</th><th className="w-[110px] px-3 py-2">耳側</th><th className="w-[300px] px-3 py-2">序號</th><th className="w-[160px] px-3 py-2">備註</th></tr>
                  </thead>
                  <tbody className="divide-y divide-[#edf0ee]">
                    {order.Items.map((item) => {
                      const serials = (item.SerialText || "").split(/\r?\n|,|、|\s+/).map((serial) => serial.trim()).filter(Boolean);
                      return (
                        <tr className="align-top" key={item.ErpStockAdjustmentOrderItemId}>
                          <td className="break-words px-3 py-3 font-bold text-[#203d32]">{item.ProductName}<p className="text-xs text-[#7b877f]">{item.VariantLabel || "單一規格"}</p></td>
                          <td className="break-all px-3 py-3">{item.Sku || "-"}</td>
                          <td className={`px-3 py-3 text-right font-extrabold ${item.QuantityDelta < 0 ? "text-[#9a2d2d]" : "text-[#245438]"}`}>{item.QuantityDelta > 0 ? "+" : ""}{item.QuantityDelta}</td>
                          <td className="px-3 py-3 text-right font-bold">{item.BeforeQuantity} / {item.AfterQuantity}</td>
                          <td className="px-3 py-3">{item.EarSideLabel || "未指定"}</td>
                          <td className="px-3 py-3">
                            {serials.length ? (
                              <div className="max-h-[118px] overflow-y-auto rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-2">
                                <div className="flex flex-wrap gap-1.5">
                                  {serials.map((serial, index) => (
                                    <span className="max-w-full break-all rounded bg-white px-2 py-1 text-xs font-bold text-[#203d32] shadow-[inset_0_0_0_1px_#dfe6e1]" key={`${item.ErpStockAdjustmentOrderItemId}-${serial}-${index}`}>{serial}</span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span className="text-[#7b877f]">-</span>
                            )}
                          </td>
                          <td className="break-words px-3 py-3">{item.Note || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <ErpActionBar className="mt-3">
                <button className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => navigate(`${basePath}/${order.ErpStockAdjustmentOrderId}`)} type="button">查看詳情</button>
                <ErpDocumentPrintMenu
                  items={[{ label: documentName, description: "列印正式單據與簽核欄", onClick: () => printStockAdjustmentOrder(order) }]}
                />
                {order.Status === "PendingReview" ? (
                  <>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => setStockAdjustmentAction({ type: "reject", order, note: "", error: "" })} type="button">退回修正</button>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-4 font-bold text-[#a33a2f]" onClick={() => setStockAdjustmentAction({ type: "cancel", order, note: "", error: "" })} type="button">作廢</button>
                  </>
                ) : null}
                {order.Status === "Draft" ? (
                  <>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => setStockAdjustmentAction({ type: "start", order, note: "", error: "" })} type="button">開始盤點</button>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#315746] px-4 font-bold text-white" onClick={() => setStockAdjustmentAction({ type: "submit", order, note: "", error: "" })} type="button">送審</button>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-4 font-bold text-[#a33a2f]" onClick={() => setStockAdjustmentAction({ type: "cancel", order, note: "", error: "" })} type="button">作廢</button>
                  </>
                ) : null}
                {order.Status === "Counting" || order.Status === "Rejected" ? (
                  <>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#315746] px-4 font-bold text-white" onClick={() => setStockAdjustmentAction({ type: "submit", order, note: "", error: "" })} type="button">送審</button>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-4 font-bold text-[#a33a2f]" onClick={() => setStockAdjustmentAction({ type: "cancel", order, note: "", error: "" })} type="button">作廢</button>
                  </>
                ) : null}
              </ErpActionBar>
            </article>
          );})}
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => loadAdjustmentList(page)} />
      </section> : null}

      {isDetailMode ? (
        <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-col gap-3 border-b border-[#edf0ee] px-4 py-4 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-extrabold uppercase tracking-[.14em] text-[#7b877f]">{inventoryCountMode ? "Inventory Count" : "Stock Adjustment"}</p>
              <h2 className="mt-1 break-all text-2xl font-extrabold text-[#203d32]">{selectedOrder?.AdjustmentNumber || documentName}</h2>
              <p className="mt-1 break-words text-sm font-bold text-[#59665f]">{selectedOrder ? `${selectedOrder.WarehouseName} / ${selectedOrder.AdjustmentTypeLabel}` : "載入中..."}</p>
            </div>
            <ErpActionBar className="lg:min-w-[360px]">
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate(basePath)} type="button">返回列表</button>
              <ErpDocumentPrintMenu
                items={[{ label: documentName, description: "列印正式單據與簽核欄", disabled: !selectedOrder, disabledReason: "尚未載入單據", onClick: () => selectedOrder && printStockAdjustmentOrder(selectedOrder) }]}
              />
            </ErpActionBar>
          </div>
          {!selectedOrder && !loading ? <div className="p-4"><EmptyState text={`找不到${documentName}。`} /></div> : null}
          {selectedOrder ? (
            <div className="grid gap-4 p-4">
              <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <InfoBox label="狀態" value={selectedOrder.StatusLabel} />
                <InfoBox label="調整日期" value={selectedOrder.AdjustmentDate} />
                <InfoBox label="調整原因" value={selectedOrder.Reason || "-"} />
                <InfoBox label="建立人 / 時間" value={`${selectedOrder.CreatedByAdminName || "-"}\n${selectedOrder.CreatedAt || "-"}`} />
                <InfoBox label="品項數" value={`${selectedOrder.ItemCount} 項`} />
                <InfoBox label="入庫數量" value={`${selectedOrder.QuantityIn} 筆`} />
                <InfoBox label="扣庫數量" value={`${selectedOrder.QuantityOut} 筆`} />
                <InfoBox label="覆核需求" value={selectedOrder.IsReviewRequired ? "需要主管覆核" : "不需主管覆核"} />
              </div>

              <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-4">
                <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                  <div className="min-w-0">
                    <h3 className="font-extrabold text-[#203d32]">調整說明</h3>
                    <p className="mt-2 whitespace-pre-wrap break-words text-sm font-semibold leading-6 text-[#59665f]">{selectedOrder.Note || "無內部備註。"}</p>
                  </div>
                  <div className="min-w-0 rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-sm font-bold text-[#59665f] md:min-w-[260px]">
                    <p>覆核人：<span className="text-[#203d32]">{selectedOrder.ReviewedByAdminName || "-"}</span></p>
                    <p className="mt-1">覆核時間：<span className="text-[#203d32]">{selectedOrder.ReviewedAt || "-"}</span></p>
                    <p className="mt-1 break-words">覆核備註：<span className="text-[#203d32]">{selectedOrder.ReviewNote || "-"}</span></p>
                  </div>
                </div>
              </div>

              {selectedOrder.Status === "PendingReview" ? (
                <div className="rounded-md border border-[#f0d49d] bg-[#fffaf0] p-4">
                  <h3 className="font-extrabold text-[#8a5b14]">主管覆核</h3>
                  <p className="mt-1 text-sm font-bold leading-6 text-[#8a5b14]">請確認倉庫、商品、數量、序號與調整原因。覆核通過後才會正式異動 ERP 庫存並寫入庫存異動紀錄。</p>
                  <div className="mt-3 grid gap-3 xl:grid-cols-[minmax(0,1fr)_minmax(280px,auto)]">
                    <input className="h-11 min-w-0 rounded-md border border-[#e7c98d] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="主管覆核備註，可留空" value={reviewNote} onChange={(event) => setReviewNote(event.target.value)} />
                    <button className="inline-flex min-h-11 w-full items-center justify-center rounded-md bg-[#315746] px-5 text-center font-bold leading-6 text-white disabled:opacity-60" disabled={loading} onClick={() => approveAdjustment(selectedOrder.ErpStockAdjustmentOrderId)} type="button">{inventoryCountMode ? "主管覆核通過並結案" : "覆核通過"}</button>
                  </div>
                  <div className="mt-3 grid gap-2 sm:grid-cols-2">
                    <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-60" disabled={loading} onClick={() => setStockAdjustmentAction({ type: "reject", order: selectedOrder, note: "", error: "" })} type="button">退回修正</button>
                    <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-4 font-bold text-[#a33a2f] disabled:opacity-60" disabled={loading} onClick={() => setStockAdjustmentAction({ type: "cancel", order: selectedOrder, note: "", error: "" })} type="button">作廢盤點單</button>
                  </div>
                </div>
              ) : null}

              {["Draft", "Counting", "Rejected"].includes(selectedOrder.Status) ? (
                <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4">
                  <h3 className="font-extrabold text-[#203d32]">{selectedOrder.Status === "Draft" ? "盤點草稿" : selectedOrder.Status === "Counting" ? "盤點作業中" : "退回修正中"}</h3>
                  <p className="mt-1 text-sm font-bold leading-6 text-[#59665f]">
                    {selectedOrder.Status === "Draft"
                      ? "草稿尚未進入盤點與覆核流程，可先開始盤點，也可確認資料後直接送主管覆核。"
                      : selectedOrder.Status === "Counting"
                        ? "盤點中可持續確認數量與序號，確認完成後送主管覆核；覆核前不會異動 ERP 庫存。"
                        : "主管已退回修正，請確認退回原因與盤點明細，修正完成後再送主管覆核。"}
                  </p>
                  <div className="rwd-action-grid mt-3">
                    {selectedOrder.Status === "Draft" ? (
                      <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-60" disabled={loading} onClick={() => setStockAdjustmentAction({ type: "start", order: selectedOrder, note: "", error: "" })} type="button">開始盤點</button>
                    ) : null}
                    <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-4 font-bold text-white disabled:opacity-60" disabled={loading} onClick={() => setStockAdjustmentAction({ type: "submit", order: selectedOrder, note: "", error: "" })} type="button">送主管覆核</button>
                    <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-4 font-bold text-[#a33a2f] disabled:opacity-60" disabled={loading} onClick={() => setStockAdjustmentAction({ type: "cancel", order: selectedOrder, note: "", error: "" })} type="button">作廢盤點單</button>
                  </div>
                </div>
              ) : null}

              <div className="overflow-x-auto rounded-md border border-[#edf0ee] bg-white">
                <table className="w-full min-w-[1240px] table-fixed text-left text-sm">
                  <thead className="bg-[#f3f7f4] text-[#59665f]">
                    <tr><th className="w-[220px] px-3 py-2">商品</th><th className="w-[130px] px-3 py-2">SKU</th><th className="w-[110px] px-3 py-2">耳側</th><th className="w-[120px] px-3 py-2 text-right">異動數量</th><th className="w-[140px] px-3 py-2 text-right">調整前 / 後</th><th className="w-[130px] px-3 py-2 text-right">單位成本</th><th className="w-[300px] px-3 py-2">序號 / 現貨</th><th className="w-[190px] px-3 py-2">備註</th></tr>
                  </thead>
                  <tbody className="divide-y divide-[#edf0ee]">
                    {selectedOrder.Items.map((item) => {
                      const serials = (item.SerialText || "").split(/\r?\n|,|、/).map((serial) => serial.trim()).filter(Boolean);
                      return (
                        <tr className="align-top" key={item.ErpStockAdjustmentOrderItemId}>
                          <td className="break-words px-3 py-3 font-bold text-[#203d32]">{item.ProductName}<p className="text-xs text-[#7b877f]">{item.VariantLabel || "單一規格"}</p></td>
                          <td className="break-all px-3 py-3">{item.Sku || "-"}</td>
                          <td className="px-3 py-3">{item.EarSideLabel || "未指定"}</td>
                          <td className={`px-3 py-3 text-right font-extrabold ${item.QuantityDelta < 0 ? "text-[#9a2d2d]" : "text-[#245438]"}`}>{item.QuantityDelta > 0 ? "+" : ""}{item.QuantityDelta}</td>
                          <td className="px-3 py-3 text-right font-bold">{item.BeforeQuantity} / {item.AfterQuantity}</td>
                          <td className="px-3 py-3 text-right font-bold">{money(item.UnitCost)}</td>
                          <td className="px-3 py-3">
                            {serials.length ? (
                              <div className="max-h-[168px] overflow-y-auto rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-2">
                                <div className="flex flex-wrap gap-1.5">
                                  {serials.map((serial, index) => (
                                    <span className="max-w-full break-all rounded bg-white px-2 py-1 text-xs font-bold text-[#203d32] shadow-[inset_0_0_0_1px_#dfe6e1]" key={`${item.ErpStockAdjustmentOrderItemId}-${serial}-${index}`}>{serial}</span>
                                  ))}
                                </div>
                              </div>
                            ) : (
                              <span className="text-[#7b877f]">-</span>
                            )}
                          </td>
                          <td className="whitespace-pre-wrap break-words px-3 py-3">{item.Note || "-"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {isCreateMode ? <div className="grid gap-5">
        <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-base font-extrabold text-[#203d32]">{inventoryCountMode ? "正式盤點單表單" : "庫存調整表單"}</h2>
              <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">{inventoryCountMode ? "盤點差異需建立主檔與明細，主管覆核通過後才會正式異動庫存。" : "盤點差異、損耗報廢或退貨入庫都必須留下原因與操作紀錄。"}</p>
            </div>
            <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => navigate("/erp/stock-movements")} type="button">
              查看異動紀錄
            </button>
          </div>

          <form className="mt-4 grid gap-4" onSubmit={submit}>
            <div className="grid gap-3 lg:grid-cols-2 2xl:grid-cols-[minmax(280px,1fr)_minmax(220px,.7fr)_minmax(240px,.75fr)_minmax(360px,1.2fr)]">
              <label className="min-w-0" data-field="ErpWarehouseId">
                <span className="mb-2 block text-sm font-bold text-[#59665f]">{inventoryCountMode ? "盤點倉庫" : "調整倉庫"}</span>
                <select className={fieldClass(formErrors, "ErpWarehouseId", "h-11 w-full min-w-0 px-3")} value={formWarehouseId} onChange={(event) => { setFormWarehouseId(event.target.value); setFormErrors((current) => ({ ...current, ErpWarehouseId: "" })); }}>
                  <option value="">請選擇倉庫</option>
                  {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}
                </select>
                <FieldError errors={formErrors} name="ErpWarehouseId" />
              </label>
              <label className="min-w-0" data-field="AdjustmentType">
                <span className="mb-2 block text-sm font-bold text-[#59665f]">{inventoryCountMode ? "差異類型" : "調整類型"}</span>
                <select className={fieldClass(formErrors, "AdjustmentType", "h-11 w-full min-w-0 px-3")} value={formAdjustmentType} onChange={(event) => { setFormAdjustmentType(event.target.value); setFormErrors((current) => ({ ...current, AdjustmentType: "" })); }}>
                  {adjustmentOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
                </select>
                <FieldError errors={formErrors} name="AdjustmentType" />
              </label>
              <label className="min-w-0" data-field="AdjustmentDate">
                <span className="mb-2 block text-sm font-bold text-[#59665f]">{inventoryCountMode ? "盤點日期" : "調整日期"}</span>
                <input className={fieldClass(formErrors, "AdjustmentDate", "h-11 w-full min-w-0 px-3")} type="date" value={formAdjustmentDate} onChange={(event) => { setFormAdjustmentDate(event.target.value); setFormErrors((current) => ({ ...current, AdjustmentDate: "" })); }} />
                <FieldError errors={formErrors} name="AdjustmentDate" />
              </label>
              <div data-field="Reason"><TextField label={inventoryCountMode ? "盤點原因" : "調整原因"} value={formReason} onChange={(value) => { setFormReason(value); setFormErrors((current) => ({ ...current, Reason: "" })); }} /><FieldError errors={formErrors} name="Reason" /></div>
            </div>
            <ErpSwitchCard
              checked={inventoryCountMode || requiresReview}
              disabled={inventoryCountMode}
              label={inventoryCountMode ? "正式盤點單一律需要主管覆核後才正式異動庫存" : "需要主管覆核後才正式異動庫存"}
              onChange={setRequiresReview}
            />

            {inventoryCountMode ? (
              <section className="rounded-md border border-[#dfe6e1] bg-white p-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h3 className="font-extrabold text-[#203d32]">盤點範圍</h3>
                    <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">可整倉載入、依條件載入，或手動抽盤。載入後差異數量預設 0，送審時只會送出有差異的品項；盤盈與盤虧請分開建立，方便主管覆核與庫存稽核。</p>
                  </div>
                  {Object.keys(countScopeCounts).length ? <span className="rounded-full bg-[#edf7f0] px-3 py-1 text-sm font-extrabold text-[#315746]">本次載入 {countScopeCounts.Selected || 0} / {countScopeCounts.Total || 0} 項</span> : null}
                </div>
                <div className="mt-4 grid gap-3 lg:grid-cols-3">
                  {[
                    { key: "full" as const, title: "全倉盤點", text: "載入此倉庫所有商品，適合年度或月度完整盤點。" },
                    { key: "filtered" as const, title: "條件盤點", text: "依商品類別、庫存狀態與關鍵字產生盤點清單。" },
                    { key: "manual" as const, title: "手動抽盤", text: "只挑指定商品，適合臨時抽查或異常追蹤。" },
                  ].map((option) => (
                    <button
                      className={`min-h-[96px] rounded-md border p-4 text-left transition ${countScopeMode === option.key ? "border-[#8ac79d] bg-[#f2fbf5] shadow-[inset_4px_0_0_#315746]" : "border-[#dfe6e1] bg-white hover:border-[#b9c9bf]"}`}
                      key={option.key}
                      onClick={() => setCountScopeMode(option.key)}
                      type="button"
                    >
                      <span className="block text-base font-extrabold text-[#203d32]">{option.title}</span>
                      <span className="mt-1 block text-sm font-semibold leading-6 text-[#59665f]">{option.text}</span>
                    </button>
                  ))}
                </div>
                {countScopeMode !== "manual" ? (
                  <div className="mt-4 grid gap-3 lg:grid-cols-2 2xl:grid-cols-[minmax(220px,.8fr)_minmax(220px,.8fr)_minmax(260px,1fr)_minmax(160px,.55fr)_minmax(180px,.65fr)]">
                    <label className="min-w-0">
                      <span className="mb-2 block text-sm font-bold text-[#59665f]">商品類別</span>
                      <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3" disabled={countScopeMode === "full"} value={countScopeCategoryId} onChange={(event) => setCountScopeCategoryId(event.target.value)}>
                        <option value="">全部類別</option>
                        {countScopeCategories.map((category) => <option key={category.ProductCategoryId} value={category.ProductCategoryId}>{category.Name}</option>)}
                      </select>
                    </label>
                    <label className="min-w-0">
                      <span className="mb-2 block text-sm font-bold text-[#59665f]">庫存狀態</span>
                      <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3" disabled={countScopeMode === "full"} value={countScopeStockStatus} onChange={(event) => setCountScopeStockStatus(event.target.value)}>
                        {(countScopeStatusOptions.length ? countScopeStatusOptions : [{ Key: "all", Label: "全部商品" }, { Key: "available", Label: "有可用庫存" }, { Key: "low", Label: "低庫存" }, { Key: "out", Label: "無庫存" }]).map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
                      </select>
                    </label>
                    <label className="min-w-0">
                      <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
                      <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3" disabled={countScopeMode === "full"} placeholder="品名、商品編號或 SKU，可留空" value={countScopeKeyword} onChange={(event) => setCountScopeKeyword(event.target.value)} />
                    </label>
                    <label className="min-w-0">
                      <span className="mb-2 block text-sm font-bold text-[#59665f]">載入上限</span>
                      <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3" min={20} max={1000} type="number" value={countScopeLimit} onChange={(event) => setCountScopeLimit(Number(event.target.value || 300))} />
                    </label>
                    <div className="grid min-w-0 gap-2">
                      <span className="text-sm font-bold text-[#59665f]">0 庫存</span>
                      <ErpCheckbox checked={countScopeMode === "full" || countScopeIncludeZero} disabled={countScopeMode === "full"} label="包含無庫存商品" onChange={setCountScopeIncludeZero} />
                    </div>
                  </div>
                ) : null}
                <div className="mt-4 rwd-action-grid">
                  {countScopeMode !== "manual" ? (
                    <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} onClick={() => void loadInventoryCountScopeCandidates()} type="button">
                      <ClipboardList size={18} /> 產生盤點明細
                    </button>
                  ) : null}
                  <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => setLines([])} type="button">清空明細</button>
                </div>
              </section>
            ) : null}

            {(!inventoryCountMode || countScopeMode === "manual") ? (
              <ErpProductSearchPanel
                keyword={productKeyword}
                loading={loading}
                onKeywordChange={setProductKeyword}
                onSearch={() => void searchProducts()}
                onSelect={addAdjustmentLine}
                placeholder="輸入品名、商品編號或 SKU"
                results={productResults}
                searchLabel="搜尋商品"
                selectLabel={inventoryCountMode ? "加入盤點" : "加入調整"}
                title={inventoryCountMode ? "手動抽盤商品搜尋" : "商品模糊搜尋"}
              />
            ) : null}

            <section className="rounded-md border border-[#dfe6e1] bg-white" data-field="Items">
              <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#edf0ee] px-4 py-3">
                <h3 className="font-extrabold text-[#203d32]">{inventoryCountMode ? "盤點差異明細" : "調整明細"}</h3>
                <span className="text-sm font-bold text-[#7b877f]">{lines.length} 項</span>
              </div>
              {lines.length === 0 ? <div className="p-4"><EmptyState text={inventoryCountMode ? "尚未載入盤點商品。" : "尚未加入調整商品。"} /></div> : (
                <div className="overflow-x-auto p-4">
                  <table className="w-full min-w-[980px] table-fixed text-left text-sm">
                    <thead className="bg-[#f3f7f4] text-[#59665f]">
                      <tr><th className="w-[260px] px-3 py-2">商品</th><th className="w-[130px] px-3 py-2">SKU</th><th className="w-[120px] px-3 py-2">目前庫存</th><th className="w-[120px] px-3 py-2">{inventoryCountMode ? "差異數量" : "數量"}</th><th className="w-[130px] px-3 py-2">單位成本</th><th className="w-[130px] px-3 py-2">耳側</th><th className="w-[90px] px-3 py-2">操作</th></tr>
                    </thead>
                    <tbody>
                      {lines.map((line, index) => (
                        <Fragment key={line.LineKey}>
                          {index > 0 ? <tr aria-hidden="true"><td className="h-3 border-t border-[#e6ece8] bg-white p-0" colSpan={7} /></tr> : null}
                          <tr className="align-top">
                            <td className="border-t border-[#edf0ee] bg-white px-3 py-4 font-bold text-[#203d32]">
                              <div className="flex min-w-0 items-start gap-3">
                                <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-[#eef5f0] text-xs font-extrabold text-[#315746]">{index + 1}</span>
                                <div className="min-w-0">
                                  <div className="break-words">{line.ProductName}</div>
                                  <p className="text-xs text-[#7b877f]">{line.VariantLabel || "單一規格"}</p>
                                </div>
                              </div>
                            </td>
                            <td className="break-all border-t border-[#edf0ee] bg-white px-3 py-4">{line.Sku || "-"}</td>
                            <td className="border-t border-[#edf0ee] bg-white px-3 py-4 font-bold">{line.AvailableStock} 筆</td>
                            <td className="border-t border-[#edf0ee] bg-white px-3 py-4" data-field={`Items.${line.LineKey}.Quantity`}><input className={fieldClass(formErrors, `Items.${line.LineKey}.Quantity`, "h-10 w-full px-2 bg-white")} min={inventoryCountMode ? 0 : 1} type="number" value={line.Quantity} onChange={(event) => updateAdjustmentLine(line.LineKey, { Quantity: Number(event.target.value || 0) })} /><FieldError errors={formErrors} name={`Items.${line.LineKey}.Quantity`} /></td>
                            <td className="border-t border-[#edf0ee] bg-white px-3 py-4" data-field={`Items.${line.LineKey}.UnitCost`}><input className={fieldClass(formErrors, `Items.${line.LineKey}.UnitCost`, "h-10 w-full px-2 bg-white")} min={0} type="number" value={line.UnitCost} onChange={(event) => updateAdjustmentLine(line.LineKey, { UnitCost: Number(event.target.value || 0) })} /><FieldError errors={formErrors} name={`Items.${line.LineKey}.UnitCost`} /></td>
                            <td className="border-t border-[#edf0ee] bg-white px-3 py-4"><select className="h-10 w-full rounded-md border border-[#cfd8d2] bg-white px-2" value={line.EarSide} onChange={(event) => updateAdjustmentLine(line.LineKey, { EarSide: event.target.value })}><option value="">不指定</option><option value="Left">左耳</option><option value="Right">右耳</option><option value="Both">雙耳</option></select></td>
                            <td className="border-t border-[#edf0ee] bg-white px-3 py-4"><button className="inline-flex h-10 w-full items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#8a1f1f]" onClick={() => setLines((current) => current.filter((item) => item.LineKey !== line.LineKey))} type="button">移除</button></td>
                          </tr>
                          <tr>
                            <td className="border-b border-[#edf0ee] bg-white px-3 pb-4 pt-0" colSpan={7}>
                              <div className="grid gap-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3 lg:grid-cols-[minmax(0,1.4fr)_minmax(240px,.6fr)]">
                                <div className="min-w-0" data-field={isDecrease ? `Items.${line.LineKey}.StockUnitIds` : `Items.${line.LineKey}.SerialText`}>
                                  <div className="mb-2 text-xs font-extrabold text-[#59665f]">序號 / 現貨挑選</div>
                                  {isDecrease ? (
                                    <div className="grid gap-2">
                                      <div className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_96px]">
                                        <input className="h-10 min-w-0 rounded-md border border-[#cfd8d2] px-2" placeholder="序號關鍵字" value={stockUnitKeyword} onChange={(event) => setStockUnitKeyword(event.target.value)} />
                                        <button className="inline-flex h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" onClick={() => searchStockUnitsForLine(line)} type="button">挑選</button>
                                      </div>
                                      <div className="max-h-[120px] overflow-auto rounded-md border border-[#edf0ee] bg-white">
                                        {stockUnitResults.length === 0 ? <p className="px-3 py-2 text-xs font-bold text-[#8c9790]">搜尋後可勾選現有序號。</p> : stockUnitResults.map((unit) => (
                                          <div className="border-b border-[#edf0ee] px-3 py-2 text-xs font-bold" key={unit.ErpStockUnitId}>
                                            <ErpCheckbox checked={line.StockUnitIds.includes(unit.ErpStockUnitId)} label={`${unit.SerialNumber || "無序號"} / ${unit.EarSideLabel}`} onChange={(checked) => {
                                              const nextIds = checked ? [...line.StockUnitIds, unit.ErpStockUnitId] : line.StockUnitIds.filter((id) => id !== unit.ErpStockUnitId);
                                              updateAdjustmentLine(line.LineKey, { StockUnitIds: nextIds, Quantity: nextIds.length || line.Quantity });
                                            }} />
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : (
                                    <div className="grid gap-2">
                                      <textarea className="min-h-[76px] w-full rounded-md border border-[#cfd8d2] px-2 py-2" placeholder="一行一筆序號，可用條碼槍掃描或批次貼上" value={line.SerialText} onChange={(event) => updateAdjustmentLine(line.LineKey, { SerialText: event.target.value })} />
                                      <div className="grid gap-2 sm:grid-cols-[minmax(160px,1fr)_110px_110px_112px_88px]">
                                        <input className="h-10 min-w-0 rounded-md border border-[#cfd8d2] px-2" placeholder={`前綴，建議 ${serialPrefixForLine(line) || "產品編號-"}`} value={line.SerialPrefix} onChange={(event) => updateAdjustmentLine(line.LineKey, { SerialPrefix: event.target.value })} />
                                        <input className="h-10 min-w-0 rounded-md border border-[#cfd8d2] px-2" placeholder="起號" value={line.SerialStart} onChange={(event) => updateAdjustmentLine(line.LineKey, { SerialStart: event.target.value })} />
                                        <input className="h-10 min-w-0 rounded-md border border-[#cfd8d2] px-2" placeholder="迄號" value={line.SerialEnd} onChange={(event) => updateAdjustmentLine(line.LineKey, { SerialEnd: event.target.value })} />
                                        <button className="inline-flex h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" onClick={() => applySerialSuggestion(line)} type="button">帶入建議</button>
                                        <button className="inline-flex h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" onClick={() => generateSerialRange(line)} type="button">產生</button>
                                      </div>
                                      <p className="text-xs font-bold leading-5 text-[#7b877f]">建議格式：{serialPrefixForLine(line) || "產品編號-"}1 ~ {serialPrefixForLine(line) || "產品編號-"}{serialEndForLine(line)}，可依實際供應商序號自行調整。</p>
                                    </div>
                                  )}
                                  <FieldError errors={formErrors} name={isDecrease ? `Items.${line.LineKey}.StockUnitIds` : `Items.${line.LineKey}.SerialText`} />
                                </div>
                                <label className="min-w-0">
                                  <span className="mb-2 block text-xs font-extrabold text-[#59665f]">備註</span>
                                  <textarea className="min-h-[124px] w-full rounded-md border border-[#cfd8d2] px-2 py-2" value={line.Note} onChange={(event) => updateAdjustmentLine(line.LineKey, { Note: event.target.value })} />
                                </label>
                              </div>
                            </td>
                          </tr>
                        </Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="px-4 pb-3"><FieldError errors={formErrors} name="Items" /></div>
            </section>

            <label>
              <span className="mb-2 block text-sm font-bold text-[#59665f]">內部備註</span>
              <textarea className="min-h-[90px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如：盤點差異、報廢原因、退貨來源、主管確認資訊。" value={formNote} onChange={(event) => setFormNote(event.target.value)} />
            </label>
            {isDecrease ? <Message tone="success" text={inventoryCountMode ? "盤虧項目可直接勾選現有庫存序號；覆核前不會異動庫存。" : "調減可直接勾選現有庫存序號；若未勾選，系統會依先進先出自動扣庫。"} /> : <Message tone="success" text="入庫可使用條碼槍逐筆掃描、批次貼上，或用連號產生器快速建立序號。" />}
            <div className="rwd-action-grid">
              {inventoryCountMode ? (
                <>
                  <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] disabled:opacity-60" disabled={loading || lines.length === 0} onClick={() => void submitStockAdjustmentForm("Draft")} type="button">儲存草稿</button>
                  <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#315746] bg-white px-5 font-bold text-[#315746] disabled:opacity-60" disabled={loading || lines.length === 0} onClick={() => void submitStockAdjustmentForm("Counting")} type="button">開始盤點</button>
                  <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading || lines.length === 0} type="submit">送主管覆核</button>
                </>
              ) : (
                <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading || lines.length === 0} type="submit">{createButtonLabel}</button>
              )}
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate(basePath)} type="button">返回列表</button>
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={resetForm} type="button">清除表單</button>
            </div>
          </form>
        </section>

        <aside className="grid content-start gap-4 xl:grid-cols-[minmax(0,1fr)_360px]">
          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <h2 className="text-base font-extrabold text-[#203d32]">作業提醒</h2>
            <div className="mt-3 grid gap-2 text-sm font-semibold leading-6 text-[#59665f]">
              {inventoryCountMode ? (
                <>
                  <p>1. 正式盤點單用於盤點差異結案，僅處理盤盈入庫與盤虧扣庫。</p>
                  <p>2. 建立後一律進入待主管覆核，覆核前不會異動 ERP 庫存。</p>
                  <p>3. 盤盈入庫可使用條碼槍掃描、批次貼上或連號產生序號；無序號商品可留空。</p>
                  <p>4. 盤虧扣庫建議從現有庫存挑選序號，避免扣到不存在或錯誤倉庫的商品。</p>
                  <p>5. 覆核通過後會保留調整前後數量、原因、備註、建立人、覆核人與覆核時間，並同步寫入庫存異動紀錄。</p>
                  <p>6. 跨倉移動、總部與門市間移轉、門市寄回總部，請使用庫存調撥單，不要用盤點單處理。</p>
                </>
              ) : (
                <>
                  <p>1. 本頁用於退貨入庫、損耗報廢或臨時庫存修正等非移轉性調整。</p>
                  <p>2. 盤盈入庫與退貨入庫會在指定倉庫建立 ERP 庫存批次；有序號商品請逐筆輸入序號。</p>
                  <p>3. 盤虧扣庫與損耗報廢會從指定倉庫現貨扣除；庫存不足時系統會阻擋送出。</p>
                  <p>4. 每筆調整會保留調整前後數量、原因、備註、建立人與建立時間，並同步寫入庫存異動紀錄。</p>
                  <p>5. 跨倉移動、總部與門市間移轉、門市寄回總部，請使用庫存調撥單並選擇對應調撥情境，不要用庫存調整處理。</p>
                  <p>6. 若調整需要主管確認，請勾選主管覆核，覆核通過後才會正式異動庫存。</p>
                </>
              )}
            </div>
            <div className="mt-3 rounded-md border border-[#f0d49d] bg-[#fffaf0] px-3 py-2 text-xs font-bold leading-5 text-[#8a5b14]">
              {inventoryCountMode ? "正式盤點會影響 ERP 庫存帳，送審前請確認倉庫、商品、盤盈盤虧數量、序號與盤點原因。" : "庫存調整會直接影響 ERP 庫存，送出前請確認倉庫、商品、數量、序號與調整原因。"}
            </div>
          </section>
          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <h2 className="text-base font-extrabold text-[#203d32]">目前選擇</h2>
            <div className="mt-3 grid gap-2">
              <InfoBox label="倉庫" value={warehouses.find((warehouse) => warehouse.ErpWarehouseId === formWarehouseId)?.Name || "尚未選擇"} />
              <InfoBox label={inventoryCountMode ? "差異方向" : "調整方向"} value={adjustmentOptions.find((option) => option.Key === formAdjustmentType)?.Label || "-"} />
              <InfoBox label={inventoryCountMode ? "盤點日期" : "調整日期"} value={formAdjustmentDate || "-"} />
              <InfoBox label="明細品項" value={`${lines.length} 項`} />
              <InfoBox label="調整數量" value={`${lines.reduce((sum, line) => sum + Number(line.Quantity || 0), 0)} 筆`} />
            </div>
          </section>
        </aside>
      </div> : null}
    </div>
  );
}

type TransferLine = {
  LineKey: string;
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  VariantLabel: string | null;
  Sku: string | null;
  Quantity: number;
  UnitCost: number;
  SerialText: string;
  Note: string;
  AvailableStock: number;
};

function StockTransfersView({ mode = "list" }: { mode?: "list" | "create" }) {
  const navigate = useNavigate();
  const isCreateMode = mode === "create";
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [transferPurpose, setTransferPurpose] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [filterFromWarehouseId, setFilterFromWarehouseId] = useState("");
  const [filterToWarehouseId, setFilterToWarehouseId] = useState("");
  const [dateFrom, setDateFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(`${new Date().getFullYear()}-12-31`);
  const [orders, setOrders] = useState<ErpStockTransferOrder[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState(emptyPagination);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [transferPurposeOptions, setTransferPurposeOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [warehouses, setWarehouses] = useState<ErpWarehouse[]>([]);
  const [formTransferPurpose, setFormTransferPurpose] = useState("WarehouseTransfer");
  const [fromWarehouseId, setFromWarehouseId] = useState("");
  const [toWarehouseId, setToWarehouseId] = useState("");
  const [note, setNote] = useState("");
  const [productKeyword, setProductKeyword] = useState("");
  const [productResults, setProductResults] = useState<ErpProductSearchItem[]>([]);
  const [lines, setLines] = useState<TransferLine[]>([]);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");

  async function load(page = 1, nextKeyword = keyword, nextStatus = status, nextTransferPurpose = transferPurpose, nextWarehouseId = warehouseId, nextFromWarehouseId = filterFromWarehouseId, nextToWarehouseId = filterToWarehouseId, nextDateFrom = dateFrom, nextDateTo = dateTo) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpStockTransfers({ Keyword: nextKeyword.trim(), Status: nextStatus, TransferPurpose: nextTransferPurpose, ErpWarehouseId: nextWarehouseId, FromWarehouseId: nextFromWarehouseId, ToWarehouseId: nextToWarehouseId, DateFrom: nextDateFrom, DateTo: nextDateTo, Page: page, PerPage: 20 });
      setOrders(data.Orders);
      setCounts(data.Counts);
      setPagination(data.Pagination);
      setStatusOptions(data.StatusOptions);
      setTransferPurposeOptions(data.TransferPurposeOptions || []);
      setWarehouses(data.Warehouses);
      setFromWarehouseId((current) => current || data.Warehouses[0]?.ErpWarehouseId || "");
      setToWarehouseId((current) => current || data.Warehouses.find((warehouse) => warehouse.ErpWarehouseId !== (data.Warehouses[0]?.ErpWarehouseId || ""))?.ErpWarehouseId || "");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "調撥單載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function resetFilters() {
    setKeyword("");
    setStatus("");
    setTransferPurpose("");
    setWarehouseId("");
    setFilterFromWarehouseId("");
    setFilterToWarehouseId("");
    const year = new Date().getFullYear();
    setDateFrom(`${year}-01-01`);
    setDateTo(`${year}-12-31`);
    void load(1, "", "", "", "", "", "", `${year}-01-01`, `${year}-12-31`);
  }

  async function searchProductsForTransfer() {
    if (productKeyword.trim().length < 2) {
      setMessage("商品搜尋至少輸入 2 個字。");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      const data = await searchErpProducts({ Keyword: productKeyword.trim() });
      setProductResults(data.Items);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "商品搜尋失敗。");
    } finally {
      setLoading(false);
    }
  }

  function addLine(item: ErpProductSearchItem) {
    const lineKey = `${item.ProductId}-${item.VariantId || "single"}-${Date.now()}`;
    setLines((current) => [
      ...current,
      {
        LineKey: lineKey,
        ProductId: item.ProductId,
        VariantId: item.VariantId,
        ProductName: item.ProductName,
        VariantLabel: item.VariantLabel,
        Sku: item.Sku,
        Quantity: 1,
        UnitCost: item.SupplierPrice?.UnitCost || 0,
        SerialText: "",
        Note: "",
        AvailableStock: item.AvailableStock,
      },
    ]);
    setProductResults([]);
    setProductKeyword("");
  }

  function updateLine(lineKey: string, patch: Partial<TransferLine>) {
    setLines((current) => current.map((line) => line.LineKey === lineKey ? { ...line, ...patch } : line));
    setFormErrors((current) => {
      const next = { ...current };
      Object.keys(patch).forEach((key) => { delete next[`Items.${lineKey}.${key}`]; });
      return next;
    });
  }

  function clearForm() {
    setNote("");
    setLines([]);
    setProductKeyword("");
    setProductResults([]);
    setFormErrors({});
  }

  function validateTransferForm() {
    const errors: FormErrors = {};
    if (!fromWarehouseId) errors.FromWarehouseId = "請選擇來源倉庫。";
    if (!toWarehouseId) errors.ToWarehouseId = "請選擇目的倉庫。";
    if (fromWarehouseId && toWarehouseId && fromWarehouseId === toWarehouseId) errors.ToWarehouseId = "來源倉與目的倉不能相同。";
    if (lines.length === 0) errors.Items = "請至少加入一項調撥品項。";
    lines.forEach((line) => {
      if (!Number.isFinite(Number(line.Quantity)) || Number(line.Quantity) < 1) errors[`Items.${line.LineKey}.Quantity`] = "調撥數量至少為 1。";
      if (!Number.isFinite(Number(line.UnitCost)) || Number(line.UnitCost) < 0) errors[`Items.${line.LineKey}.UnitCost`] = "單位成本不可小於 0。";
      const serialCount = line.SerialText.split(/\r?\n/).map((row) => row.trim()).filter(Boolean).length;
      if (serialCount > 0 && serialCount !== Number(line.Quantity)) errors[`Items.${line.LineKey}.SerialText`] = "序號筆數需與數量一致；非序號品可留空。";
    });
    return errors;
  }

  async function submitTransfer(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validateTransferForm();
    if (Object.keys(validationErrors).length > 0) {
      applySubmitErrors(validationErrors, setFormErrors, setMessage);
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    setFormErrors({});
    try {
      const result = await createErpStockTransfer({
        TransferPurpose: formTransferPurpose,
        FromWarehouseId: fromWarehouseId,
        ToWarehouseId: toWarehouseId,
        Note: note,
        Items: lines.map((line) => ({
          ProductId: line.ProductId,
          VariantId: line.VariantId,
          Quantity: Number(line.Quantity || 0),
          UnitCost: Number(line.UnitCost || 0),
          SerialText: line.SerialText,
          Note: line.Note,
        })),
      });
      setSuccess(result.message || "調撥單已建立。");
      clearForm();
      await load(1);
      navigate("/erp/stock-transfers");
    } catch (error) {
      const errors = apiErrors(error);
      if (Object.keys(errors).length > 0) applySubmitErrors(errors, setFormErrors, setMessage);
      else setMessage(errorText(error, "調撥單建立失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function advance(id: string, action: "ship" | "receive") {
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = action === "ship" ? await shipErpStockTransfer(id) : await receiveErpStockTransfer(id);
      setSuccess(result.message || "調撥狀態已更新。");
      await load(pagination.Page);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "調撥狀態更新失敗。");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid gap-5">
      {!isCreateMode ? <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6">
          <label className="min-w-0 md:col-span-2 xl:col-span-1 2xl:col-span-2">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="調撥單號、商品、SKU、倉庫" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </label>
          <label className="min-w-0 2xl:col-span-1">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">狀態</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">全部</option>
              {statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="min-w-0 2xl:col-span-1">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">調撥用途</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={transferPurpose} onChange={(event) => setTransferPurpose(event.target.value)}>
              <option value="">全部用途</option>
              {transferPurposeOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="min-w-0 2xl:col-span-1">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">倉庫</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}>
              <option value="">全部倉庫</option>
              {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}
            </select>
          </label>
          <label className="min-w-0 2xl:col-span-1">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">來源倉</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={filterFromWarehouseId} onChange={(event) => setFilterFromWarehouseId(event.target.value)}>
              <option value="">全部來源倉</option>
              {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}
            </select>
          </label>
          <label className="min-w-0 2xl:col-span-1">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">目的倉</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={filterToWarehouseId} onChange={(event) => setFilterToWarehouseId(event.target.value)}>
              <option value="">全部目的倉</option>
              {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}
            </select>
          </label>
          <label className="min-w-0 2xl:col-span-1">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="min-w-0 2xl:col-span-1">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
        </div>
        <div className="mt-4 flex flex-col-reverse gap-2 border-t border-[#edf0ee] pt-4 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
          <button className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white sm:w-auto" onClick={() => navigate("/erp/stock-transfers/new")} type="button"><ClipboardList size={18} />新增調撥單</button>
          <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
            <button className="inline-flex min-h-11 min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60 sm:min-w-[112px]" disabled={loading} type="submit"><Search size={18} />查詢</button>
            <button className="inline-flex min-h-11 min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] disabled:opacity-60 sm:min-w-[112px]" disabled={loading} onClick={resetFilters} type="button"><RefreshCw size={18} />重整</button>
          </div>
        </div>
      </form> : null}

      <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />

      {isCreateMode ? <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-extrabold text-[#203d32]">建立調撥單</h2>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">多品項調撥使用完整寬度表單，避免商品、序號與備註欄位被右側窄欄擠壓。</p>
          </div>
          <div className="rwd-action-grid lg:min-w-[260px]">
            <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/stock-transfers")} type="button">返回列表</button>
            <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={clearForm} type="button">清除內容</button>
          </div>
        </div>
      </section> : null}

      {isCreateMode ? (
        <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <form className="grid gap-4" onSubmit={submitTransfer}>
            <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
              <label className="block min-w-0">
                <span className="mb-2 block text-sm font-bold text-[#59665f]">調撥情境</span>
                <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={formTransferPurpose} onChange={(event) => setFormTransferPurpose(event.target.value)}>
                  {(transferPurposeOptions.length > 0 ? transferPurposeOptions : [{ Key: "WarehouseTransfer", Label: "一般倉庫調撥" }, { Key: "StoreReturnToHeadquarters", Label: "門市寄回總部" }, { Key: "HeadquartersToStore", Label: "總部補貨到門市" }]).map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
                </select>
              </label>
              <p className="mt-2 text-sm font-semibold leading-6 text-[#7b877f]">
                {formTransferPurpose === "StoreReturnToHeadquarters" ? "門市退回、檢修、換貨或客訴商品寄回總部時使用；來源倉請選門市倉，目的倉請選總部倉。" : formTransferPurpose === "HeadquartersToStore" ? "總部補貨或備品調撥到門市時使用；來源倉請選總部倉，目的倉請選門市倉。" : "一般倉庫間庫存移動使用；若是門市寄回總部，請切換為對應情境方便追蹤與報表統計。"}
              </p>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <label className="min-w-0" data-field="FromWarehouseId"><span className="mb-2 block text-sm font-bold text-[#59665f]">來源倉庫</span><select className={fieldClass(formErrors, "FromWarehouseId", "h-11 w-full px-3")} value={fromWarehouseId} onChange={(event) => { setFromWarehouseId(event.target.value); setFormErrors((current) => ({ ...current, FromWarehouseId: "" })); }}><option value="">請選擇</option>{warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}</select><FieldError errors={formErrors} name="FromWarehouseId" /></label>
              <label className="min-w-0" data-field="ToWarehouseId"><span className="mb-2 block text-sm font-bold text-[#59665f]">目的倉庫</span><select className={fieldClass(formErrors, "ToWarehouseId", "h-11 w-full px-3")} value={toWarehouseId} onChange={(event) => { setToWarehouseId(event.target.value); setFormErrors((current) => ({ ...current, ToWarehouseId: "" })); }}><option value="">請選擇</option>{warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}</select><FieldError errors={formErrors} name="ToWarehouseId" /></label>
            </div>

            <ErpProductSearchPanel
              keyword={productKeyword}
              loading={loading}
              onKeywordChange={setProductKeyword}
              onSearch={() => void searchProductsForTransfer()}
              onSelect={addLine}
              placeholder="搜尋商品、商品編號或 SKU"
              results={productResults}
              searchLabel="搜尋"
              selectLabel="加入調撥"
              title="商品搜尋"
            />

            <div data-field="Items">
            {lines.length > 0 ? (
              <div className="overflow-x-auto rounded-md border border-[#edf0ee]">
                <table className="w-full min-w-[1120px] table-fixed text-left text-sm">
                  <thead className="bg-[#f3f7f4] text-[#59665f]"><tr><th className="w-[300px] px-3 py-2">商品</th><th className="w-[110px] px-3 py-2">數量</th><th className="w-[130px] px-3 py-2">成本</th><th className="w-[280px] px-3 py-2">序號清單</th><th className="px-3 py-2">備註</th><th className="w-[100px] px-3 py-2">操作</th></tr></thead>
                  <tbody className="divide-y divide-[#edf0ee]">
                    {lines.map((line) => (
                      <tr key={line.LineKey}>
                        <td className="break-words px-3 py-3 font-bold text-[#203d32]">{line.ProductName}<p className="text-xs text-[#7b877f]">{line.VariantLabel || "單一規格"} / {line.Sku}</p><p className="mt-1 text-xs font-bold text-[#8c9790]">可用 {line.AvailableStock}</p></td>
                        <td className="px-3 py-3" data-field={`Items.${line.LineKey}.Quantity`}><input className={fieldClass(formErrors, `Items.${line.LineKey}.Quantity`, "h-10 w-full px-2")} min={1} type="number" value={line.Quantity} onChange={(event) => updateLine(line.LineKey, { Quantity: Number(event.target.value || 0) })} /><FieldError errors={formErrors} name={`Items.${line.LineKey}.Quantity`} /></td>
                        <td className="px-3 py-3" data-field={`Items.${line.LineKey}.UnitCost`}><input className={fieldClass(formErrors, `Items.${line.LineKey}.UnitCost`, "h-10 w-full px-2")} min={0} type="number" value={line.UnitCost} onChange={(event) => updateLine(line.LineKey, { UnitCost: Number(event.target.value || 0) })} /><FieldError errors={formErrors} name={`Items.${line.LineKey}.UnitCost`} /></td>
                        <td className="px-3 py-3" data-field={`Items.${line.LineKey}.SerialText`}><textarea className={fieldClass(formErrors, `Items.${line.LineKey}.SerialText`, "min-h-[80px] w-full px-2 py-2")} placeholder="一行一筆，可空白" value={line.SerialText} onChange={(event) => updateLine(line.LineKey, { SerialText: event.target.value })} /><FieldError errors={formErrors} name={`Items.${line.LineKey}.SerialText`} /></td>
                        <td className="px-3 py-3"><textarea className="min-h-[80px] w-full rounded-md border border-[#cfd8d2] px-2 py-2" value={line.Note} onChange={(event) => updateLine(line.LineKey, { Note: event.target.value })} /></td>
                        <td className="px-3 py-3"><button className="inline-flex min-h-10 w-full items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#8a1f1f]" onClick={() => setLines((current) => current.filter((item) => item.LineKey !== line.LineKey))} type="button">移除</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <EmptyState text="尚未加入調撥商品。" />}
            <FieldError errors={formErrors} name="Items" />
            </div>

            <label><span className="mb-2 block text-sm font-bold text-[#59665f]">調撥備註</span><textarea className="min-h-[90px] w-full rounded-md border border-[#cfd8d2] px-3 py-2" value={note} onChange={(event) => setNote(event.target.value)} /></label>
            <div className="rwd-action-grid justify-end">
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/stock-transfers")} type="button">取消</button>
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={clearForm} type="button">清除</button>
              <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading || lines.length === 0} type="submit">建立調撥單</button>
            </div>
          </form>
        </section>
      ) : null}

      {!isCreateMode ? <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
            <div>
              <h2 className="text-base font-extrabold text-[#203d32]">調撥單列表</h2>
              <p className="mt-1 text-sm font-semibold text-[#7b877f]">草稿出庫後成為在途，目的倉確認入庫後才完成。</p>
            </div>
            <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
          </div>
          <div className="grid gap-3 px-4 pb-4">
            <div className="grid gap-2 sm:grid-cols-4">
              <MetricCard icon={<ClipboardList size={20} />} label="草稿" value={count(counts.Draft || 0)} />
              <MetricCard icon={<Send size={20} />} label="已出庫" value={count(counts.Shipped || 0)} tone={(counts.Shipped || 0) > 0 ? "warning" : "default"} />
              <MetricCard icon={<Warehouse size={20} />} label="已入庫" value={count(counts.Received || 0)} />
              <MetricCard icon={<AlertTriangle size={20} />} label="已取消" value={count(counts.Cancelled || 0)} tone={(counts.Cancelled || 0) > 0 ? "danger" : "default"} />
            </div>
            {orders.length === 0 && !loading ? <EmptyState text="目前沒有符合條件的調撥單。" /> : null}
            {orders.map((order) => (
              <article className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4" key={order.ErpStockTransferOrderId}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="break-all text-xl font-extrabold text-[#203d32]">{order.TransferNumber}</p>
                    <p className="mt-1 break-words text-sm font-bold text-[#59665f]">{order.FromWarehouseName} → {order.ToWarehouseName}</p>
                    <p className="mt-1 text-xs font-semibold text-[#7b877f]">{order.CreatedAt}</p>
                  </div>
                  <div className="flex flex-wrap justify-end gap-2">
                    <StatusBadge tone={order.TransferPurpose === "StoreReturnToHeadquarters" ? "warning" : "default"}>{order.TransferPurposeLabel || "一般倉庫調撥"}</StatusBadge>
                    <StatusBadge tone={order.Status === "Shipped" ? "warning" : "default"}>{order.StatusLabel}</StatusBadge>
                  </div>
                </div>
                <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  <InfoBox label="調撥用途" value={order.TransferPurposeLabel || "一般倉庫調撥"} />
                  <InfoBox label="品項" value={`${order.ItemCount} 項`} />
                  <InfoBox label="總數量" value={`${order.QuantityTotal} 筆`} />
                  <InfoBox label="建立人" value={order.CreatedByAdminName || "-"} />
                </div>
                <div className="mt-3 overflow-x-auto rounded-md border border-[#edf0ee] bg-white">
                  <table className="w-full min-w-[720px] table-fixed text-left text-sm">
                    <thead className="bg-[#f3f7f4] text-[#59665f]">
                      <tr><th className="px-3 py-2">商品</th><th className="w-[130px] px-3 py-2">SKU</th><th className="w-[90px] px-3 py-2 text-right">數量</th><th className="w-[120px] px-3 py-2 text-right">單位成本</th><th className="w-[180px] px-3 py-2">備註</th></tr>
                    </thead>
                    <tbody className="divide-y divide-[#edf0ee]">
                      {order.Items.map((item) => (
                        <tr key={item.ErpStockTransferOrderItemId}>
                          <td className="break-words px-3 py-3 font-bold text-[#203d32]">{item.ProductName}<span className="text-[#7b877f]"> / {item.VariantLabel || "單一規格"}</span></td>
                          <td className="break-all px-3 py-3">{item.Sku || "-"}</td>
                          <td className="px-3 py-3 text-right font-bold">{item.Quantity}</td>
                          <td className="px-3 py-3 text-right">{money(item.UnitCost)}</td>
                          <td className="break-words px-3 py-3 text-[#59665f]">{item.Note || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <ErpActionBar className="mt-3">
                  <ErpDocumentPrintMenu
                    items={[{ label: "調撥單", description: "列印正式調撥明細與簽核欄", onClick: () => printTransferOrder(order) }]}
                  />
                  <button className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#315746] px-4 font-bold text-white disabled:bg-[#cfd8d2]" disabled={loading || !canShipTransfer(order)} onClick={() => advance(order.ErpStockTransferOrderId, "ship")} title={canShipTransfer(order) ? "確認出庫" : "只有草稿調撥單可以確認出庫"} type="button">確認出庫</button>
                  <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={loading || !canReceiveTransfer(order)} onClick={() => advance(order.ErpStockTransferOrderId, "receive")} title={canReceiveTransfer(order) ? "確認入庫" : "只有已出庫調撥單可以確認入庫"} type="button">確認入庫</button>
                </ErpActionBar>
              </article>
            ))}
          </div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
        </section> : null}

    </div>
  );
}

function CandidateCard({ active, onSelect, row }: { active: boolean; onSelect: () => void; row: AdminErpStoreOrderCandidate }) {
  const itemQuantity = (row.Items || []).reduce((sum, item) => sum + Number(item.Quantity || 0), 0);
  return (
    <article className={`rounded-md border p-4 ${active ? "border-[#85c69b] bg-[#f1faf4]" : "border-[#dfe6e1] bg-[#fbfcfb]"}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-all text-lg font-extrabold text-[#203d32]">{row.OrderNumber}</p>
          <p className="mt-1 break-words text-sm font-bold text-[#59665f]">{row.LocationName || "-"} / {row.CustomerName}</p>
          <p className="mt-1 break-all text-sm font-semibold text-[#7b877f]">{row.CustomerPhone}</p>
        </div>
        <StatusBadge>{row.StatusLabel}</StatusBadge>
      </div>
      <StoreOrderAuditLine row={row} />
      <div className="mt-3 grid gap-2 sm:grid-cols-3 xl:grid-cols-1 2xl:grid-cols-3">
        <InfoBox label="商品" value={row.ProductSummary || "-"} />
        <InfoBox label="需求數量" value={`${itemQuantity.toLocaleString("zh-TW")} 件`} />
        <InfoBox label="剩餘待付" value={money(row.BalanceAmount)} />
      </div>
      <button className="mt-3 inline-flex h-10 w-full items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={onSelect} type="button">
        {active ? "已加入採購建議" : "加入採購建議"}
      </button>
    </article>
  );
}

function OutboundCandidateRow({ active, expanded, onSelect, onToggle, row }: { active: boolean; expanded: boolean; onSelect: () => void; onToggle: () => void; row: AdminErpStoreOrderCandidate }) {
  return (
    <article className={`rounded-md border ${active ? "border-[#85c69b] bg-[#f1faf4]" : "border-[#dfe6e1] bg-[#fbfcfb]"}`}>
      <div className="grid gap-3 p-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="break-all font-extrabold text-[#203d32]">{row.OrderNumber}</p>
            <p className="mt-1 break-words text-sm font-bold text-[#59665f]">{row.LocationName || "-"} / {row.CustomerName}</p>
          </div>
          <StatusBadge>{row.StatusLabel}</StatusBadge>
        </div>
        <StoreOrderAuditLine row={row} />
        <div className="grid gap-2 sm:grid-cols-2">
          <InfoBox label="商品摘要" value={row.ProductSummary || "-"} />
          <InfoBox label="訂單金額" value={money(row.TotalAmount)} />
        </div>
        <div className="grid gap-2 sm:grid-cols-2">
          <button className="inline-flex min-h-10 w-full items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={onToggle} type="button">
            {expanded ? "收合明細" : "檢視明細"}
          </button>
          <button className="inline-flex min-h-10 w-full items-center justify-center rounded-md bg-[#315746] px-4 font-bold text-white disabled:bg-[#cfd8d2]" disabled={active} onClick={onSelect} type="button">
            {active ? "已選擇" : "選擇出庫"}
          </button>
        </div>
      </div>
      {expanded ? (
        <div className="grid gap-2 border-t border-[#edf0ee] bg-white p-3">
          <div className="grid gap-2 sm:grid-cols-2">
            <InfoBox label="客戶電話" value={row.CustomerPhone || "-"} />
            <InfoBox label="剩餘待付" value={money(row.BalanceAmount)} />
            <InfoBox label="建立時間" value={row.CreatedAt || "-"} />
            <InfoBox label="成交門市" value={row.LocationName || "-"} />
          </div>
          {row.Items && row.Items.length > 0 ? (
            <div className="overflow-x-auto rounded-md border border-[#edf0ee]">
              <table className="w-full min-w-[560px] table-fixed text-left text-sm">
                <thead className="bg-[#f3f7f4] text-[#59665f]">
                  <tr><th className="px-3 py-2">商品</th><th className="w-[120px] px-3 py-2">SKU</th><th className="w-[80px] px-3 py-2 text-right">數量</th><th className="w-[110px] px-3 py-2 text-right">成交價</th></tr>
                </thead>
                <tbody className="divide-y divide-[#edf0ee]">
                  {row.Items.map((item) => (
                    <tr key={item.StoreSalesOrderItemId}>
                      <td className="px-3 py-2"><p className="break-words font-bold text-[#203d32]">{item.ProductName}</p><p className="break-words text-[#7b877f]">{item.VariantLabel || "單一規格"}</p></td>
                      <td className="break-all px-3 py-2">{item.Sku || "-"}</td>
                      <td className="px-3 py-2 text-right font-bold">{item.Quantity}</td>
                      <td className="px-3 py-2 text-right font-bold">{money(item.DealPrice)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : <EmptyState text="此訂單尚無商品明細。" />}
        </div>
      ) : null}
    </article>
  );
}

function InfoBox({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="min-w-0 rounded-md bg-white px-3 py-2">
      <p className="text-xs font-bold text-[#7b877f]">{label}</p>
      <p className="mt-1 break-words font-bold text-[#203d32]">{value}</p>
    </div>
  );
}

function PurchaseView({ mode = "list" }: { mode?: "list" | "create" | "suggestions" }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isSuggestionMode = mode === "suggestions";
  const isCreateMode = mode === "create" || isSuggestionMode;
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [filterSupplierId, setFilterSupplierId] = useState("");
  const [sourceType, setSourceType] = useState("");
  const [dateFrom, setDateFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(`${new Date().getFullYear()}-12-31`);
  const [orders, setOrders] = useState<AdminErpPurchaseOrder[]>([]);
  const [candidates, setCandidates] = useState<AdminErpStoreOrderCandidate[]>([]);
  const [demandGroups, setDemandGroups] = useState<ErpPurchaseDemandGroup[]>([]);
  const [suppliers, setSuppliers] = useState<ErpSupplierOption[]>([]);
  const [warehouses, setWarehouses] = useState<ErpWarehouse[]>([]);
  const [locations, setLocations] = useState<ErpLocationOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [selectedId, setSelectedId] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [sourceLocationId, setSourceLocationId] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [supplierName, setSupplierName] = useState("");
  const [supplierKeyword, setSupplierKeyword] = useState("");
  const [externalOrderNumber, setExternalOrderNumber] = useState("");
  const [currency, setCurrency] = useState("TWD");
  const [exchangeRate, setExchangeRate] = useState(1);
  const [expectedArrivalDate, setExpectedArrivalDate] = useState("");
  const [note, setNote] = useState("");
  const [revisionSource, setRevisionSource] = useState<AdminErpPurchaseOrder | null>(null);
  const [revisionNote, setRevisionNote] = useState("");
  const [purchaseLines, setPurchaseLines] = useState<PurchaseLine[]>([]);
  const [productKeyword, setProductKeyword] = useState("");
  const [productResults, setProductResults] = useState<ErpProductSearchItem[]>([]);
  const [receivingId, setReceivingId] = useState("");
  const [receiveWarehouseId, setReceiveWarehouseId] = useState("");
  const [receiveNote, setReceiveNote] = useState("");
  const [receiveItems, setReceiveItems] = useState<Record<string, ReceiveLine>>({});
  const [receiveSerialRanges, setReceiveSerialRanges] = useState<Record<string, SerialRangeInput>>({});
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [purchaseAction, setPurchaseAction] = useState<{ type: "approve" | "cancel"; row: AdminErpPurchaseOrder; note: string; error: string } | null>(null);
  const handledPrefillKeyRef = useRef("");
  const selected = useMemo(() => candidates.find((row) => row.StoreSalesOrderId === selectedId) || null, [candidates, selectedId]);
  const selectedOrders = useMemo(() => candidates.filter((row) => selectedIds.includes(row.StoreSalesOrderId)), [candidates, selectedIds]);
  const candidateItemCount = useMemo(() => candidates.reduce((sum, row) => sum + (row.Items || []).reduce((lineSum, item) => lineSum + Number(item.Quantity || 0), 0), 0), [candidates]);
  const demandQuantityTotal = useMemo(() => demandGroups.reduce((sum, row) => sum + Number(row.DemandQuantity || 0), 0), [demandGroups]);
  const demandMissingPriceCount = useMemo(() => demandGroups.filter((row) => !row.RecommendedSupplier).length, [demandGroups]);
  const selectedDemandTotal = useMemo(() => selectedOrders.reduce((sum, row) => sum + Number(row.TotalAmount || 0), 0), [selectedOrders]);
  const purchaseLineTotal = useMemo(() => purchaseLines.reduce((sum, line) => sum + Number(line.Quantity || 0) * Number(line.UnitCost || 0), 0), [purchaseLines]);
  const missingCostCount = useMemo(() => purchaseLines.filter((line) => Number(line.UnitCost || 0) <= 0).length, [purchaseLines]);
  const selectedSourceLocation = useMemo(() => locations.find((location) => location.LocationId === sourceLocationId) || null, [locations, sourceLocationId]);
  const selectedSupplier = useMemo(() => suppliers.find((supplier) => supplier.ErpSupplierId === supplierId) || null, [suppliers, supplierId]);
  const supplierMatches = useMemo(() => {
    const key = supplierKeyword.trim().toLowerCase();
    if (key === "") { return suppliers.slice(0, 8); }
    return suppliers.filter((supplier) => `${supplier.Code} ${supplier.Name}`.toLowerCase().includes(key)).slice(0, 8);
  }, [supplierKeyword, suppliers]);

  const prefillProductId = searchParams.get("ProductId") || "";
  const prefillVariantId = searchParams.get("VariantId") || "";
  const prefillQuantity = Math.max(1, Number(searchParams.get("Quantity") || 1));
  const reviseFromId = searchParams.get("ReviseFrom") || "";
  const prefillKey = prefillProductId ? `${prefillProductId}:${prefillVariantId || "single"}:${prefillQuantity}` : "";

  function lineExistsForProduct(item: ErpProductSearchItem) {
    return purchaseLines.some((line) => line.ProductId === item.ProductId && (line.VariantId || "") === (item.VariantId || ""));
  }

  async function load(page = 1, nextKeyword = keyword, nextStatus = status, nextSupplierId = filterSupplierId, nextSourceType = sourceType, nextDateFrom = dateFrom, nextDateTo = dateTo, nextSourceLocationId = sourceLocationId) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpPurchaseOrders({ Keyword: nextKeyword.trim(), Status: nextStatus, ErpSupplierId: nextSupplierId, SourceType: nextSourceType, DateFrom: nextDateFrom, DateTo: nextDateTo, CandidateLocationId: nextSourceLocationId, Page: page, PerPage: 20 });
      setOrders(data.Orders);
      setCandidates(data.Candidates);
      setDemandGroups(data.DemandGroups || []);
      setSuppliers(data.SupplierOptions || []);
      setWarehouses(data.WarehouseOptions || []);
      setLocations(data.LocationOptions || []);
      setStatusOptions(data.StatusOptions);
      setPagination(data.Pagination);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "採購單載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isCreateMode || !prefillProductId) return;
    if (handledPrefillKeyRef.current === prefillKey) return;
    handledPrefillKeyRef.current = prefillKey;
    if (purchaseLines.some((line) => line.ProductId === prefillProductId && (line.VariantId || "") === prefillVariantId)) return;
    setLoading(true);
    setMessage("");
    searchErpProducts({ ProductId: prefillProductId, VariantId: prefillVariantId || undefined, ErpSupplierId: supplierId || undefined })
      .then((data) => {
        const item = data.Items.find((row) => row.ProductId === prefillProductId && (row.VariantId || "") === prefillVariantId);
        if (!item) {
          setMessage("找不到要帶入採購單的庫存商品，請改用商品模糊查詢加入。");
          return;
        }
        addProductLine(item, prefillQuantity);
        setProductKeyword(`${item.ProductName} ${item.Sku}`);
      })
      .catch((error) => setMessage(errorText(error, "庫存商品帶入採購單失敗。")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, prefillProductId, prefillVariantId, prefillKey]);

  useEffect(() => {
    if (!isCreateMode || !reviseFromId) return;
    setLoading(true);
    setMessage("");
    getErpPurchaseOrder(reviseFromId)
      .then((data) => {
        const order = data.Order;
        if (!order) {
          setMessage("找不到要修訂的採購單。");
          return;
        }
        setRevisionSource(order);
        setSelectedId("");
        setSelectedIds((order.SourceOrders || []).map((source) => source.StoreSalesOrderId));
        setSupplierId(order.ErpSupplierId || "");
        setSupplierName(order.SupplierName || order.SupplierMasterName || "");
        setSupplierKeyword(order.SupplierName || order.SupplierMasterName || "");
        setExternalOrderNumber(order.ExternalOrderNumber || "");
        setCurrency(order.Currency || "TWD");
        setExchangeRate(Number(order.ExchangeRate || 1));
        setExpectedArrivalDate(order.ExpectedArrivalDate || "");
        setNote(order.Note || "");
        setPurchaseLines((order.Items || []).map((item, index) => ({
          LineKey: `revision-${order.ErpPurchaseOrderId}-${index}-${item.ProductId}-${item.VariantId || "single"}`,
          ProductId: item.ProductId,
          VariantId: item.VariantId,
          ProductName: item.ProductName,
          Sku: item.Sku,
          VariantLabel: item.VariantLabel,
          Quantity: item.Quantity,
          UnitCost: item.UnitCost,
          SupplierSku: item.SupplierSku || "",
          PurchaseUnit: item.PurchaseUnit || "台",
          SupplierPriceStatus: item.UnitCost > 0 ? "Matched" : "Missing",
          Note: item.Note || "",
        })));
      })
      .catch((error) => setMessage(errorText(error, "採購單修訂資料載入失敗。")))
      .finally(() => setLoading(false));
  }, [isCreateMode, reviseFromId]);

  function purchaseLinesFromSelectedOrders(nextSelectedIds: string[]) {
    const lineMap = new Map<string, PurchaseLine>();
    candidates.filter((candidate) => nextSelectedIds.includes(candidate.StoreSalesOrderId)).forEach((row) => {
      (row.Items || []).forEach((item) => {
        const key = `${item.ProductId}:${item.VariantId || ""}`;
        const existing = lineMap.get(key);
        if (existing) {
          lineMap.set(key, { ...existing, Quantity: existing.Quantity + item.Quantity });
          return;
        }
        const price = item.RecommendedSupplier || null;
        const minimumQuantity = price?.MinimumOrderQuantity || 1;
        lineMap.set(key, {
          LineKey: `merged-${key}`,
          ProductId: item.ProductId,
          VariantId: item.VariantId,
          ProductName: item.ProductName,
          Sku: item.Sku,
          VariantLabel: item.VariantLabel,
          Quantity: Math.max(item.Quantity, minimumQuantity),
          UnitCost: price?.UnitCost || 0,
          SupplierSku: price?.SupplierSku || "",
          PurchaseUnit: price?.PurchaseUnit || "台",
          Currency: price?.Currency || currency,
          MinimumOrderQuantity: minimumQuantity,
          LeadTimeDays: price?.LeadTimeDays || 0,
          SupplierPriceStatus: price ? "Matched" as const : "Missing" as const,
          Note: "",
        });
      });
    });
    return Array.from(lineMap.values()).map((line) => ({ ...line, Quantity: Math.max(line.Quantity, line.MinimumOrderQuantity || 1) }));
  }

  function selectCandidate(row: AdminErpStoreOrderCandidate) {
    const nextIds = selectedIds.includes(row.StoreSalesOrderId)
      ? selectedIds.filter((id) => id !== row.StoreSalesOrderId)
      : [...selectedIds, row.StoreSalesOrderId];
    setSelectedIds(nextIds);
    setSelectedId(nextIds[0] || "");
    const nextLines = purchaseLinesFromSelectedOrders(nextIds);
    setPurchaseLines(nextLines);
    if (supplierId) { void enrichPurchaseLinesForSupplier(supplierId, nextLines); }
  }

  function applyDemandGroup(group: ErpPurchaseDemandGroup) {
    const nextIds = group.SourceOrderIds || [];
    setSelectedIds(nextIds);
    setSelectedId(nextIds[0] || "");
    const nextLines = purchaseLinesFromSelectedOrders(nextIds);
    const recommended = group.RecommendedSupplier || null;
    const recommendedSupplier = recommended ? suppliers.find((supplier) => supplier.ErpSupplierId === recommended.ErpSupplierId) : null;
    if (!supplierId && recommendedSupplier) {
      setSupplierId(recommendedSupplier.ErpSupplierId);
      setSupplierName(recommendedSupplier.Name);
      setSupplierKeyword(`${recommendedSupplier.Code} / ${recommendedSupplier.Name}`);
      if (recommendedSupplier.Currency) { setCurrency(recommendedSupplier.Currency); }
      setFormErrors((current) => ({ ...current, Supplier: "" }));
    }
    setPurchaseLines(nextLines);
    const nextSupplierId = supplierId || recommendedSupplier?.ErpSupplierId || "";
    if (nextSupplierId) { void enrichPurchaseLinesForSupplier(nextSupplierId, nextLines); }
  }

  function clearCandidate() {
    setSelectedId("");
    setSelectedIds([]);
    setPurchaseLines([]);
  }

  function selectSupplier(supplier: ErpSupplierOption) {
    setSupplierId(supplier.ErpSupplierId);
    setSupplierName(supplier.Name);
    setSupplierKeyword(`${supplier.Code} / ${supplier.Name}`);
    setFormErrors((current) => ({ ...current, Supplier: "" }));
    if (supplier.Currency) { setCurrency(supplier.Currency); }
    void enrichPurchaseLinesForSupplier(supplier.ErpSupplierId);
  }

  function clearSupplier() {
    setSupplierId("");
    setSupplierName("");
    setSupplierKeyword("");
    setPurchaseLines((current) => current.map((line) => ({ ...line, SupplierPriceStatus: line.UnitCost > 0 ? "Manual" : "Missing" })));
  }

  async function enrichPurchaseLinesForSupplier(nextSupplierId: string, baseLines = purchaseLines) {
    if (!nextSupplierId || baseLines.length === 0) { return; }
    setLoading(true);
    setMessage("");
    try {
      const enriched = await Promise.all(baseLines.map(async (line) => {
        const keyword = line.Sku || line.ProductCode || line.ProductName;
        const data = await searchErpProducts({ Keyword: keyword, ErpSupplierId: nextSupplierId });
        const matched = data.Items.find((item) => item.ProductId === line.ProductId && (item.VariantId || "") === (line.VariantId || ""));
        if (!matched?.SupplierPrice) {
          return { ...line, SupplierPriceStatus: line.UnitCost > 0 ? "Manual" as const : "Missing" as const };
        }
        return {
          ...line,
          UnitCost: matched.SupplierPrice.UnitCost,
          SupplierSku: matched.SupplierPrice.SupplierSku || "",
          PurchaseUnit: matched.SupplierPrice.PurchaseUnit || line.PurchaseUnit || "台",
          Currency: matched.SupplierPrice.Currency || currency,
          MinimumOrderQuantity: matched.SupplierPrice.MinimumOrderQuantity || 1,
          LeadTimeDays: matched.SupplierPrice.LeadTimeDays || 0,
          Quantity: Math.max(line.Quantity, matched.SupplierPrice.MinimumOrderQuantity || 1),
          SupplierPriceStatus: "Matched" as const,
        };
      }));
      setPurchaseLines(enriched);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "供應商價格帶入失敗，請手動確認採購成本。");
    } finally {
      setLoading(false);
    }
  }

  async function searchProducts() {
    if (productKeyword.trim() === "") { setProductResults([]); return; }
    setLoading(true); setMessage("");
    try {
      const data = await searchErpProducts({ Keyword: productKeyword.trim(), ErpSupplierId: supplierId || undefined });
      setProductResults(data.Items);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "商品查詢失敗。");
    } finally {
      setLoading(false);
    }
  }

  function addProductLine(item: ErpProductSearchItem, requestedQuantity = 1) {
    const price = item.SupplierPrice;
    const nextQuantity = Math.max(requestedQuantity, price?.MinimumOrderQuantity || 1);
    if (lineExistsForProduct(item)) {
      setPurchaseLines((current) => current.map((line) => line.ProductId === item.ProductId && (line.VariantId || "") === (item.VariantId || "") ? { ...line, Quantity: Number(line.Quantity || 0) + nextQuantity } : line));
      setProductKeyword("");
      setProductResults([]);
      setFormErrors((current) => ({ ...current, Items: "" }));
      return;
    }
    const lineKey = `manual-${item.ProductId}-${item.VariantId || "single"}-${Date.now()}`;
    setPurchaseLines((current) => {
      const existing = current.find((line) => line.ProductId === item.ProductId && (line.VariantId || "") === (item.VariantId || ""));
      if (existing) {
        return current.map((line) => line.LineKey === existing.LineKey ? { ...line, Quantity: Number(line.Quantity || 0) + nextQuantity } : line);
      }
      return [...current, {
        LineKey: lineKey,
        ProductId: item.ProductId,
        VariantId: item.VariantId,
        ProductCode: item.ProductCode,
        ProductName: item.ProductName,
        Sku: item.Sku,
        VariantLabel: item.VariantLabel,
        Quantity: nextQuantity,
        UnitCost: price?.UnitCost || 0,
        SupplierSku: price?.SupplierSku || "",
        PurchaseUnit: price?.PurchaseUnit || "台",
        Currency: price?.Currency || currency,
        MinimumOrderQuantity: price?.MinimumOrderQuantity || 1,
        LeadTimeDays: price?.LeadTimeDays || 0,
        SupplierPriceStatus: price ? "Matched" : "Missing",
        Note: "",
      }];
    });
    setProductKeyword("");
    setProductResults([]);
    setFormErrors((current) => ({ ...current, Items: "" }));
  }

  function updatePurchaseLine(lineKey: string, patch: Partial<PurchaseLine>) {
    setPurchaseLines((current) => current.map((item) => item.LineKey === lineKey ? { ...item, ...patch } : item));
    setFormErrors((current) => {
      const next = { ...current };
      Object.keys(patch).forEach((key) => { delete next[`Items.${lineKey}.${key}`]; });
      return next;
    });
  }

  function validatePurchaseForm() {
    const errors: FormErrors = {};
    if (!supplierId && supplierName.trim() === "") errors.Supplier = "請先選擇或輸入供應商。";
    if (revisionSource && revisionNote.trim() === "") errors.RevisionNote = "修訂版需要填寫修訂原因。";
    if (!Number.isFinite(Number(exchangeRate)) || Number(exchangeRate) <= 0) errors.ExchangeRate = "匯率必須大於 0。";
    if (purchaseLines.length === 0) errors.Items = "請至少加入一項採購品項。";
    purchaseLines.forEach((line) => {
      if (!Number.isFinite(Number(line.Quantity)) || Number(line.Quantity) < 1) errors[`Items.${line.LineKey}.Quantity`] = "採購數量至少為 1。";
      if (!Number.isFinite(Number(line.UnitCost)) || Number(line.UnitCost) <= 0) errors[`Items.${line.LineKey}.UnitCost`] = `採購品項「${line.ProductName}」尚未設定單位成本。`;
      if (!line.PurchaseUnit.trim()) errors[`Items.${line.LineKey}.PurchaseUnit`] = "請輸入採購單位。";
    });
    return errors;
  }

  async function submitPurchase(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validatePurchaseForm();
    if (Object.keys(validationErrors).length > 0) {
      applySubmitErrors(validationErrors, setFormErrors, setMessage);
      return;
    }
    setLoading(true); setMessage(""); setSuccess(""); setFormErrors({});
    try {
      const payload = {
        StoreSalesOrderId: selectedId,
        StoreSalesOrderIds: selectedIds,
        ErpSupplierId: supplierId || undefined,
        SupplierName: supplierId ? undefined : supplierName,
        ExternalOrderNumber: externalOrderNumber,
        Currency: currency,
        ExchangeRate: exchangeRate,
        ExpectedArrivalDate: expectedArrivalDate,
        Note: note,
        Items: purchaseLines.map((item) => ({
          LineKey: item.LineKey,
          StoreSalesOrderItemId: item.StoreSalesOrderItemId,
          ProductId: item.ProductId,
          VariantId: item.VariantId,
          Quantity: item.Quantity,
          UnitCost: item.UnitCost,
          SupplierSku: item.SupplierSku,
          PurchaseUnit: item.PurchaseUnit,
          Note: item.Note,
        })),
      };
      const result = revisionSource
        ? await reviseErpPurchaseOrder(revisionSource.ErpPurchaseOrderId, { ...payload, RevisionNote: revisionNote.trim() })
        : await createErpPurchaseOrder(payload);
      setSuccess(result.message || (revisionSource ? "採購單修訂版已送出。" : "採購單已建立。"));
      setSelectedId(""); setSelectedIds([]); setSupplierId(""); setSupplierName(""); setSupplierKeyword(""); setExternalOrderNumber(""); setCurrency("TWD"); setExchangeRate(1); setExpectedArrivalDate(""); setNote(""); setRevisionSource(null); setRevisionNote(""); setPurchaseLines([]);
      await load(1);
      navigate("/erp/purchase-orders");
    } catch (error) {
      const errors = apiErrors(error);
      if (Object.keys(errors).length > 0) applySubmitErrors(errors, setFormErrors, setMessage);
      else setMessage(errorText(error, revisionSource ? "採購單修訂失敗。" : "採購單建立失敗。"));
    } finally {
      setLoading(false);
    }
  }

  function openReceive(row: AdminErpPurchaseOrder) {
    if (!canReceivePurchase(row)) {
      setMessage("此採購單目前沒有可登錄到貨的品項。");
      return;
    }
    setReceivingId(row.ErpPurchaseOrderId);
    setReceiveWarehouseId(warehouses[0]?.ErpWarehouseId || "");
    setReceiveNote("");
    const next: Record<string, ReceiveLine> = {};
    const nextRanges: Record<string, SerialRangeInput> = {};
    (row.Items || []).forEach((item) => {
      next[item.ErpPurchaseOrderItemId] = {
        ErpPurchaseOrderItemId: item.ErpPurchaseOrderItemId,
        ReceiveQuantity: item.RemainingQuantity,
        AcceptedQuantity: 0,
        UnitCost: item.UnitCost,
        SerialText: "",
        EarSide: "",
        ExceptionType: "",
        ResolutionAction: "",
        InspectionChecklist: Object.fromEntries(purchaseInspectionChecks.map(([key]) => [key, true])),
        Note: "",
      };
      nextRanges[item.ErpPurchaseOrderItemId] = { Prefix: receiveSerialPrefix(item), Start: "", End: "" };
    });
    setReceiveItems(next);
    setReceiveSerialRanges(nextRanges);
  }

  function receiveSerialPrefix(item: NonNullable<AdminErpPurchaseOrder["Items"]>[number]) {
    const source = item.ProductCode || item.Sku || item.SupplierSku || "";
    const normalized = source.trim();
    return normalized ? `${normalized.replace(/-+$/, "")}-` : "";
  }

  function receiveSerialRows(text: string) {
    return text
      .split(/[\r\n,，;；\t]+/)
      .map((value) => value.trim())
      .filter(Boolean);
  }

  function normalizeReceiveSerialText(itemId: string, value: ReceiveLine) {
    const rows = Array.from(new Set(receiveSerialRows(value.SerialText)));
    setReceiveItems((current) => ({ ...current, [itemId]: { ...value, SerialText: rows.join("\n") } }));
  }

  function generateReceiveSerialRange(itemId: string, value: ReceiveLine) {
    const range = receiveSerialRanges[itemId] || { Prefix: "", Start: "", End: "" };
    const start = Number(range.Start);
    const end = Number(range.End);
    if (!Number.isInteger(start) || !Number.isInteger(end) || end < start) {
      setMessage("請輸入有效的序號起號與迄號。");
      return;
    }
    const width = Math.max(range.Start.length, range.End.length);
    const rows = Array.from({ length: end - start + 1 }, (_, index) => `${range.Prefix}${String(start + index).padStart(width, "0")}`);
    setReceiveItems((current) => ({ ...current, [itemId]: { ...value, SerialText: rows.join("\n"), ReceiveQuantity: rows.length } }));
  }

  function validateReceiveForm(id: string) {
    const row = orders.find((order) => order.ErpPurchaseOrderId === id);
    if (!row) return "找不到要登錄到貨的採購單，請重新整理後再試。";
    if (!receiveWarehouseId) return "請先選擇入庫倉庫。";
    const receiveRows = Object.values(receiveItems).filter((item) => Number(item.ReceiveQuantity || 0) > 0);
    if (receiveRows.length === 0) return "請至少輸入一筆本次到貨數量。";
    for (const item of receiveRows) {
      const source = row.Items.find((line) => line.ErpPurchaseOrderItemId === item.ErpPurchaseOrderItemId);
      if (!source) return "到貨品項資料異常，請重新開啟到貨登錄表單。";
      const arrivedQuantity = Number(item.ReceiveQuantity);
      if (!Number.isFinite(arrivedQuantity) || arrivedQuantity < 1) return `「${source.ProductName}」本次到貨數量需至少為 1。`;
      if (arrivedQuantity > Number(source.RemainingQuantity || 0)) return `「${source.ProductName}」本次到貨數量不可超過待到貨數量。`;
      if (!Number.isFinite(Number(item.UnitCost)) || Number(item.UnitCost) < 0) return `「${source.ProductName}」單位成本不可小於 0。`;
      const serialRows = receiveSerialRows(item.SerialText);
      const serialCount = serialRows.length;
      if (serialCount > 0 && serialCount !== arrivedQuantity) return `「${source.ProductName}」序號筆數需與本次到貨數量一致；非序號商品可留空。`;
      if (new Set(serialRows).size !== serialRows.length) return `「${source.ProductName}」序號清單有重複，請先整理後再登錄到貨。`;
    }
    return "";
  }

  async function receive(id: string) {
    const validationMessage = validateReceiveForm(id);
    if (validationMessage) {
      setMessage(validationMessage);
      return;
    }
    setLoading(true); setMessage(""); setSuccess("");
    try {
      const items: ErpPurchaseReceiveItem[] = Object.values(receiveItems).filter((item) => item.ReceiveQuantity > 0).map((item) => ({
        ErpPurchaseOrderItemId: item.ErpPurchaseOrderItemId,
        ReceiveQuantity: item.ReceiveQuantity,
        AcceptedQuantity: 0,
        UnitCost: item.UnitCost,
        InspectionNote: item.Note || undefined,
        Serials: receiveSerialRows(item.SerialText).map((serialNumber) => ({ SerialNumber: serialNumber, EarSide: item.EarSide || undefined, Note: item.Note || undefined })),
      }));
      const result = await receiveErpPurchaseOrder(id, { WarehouseId: receiveWarehouseId || undefined, Note: receiveNote, Items: items });
      setSuccess(result.message || "採購到貨已登錄，請到採購入庫驗收完成放行。");
      setReceivingId(""); setReceiveItems({}); setReceiveSerialRanges({});
      await load(pagination.Page);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "採購到貨登錄失敗。");
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    const year = new Date().getFullYear();
    setKeyword(""); setStatus(""); setFilterSupplierId(""); setSourceType(""); setDateFrom(`${year}-01-01`); setDateTo(`${year}-12-31`);
    void load(1, "", "", "", "", `${year}-01-01`, `${year}-12-31`);
  }

  function canApprovePurchase(row: AdminErpPurchaseOrder) {
    return ["Draft", "PendingApproval"].includes(row.Status);
  }

  function canRevisePurchase(row: AdminErpPurchaseOrder) {
    return ["Draft", "PendingApproval", "Ordered"].includes(row.Status) && Number(row.ArrivedQuantityTotal || 0) <= 0 && (row.Receipts || []).length === 0;
  }

  function canCancelPurchase(row: AdminErpPurchaseOrder) {
    return row.Status !== "Cancelled" && row.Status !== "Received" && Number(row.ArrivedQuantityTotal || 0) <= 0 && (row.Receipts || []).length === 0;
  }

  function approvePurchase(row: AdminErpPurchaseOrder) {
    if (!canApprovePurchase(row)) {
      setMessage("此採購單目前狀態不可核准。");
      return;
    }
    setPurchaseAction({ type: "approve", row, note: "", error: "" });
  }

  function cancelPurchase(row: AdminErpPurchaseOrder) {
    if (!canCancelPurchase(row)) {
      setMessage("此採購單已有到貨或已完成，不能直接取消。");
      return;
    }
    setPurchaseAction({ type: "cancel", row, note: "", error: "" });
  }

  async function confirmPurchaseAction() {
    if (!purchaseAction) return;
    const note = purchaseAction.note.trim();
    if (purchaseAction.type === "cancel" && note === "") {
      setPurchaseAction((current) => current ? { ...current, error: "取消採購單需要填寫取消原因。" } : current);
      return;
    }
    setLoading(true); setMessage(""); setSuccess("");
    try {
      if (purchaseAction.type === "approve") {
        const result = await approveErpPurchaseOrder(purchaseAction.row.ErpPurchaseOrderId, { ApprovalNote: note });
        setSuccess(result.Order ? `採購單 ${result.Order.PurchaseNumber} 已核准。` : "採購單已核准。");
      } else {
        await cancelErpPurchaseOrder(purchaseAction.row.ErpPurchaseOrderId, { CancelReason: note });
        setSuccess(`採購單 ${purchaseAction.row.PurchaseNumber} 已取消。`);
      }
      setPurchaseAction(null);
      await load(pagination.Page);
    } catch (error) {
      setMessage(errorText(error, purchaseAction.type === "approve" ? "採購單核准失敗。" : "採購單取消失敗。"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid gap-5">
      {!isCreateMode ? <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <div className="grid gap-3 2xl:grid-cols-[minmax(240px,1.2fr)_180px_200px_180px_180px_180px] 2xl:items-end">
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="單號、供應商、商品、SKU" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">狀態</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">全部</option>
              {statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">供應商</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={filterSupplierId} onChange={(event) => setFilterSupplierId(event.target.value)}>
              <option value="">全部供應商</option>
              {suppliers.map((supplier) => <option key={supplier.ErpSupplierId} value={supplier.ErpSupplierId}>{supplier.Code} / {supplier.Name}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">來源</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={sourceType} onChange={(event) => setSourceType(event.target.value)}>
              <option value="">全部來源</option>
              <option value="General">一般採購</option>
              <option value="StoreOrder">門市訂單</option>
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
        </div>
        <div className="rwd-filter-actions mt-3 justify-end">
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => navigate("/erp/purchase-suggestions")} type="button"><ClipboardList size={18} />採購需求 / 建議</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={() => navigate("/erp/purchase-orders/new")} type="button"><ClipboardList size={18} />新增採購單</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:opacity-60" disabled={loading} type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={reset} type="button"><RefreshCw size={18} />重整</button>
        </div>
      </form> : null}
      <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />
      <ErpTextActionDialog
        open={Boolean(purchaseAction)}
        title={purchaseAction?.type === "cancel" ? "取消採購單" : "核准採購單"}
        description={purchaseAction ? `${purchaseAction.type === "cancel" ? "取消" : "核准"}採購單 ${purchaseAction.row.PurchaseNumber}。${purchaseAction.type === "cancel" ? "取消後會停止後續到貨與驗收流程，請留下正式原因。" : "核准後才可進入後續下訂、到貨與驗收流程，可留下核准備註。"}` : ""}
        label={purchaseAction?.type === "cancel" ? "取消原因" : "核准備註"}
        placeholder={purchaseAction?.type === "cancel" ? "例如：供應商無法供貨、重複建立、需求取消。" : "例如：價格與交期已確認，可進行下訂。"}
        value={purchaseAction?.note || ""}
        required={purchaseAction?.type === "cancel"}
        confirmText={purchaseAction?.type === "cancel" ? "確認取消" : "確認核准"}
        tone={purchaseAction?.type === "cancel" ? "danger" : "default"}
        loading={loading}
        error={purchaseAction?.error || ""}
        onChange={(value) => setPurchaseAction((current) => current ? { ...current, note: value, error: "" } : current)}
        onClose={() => { if (!loading) setPurchaseAction(null); }}
        onConfirm={confirmPurchaseAction}
      />
      <div className="grid min-w-0 gap-5">
        {!isCreateMode ? <section className="min-w-0 rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
            <h2 className="text-base font-extrabold text-[#203d32]">採購單列表</h2>
            <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
          </div>
          <div className="grid gap-3 px-4 pb-4">
            {orders.length === 0 ? <EmptyState text="目前尚未建立採購單。" /> : null}
            {orders.map((row) => (
              <article className="min-w-0 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4" key={row.ErpPurchaseOrderId}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2"><p className="break-all text-lg font-extrabold text-[#203d32]">{row.PurchaseNumber}</p><StatusBadge>{row.StatusLabel}</StatusBadge></div>
                    <p className="mt-1 break-all text-sm font-bold text-[#59665f]">門市訂單：{(row.SourceOrders || []).length ? (row.SourceOrders || []).map((source) => source.OrderNumber || source.StoreSalesOrderId).join("、") : (row.StoreOrderNumber || "-")}</p>
                    {purchaseSourceAuditSummary(row) ? <p className="mt-2 inline-flex max-w-full rounded-md bg-[#fff8e8] px-3 py-2 text-xs font-extrabold leading-5 text-[#8a5b14]">來源訂單性質：{purchaseSourceAuditSummary(row)}</p> : null}
                  </div>
                  <ErpActionBar className="lg:min-w-[240px]">
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#315746] px-4 font-bold text-white disabled:bg-[#cfd8d2]" disabled={loading || !canApprovePurchase(row)} onClick={() => void approvePurchase(row)} title={canApprovePurchase(row) ? "核准後才可登錄到貨" : "此狀態不可核准"} type="button">核准</button>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={loading || !canRevisePurchase(row)} onClick={() => navigate(`/erp/purchase-orders/new?ReviseFrom=${row.ErpPurchaseOrderId}`)} title={canRevisePurchase(row) ? "建立修訂版" : "已有到貨或狀態不可修訂"} type="button">修訂</button>
                    <ErpDocumentPrintMenu
                      items={[
                        { label: "採購單", description: "正式採購單與簽核欄", onClick: () => printPurchaseOrder(row) },
                        { label: "驗收單", description: "列印最近驗收紀錄", disabled: (row.Receipts || []).length === 0, disabledReason: "尚未有驗收紀錄", onClick: () => printPurchaseReceipt(row) },
                      ]}
                    />
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#315746] px-4 font-bold text-white disabled:bg-[#cfd8d2]" disabled={loading || !canReceivePurchase(row)} onClick={() => openReceive(row)} title={canReceivePurchase(row) ? "登錄到貨" : "此採購單目前沒有待到貨數量"} type="button">到貨登錄</button>
                    <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-4 font-bold text-[#a33a2f] disabled:opacity-50" disabled={loading || !canCancelPurchase(row)} onClick={() => void cancelPurchase(row)} title={canCancelPurchase(row) ? "取消採購單" : "已有到貨或狀態不可取消"} type="button">取消</button>
                  </ErpActionBar>
                </div>
                <div className="mt-3 grid gap-2 md:grid-cols-4">
                  <InfoBox label="供應商" value={row.SupplierName || row.SupplierMasterName || "-"} />
                  <InfoBox label="預計到貨" value={row.ExpectedArrivalDate || "-"} />
                  <InfoBox label="採購總額" value={money(row.PurchaseTotal || 0)} />
                  <InfoBox label="到貨 / 合格入庫 / 採購量" value={`${row.ArrivedQuantityTotal || 0} / ${row.ReceivedQuantityTotal || 0} / ${row.QuantityTotal}`} />
                </div>
                <div className="mt-3 grid gap-2 md:grid-cols-4">
                  <InfoBox label="版次" value={`第 ${row.RevisionNo || 1} 版`} />
                  <InfoBox label="核准時間" value={row.ApprovedAt || "-"} />
                  <InfoBox label="核准人" value={row.ApprovedByAdminName || "-"} />
                  <InfoBox label="取消 / 修訂原因" value={row.CancelReason || row.RevisionNote || "-"} />
                </div>
                {(row.SourceOrders || []).length > 0 ? (
                  <div className="mt-3 rounded-md border border-[#dfe6e1] bg-white p-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-extrabold text-[#203d32]">來源門市訂單追蹤</p>
                        <p className="mt-1 text-xs font-bold leading-5 text-[#7b877f]">此採購單由下列門市訂單合併建立；後續到貨、驗收與出庫可回追來源。</p>
                      </div>
                      <StatusBadge>{(row.SourceOrders || []).length} 張來源單</StatusBadge>
                    </div>
                    <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                      {(row.SourceOrders || []).map((source) => (
                        <div className="min-w-0 rounded-md bg-[#f7faf8] px-3 py-2" key={source.StoreSalesOrderId}>
                            <p className="break-all text-sm font-extrabold text-[#203d32]">{source.OrderNumber || source.StoreSalesOrderId}</p>
                            <p className="mt-1 break-words text-xs font-bold text-[#59665f]">{source.LocationName || "-"} / {source.CustomerName || "-"}</p>
                            <StoreOrderAuditLine row={source} />
                          </div>
                        ))}
                    </div>
                  </div>
                ) : null}
                {(row.Receipts || []).length > 0 ? (
                  <div className="mt-3 rounded-md border border-[#edf0ee] bg-white">
                    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#edf0ee] px-3 py-2">
                      <p className="text-sm font-extrabold text-[#203d32]">到貨與驗收紀錄</p>
                      <span className="text-xs font-bold text-[#7b877f]">{count(row.Receipts?.length || 0)}</span>
                    </div>
                    <div className="grid gap-2 p-3">
                      {(row.Receipts || []).slice(0, 3).map((receipt) => (
                        <div className="grid gap-2 rounded-md bg-[#f7faf8] p-3 text-sm md:grid-cols-[minmax(160px,1fr)_120px_120px_120px_minmax(180px,1fr)]" key={receipt.ErpPurchaseReceiptId}>
                          <div className="min-w-0">
                            <p className="break-all font-extrabold text-[#203d32]">{receipt.ReceiptNumber}</p>
                            <p className="mt-1 break-words font-semibold text-[#7b877f]">{receipt.ReceivedAt} / {receipt.WarehouseName || "-"}</p>
                          </div>
                          <InfoBox label="到貨" value={`${receipt.ArrivedQuantity ?? receipt.QuantityTotal} 件`} />
                          <InfoBox label="已放行入庫" value={`${receipt.AcceptedQuantity ?? 0} 件`} />
                          <InfoBox label="異常" value={`${receipt.RejectedQuantity ?? 0} 件`} />
                          <div className="min-w-0">
                            <StatusBadge>{receipt.InspectionStatusLabel || "全部合格"}</StatusBadge>
                            {receipt.ExceptionSummary ? <p className="mt-2 break-words font-bold text-[#9a5b00]">{receipt.ExceptionSummary}</p> : null}
                            {receipt.ResolutionActionLabel ? <p className="mt-1 break-words font-semibold text-[#59665f]">處理：{receipt.ResolutionActionLabel}</p> : null}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
                {receivingId === row.ErpPurchaseOrderId ? (
                  <div className="min-w-0 mt-4 rounded-md border border-[#cfd8d2] bg-white p-4">
                    <div className="grid gap-3 md:grid-cols-[220px_1fr]">
                      <label><span className="mb-2 block text-sm font-bold text-[#59665f]">入庫倉庫</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={receiveWarehouseId} onChange={(event) => setReceiveWarehouseId(event.target.value)}>{warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}</select></label>
                      <TextField label="整張到貨備註" value={receiveNote} onChange={setReceiveNote} />
                    </div>
                    <div className="mt-3 max-w-full overflow-x-auto rounded-md border border-[#edf0ee]">
                      <table className="w-full min-w-[980px] table-fixed text-left text-sm">
                        <thead className="bg-[#f3f7f4] text-[#59665f]"><tr><th className="w-[360px] px-3 py-2">商品</th><th className="w-[100px] px-3 py-2 text-right">待到貨</th><th className="w-[130px] px-3 py-2">本次到貨</th><th className="w-[140px] px-3 py-2">單位成本</th><th className="w-[130px] px-3 py-2">耳側</th></tr></thead>
                        <tbody className="divide-y divide-[#edf0ee]">
                          {(row.Items || []).map((item) => {
                            const value = receiveItems[item.ErpPurchaseOrderItemId] || { ErpPurchaseOrderItemId: item.ErpPurchaseOrderItemId, ReceiveQuantity: 0, AcceptedQuantity: 0, UnitCost: item.UnitCost, SerialText: "", EarSide: "", ExceptionType: "", ResolutionAction: "", InspectionChecklist: Object.fromEntries(purchaseInspectionChecks.map(([key]) => [key, true])), Note: "" };
                            const serialRange = receiveSerialRanges[item.ErpPurchaseOrderItemId] || { Prefix: receiveSerialPrefix(item), Start: "", End: "" };
                            return (
                              <Fragment key={item.ErpPurchaseOrderItemId}>
                              <tr>
                                <td className="px-3 py-3"><p className="break-words font-bold text-[#203d32]">{item.ProductName}</p><p className="break-words text-[#7b877f]">{item.VariantLabel || item.Sku || "-"}</p><p className="mt-1 break-all text-xs font-bold text-[#8c9790]">商品編號：{item.ProductCode || "-"} / 供應商料號：{item.SupplierSku || "-"}</p></td>
                                <td className="px-3 py-3 text-right font-bold">{item.RemainingQuantity}</td>
                                <td className="px-3 py-3"><input className="h-10 w-full rounded-md border border-[#cfd8d2] px-2" min={0} max={item.RemainingQuantity} type="number" value={value.ReceiveQuantity} onChange={(event) => { const nextQuantity = Number(event.target.value || 0); setReceiveItems((current) => ({ ...current, [item.ErpPurchaseOrderItemId]: { ...value, ReceiveQuantity: nextQuantity, AcceptedQuantity: 0 } })); }} /></td>
                                <td className="px-3 py-3"><input className="h-10 w-full rounded-md border border-[#cfd8d2] px-2" min={0} type="number" value={value.UnitCost} onChange={(event) => setReceiveItems((current) => ({ ...current, [item.ErpPurchaseOrderItemId]: { ...value, UnitCost: Number(event.target.value || 0) } }))} /></td>
                                <td className="px-3 py-3"><select className="h-10 w-full rounded-md border border-[#cfd8d2] px-2" value={value.EarSide} onChange={(event) => setReceiveItems((current) => ({ ...current, [item.ErpPurchaseOrderItemId]: { ...value, EarSide: event.target.value } }))}><option value="">不指定</option><option value="Left">左耳</option><option value="Right">右耳</option></select></td>
                              </tr>
                              <tr className="bg-[#fbfcfb]">
                                <td className="px-3 pb-4 pt-0" colSpan={5}>
                                  <div className="grid gap-3 rounded-md border border-[#edf0ee] bg-white p-3 xl:grid-cols-[minmax(520px,1.35fr)_minmax(300px,.75fr)]">
                                    <div className="grid min-w-0 gap-2">
                                      <div className="flex flex-wrap items-center justify-between gap-2">
                                        <p className="text-sm font-extrabold text-[#203d32]">序號清單</p>
                                        <button className="inline-flex min-h-8 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 text-xs font-extrabold text-[#315746]" onClick={() => normalizeReceiveSerialText(item.ErpPurchaseOrderItemId, value)} type="button">整理去重</button>
                                      </div>
                                      <textarea className="min-h-[112px] w-full rounded-md border border-[#cfd8d2] px-3 py-2" placeholder="可用條碼槍掃描、Excel 貼上；支援換行、逗號、Tab 分隔" value={value.SerialText} onChange={(event) => setReceiveItems((current) => ({ ...current, [item.ErpPurchaseOrderItemId]: { ...value, SerialText: event.target.value } }))} />
                                      <div className="grid gap-2 md:grid-cols-[minmax(180px,1fr)_96px_96px_88px]">
                                        <input className="h-10 min-w-0 rounded-md border border-[#cfd8d2] px-3" placeholder={`前綴，建議 ${receiveSerialPrefix(item) || "商品編號-"}`} value={serialRange.Prefix} onChange={(event) => setReceiveSerialRanges((current) => ({ ...current, [item.ErpPurchaseOrderItemId]: { ...(current[item.ErpPurchaseOrderItemId] || { Prefix: receiveSerialPrefix(item), Start: "", End: "" }), Prefix: event.target.value } }))} />
                                        <input className="h-10 min-w-0 rounded-md border border-[#cfd8d2] px-3" placeholder="起號" value={serialRange.Start} onChange={(event) => setReceiveSerialRanges((current) => ({ ...current, [item.ErpPurchaseOrderItemId]: { ...(current[item.ErpPurchaseOrderItemId] || { Prefix: receiveSerialPrefix(item), Start: "", End: "" }), Start: event.target.value } }))} />
                                        <input className="h-10 min-w-0 rounded-md border border-[#cfd8d2] px-3" placeholder="迄號" value={serialRange.End} onChange={(event) => setReceiveSerialRanges((current) => ({ ...current, [item.ErpPurchaseOrderItemId]: { ...(current[item.ErpPurchaseOrderItemId] || { Prefix: receiveSerialPrefix(item), Start: "", End: "" }), End: event.target.value } }))} />
                                        <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" onClick={() => generateReceiveSerialRange(item.ErpPurchaseOrderItemId, value)} type="button">產生</button>
                                      </div>
                                      <p className="text-xs font-bold leading-5 text-[#7b877f]">已輸入 {receiveSerialRows(value.SerialText).length} / {value.ReceiveQuantity || 0} 筆；序號產生會使用「{serialRange.Prefix || receiveSerialPrefix(item) || "前綴"}」加上起訖號。</p>
                                    </div>
                                    <label className="grid min-w-0 content-start gap-2 self-start">
                                      <span className="text-sm font-extrabold text-[#203d32]">品項到貨備註</span>
                                      <textarea className="min-h-[184px] w-full rounded-md border border-[#cfd8d2] px-3 py-2" placeholder="外箱狀況、到貨文件、短缺備註；品質檢核請到採購入庫驗收處理" value={value.Note} onChange={(event) => setReceiveItems((current) => ({ ...current, [item.ErpPurchaseOrderItemId]: { ...value, Note: event.target.value } }))} />
                                    </label>
                                  </div>
                                </td>
                              </tr>
                              </Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div className="rwd-action-grid mt-3"><button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white" disabled={loading} onClick={() => receive(row.ErpPurchaseOrderId)} type="button">確認到貨登錄</button><button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => setReceivingId("")} type="button">取消</button></div>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
        </section> : null}

        {isCreateMode ? <aside className="grid min-w-0 max-w-full gap-4 overflow-hidden">
          {isSuggestionMode ? (
            <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <h2 className="text-lg font-extrabold text-[#203d32]">採購需求池</h2>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">彙總總管理端分流為「待廠商採購」的門市訂單，可多選後合併相同商品規格，依供應商價格、MOQ 與交期產生採購單。</p>
                </div>
                <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/purchase-orders/new")} type="button">一般採購</button>
              </div>
              <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
                <InfoBox label="待採購來源單" value={`${candidates.length.toLocaleString("zh-TW")} 張`} />
                <InfoBox label="待採購商品數" value={`${candidateItemCount.toLocaleString("zh-TW")} 件 / ${demandGroups.length.toLocaleString("zh-TW")} 種`} />
                <InfoBox label="需求彙總數量" value={`${demandQuantityTotal.toLocaleString("zh-TW")} 件${demandMissingPriceCount ? ` / ${demandMissingPriceCount} 種缺成本` : ""}`} />
                <InfoBox label="已選門市訂單" value={`${selectedOrders.length.toLocaleString("zh-TW")} 張 / ${money(selectedDemandTotal)}`} />
                <InfoBox label="建議採購金額" value={`${money(purchaseLineTotal)}${missingCostCount ? ` / ${missingCostCount} 項缺成本` : ""}`} />
              </div>
              <div className="mt-4 rounded-md border border-[#dfe6e1] bg-[#fbfcfb]">
                <div className="flex flex-col gap-2 border-b border-[#edf0ee] px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <h3 className="text-sm font-extrabold text-[#203d32]">採購需求彙總</h3>
                    <p className="mt-1 text-xs font-bold leading-5 text-[#7b877f]">同商品規格會合併需求量，並顯示在途量、建議採購量與供應商價格狀態。</p>
                  </div>
                  <StatusBadge>{demandGroups.length} 種需求</StatusBadge>
                </div>
                {demandGroups.length === 0 ? (
                  <div className="p-3"><EmptyState text={`${selectedSourceLocation ? selectedSourceLocation.Name : "目前範圍"}沒有可彙總的待採購需求。`} /></div>
                ) : (
                  <div className="max-w-full overflow-x-auto p-3">
                    <table className="w-full min-w-[1420px] table-fixed text-left text-sm">
                      <thead className="sticky top-0 z-10 bg-[#f3f7f4] text-[#59665f]">
                        <tr>
                          <th className="w-[240px] px-3 py-3">商品 / 規格</th>
                          <th className="w-[130px] px-3 py-3">SKU</th>
                          <th className="w-[170px] px-3 py-3">建議原因</th>
                          <th className="w-[130px] px-3 py-3">需求</th>
                          <th className="w-[130px] px-3 py-3">在途 / 建議</th>
                          <th className="w-[210px] px-3 py-3">來源門市訂單</th>
                          <th className="w-[260px] px-3 py-3">供應商比價</th>
                          <th className="w-[230px] px-3 py-3">推薦供應商</th>
                          <th className="w-[140px] px-3 py-3 text-right">成本參考</th>
                          <th className="w-[120px] px-3 py-3 text-right">操作</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#edf0ee] bg-white">
                        {demandGroups.map((group) => {
                          const supplier = group.RecommendedSupplier;
                          return (
                            <tr className="align-top" key={`${group.ProductId}-${group.VariantId || "single"}`}>
                              <td className="px-3 py-3">
                                <p className="break-words font-extrabold text-[#203d32]">{group.ProductName}</p>
                                <p className="mt-1 break-words text-xs font-bold text-[#7b877f]">{group.VariantLabel || "單一規格"}</p>
                              </td>
                              <td className="break-all px-3 py-3 font-bold text-[#59665f]">{group.Sku || "-"}</td>
                              <td className="px-3 py-3">
                                <div className="flex flex-wrap gap-1.5">
                                  {(group.SuggestedReasons || []).length ? (group.SuggestedReasons || []).map((reason) => (
                                    <StatusBadge key={reason} tone={reason.includes("缺") ? "warning" : "default"}>{reason}</StatusBadge>
                                  )) : <StatusBadge>門市需求</StatusBadge>}
                                </div>
                              </td>
                              <td className="px-3 py-3 font-bold text-[#203d32]">
                                <p>{group.DemandQuantity.toLocaleString("zh-TW")} 件</p>
                                <p className="mt-1 text-xs text-[#7b877f]">{group.StoreOrderCount} 張單</p>
                              </td>
                              <td className="px-3 py-3 font-bold text-[#59665f]">
                                <p>在途 {group.IncomingQuantity.toLocaleString("zh-TW")}</p>
                                <p className="mt-1 text-[#203d32]">建議 {group.SuggestedPurchaseQuantity.toLocaleString("zh-TW")}</p>
                              </td>
                              <td className="px-3 py-3">
                                <p className="break-words font-bold text-[#203d32]">{group.LocationSummary || "-"}</p>
                                <p className="mt-1 break-words text-xs font-semibold text-[#7b877f]">{group.SourceOrders.slice(0, 3).map((order) => order.OrderNumber).filter(Boolean).join("、")}{group.SourceOrders.length > 3 ? "..." : ""}</p>
                                {Array.from(new Set(group.SourceOrders.map((order) => storeOrderAuditSummary(order)).filter(Boolean))).length > 0 ? (
                                  <p className="mt-2 break-words rounded-md bg-[#fff8e8] px-2 py-1.5 text-xs font-extrabold leading-5 text-[#8a5b14]">
                                    {Array.from(new Set(group.SourceOrders.map((order) => storeOrderAuditSummary(order)).filter(Boolean))).join("；")}
                                  </p>
                                ) : null}
                              </td>
                              <td className="px-3 py-3">
                                {group.SupplierPriceOptions.length ? (
                                  <div className="grid gap-1.5">
                                    {group.SupplierPriceOptions.slice(0, 3).map((option) => (
                                      <div className={`rounded-md px-2 py-1.5 ${supplier?.ErpSupplierId === option.ErpSupplierId ? "bg-[#eef8f1] text-[#203d32]" : "bg-[#fbfcfb] text-[#59665f]"}`} key={`${group.ProductId}-${group.VariantId || "single"}-${option.ErpSupplierId}`}>
                                        <p className="break-words text-xs font-extrabold">{option.SupplierName || option.SupplierCode || "未命名供應商"}</p>
                                        <p className="mt-0.5 text-xs font-bold">{money(option.UnitCost)} / MOQ {option.MinimumOrderQuantity} / {option.LeadTimeDays} 天</p>
                                        {option.RecommendationReason ? <p className="mt-0.5 break-words text-[11px] font-bold text-[#7b877f]">{option.RecommendationReason}</p> : null}
                                      </div>
                                    ))}
                                    {(group.SupplierOptionCount || group.SupplierPriceOptions.length) > 3 ? <p className="text-xs font-bold text-[#7b877f]">另有 {(group.SupplierOptionCount || group.SupplierPriceOptions.length) - 3} 家可比價</p> : null}
                                  </div>
                                ) : (
                                  <StatusBadge tone="warning">無價格資料</StatusBadge>
                                )}
                              </td>
                              <td className="px-3 py-3">
                                {supplier ? (
                                  <>
                                    <p className="break-words font-extrabold text-[#203d32]">{supplier.SupplierName || "-"}</p>
                                    <p className="mt-1 break-all text-xs font-bold text-[#7b877f]">{supplier.SupplierCode || "-"} / {supplier.SupplierSku || "未設定料號"}</p>
                                    <p className="mt-1 text-xs font-bold text-[#7b877f]">MOQ {supplier.MinimumOrderQuantity} / 交期 {supplier.LeadTimeDays} 天</p>
                                    {supplier.RecommendationReason ? <p className="mt-1 break-words text-xs font-bold text-[#9a6a00]">{supplier.RecommendationReason}</p> : null}
                                  </>
                                ) : (
                                  <StatusBadge tone="warning">未建供應商價格</StatusBadge>
                                )}
                              </td>
                              <td className="px-3 py-3 text-right">
                                <p className="font-extrabold text-[#203d32]">{supplier ? money(supplier.UnitCost) : "-"}</p>
                                <p className="mt-1 text-xs font-bold text-[#7b877f]">{supplier?.Currency || currency}</p>
                              </td>
                              <td className="px-3 py-3 text-right">
                                <button className="inline-flex min-h-10 min-w-[96px] items-center justify-center rounded-md bg-[#315746] px-3 text-sm font-bold text-white" onClick={() => applyDemandGroup(group)} type="button">帶入</button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </section>
          ) : null}
          <section className="min-w-0 overflow-hidden rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-base font-extrabold text-[#203d32]">{revisionSource ? "建立採購單修訂版" : isSuggestionMode ? "建立建議採購單" : "建立採購單"}</h2>
                <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">
                  {revisionSource
                    ? `目前修訂 ${revisionSource.PurchaseNumber} 第 ${revisionSource.RevisionNo || 1} 版；送出後會成為第 ${(revisionSource.RevisionNo || 1) + 1} 版，並重新進入待審核。`
                    : isSuggestionMode
                      ? "流程：篩選門市、勾選待採購訂單、選供應商、自動帶入成本與 MOQ、確認明細後建立待審核採購單。"
                      : "流程：選來源、選供應商、自動帶入供應商成本、確認明細後建立採購單。"}
                </p>
              </div>
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate(isSuggestionMode ? "/erp/purchase-orders" : "/erp/purchase-orders")} type="button">
                返回列表
              </button>
            </div>
            {prefillProductId ? (
              <div className="mt-3 rounded-md border border-[#f0d49d] bg-[#fffaf0] px-4 py-3 text-sm font-extrabold leading-6 text-[#8a5b14]">
                已由庫存總覽帶入建議採購品項；請選擇供應商後確認成本、MOQ、交期與採購數量。
              </div>
            ) : null}
            {revisionSource ? (
              <div className="mt-3 grid gap-3 rounded-md border border-[#f0d49d] bg-[#fffaf0] p-3 sm:grid-cols-3">
                <InfoBox label="修訂來源" value={revisionSource.PurchaseNumber} />
                <InfoBox label="目前版次" value={`第 ${revisionSource.RevisionNo || 1} 版`} />
                <InfoBox label="送出後狀態" value="待審核" />
              </div>
            ) : null}
            <form className="mt-4 grid gap-4" onSubmit={submitPurchase}>
              <div className="min-w-0 overflow-hidden rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
                <div className="grid gap-3">
                  <InfoBox label="來源門市訂單" value={selectedOrders.length ? `${selectedOrders.length} 張已選：${selectedOrders.map((row) => row.OrderNumber).join("、")}` : "一般採購，不綁定門市訂單"} />
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">來源門市</span>
                    <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={sourceLocationId} onChange={(event) => {
                      const nextLocationId = event.target.value;
                      setSourceLocationId(nextLocationId);
                      clearCandidate();
                      void load(1, keyword, status, filterSupplierId, sourceType, dateFrom, dateTo, nextLocationId);
                    }}>
                      <option value="">全部門市</option>
                      {locations.map((location) => <option key={location.LocationId} value={location.LocationId}>{location.Name}{location.IsActive ? "" : "（停用）"}</option>)}
                    </select>
                  </label>
                  <div className="grid min-w-0 gap-2 lg:grid-cols-[minmax(0,1fr)_140px]">
                    <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={selectedId} onChange={(event) => {
                      const row = candidates.find((candidate) => candidate.StoreSalesOrderId === event.target.value);
                      if (row && selectedIds.includes(row.StoreSalesOrderId)) { setSelectedId(row.StoreSalesOrderId); }
                      else if (row) { selectCandidate(row); }
                      else { clearCandidate(); }
                    }}>
                      <option value="">一般採購，不綁定門市訂單</option>
                      {candidates.length === 0 ? <option disabled value="__empty">目前沒有待廠商採購訂單</option> : null}
                      {candidates.map((row) => <option key={row.StoreSalesOrderId} value={row.StoreSalesOrderId}>{row.LocationName || "未指定門市"} / {row.OrderNumber} / {row.CustomerName} / {row.ProductSummary}</option>)}
                    </select>
                    <button className="inline-flex min-h-11 w-full items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={clearCandidate} type="button">清除來源</button>
                  </div>
                  {candidates.length === 0 ? (
                    <div className="rounded-md border border-[#f3d9a7] bg-[#fffaf0] px-3 py-2 text-sm font-bold leading-6 text-[#8a5b14]">
                      {selectedSourceLocation ? selectedSourceLocation.Name : "目前範圍"}沒有「待廠商採購」的門市訂單。只有總管理端將門市訂單流程狀態分流為「待廠商採購」後，才會出現在這裡。
                    </div>
                  ) : null}
                  {selectedOrders.length > 0 ? (
                    <div className="rounded-md border border-[#b8dcc5] bg-[#f3fbf6] p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-extrabold text-[#203d32]">已選採購來源</p>
                        <StatusBadge>{selectedOrders.length} 張門市訂單</StatusBadge>
                      </div>
                      <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-3">
                        {selectedOrders.map((order) => (
                          <div className="min-w-0 rounded-md bg-white px-3 py-2" key={order.StoreSalesOrderId}>
                            <p className="break-all text-sm font-extrabold text-[#203d32]">{order.OrderNumber}</p>
                            <p className="mt-1 break-words text-xs font-bold text-[#59665f]">{order.LocationName || "-"} / {order.CustomerName}</p>
                            <p className="mt-1 break-words text-xs font-semibold text-[#7b877f]">{order.ProductSummary || "-"}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : null}
                  <p className="text-xs font-bold leading-5 text-[#7b877f]">先選門市可縮小待採購訂單範圍；可從下方待採購清單多選門市訂單，系統會合併相同商品規格數量；不選則為補貨、備料或耗材的一般採購。</p>
                </div>
              </div>
              <div className="min-w-0 overflow-hidden rounded-md border border-[#dfe6e1] p-3" data-field="Supplier">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <div className="text-sm font-extrabold text-[#203d32]">供應商</div>
                    <p className="mt-1 text-xs font-bold leading-5 text-[#7b877f]">可用關鍵字快速搜尋，也可直接用下拉選擇。</p>
                  </div>
                  {selectedSupplier ? <StatusBadge>已選擇</StatusBadge> : <StatusBadge tone="warning">尚未選擇</StatusBadge>}
                </div>

                {selectedSupplier ? (
                  <div className="mt-3 grid min-w-0 gap-2 rounded-md border border-[#b8dcc5] bg-[#f3fbf6] p-3 xl:grid-cols-[minmax(0,1fr)_180px_160px]">
                    <div className="min-w-0">
                      <p className="break-words text-base font-extrabold text-[#203d32]">{selectedSupplier.Name}</p>
                      <p className="mt-1 break-all text-xs font-bold text-[#59665f]">{selectedSupplier.Code}</p>
                    </div>
                    <div className="min-w-0 text-sm font-bold text-[#59665f]">
                      <p>交期 {selectedSupplier.LeadTimeDays} 天</p>
                      <p className="mt-1 break-words">{selectedSupplier.PaymentTerms || "未設定付款條件"}</p>
                    </div>
                    <button className="inline-flex min-h-10 w-full items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={clearSupplier} type="button">更換供應商</button>
                  </div>
                ) : null}

                <div className="mt-3 grid min-w-0 gap-3 xl:grid-cols-[minmax(240px,1fr)_minmax(240px,1fr)]">
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">快速搜尋</span>
                    <input className={fieldClass(formErrors, "Supplier", "h-11 w-full min-w-0 px-3")} placeholder="輸入供應商代碼或名稱" value={supplierKeyword} onChange={(event) => { setSupplierKeyword(event.target.value); setSupplierId(""); setFormErrors((current) => ({ ...current, Supplier: "" })); }} />
                  </label>
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">下拉選擇</span>
                    <select className={fieldClass(formErrors, "Supplier", "h-11 w-full min-w-0 px-3")} value={supplierId} onChange={(event) => {
                      const next = suppliers.find((supplier) => supplier.ErpSupplierId === event.target.value);
                      if (next) { selectSupplier(next); } else { clearSupplier(); }
                    }}>
                      <option value="">選擇供應商</option>
                      {suppliers.map((supplier) => <option key={supplier.ErpSupplierId} value={supplier.ErpSupplierId}>{supplier.Code} / {supplier.Name}</option>)}
                    </select>
                  </label>
                </div>

                {supplierKeyword.trim() ? (
                  <div className="mt-3 max-h-[180px] overflow-y-auto rounded-md border border-[#edf0ee] bg-white p-2">
                    {supplierMatches.length === 0 ? <EmptyState text="沒有符合關鍵字的供應商。" /> : (
                      <div className="grid min-w-0 gap-2 lg:grid-cols-2 min-[1680px]:grid-cols-3">
                        {supplierMatches.map((supplier) => (
                          <button className={`grid min-w-0 gap-1 rounded-md border px-3 py-2 text-left ${supplierId === supplier.ErpSupplierId ? "border-[#9ccbb0] bg-[#eef8f1]" : "border-[#edf0ee] bg-[#fbfcfb] hover:border-[#9ccbb0]"}`} key={supplier.ErpSupplierId} onClick={() => selectSupplier(supplier)} type="button">
                            <span className="min-w-0 break-words text-sm font-extrabold leading-6 text-[#203d32]">{supplier.Name}</span>
                            <span className="break-all text-xs font-bold leading-5 text-[#7b877f]">{supplier.Code}</span>
                            <span className="break-words text-xs font-bold leading-5 text-[#7b877f]">交期 {supplier.LeadTimeDays} 天{supplier.PaymentTerms ? ` / ${supplier.PaymentTerms}` : ""}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}

                {!supplierId ? <div className="mt-3"><TextField label="未建主檔時手動輸入供應商名稱" value={supplierName} onChange={(value) => { setSupplierName(value); setFormErrors((current) => ({ ...current, Supplier: "" })); }} /></div> : null}
                <FieldError errors={formErrors} name="Supplier" />
              </div>
              <div className="grid min-w-0 gap-3 xl:grid-cols-2"><TextField label="外部採購單號" value={externalOrderNumber} onChange={setExternalOrderNumber} /><TextField label="幣別" value={currency} onChange={setCurrency} /></div>
              <div className="grid min-w-0 gap-3 xl:grid-cols-2"><div data-field="ExchangeRate"><NumberField label="匯率" value={exchangeRate} onChange={(value) => { setExchangeRate(value); setFormErrors((current) => ({ ...current, ExchangeRate: "" })); }} /><FieldError errors={formErrors} name="ExchangeRate" /></div><label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">預計到貨日</span><input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3" type="date" value={expectedArrivalDate} onChange={(event) => setExpectedArrivalDate(event.target.value)} /></label></div>
              <ErpProductSearchPanel
                description="選擇供應商後會優先顯示該供應商價格、MOQ、交期與料號；未建價格時仍可加入，但需手動確認成本。"
                keyword={productKeyword}
                loading={loading}
                onKeywordChange={setProductKeyword}
                onSearch={() => void searchProducts()}
                onSelect={addProductLine}
                placeholder="輸入品名、商品編號、SKU"
                results={productResults}
                searchLabel="查品項"
                selectedPredicate={lineExistsForProduct}
                selectLabel="加入採購"
                title="商品模糊查詢"
              />
              {purchaseLines.length ? (
                <div className="rounded-md border border-[#dfe6e1]" data-field="Items">
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#edf0ee] px-3 py-3">
                    <div>
                      <h3 className="text-sm font-extrabold text-[#203d32]">採購品項</h3>
                      <p className="mt-1 text-xs font-bold text-[#7b877f]">列表可橫向捲動，請確認成本、單位與供應商料號。</p>
                    </div>
                    <StatusBadge>{count(purchaseLines.length)}</StatusBadge>
                  </div>
                  <div className="erp-table-scroll p-3">
                    <table className="w-full min-w-[1240px] table-fixed text-left text-sm">
                      <thead className="bg-[#f3f7f4] text-[#59665f]">
                        <tr>
                          <th className="w-[280px] px-3 py-3">商品</th>
                          <th className="w-[140px] px-3 py-3">SKU / 編號</th>
                          <th className="w-[130px] px-3 py-3">價格狀態</th>
                          <th className="w-[130px] px-3 py-3">MOQ / 交期</th>
                          <th className="w-[120px] px-3 py-3">數量</th>
                          <th className="w-[140px] px-3 py-3">單位成本</th>
                          <th className="w-[110px] px-3 py-3">單位</th>
                          <th className="w-[180px] px-3 py-3">供應商料號</th>
                          <th className="w-[140px] px-3 py-3 text-right">小計</th>
                          <th className="w-[90px] px-3 py-3 text-right">操作</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#edf0ee]">
                        {purchaseLines.map((line) => {
                          const statusText = line.SupplierPriceStatus === "Matched" ? "已帶價" : line.UnitCost > 0 ? "手動成本" : "缺成本";
                          return (
                            <tr className="align-top" key={line.LineKey}>
                              <td className="px-3 py-3">
                                <p className="break-words font-extrabold text-[#203d32]">{line.ProductName}</p>
                                <p className="mt-1 break-words text-xs font-bold text-[#7b877f]">{line.VariantLabel || "單一規格"}</p>
                              </td>
                              <td className="break-all px-3 py-3 text-[#59665f]">{line.Sku || line.ProductCode || "-"}</td>
                              <td className="px-3 py-3">
                                <StatusBadge tone={line.SupplierPriceStatus === "Matched" || line.UnitCost > 0 ? "default" : "warning"}>{statusText}</StatusBadge>
                                <p className="mt-2 text-xs font-bold text-[#7b877f]">{line.Currency || currency || "TWD"}</p>
                              </td>
                              <td className="px-3 py-3 text-sm font-bold text-[#59665f]">
                                <p>MOQ {line.MinimumOrderQuantity || 1}</p>
                                <p className="mt-1">交期 {line.LeadTimeDays || 0} 天</p>
                              </td>
                              <td className="px-3 py-3" data-field={`Items.${line.LineKey}.Quantity`}><input className={fieldClass(formErrors, `Items.${line.LineKey}.Quantity`, "h-10 w-full px-2")} min={1} type="number" value={line.Quantity} onChange={(event) => updatePurchaseLine(line.LineKey, { Quantity: Math.max(1, Number(event.target.value || 1)) })} /><FieldError errors={formErrors} name={`Items.${line.LineKey}.Quantity`} /></td>
                              <td className="px-3 py-3" data-field={`Items.${line.LineKey}.UnitCost`}><input className={fieldClass(formErrors, `Items.${line.LineKey}.UnitCost`, "h-10 w-full px-2")} min={0} type="number" value={line.UnitCost} onChange={(event) => updatePurchaseLine(line.LineKey, { UnitCost: Math.max(0, Number(event.target.value || 0)), SupplierPriceStatus: line.SupplierPriceStatus === "Matched" ? "Matched" : "Manual" })} /><FieldError errors={formErrors} name={`Items.${line.LineKey}.UnitCost`} /></td>
                              <td className="px-3 py-3" data-field={`Items.${line.LineKey}.PurchaseUnit`}><input className={fieldClass(formErrors, `Items.${line.LineKey}.PurchaseUnit`, "h-10 w-full px-2")} value={line.PurchaseUnit} onChange={(event) => updatePurchaseLine(line.LineKey, { PurchaseUnit: event.target.value })} /><FieldError errors={formErrors} name={`Items.${line.LineKey}.PurchaseUnit`} /></td>
                              <td className="px-3 py-3"><input className="h-10 w-full rounded-md border border-[#cfd8d2] px-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={line.SupplierSku} onChange={(event) => updatePurchaseLine(line.LineKey, { SupplierSku: event.target.value })} /></td>
                              <td className="px-3 py-3 text-right font-extrabold text-[#203d32]">{money(line.Quantity * line.UnitCost)}</td>
                              <td className="px-3 py-3 text-right"><button className="inline-flex min-h-10 min-w-[64px] items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-3 text-sm font-bold text-[#a33a2f]" onClick={() => setPurchaseLines((current) => current.filter((item) => item.LineKey !== line.LineKey))} type="button">刪除</button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <FieldError errors={formErrors} name="Items" />
                </div>
              ) : null}
              <label><span className="mb-2 block text-sm font-bold text-[#59665f]">採購備註</span><textarea className="min-h-[96px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={note} onChange={(event) => setNote(event.target.value)} /></label>
              {revisionSource ? (
                <label data-field="RevisionNote">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">修訂原因</span>
                  <textarea
                    className={fieldClass(formErrors, "RevisionNote", "min-h-[88px] w-full px-3 py-2")}
                    placeholder="例如：供應商成本調整、數量修正、改由其他供應商供貨。"
                    value={revisionNote}
                    onChange={(event) => {
                      setRevisionNote(event.target.value);
                      setFormErrors((current) => ({ ...current, RevisionNote: "" }));
                    }}
                  />
                  <FieldError errors={formErrors} name="RevisionNote" />
                </label>
              ) : null}
              <div className="rwd-action-grid">
                <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/purchase-orders")} type="button">取消</button>
                <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading || purchaseLines.length === 0} type="submit"><ClipboardList size={18} />{revisionSource ? "送出修訂版" : "建立採購單"}</button>
              </div>
            </form>
          </section>

          <section className="min-w-0 overflow-hidden rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-base font-extrabold text-[#203d32]">{isSuggestionMode ? "待採購需求池" : "待採購門市訂單"}</h2>
                <p className="mt-1 text-xs font-bold leading-5 text-[#7b877f]">只顯示尚未綁定未取消採購單的「待廠商採購」門市訂單。</p>
              </div>
              <span className="text-sm font-bold text-[#7b877f]">{count(candidates.length)}</span>
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">{candidates.length === 0 ? <EmptyState text={`${selectedSourceLocation ? selectedSourceLocation.Name : "目前"}沒有待廠商採購的門市訂單。`} /> : null}{candidates.map((row) => <CandidateCard active={selectedIds.includes(row.StoreSalesOrderId)} key={row.StoreSalesOrderId} row={row} onSelect={() => selectCandidate(row)} />)}</div>
          </section>
        </aside> : null}
      </div>
    </div>
  );
}

function PurchaseReceiptsView({ mode = "list" }: { mode?: "list" | "detail" }) {
  const navigate = useNavigate();
  const { purchaseReceiptId } = useParams();
  const isDetailMode = mode === "detail";
  const [keyword, setKeyword] = useState("");
  const [inspectionStatus, setInspectionStatus] = useState("");
  const [exceptionStatus, setExceptionStatus] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [dateFrom, setDateFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(`${new Date().getFullYear()}-12-31`);
  const [receipts, setReceipts] = useState<ErpPurchaseReceiptRecord[]>([]);
  const [selectedReceipt, setSelectedReceipt] = useState<ErpPurchaseReceiptRecord | null>(null);
  const [inspectionOptions, setInspectionOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [exceptionOptions, setExceptionOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [resolutionOptions, setResolutionOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [suppliers, setSuppliers] = useState<ErpSupplierOption[]>([]);
  const [warehouses, setWarehouses] = useState<ErpWarehouse[]>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [releaseMode, setReleaseMode] = useState<"AcceptAll" | "QuarantineAll">("AcceptAll");
  const [releaseChecklist, setReleaseChecklist] = useState<Record<string, boolean>>(defaultPurchaseInspectionChecklist);
  const [releaseExceptionType, setReleaseExceptionType] = useState("");
  const [releaseAction, setReleaseAction] = useState("PendingReview");
  const [releaseNote, setReleaseNote] = useState("");
  const [resolutionAction, setResolutionAction] = useState("PendingReview");
  const [resolutionNote, setResolutionNote] = useState("");
  const [rmaNumber, setRmaNumber] = useState("");
  const [rmaStatus, setRmaStatus] = useState("Closed");
  const [supplierContactNote, setSupplierContactNote] = useState("");
  const [returnTrackingNumber, setReturnTrackingNumber] = useState("");
  const [replacementExpectedDate, setReplacementExpectedDate] = useState("");
  const [replacementReceivedAt, setReplacementReceivedAt] = useState("");
  const [creditAmount, setCreditAmount] = useState("");
  const [receiptAttachments, setReceiptAttachments] = useState<ErpAttachment[]>([]);
  const [receiptAttachmentForm, setReceiptAttachmentForm] = useState({ AttachmentType: "InspectionPhoto", DocumentCategory: "Receipt", FileName: "", FileUrl: "", Note: "" });
  const [receiptAttachmentFile, setReceiptAttachmentFile] = useState<File | null>(null);
  const [receiptAttachmentProgress, setReceiptAttachmentProgress] = useState<OperationProgressState | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");

  async function load(page = 1, nextKeyword = keyword, nextInspection = inspectionStatus, nextException = exceptionStatus, nextSupplier = supplierId, nextWarehouse = warehouseId, nextDateFrom = dateFrom, nextDateTo = dateTo) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpPurchaseReceipts({
        Keyword: nextKeyword.trim(),
        InspectionStatus: nextInspection,
        ExceptionStatus: nextException,
        ErpSupplierId: nextSupplier,
        ErpWarehouseId: nextWarehouse,
        DateFrom: nextDateFrom,
        DateTo: nextDateTo,
        Page: page,
        PerPage: 20,
      });
      setReceipts(data.Receipts || []);
      setInspectionOptions(data.InspectionStatusOptions || []);
      setExceptionOptions(data.ExceptionStatusOptions || []);
      setResolutionOptions(data.ResolutionActionOptions || []);
      setSuppliers(data.SupplierOptions || []);
      setWarehouses(data.WarehouseOptions || []);
      setPagination(data.Pagination || emptyPagination);
    } catch (error) {
      setMessage(errorText(error, "採購入庫驗收載入失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function loadDetail(id: string) {
    setLoading(true);
    setMessage("");
    try {
      const data = await getErpPurchaseReceipt(id);
      setSelectedReceipt(data.Receipt);
      setResolutionOptions(data.ResolutionActionOptions || []);
      setResolutionAction(data.Receipt?.ResolutionAction || "PendingReview");
      setResolutionNote(data.Receipt?.ResolutionNote || "");
      setRmaNumber(data.Receipt?.RmaNumber || "");
      setRmaStatus(data.Receipt?.RmaStatus || (data.Receipt?.ExceptionStatus === "Resolved" ? "Closed" : defaultRmaStatusForAction(data.Receipt?.ResolutionAction || "PendingReview")));
      setSupplierContactNote(data.Receipt?.SupplierContactNote || "");
      setReturnTrackingNumber(data.Receipt?.ReturnTrackingNumber || "");
      setReplacementExpectedDate((data.Receipt?.ReplacementExpectedDate || "").slice(0, 10));
      setReplacementReceivedAt((data.Receipt?.ReplacementReceivedAt || "").slice(0, 10));
      setCreditAmount(data.Receipt?.CreditAmount ? String(data.Receipt.CreditAmount) : "");
      setReleaseMode("AcceptAll");
      setReleaseChecklist(defaultPurchaseInspectionChecklist());
      setReleaseExceptionType("");
      setReleaseAction("PendingReview");
      setReleaseNote("");
      await loadReceiptAttachments(id);
    } catch (error) {
      setMessage(errorText(error, "驗收單詳情載入失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function loadReceiptAttachments(id: string) {
    try {
      const data = await listErpAttachments({ RelatedType: "PurchaseReceipt", RelatedId: id, Page: 1, PerPage: 50 });
      setReceiptAttachments(data.Items || []);
    } catch {
      setReceiptAttachments([]);
    }
  }

  useEffect(() => {
    if (isDetailMode && purchaseReceiptId) {
      void loadDetail(purchaseReceiptId);
    } else {
      void load(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDetailMode, purchaseReceiptId]);

  function reset() {
    const year = new Date().getFullYear();
    setKeyword("");
    setInspectionStatus("");
    setExceptionStatus("");
    setSupplierId("");
    setWarehouseId("");
    setDateFrom(`${year}-01-01`);
    setDateTo(`${year}-12-31`);
    void load(1, "", "", "", "", "", `${year}-01-01`, `${year}-12-31`);
  }

  function defaultRmaStatusForAction(action: string) {
    if (action === "ReturnToSupplier") return "ReturnPending";
    if (action === "ExchangeRequired") return "ExchangePending";
    if (action === "ReplenishRequired") return "SupplementPending";
    if (action === "ConcessionAccepted") return "CreditPending";
    if (action === "WriteOff") return "WriteOffPending";
    return "PendingSupplier";
  }

  function changeResolutionAction(action: string) {
    setResolutionAction(action);
    if (selectedReceipt?.ExceptionStatus !== "Resolved") {
      setRmaStatus(defaultRmaStatusForAction(action));
    }
  }

  async function submitResolution(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedReceipt) return;
    if (!resolutionAction) {
      setMessage("請選擇異常處理方式。");
      return;
    }
    if (!resolutionNote.trim()) {
      setMessage("請填寫異常結案說明。");
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    setReceiptAttachmentProgress(receiptAttachmentFile ? { label: "驗收附件上傳中", detail: receiptAttachmentFile.name, percent: 0 } : { label: "驗收附件紀錄建立中", percent: null });
    try {
      const result = await resolveErpPurchaseReceipt(selectedReceipt.ErpPurchaseReceiptId, {
        ResolutionAction: resolutionAction,
        ResolutionNote: resolutionNote.trim(),
        RmaNumber: rmaNumber.trim(),
        RmaStatus: rmaStatus,
        SupplierContactNote: supplierContactNote.trim(),
        ReturnTrackingNumber: returnTrackingNumber.trim(),
        ReplacementExpectedDate: replacementExpectedDate,
        ReplacementReceivedAt: replacementReceivedAt,
        CreditAmount: Number(creditAmount || 0),
      });
      setSelectedReceipt(result.Receipt);
      setRmaNumber(result.Receipt?.RmaNumber || "");
      setRmaStatus(result.Receipt?.RmaStatus || "Closed");
      setSupplierContactNote(result.Receipt?.SupplierContactNote || "");
      setReturnTrackingNumber(result.Receipt?.ReturnTrackingNumber || "");
      setReplacementExpectedDate((result.Receipt?.ReplacementExpectedDate || "").slice(0, 10));
      setReplacementReceivedAt((result.Receipt?.ReplacementReceivedAt || "").slice(0, 10));
      setCreditAmount(result.Receipt?.CreditAmount ? String(result.Receipt.CreditAmount) : "");
      setSuccess("異常處理已結案。");
    } catch (error) {
      setMessage(errorText(error, "異常處理結案失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function submitReceiptAttachment(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedReceipt) return;
    if (!receiptAttachmentFile && !receiptAttachmentForm.FileUrl.trim()) {
      setMessage("請選擇要上傳的檔案，或填寫外部檔案連結。");
      return;
    }
    if (!receiptAttachmentFile && !receiptAttachmentForm.FileName.trim()) {
      setMessage("請輸入附件名稱。");
      return;
    }
    if (receiptAttachmentFile && receiptAttachmentFile.size > 20 * 1024 * 1024) {
      setMessage("附件檔案大小不可超過 20MB。");
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const payload = {
        RelatedType: "PurchaseReceipt",
        RelatedId: selectedReceipt.ErpPurchaseReceiptId,
        AttachmentType: receiptAttachmentForm.AttachmentType,
        DocumentCategory: receiptAttachmentForm.DocumentCategory,
        FileName: receiptAttachmentForm.FileName.trim() || receiptAttachmentFile?.name || "",
        FileUrl: receiptAttachmentForm.FileUrl.trim() || undefined,
        Note: receiptAttachmentForm.Note.trim() || undefined,
      };
      if (receiptAttachmentFile) {
        await uploadErpAttachment(payload, receiptAttachmentFile, (progress: UploadProgressInfo) => {
          setReceiptAttachmentProgress({ label: "驗收附件上傳中", detail: `${receiptAttachmentFile.name} / ${formatBytes(progress.loaded)} of ${formatBytes(progress.total)}`, percent: progress.percent });
        });
        setReceiptAttachmentProgress({ label: "附件已上傳，正在建立驗收附件紀錄", detail: receiptAttachmentFile.name, percent: null });
      } else {
        await createErpAttachment(payload);
      }
      setReceiptAttachmentForm({ AttachmentType: "InspectionPhoto", DocumentCategory: "Receipt", FileName: "", FileUrl: "", Note: "" });
      setReceiptAttachmentFile(null);
      await loadReceiptAttachments(selectedReceipt.ErpPurchaseReceiptId);
      setSuccess(receiptAttachmentFile ? "驗收附件已上傳。" : "驗收附件已建立。");
    } catch (error) {
      setMessage(errorText(error, receiptAttachmentFile ? "驗收附件上傳失敗。" : "驗收附件建立失敗。"));
    } finally {
      setReceiptAttachmentProgress(null);
      setLoading(false);
    }
  }

  async function openReceiptAttachment(attachment: ErpAttachment) {
    setLoading(true);
    setMessage("");
    try {
      const data = await downloadErpAttachment(attachment.ErpAttachmentId);
      window.open(data.DownloadUrl, "_blank", "noopener,noreferrer");
      if (selectedReceipt) await loadReceiptAttachments(selectedReceipt.ErpPurchaseReceiptId);
    } catch (error) {
      setMessage(errorText(error, "附件開啟失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function submitRelease(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedReceipt) return;
    if (selectedReceipt.InspectionStatus !== "Pending") {
      setMessage("此到貨單已完成驗收處理，不能重複放行。");
      return;
    }
    if (releaseMode === "QuarantineAll" && !releaseExceptionType) {
      setMessage("異常隔離時請選擇異常原因。");
      return;
    }
    if (releaseMode === "QuarantineAll" && !releaseAction) {
      setMessage("異常隔離時請選擇後續處理方式。");
      return;
    }
    if (releaseMode === "QuarantineAll" && !releaseNote.trim()) {
      setMessage("異常隔離時請填寫驗收備註，方便後續追蹤。");
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = await releaseErpPurchaseReceipt(selectedReceipt.ErpPurchaseReceiptId, {
        Mode: releaseMode,
        Note: releaseNote.trim(),
        InspectionChecklist: releaseChecklist,
        ExceptionType: releaseMode === "QuarantineAll" ? releaseExceptionType : undefined,
        ResolutionAction: releaseMode === "QuarantineAll" ? releaseAction : undefined,
      });
      setSelectedReceipt(result.Receipt);
      setSuccess(releaseMode === "AcceptAll" ? "驗收合格已放行入庫。" : "驗收異常已轉入隔離待處理。");
    } catch (error) {
      setMessage(errorText(error, "驗收放行失敗。"));
    } finally {
      setLoading(false);
    }
  }

  const abnormalCount = selectedReceipt?.RejectedQuantity || 0;
  const canResolve = Boolean(selectedReceipt && abnormalCount > 0 && selectedReceipt.ExceptionStatus !== "Resolved");
  const canRelease = Boolean(selectedReceipt && selectedReceipt.InspectionStatus === "Pending");

  if (isDetailMode) {
    return (
      <div className="grid gap-5">
        <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />
        <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="min-w-0">
              <p className="text-xs font-extrabold uppercase tracking-[.14em] text-[#7b877f]">Purchase Receipt</p>
              <h2 className="mt-1 break-all text-2xl font-extrabold text-[#203d32]">{selectedReceipt?.ReceiptNumber || "驗收單詳情"}</h2>
              <p className="mt-1 break-words font-bold text-[#59665f]">採購單：{selectedReceipt?.PurchaseNumber || "-"} / {selectedReceipt?.SupplierName || "-"}</p>
            </div>
            <ErpActionBar className="lg:min-w-[260px]">
              <ErpDocumentPrintMenu
                items={[
                  { label: "驗收異常單", description: "列印異常明細、處理方式與簽核欄", disabled: !selectedReceipt, disabledReason: "尚未載入驗收單", onClick: () => selectedReceipt && printReceiptExceptionDocument(selectedReceipt, "exception") },
                  { label: "退換貨處理單", description: "列印退回、換貨與後續追蹤欄位", disabled: !selectedReceipt || abnormalCount === 0, disabledReason: abnormalCount === 0 ? "此驗收單沒有異常品項" : "尚未載入驗收單", onClick: () => selectedReceipt && printReceiptExceptionDocument(selectedReceipt, "return-exchange") },
                ]}
              />
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/purchase-receipts")} type="button">返回列表</button>
            </ErpActionBar>
          </div>
          {selectedReceipt ? (
            <>
              <div className="mt-4 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                <InfoBox label="驗收狀態" value={selectedReceipt.InspectionStatusLabel} />
                <InfoBox label="異常狀態" value={selectedReceipt.ExceptionStatusLabel} />
                <InfoBox label="到貨 / 放行 / 異常" value={`${selectedReceipt.ArrivedQuantity} / ${selectedReceipt.AcceptedQuantity} / ${selectedReceipt.RejectedQuantity}`} />
                <InfoBox label="入庫倉庫" value={selectedReceipt.WarehouseName || "-"} />
                <InfoBox label="到貨時間" value={selectedReceipt.ReceivedAt || "-"} />
                <InfoBox label="登錄人員" value={selectedReceipt.CreatedByAdminName || "-"} />
                <InfoBox label="放行時間" value={selectedReceipt.ReleasedAt || "-"} />
                <InfoBox label="處理方式" value={selectedReceipt.ResolutionActionLabel || "-"} />
                <InfoBox label="結案時間" value={selectedReceipt.ResolvedAt || "-"} />
                {abnormalCount > 0 ? <InfoBox label="RMA 狀態" value={selectedReceipt.RmaStatusLabel || "-"} /> : null}
                {abnormalCount > 0 ? <InfoBox label="RMA / 退貨單號" value={selectedReceipt.RmaNumber || "-"} /> : null}
                {abnormalCount > 0 ? <InfoBox label="退貨物流單號" value={selectedReceipt.ReturnTrackingNumber || "-"} /> : null}
                {abnormalCount > 0 ? <InfoBox label="換貨 / 補件預計到貨" value={selectedReceipt.ReplacementExpectedDate || "-"} /> : null}
                {abnormalCount > 0 ? <InfoBox label="折讓 / 扣款" value={selectedReceipt.CreditAmount ? plainMoney(selectedReceipt.CreditAmount) : "-"} /> : null}
              </div>
              {selectedReceipt.ReleaseNote ? <div className="mt-4 rounded-md border border-[#dfe6e1] bg-[#f7faf8] p-3 font-bold leading-7 text-[#59665f]">放行備註：{selectedReceipt.ReleaseNote}</div> : null}
              {selectedReceipt.SupplierContactNote ? <div className="mt-4 rounded-md border border-[#dfe6e1] bg-[#f7faf8] p-3 font-bold leading-7 text-[#59665f]">供應商聯絡紀錄：{selectedReceipt.SupplierContactNote}</div> : null}
              {selectedReceipt.ExceptionSummary ? <div className="mt-4 rounded-md border border-[#f4d7a1] bg-[#fffaf0] p-3 font-bold leading-7 text-[#8a5b14]">{selectedReceipt.ExceptionSummary}</div> : null}
            </>
          ) : <EmptyState text={loading ? "載入驗收單中..." : "找不到驗收單資料。"} />}
        </section>

        {selectedReceipt ? (
          <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#edf0ee] px-4 py-4">
              <h2 className="text-base font-extrabold text-[#203d32]">驗收明細</h2>
              <span className="text-sm font-bold text-[#7b877f]">{count(selectedReceipt.Items?.length || 0)}</span>
            </div>
            <div className="max-w-full overflow-x-auto p-4">
              <table className="w-full min-w-[1120px] table-fixed text-left text-sm">
                <thead className="bg-[#f3f7f4] text-[#59665f]">
                  <tr>
                    <th className="w-[240px] px-3 py-3">商品</th>
                    <th className="w-[150px] px-3 py-3">SKU / 序號</th>
                    <th className="w-[120px] px-3 py-3">耳側</th>
                    <th className="w-[160px] px-3 py-3">到貨 / 放行 / 異常</th>
                    <th className="w-[160px] px-3 py-3">驗收狀態</th>
                    <th className="w-[170px] px-3 py-3">異常 / 處理</th>
                    <th className="w-[240px] px-3 py-3">備註</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#edf0ee]">
                  {(selectedReceipt.Items || []).map((item) => (
                    <tr className="align-top" key={item.ErpPurchaseReceiptItemId}>
                      <td className="px-3 py-3"><p className="break-words font-extrabold text-[#203d32]">{item.ProductName}</p><p className="mt-1 text-xs font-bold text-[#7b877f]">{item.VariantLabel || "單一規格"}</p></td>
                      <td className="break-all px-3 py-3 text-[#59665f]"><p>{item.Sku || "-"}</p><p className="mt-1">{item.SerialNumber || "-"}</p></td>
                      <td className="px-3 py-3 font-bold text-[#59665f]">{item.EarSideLabel || "-"}</td>
                      <td className="px-3 py-3 font-extrabold text-[#203d32]">{item.ArrivedQuantity} / {item.ReleasedQuantity ?? item.AcceptedQuantity} / {item.QuarantinedQuantity ?? item.RejectedQuantity}</td>
                      <td className="px-3 py-3"><StatusBadge tone={item.RejectedQuantity > 0 ? "warning" : "default"}>{item.InspectionStatusLabel}</StatusBadge><p className="mt-2 text-xs font-bold text-[#7b877f]">{item.ExceptionStatusLabel}</p></td>
                      <td className="break-words px-3 py-3 font-bold text-[#59665f]"><p>{item.ExceptionTypeLabel || "-"}</p><p className="mt-1">{item.ResolutionActionLabel || "-"}</p></td>
                      <td className="break-words px-3 py-3 text-[#59665f]">{item.InspectionNote || item.Note || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        {selectedReceipt && canRelease ? (
          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-base font-extrabold text-[#203d32]">驗收放行</h2>
                <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">到貨登錄只會進待驗區；合格放行後才會轉為可用庫存，異常則轉入隔離庫存並建立追蹤。</p>
              </div>
              <StatusBadge tone="warning">待驗收</StatusBadge>
            </div>
            <form className="mt-4 grid gap-4" onSubmit={submitRelease}>
              <div className="grid gap-3 md:grid-cols-2">
                <button className={`min-h-16 rounded-md border px-4 text-left font-extrabold ${releaseMode === "AcceptAll" ? "border-[#86b99a] bg-[#edf8f1] text-[#203d32]" : "border-[#dfe6e1] bg-white text-[#59665f]"}`} onClick={() => setReleaseMode("AcceptAll")} type="button">
                  全部合格放行
                  <span className="mt-1 block text-sm font-bold">庫存轉為可用，寫入採購驗收入庫異動。</span>
                </button>
                <button className={`min-h-16 rounded-md border px-4 text-left font-extrabold ${releaseMode === "QuarantineAll" ? "border-[#f0d49d] bg-[#fffaf0] text-[#8a5b14]" : "border-[#dfe6e1] bg-white text-[#59665f]"}`} onClick={() => setReleaseMode("QuarantineAll")} type="button">
                  異常隔離待處理
                  <span className="mt-1 block text-sm font-bold">不進可用庫存，保留在隔離狀態。</span>
                </button>
              </div>
              <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <p className="font-extrabold text-[#203d32]">驗收檢核</p>
                  <span className="text-sm font-bold text-[#7b877f]">{Object.values(releaseChecklist).filter(Boolean).length} / {purchaseInspectionChecks.length} 項通過</span>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                  {purchaseInspectionChecks.map(([key, label]) => (
                    <ErpCheckboxCard checked={releaseChecklist[key] ?? true} key={key} label={label} onChange={(checked) => setReleaseChecklist((current) => ({ ...current, [key]: checked }))} />
                  ))}
                </div>
              </div>
              {releaseMode === "QuarantineAll" ? (
                <div className="grid gap-3 md:grid-cols-[240px_240px_1fr]">
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">異常原因</span>
                    <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={releaseExceptionType} onChange={(event) => setReleaseExceptionType(event.target.value)}>
                      {purchaseExceptionOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  </label>
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">後續處理</span>
                    <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={releaseAction} onChange={(event) => setReleaseAction(event.target.value)}>
                      {purchaseResolutionOptions.filter(([key]) => key !== "").map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  </label>
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">異常備註</span>
                    <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" placeholder="例如：外箱破損，右耳機身序號與出貨單不符。" value={releaseNote} onChange={(event) => setReleaseNote(event.target.value)} />
                  </label>
                </div>
              ) : (
                <label className="min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">放行備註</span>
                  <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" placeholder="可填寫驗收補充，例如：外箱完整、序號核對無誤。" value={releaseNote} onChange={(event) => setReleaseNote(event.target.value)} />
                </label>
              )}
              <div className="rwd-action-grid justify-end">
                <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} type="submit">{releaseMode === "AcceptAll" ? "確認合格放行" : "確認異常隔離"}</button>
              </div>
            </form>
          </section>
        ) : null}

        {selectedReceipt && abnormalCount > 0 ? (
          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-base font-extrabold text-[#203d32]">異常處理結案</h2>
                <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">處理退貨、換貨、補件、讓步接收或報廢後，在這裡留下可追溯的結案紀錄。</p>
              </div>
              <StatusBadge tone={selectedReceipt.ExceptionStatus === "Resolved" ? "default" : "warning"}>{selectedReceipt.ExceptionStatusLabel}</StatusBadge>
            </div>
            <form className="mt-4 grid gap-3" onSubmit={submitResolution}>
              <div className="grid gap-3 md:grid-cols-[240px_1fr]">
                <label className="min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">結案方式</span>
                  <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} value={resolutionAction} onChange={(event) => changeResolutionAction(event.target.value)}>
                    {resolutionOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
                  </select>
                </label>
                <label className="min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">結案說明</span>
                  <textarea className="min-h-[96px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} placeholder="例如：供應商已補寄配件，2026/08/23 驗收無誤。" value={resolutionNote} onChange={(event) => setResolutionNote(event.target.value)} />
                </label>
              </div>
              <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                <div className="mb-3 flex flex-col gap-1 md:flex-row md:items-end md:justify-between">
                  <div>
                    <p className="font-extrabold text-[#203d32]">退換貨 / RMA 追蹤</p>
                    <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">退貨、換貨、補件、折讓或報廢都需留下可追溯資訊，方便後續請款、對帳與供應商責任確認。</p>
                  </div>
                  <StatusBadge tone={selectedReceipt.ExceptionStatus === "Resolved" ? "default" : "warning"}>{purchaseRmaStatusOptions.find(([key]) => key === rmaStatus)?.[1] || rmaStatus || "待供應商回覆"}</StatusBadge>
                </div>
                <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">RMA 狀態</span>
                    <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} value={rmaStatus} onChange={(event) => setRmaStatus(event.target.value)}>
                      {purchaseRmaStatusOptions.map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                    </select>
                  </label>
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">RMA / 退貨單號</span>
                    <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} placeholder="例如：RMA-20260824-001" value={rmaNumber} onChange={(event) => setRmaNumber(event.target.value)} />
                  </label>
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">退貨物流單號</span>
                    <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} placeholder="例如：黑貓 1234567890" value={returnTrackingNumber} onChange={(event) => setReturnTrackingNumber(event.target.value)} />
                  </label>
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">換貨 / 補件預計到貨</span>
                    <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} type="date" value={replacementExpectedDate} onChange={(event) => setReplacementExpectedDate(event.target.value)} />
                  </label>
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">換貨 / 補件實際到貨</span>
                    <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} type="date" value={replacementReceivedAt} onChange={(event) => setReplacementReceivedAt(event.target.value)} />
                  </label>
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">折讓 / 扣款金額</span>
                    <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} min="0" type="number" value={creditAmount} onChange={(event) => setCreditAmount(event.target.value)} />
                  </label>
                </div>
                <label className="mt-3 block min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">供應商聯絡 / 異常證明摘要</span>
                  <textarea className="min-h-[92px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" disabled={!canResolve} placeholder="例如：已於 2026/08/24 寄出異常照片給供應商窗口，供應商同意換貨並預計 9/3 到貨。" value={supplierContactNote} onChange={(event) => setSupplierContactNote(event.target.value)} />
                </label>
              </div>
              <div className="rwd-action-grid justify-end">
                <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading || !canResolve} type="submit">確認結案</button>
              </div>
            </form>
          </section>
        ) : null}

        {selectedReceipt?.RmaEvents?.length ? (
          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                <h2 className="text-base font-extrabold text-[#203d32]">RMA / 異常處理時間軸</h2>
                <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">每次退貨、換貨、補件、折讓或報廢結案都會留下事件紀錄，方便追溯供應商責任與後續請款對帳。</p>
              </div>
              <StatusBadge>{selectedReceipt.RmaEvents.length} 筆紀錄</StatusBadge>
            </div>
            <div className="mt-4 grid gap-3">
              {selectedReceipt.RmaEvents.map((event) => (
                <article className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3" key={event.ErpPurchaseReceiptRmaEventId}>
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <p className="break-words font-extrabold text-[#203d32]">{event.EventLabel}</p>
                      <p className="mt-1 text-sm font-bold text-[#7b877f]">{event.CreatedAt} / {event.CreatedByAdminName || "-"}</p>
                    </div>
                    <StatusBadge tone={event.RmaStatus === "Closed" ? "default" : "warning"}>{event.RmaStatusLabel}</StatusBadge>
                  </div>
                  <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                    <InfoBox label="退貨物流單號" value={event.ReturnTrackingNumber || "-"} />
                    <InfoBox label="預計補回" value={event.ReplacementExpectedDate || "-"} />
                    <InfoBox label="實際補回" value={event.ReplacementReceivedAt || "-"} />
                    <InfoBox label="折讓金額" value={money(event.CreditAmount || 0)} />
                  </div>
                  <p className="mt-3 break-words text-sm font-semibold leading-6 text-[#59665f]">{event.Note || event.SupplierContactNote || "未填寫補充說明。"}</p>
                </article>
              ))}
            </div>
          </section>
        ) : null}

        {selectedReceipt ? (
          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div>
                <h2 className="text-base font-extrabold text-[#203d32]">附件 / 驗收照片 / 異常證明</h2>
                <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">可登錄裝箱單、Invoice、驗收照片、異常照片或供應商回覆，後續退貨、換貨、補件與對帳都能追溯。</p>
              </div>
              <span className="rounded-full bg-[#edf5f0] px-3 py-1 text-sm font-extrabold text-[#315746]">{count(receiptAttachments.length)}</span>
            </div>
            <form className="mt-4 grid gap-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3" onSubmit={submitReceiptAttachment}>
              <OperationProgress progress={receiptAttachmentProgress} />
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[180px_180px_minmax(220px,1fr)_minmax(220px,1.2fr)]">
                <label className="min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">附件類型</span>
                  <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={receiptAttachmentForm.AttachmentType} onChange={(event) => setReceiptAttachmentForm((current) => ({ ...current, AttachmentType: event.target.value }))}>
                    <option value="InspectionPhoto">驗收照片</option>
                    <option value="PackingList">裝箱單</option>
                    <option value="Invoice">發票 / Invoice</option>
                    <option value="DeliveryProof">物流 / 簽收</option>
                    <option value="ExceptionProof">異常證明</option>
                    <option value="RmaProof">退換貨證明</option>
                    <option value="Other">其他</option>
                  </select>
                </label>
                <label className="min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">文件分類</span>
                  <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={receiptAttachmentForm.DocumentCategory} onChange={(event) => setReceiptAttachmentForm((current) => ({ ...current, DocumentCategory: event.target.value }))}>
                    <option value="Receipt">驗收文件</option>
                    <option value="Exception">異常 / RMA</option>
                    <option value="Purchase">採購文件</option>
                    <option value="Shipment">物流 / 簽收</option>
                    <option value="General">一般文件</option>
                  </select>
                </label>
                <TextField label="附件名稱" placeholder="例如：外箱照片、序號清單、異常證明" value={receiptAttachmentForm.FileName} onChange={(value) => setReceiptAttachmentForm((current) => ({ ...current, FileName: value }))} />
                <TextField label="外部檔案連結" placeholder="可留空；若檔案在雲端再貼連結" value={receiptAttachmentForm.FileUrl} onChange={(value) => setReceiptAttachmentForm((current) => ({ ...current, FileUrl: value }))} />
              </div>
              <label className="min-w-0 rounded-md border border-[#dfe6e1] bg-white p-3">
                <span className="mb-2 block text-sm font-bold text-[#59665f]">直接上傳檔案 / 圖片</span>
                <input
                  className="block w-full min-w-0 text-sm font-bold text-[#59665f] file:mr-3 file:min-h-10 file:rounded-md file:border-0 file:bg-[#315746] file:px-4 file:font-bold file:text-white"
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                  onChange={(event) => {
                    const file = event.target.files?.[0] || null;
                    setReceiptAttachmentFile(file);
                    if (file && receiptAttachmentForm.FileName.trim() === "") {
                      setReceiptAttachmentForm((current) => ({ ...current, FileName: file.name }));
                    }
                  }}
                />
                <p className="mt-2 break-words text-xs font-bold leading-5 text-[#7b877f]">{receiptAttachmentFile ? `${receiptAttachmentFile.name} / ${(receiptAttachmentFile.size / 1024 / 1024).toFixed(2)} MB` : "支援 PDF、圖片、Office、CSV、TXT、ZIP，單檔上限 20MB。"}</p>
              </label>
              <label className="min-w-0">
                <span className="mb-2 block text-sm font-bold text-[#59665f]">附件備註</span>
                <textarea className="min-h-[76px] w-full rounded-md border border-[#cfd8d2] px-3 py-2" placeholder="例如：右耳外殼刮傷，已通知供應商補件。" value={receiptAttachmentForm.Note} onChange={(event) => setReceiptAttachmentForm((current) => ({ ...current, Note: event.target.value }))} />
              </label>
              <div className="rwd-action-grid justify-end">
                <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" disabled={loading} onClick={() => { setReceiptAttachmentFile(null); setReceiptAttachmentForm((current) => ({ ...current, FileName: "", FileUrl: "" })); }} type="button">清除檔案</button>
                <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" aria-busy={loading && Boolean(receiptAttachmentProgress) ? "true" : "false"} disabled={loading} type="submit">{loading && receiptAttachmentProgress ? receiptAttachmentFile ? "上傳中" : "建立中" : receiptAttachmentFile ? "上傳附件" : "新增附件紀錄"}</button>
              </div>
            </form>
            <div className="mt-4 grid gap-2">
              {receiptAttachments.length === 0 ? <EmptyState text="目前尚未登錄驗收附件。" /> : receiptAttachments.map((attachment) => (
                <div className="grid gap-2 rounded-md border border-[#edf0ee] bg-white p-3 md:grid-cols-[170px_minmax(220px,1fr)_minmax(180px,1fr)] md:items-center" key={attachment.ErpAttachmentId}>
                  <StatusBadge>{attachment.AttachmentTypeLabel}</StatusBadge>
                  <div className="min-w-0">
                    <p className="break-words font-extrabold text-[#203d32]">{attachment.FileName}</p>
                    <p className="mt-1 break-words text-xs font-bold text-[#7b877f]">{attachment.Note || "無備註"} / {attachment.CreatedAt}</p>
                  </div>
                  <div className="rwd-action-grid md:justify-end">
                    {attachment.FileUrl ? <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => openReceiptAttachment(attachment)} type="button">開啟 / 下載</button> : <span className="text-sm font-bold text-[#7b877f]">未提供連結</span>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        ) : null}
      </div>
    );
  }

  return (
    <div className="grid gap-5">
      <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <div className="grid gap-3 xl:grid-cols-[minmax(260px,1.4fr)_170px_170px_190px_190px_160px_160px] xl:items-end">
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span><input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="驗收單、採購單、供應商、商品、SKU" value={keyword} onChange={(event) => setKeyword(event.target.value)} /></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">驗收狀態</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={inspectionStatus} onChange={(event) => setInspectionStatus(event.target.value)}><option value="">全部</option>{inspectionOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">異常狀態</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={exceptionStatus} onChange={(event) => setExceptionStatus(event.target.value)}><option value="">全部</option>{exceptionOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">供應商</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={supplierId} onChange={(event) => setSupplierId(event.target.value)}><option value="">全部供應商</option>{suppliers.map((supplier) => <option key={supplier.ErpSupplierId} value={supplier.ErpSupplierId}>{supplier.Code} / {supplier.Name}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">倉庫</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}><option value="">全部倉庫</option>{warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
        </div>
        <div className="rwd-filter-actions mt-3 justify-end">
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={reset} type="button"><RefreshCw size={18} />重整</button>
        </div>
      </form>
      <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />
      <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#edf0ee] px-4 py-4">
          <h2 className="text-base font-extrabold text-[#203d32]">採購入庫驗收列表</h2>
          <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
        </div>
        <div className="erp-table-scroll border-t border-[#edf0ee]">
          <table className="w-full min-w-[1180px] table-fixed text-left text-sm">
            <thead className="bg-[#f3f7f4] text-[#59665f]">
              <tr>
                <th className="w-[180px] px-3 py-3">驗收單</th>
                <th className="w-[180px] px-3 py-3">採購單</th>
                <th className="w-[220px] px-3 py-3">供應商 / 倉庫</th>
                <th className="w-[180px] px-3 py-3">到貨 / 放行 / 異常</th>
                <th className="w-[170px] px-3 py-3">驗收狀態</th>
                <th className="w-[200px] px-3 py-3">異常處理</th>
                <th className="w-[170px] px-3 py-3">到貨時間</th>
                <th className="w-[120px] px-3 py-3 text-right">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#edf0ee]">
              {receipts.map((receipt) => (
                <tr className="align-top" key={receipt.ErpPurchaseReceiptId}>
                  <td className="break-all px-3 py-3 font-extrabold text-[#203d32]">{receipt.ReceiptNumber}</td>
                  <td className="break-all px-3 py-3 text-[#59665f]">{receipt.PurchaseNumber}</td>
                  <td className="px-3 py-3"><p className="break-words font-bold text-[#203d32]">{receipt.SupplierName || "-"}</p><p className="mt-1 text-xs font-bold text-[#7b877f]">{receipt.WarehouseName || "-"}</p></td>
                  <td className="px-3 py-3 font-extrabold text-[#203d32]">{receipt.ArrivedQuantity} / {receipt.AcceptedQuantity} / {receipt.RejectedQuantity}</td>
                  <td className="px-3 py-3"><StatusBadge tone={receipt.RejectedQuantity > 0 ? "warning" : "default"}>{receipt.InspectionStatusLabel}</StatusBadge></td>
                  <td className="px-3 py-3"><StatusBadge tone={receipt.ExceptionStatus === "Open" ? "warning" : "default"}>{receipt.ExceptionStatusLabel}</StatusBadge>{receipt.ExceptionSummary ? <p className="mt-2 break-words text-xs font-bold text-[#8a5b14]">{receipt.ExceptionSummary}</p> : null}</td>
                  <td className="break-words px-3 py-3 font-bold text-[#59665f]">{receipt.ReceivedAt}</td>
                  <td className="px-3 py-3 text-right"><button className="inline-flex min-h-10 min-w-[88px] items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" onClick={() => navigate(`/erp/purchase-receipts/${receipt.ErpPurchaseReceiptId}`)} type="button">查看</button></td>
                </tr>
              ))}
            </tbody>
          </table>
          {receipts.length === 0 ? <EmptyState text="目前沒有符合條件的驗收單。" /> : null}
        </div>
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => void load(page)} />
      </section>
    </div>
  );
}

function OutboundView({ mode = "list" }: { mode?: "list" | "create" }) {
  const navigate = useNavigate();
  const isCreateMode = mode === "create";
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [dateFrom, setDateFrom] = useState(`${new Date().getFullYear()}-01-01`);
  const [dateTo, setDateTo] = useState(`${new Date().getFullYear()}-12-31`);
  const [orders, setOrders] = useState<AdminErpOutboundOrder[]>([]);
  const [candidates, setCandidates] = useState<AdminErpStoreOrderCandidate[]>([]);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [warehouses, setWarehouses] = useState<AdminErpWarehouse[]>([]);
  const [locations, setLocations] = useState<ErpLocationOption[]>([]);
  const [logisticsCarriers, setLogisticsCarriers] = useState<ErpLogisticsCarrierOption[]>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [selectedId, setSelectedId] = useState("");
  const [expandedCandidateId, setExpandedCandidateId] = useState("");
  const [selectedStockUnits, setSelectedStockUnits] = useState<Record<string, string[]>>({});
  const [note, setNote] = useState("");
  const [inspectionStatus, setInspectionStatus] = useState("Passed");
  const [inspectionChecklist, setInspectionChecklist] = useState<Record<string, boolean>>({ ProductMatched: true, SerialMatched: true, AccessoryChecked: true, WarrantyChecked: true });
  const [abnormalNote, setAbnormalNote] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [pickingChecklist, setPickingChecklist] = useState<Record<string, boolean>>({ Picked: true, QuantityChecked: true });
  const [packingChecklist, setPackingChecklist] = useState<Record<string, boolean>>({ Packed: true, DocumentIncluded: true });
  const [logisticsCarrier, setLogisticsCarrier] = useState("");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [logisticsNote, setLogisticsNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [storeSignAction, setStoreSignAction] = useState<{ row: AdminErpOutboundOrder; note: string; error: string } | null>(null);
  const [logisticsAction, setLogisticsAction] = useState<{ row: AdminErpOutboundOrder; picking: Record<string, boolean>; packing: Record<string, boolean>; carrier: string; tracking: string; note: string; error: string } | null>(null);
  const selected = useMemo(() => candidates.find((row) => row.StoreSalesOrderId === selectedId) || null, [candidates, selectedId]);
  const selectedWarehouse = useMemo(() => warehouses.find((warehouse) => warehouse.ErpWarehouseId === warehouseId) || null, [warehouses, warehouseId]);
  const outboundSteps = [
    { key: "Draft", label: "建立出庫單" },
    { key: "Shipped", label: "確認出庫" },
    { key: "Arrived", label: "確認到店" },
  ];

  async function load(page = 1, nextKeyword = keyword, nextStatus = status, nextWarehouseId = warehouseId, nextLocationId = locationId, nextDateFrom = dateFrom, nextDateTo = dateTo) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpOutboundOrders({ Keyword: nextKeyword.trim(), Status: nextStatus, ErpWarehouseId: nextWarehouseId, LocationId: nextLocationId, DateFrom: nextDateFrom, DateTo: nextDateTo, Page: page, PerPage: 20 });
      setOrders(data.Orders);
      setCandidates(data.Candidates);
      setStatusOptions(data.StatusOptions);
      setWarehouses(data.Warehouses || []);
      setLocations(data.LocationOptions || []);
      setLogisticsCarriers(data.LogisticsCarrierOptions || []);
      setPagination(data.Pagination);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "出庫單載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isCreateMode || warehouseId || warehouses.length === 0) return;
    const firstActiveWarehouse = warehouses.find((warehouse) => warehouse.IsActive) || warehouses[0];
    if (firstActiveWarehouse?.ErpWarehouseId) {
      setWarehouseId(firstActiveWarehouse.ErpWarehouseId);
      void load(1, keyword, status, firstActiveWarehouse.ErpWarehouseId, locationId, dateFrom, dateTo);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, warehouses, warehouseId]);

  async function submitOutbound(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedId) {
      applySubmitErrors({ StoreSalesOrderId: "請先選擇一筆待備貨或待出庫門市訂單。" }, setFormErrors, setMessage);
      return;
    }
    if (!warehouseId) {
      applySubmitErrors({ FromWarehouseId: "請先選擇出庫倉庫。" }, setFormErrors, setMessage);
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    setFormErrors({});
    try {
      const result = await createErpOutboundOrder({
        StoreSalesOrderId: selectedId,
        FromWarehouseId: warehouseId,
        Note: note,
        InspectionStatus: inspectionStatus,
        InspectionChecklist: inspectionChecklist,
        AbnormalNote: abnormalNote,
        ResolutionNote: resolutionNote,
        PickingChecklist: pickingChecklist,
        PackingChecklist: packingChecklist,
        LogisticsCarrier: logisticsCarrier,
        TrackingNumber: trackingNumber,
        LogisticsNote: logisticsNote,
        Items: (selected?.Items || []).map((item) => ({
          StoreSalesOrderItemId: item.StoreSalesOrderItemId,
          ErpStockUnitIds: selectedStockUnits[item.StoreSalesOrderItemId] || [],
        })),
      });
      setSuccess(result.message || "出庫單已建立。");
      setSelectedId("");
      setExpandedCandidateId("");
      setSelectedStockUnits({});
      setNote("");
      setInspectionStatus("Passed");
      setAbnormalNote("");
      setResolutionNote("");
      setLogisticsCarrier("");
      setTrackingNumber("");
      setLogisticsNote("");
      await load(1);
      navigate("/erp/outbound-orders");
    } catch (error) {
      const errors = apiErrors(error);
      if (Object.keys(errors).length > 0) applySubmitErrors(errors, setFormErrors, setMessage);
      else setMessage(errorText(error, "出庫單建立失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function advance(id: string, action: "ship" | "arrive") {
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = action === "ship" ? await shipErpOutboundOrder(id) : await arriveErpOutboundOrder(id);
      setSuccess(result.message || (action === "ship" ? "出庫完成。" : "已確認到店。"));
      await load(pagination.Page);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "出庫狀態更新失敗。");
    } finally {
      setLoading(false);
    }
  }

  function openStoreSign(row: AdminErpOutboundOrder) {
    setStoreSignAction({ row, note: "", error: "" });
  }

  function openLogistics(row: AdminErpOutboundOrder) {
    setLogisticsAction({
      row,
      picking: row.PickingChecklist || { Picked: false, QuantityChecked: false },
      packing: row.PackingChecklist || { Packed: false, DocumentIncluded: false },
      carrier: row.LogisticsCarrier || "",
      tracking: row.TrackingNumber || "",
      note: row.LogisticsNote || "",
      error: "",
    });
  }

  async function saveLogisticsAction() {
    if (!logisticsAction) return;
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = await updateErpOutboundLogistics(logisticsAction.row.ErpOutboundOrderId, {
        PickingChecklist: logisticsAction.picking,
        PackingChecklist: logisticsAction.packing,
        LogisticsCarrier: logisticsAction.carrier.trim(),
        TrackingNumber: logisticsAction.tracking.trim(),
        LogisticsNote: logisticsAction.note.trim(),
      });
      setSuccess(result.message || "出庫物流與檢核資料已更新。");
      setLogisticsAction(null);
      await load(pagination.Page);
    } catch (error) {
      setLogisticsAction((current) => current ? { ...current, error: errorText(error, "出庫物流資料更新失敗。") } : current);
    } finally {
      setLoading(false);
    }
  }

  async function storeSign() {
    if (!storeSignAction) return;
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = await storeSignErpOutboundOrder(storeSignAction.row.ErpOutboundOrderId, { StoreSignedNote: storeSignAction.note.trim() });
      setSuccess(result.message || "門市簽收已登錄。");
      setStoreSignAction(null);
      await load(pagination.Page);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "門市簽收登錄失敗。");
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    const year = new Date().getFullYear();
    setKeyword("");
    setStatus("");
    setWarehouseId("");
    setLocationId("");
    setDateFrom(`${year}-01-01`);
    setDateTo(`${year}-12-31`);
    void load(1, "", "", "", "", `${year}-01-01`, `${year}-12-31`);
  }

  function selectOutboundOrderCandidate(id: string) {
    setSelectedId(id);
    setSelectedStockUnits({});
    setFormErrors((current) => ({ ...current, StoreSalesOrderId: "" }));
  }

  function changeOutboundWarehouse(nextWarehouseId: string) {
    setWarehouseId(nextWarehouseId);
    setSelectedStockUnits({});
    setFormErrors((current) => ({ ...current, FromWarehouseId: "" }));
    void load(1, keyword, status, nextWarehouseId, locationId, dateFrom, dateTo);
  }

  function toggleStockUnit(storeItemId: string, stockUnitId: string, max: number) {
    setSelectedStockUnits((current) => {
      const existing = current[storeItemId] || [];
      if (existing.includes(stockUnitId)) {
        return { ...current, [storeItemId]: existing.filter((id) => id !== stockUnitId) };
      }
      if (existing.length >= max) {
        setMessage(`此品項最多只能選 ${max} 筆序號。`);
        return current;
      }
      return { ...current, [storeItemId]: [...existing, stockUnitId] };
    });
  }

  return (
    <div className="grid gap-5">
      {!isCreateMode ? <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-[minmax(220px,1fr)_150px_180px_180px_170px_170px] 2xl:items-end">
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="出庫單號、門市訂單、客戶、商品、SKU" value={keyword} onChange={(event) => setKeyword(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">狀態</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={status} onChange={(event) => setStatus(event.target.value)}>
              <option value="">全部</option>
              {statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">出庫倉</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}>
              <option value="">全部倉庫</option>
              {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">目的門市</span>
            <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={locationId} onChange={(event) => setLocationId(event.target.value)}>
              <option value="">全部門市</option>
              {locations.map((location) => <option key={location.LocationId} value={location.LocationId}>{location.Name}{location.IsActive ? "" : "（停用）"}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="min-w-0">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span>
            <input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 text-base outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
        </div>
        <div className="rwd-filter-actions mt-3 justify-end">
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={() => navigate("/erp/outbound-orders/new")} type="button"><Send size={18} />建立出庫單</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={reset} type="button"><RefreshCw size={18} />重整</button>
        </div>
      </form> : null}
      <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />
      <ErpTextActionDialog
        open={Boolean(storeSignAction)}
        title="登錄門市簽收"
        description={storeSignAction ? `出庫單 ${storeSignAction.row.OutboundNumber} 已確認到店。登錄後會留下門市簽收時間與備註，作為後續交付追蹤依據。` : ""}
        label="簽收備註"
        placeholder="例如：門市同仁已確認箱數與外觀，待通知客戶取件。可留空。"
        value={storeSignAction?.note || ""}
        confirmText="確認簽收"
        loading={loading}
        error={storeSignAction?.error || ""}
        onChange={(value) => setStoreSignAction((current) => current ? { ...current, note: value, error: "" } : current)}
        onClose={() => { if (!loading) setStoreSignAction(null); }}
        onConfirm={storeSign}
      />
      {logisticsAction ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-[#203d32]/35 px-4 py-6">
          <div className="max-h-[90vh] w-full max-w-3xl overflow-auto rounded-md border border-[#dfe6e1] bg-white shadow-[0_24px_70px_rgba(32,61,50,.22)]">
            <div className="flex flex-col gap-2 border-b border-[#edf0ee] px-5 py-4 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <p className="text-xs font-extrabold uppercase tracking-[.18em] text-[#8a968f]">Outbound Logistics</p>
                <h3 className="mt-1 break-all text-xl font-extrabold text-[#203d32]">物流與出庫檢核</h3>
                <p className="mt-1 break-words text-sm font-bold leading-6 text-[#59665f]">{logisticsAction.row.OutboundNumber} / {logisticsAction.row.ToLocationName || "-"}</p>
              </div>
              <button className="inline-flex min-h-10 min-w-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" disabled={loading} onClick={() => setLogisticsAction(null)} type="button">關閉</button>
            </div>
            <div className="grid gap-4 px-5 py-5">
              {logisticsAction.error ? <div className="rounded-md border border-[#f3d9a7] bg-[#fffaf0] px-3 py-2 text-sm font-bold text-[#8a5b14]">{logisticsAction.error}</div> : null}
              <section className="grid gap-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                <div>
                  <p className="font-extrabold text-[#203d32]">揀貨檢核</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">出庫前確認已揀貨與數量核對，會同步到出庫單、揀貨單與稽核紀錄。</p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[["Picked", "已完成揀貨"], ["QuantityChecked", "數量核對完成"]].map(([key, label]) => (
                    <ErpCheckboxCard checked={Boolean(logisticsAction.picking[key])} key={key} label={label} onChange={(checked) => setLogisticsAction((current) => current ? { ...current, picking: { ...current.picking, [key]: checked }, error: "" } : current)} />
                  ))}
                </div>
              </section>
              <section className="grid gap-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                <div>
                  <p className="font-extrabold text-[#203d32]">包裝檢核</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">保留包裝與單據放入紀錄，後續有物流或門市簽收爭議時可追溯。</p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2">
                  {[["Packed", "已完成包裝"], ["DocumentIncluded", "出庫/簽收單已放入"]].map(([key, label]) => (
                    <ErpCheckboxCard checked={Boolean(logisticsAction.packing[key])} key={key} label={label} onChange={(checked) => setLogisticsAction((current) => current ? { ...current, packing: { ...current.packing, [key]: checked }, error: "" } : current)} />
                  ))}
                </div>
              </section>
              <section className="grid gap-3 rounded-md border border-[#edf0ee] bg-white p-3 md:grid-cols-3">
                <LogisticsCarrierPicker label="物流公司" options={logisticsCarriers} value={logisticsAction.carrier} onChange={(value) => setLogisticsAction((current) => current ? { ...current, carrier: value, error: "" } : current)} />
                <TextField label="物流單號" value={logisticsAction.tracking} onChange={(value) => setLogisticsAction((current) => current ? { ...current, tracking: value, error: "" } : current)} />
                <TextField label="物流備註" value={logisticsAction.note} onChange={(value) => setLogisticsAction((current) => current ? { ...current, note: value, error: "" } : current)} />
              </section>
            </div>
            <div className="flex flex-col-reverse gap-2 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" disabled={loading} onClick={() => setLogisticsAction(null)} type="button">取消</button>
              <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} onClick={() => void saveLogisticsAction()} type="button">儲存物流檢核</button>
            </div>
          </div>
        </div>
      ) : null}
      {isCreateMode ? <section className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)] lg:grid-cols-3">
        {[
          { label: "1. 選擇門市訂單", text: "從待備貨或待出庫清單選取要處理的門市訂單。" },
          { label: "2. 建立出庫單", text: "確認商品與目的門市，建立後門市訂單進入待出庫。" },
          { label: "3. 出庫與到店", text: "確認庫存扣帳後出庫，貨到門市後再確認到店。" },
        ].map((step) => (
          <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3" key={step.label}>
            <p className="font-extrabold text-[#203d32]">{step.label}</p>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">{step.text}</p>
          </div>
        ))}
      </section> : null}

      <div className="grid min-w-0 gap-5">
        {!isCreateMode ? <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
            <h2 className="text-base font-extrabold text-[#203d32]">出庫單列表</h2>
            <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
          </div>
          <div className="grid gap-3 px-4 pb-4">
            {orders.length === 0 ? <EmptyState text="目前尚未建立出庫單。" /> : null}
            {orders.map((row) => {
              const currentStep = row.Status === "Arrived" ? 2 : row.Status === "Shipped" ? 1 : 0;
              return (
                <article className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-4" key={row.ErpOutboundOrderId}>
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className="break-all text-lg font-extrabold text-[#203d32]">{row.OutboundNumber}</p>
                        <StatusBadge>{row.StatusLabel}</StatusBadge>
                      </div>
                      <p className="mt-1 break-all text-sm font-bold text-[#59665f]">來源門市訂單：{row.StoreOrderNumber || "-"}</p>
                      <StoreOrderAuditLine row={row} />
                    </div>
                    <ErpActionBar className="lg:min-w-[280px]">
                      <ErpDocumentPrintMenu
                        items={[
                          { label: "出庫單", description: "倉庫出貨主單與明細", onClick: () => printOutboundOrder(row) },
                          { label: "揀貨單", description: "工程/倉管揀貨檢核", onClick: () => printOutboundProcessDocument(row, "picking") },
                          { label: "包裝單", description: "包裝檢核與裝箱資訊", onClick: () => printOutboundProcessDocument(row, "packing") },
                          { label: "物流單", description: "物流資訊與交寄欄位", onClick: () => printOutboundProcessDocument(row, "logistics") },
                          { label: "門市簽收單", description: "門市收貨與簽收欄", onClick: () => printOutboundProcessDocument(row, "store-sign") },
                        ]}
                      />
                      <button className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => navigate(`/erp/attachments?RelatedType=OutboundOrder&RelatedId=${row.ErpOutboundOrderId}&DocumentCategory=Shipment`)} type="button"><Upload size={17} />出貨附件</button>
                      <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={loading || row.Status === "Arrived"} onClick={() => openLogistics(row)} type="button">物流/檢核</button>
                      <button className="inline-flex min-h-10 items-center justify-center rounded-md bg-[#315746] px-4 font-bold text-white disabled:bg-[#cfd8d2]" disabled={loading || !canShipOutbound(row)} onClick={() => advance(row.ErpOutboundOrderId, "ship")} title={canShipOutbound(row) ? "確認出庫" : "只有草稿或已撿貨出庫單可以確認出庫"} type="button">確認出庫</button>
                      <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={loading || !canArriveOutbound(row)} onClick={() => advance(row.ErpOutboundOrderId, "arrive")} title={canArriveOutbound(row) ? "確認到店" : "只有已出庫單可以確認到店"} type="button">確認到店</button>
                      <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-50" disabled={loading || row.Status !== "Arrived" || Boolean(row.StoreSignedAt)} onClick={() => openStoreSign(row)} title={row.StoreSignedAt ? "此出庫單已完成簽收登錄" : row.Status === "Arrived" ? "登錄門市簽收" : "需先確認到店後才能登錄簽收"} type="button">登錄簽收</button>
                    </ErpActionBar>
                  </div>

                  <div className="mt-3 grid gap-2 md:grid-cols-4">
                    <InfoBox label="出庫倉庫" value={row.FromWarehouseName || "-"} />
                    <InfoBox label="目的門市" value={row.ToLocationName || "-"} />
                    <InfoBox label="品項 / 數量" value={`${row.ItemCount} 項 / ${row.QuantityTotal}`} />
                    <InfoBox label="建立時間" value={row.CreatedAt || "-"} />
                  </div>

                  <div className="mt-3 grid gap-2 md:grid-cols-2 xl:grid-cols-4">
                    <InfoBox label="揀貨單" value={checklistSummary(row.PickingChecklist)} />
                    <InfoBox label="包裝單" value={checklistSummary(row.PackingChecklist)} />
                    <InfoBox label="物流資訊" value={row.LogisticsCarrier || row.TrackingNumber ? `${row.LogisticsCarrier || "未填物流"} / ${row.TrackingNumber || "未填單號"}` : "尚未填寫"} />
                    <InfoBox label="門市簽收" value={row.StoreSignedAt ? `${row.StoreSignedAt}${row.StoreSignedNote ? ` / ${row.StoreSignedNote}` : ""}` : row.Status === "Arrived" ? "已到店，待門市簽收" : "尚未到店"} />
                  </div>

                  {row.EngineeringInspection ? (
                    <div className="mt-3 rounded-md border border-[#edf0ee] bg-white p-3">
                      <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                        <div className="min-w-0">
                          <p className="font-extrabold text-[#203d32]">工程檢查 / QC：{row.EngineeringInspection.InspectionNumber}</p>
                          <p className="mt-1 break-words text-sm font-bold text-[#7b877f]">檢核：{checklistSummary(row.EngineeringInspection.Checklist)} / 檢查時間：{row.EngineeringInspection.InspectedAt || "-"}</p>
                        </div>
                        <StatusBadge tone={row.EngineeringInspection.InspectionStatus === "Blocked" ? "danger" : row.EngineeringInspection.InspectionStatus === "Abnormal" ? "warning" : "default"}>{row.EngineeringInspection.InspectionStatus === "Passed" ? "通過" : row.EngineeringInspection.InspectionStatus === "Abnormal" ? "異常處理" : row.EngineeringInspection.InspectionStatus === "Blocked" ? "阻擋出庫" : row.EngineeringInspection.InspectionStatus}</StatusBadge>
                      </div>
                      {row.EngineeringInspection.AbnormalNote || row.EngineeringInspection.ResolutionNote ? <p className="mt-2 break-words text-sm font-semibold leading-6 text-[#59665f]">異常 / 處理：{row.EngineeringInspection.AbnormalNote || "-"} / {row.EngineeringInspection.ResolutionNote || "-"}</p> : null}
                    </div>
                  ) : null}

                  <div className="mt-4 grid gap-2 md:grid-cols-3">
                    {outboundSteps.map((step, index) => (
                      <div className={`rounded-md border px-3 py-2 ${index <= currentStep ? "border-[#acd7bb] bg-[#eff8f1]" : "border-[#edf0ee] bg-white"}`} key={step.key}>
                        <p className={`text-sm font-extrabold ${index <= currentStep ? "text-[#245438]" : "text-[#8c9790]"}`}>{step.label}</p>
                      </div>
                    ))}
                  </div>

                  {row.Note ? <p className="mt-3 rounded-md bg-white px-3 py-2 text-sm font-semibold leading-6 text-[#59665f]">備註：{row.Note}</p> : null}
                </article>
              );
            })}
          </div>
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
        </section> : null}

        {isCreateMode ? <aside className="grid min-w-0 gap-4">
          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <h2 className="text-base font-extrabold text-[#203d32]">建立出庫單</h2>
                <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">建立正式出庫主檔，確認來源門市訂單、出庫倉、目的門市與品項序號後再送出。</p>
              </div>
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/outbound-orders")} type="button">返回列表</button>
            </div>
            <form className="mt-4 grid gap-4" onSubmit={submitOutbound}>
              <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  <h3 className="text-base font-extrabold text-[#203d32]">出庫主檔</h3>
                  <span className="rounded-full bg-[#eaf4ef] px-3 py-1 text-xs font-extrabold text-[#315746]">草稿建立</span>
                </div>
                <div className="grid gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(260px,2fr)]">
                  <label className="min-w-0" data-field="FromWarehouseId">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">出庫倉庫</span>
                    <select className={fieldClass(formErrors, "FromWarehouseId", "h-11 w-full min-w-0 px-3")} value={warehouseId} onChange={(event) => changeOutboundWarehouse(event.target.value)}>
                      <option value="">請選擇出庫倉庫</option>
                      {warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}{warehouse.IsActive ? "" : "（停用）"}</option>)}
                    </select>
                    <FieldError errors={formErrors} name="FromWarehouseId" />
                  </label>
                  <label className="min-w-0" data-field="StoreSalesOrderId">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">來源門市訂單</span>
                    <select className={fieldClass(formErrors, "StoreSalesOrderId", "h-11 w-full min-w-0 px-3")} value={selectedId} onChange={(event) => selectOutboundOrderCandidate(event.target.value)}>
                      <option value="">請選擇門市訂單</option>
                      {candidates.map((row) => <option key={row.StoreSalesOrderId} value={row.StoreSalesOrderId}>{row.LocationName || "未指定門市"} / {row.OrderNumber} / {row.CustomerName} / {row.ProductSummary}</option>)}
                    </select>
                    <FieldError errors={formErrors} name="StoreSalesOrderId" />
                  </label>
                </div>
              </div>
              {selected ? (
                <div className="grid gap-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="break-all text-lg font-extrabold text-[#203d32]">{selected.OrderNumber}</p>
                      <p className="mt-1 break-words text-sm font-bold text-[#59665f]">{selected.CustomerName} / {selected.CustomerPhone || "-"}</p>
                    </div>
                    <StatusBadge>{selected.StatusLabel}</StatusBadge>
                  </div>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                    <InfoBox label="目的門市" value={selected.LocationName || "-"} />
                    <InfoBox label="訂單金額" value={money(selected.TotalAmount)} />
                    <InfoBox label="剩餘待付" value={money(selected.BalanceAmount)} />
                    <InfoBox label="建立時間" value={selected.CreatedAt || "-"} />
                  </div>
                </div>
              ) : null}
              <div className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3">
                <div>
                  <p className="font-extrabold text-[#203d32]">工程檢查 / QC</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">建立出庫前先留下工程檢查結果；若檢查阻擋，系統不會建立出庫單。</p>
                </div>
                <div className="grid gap-3 lg:grid-cols-[180px_1fr]">
                  <label className="min-w-0">
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">檢查結果</span>
                    <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={inspectionStatus} onChange={(event) => setInspectionStatus(event.target.value)}>
                      <option value="Passed">通過</option>
                      <option value="Abnormal">有異常但可處理</option>
                      <option value="Blocked">阻擋出庫</option>
                    </select>
                  </label>
                  <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                    {[
                      ["ProductMatched", "商品規格確認"],
                      ["SerialMatched", "序號 / 耳側確認"],
                      ["AccessoryChecked", "配件包裝確認"],
                      ["WarrantyChecked", "保固資料確認"],
                    ].map(([key, label]) => (
                      <ErpCheckboxCard checked={Boolean(inspectionChecklist[key])} key={key} label={label} onChange={(checked) => setInspectionChecklist((current) => ({ ...current, [key]: checked }))} />
                    ))}
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <label>
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">異常說明</span>
                    <textarea className="min-h-[76px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如：外觀刮傷、序號不符、需重配件。" value={abnormalNote} onChange={(event) => setAbnormalNote(event.target.value)} />
                  </label>
                  <label>
                    <span className="mb-2 block text-sm font-bold text-[#59665f]">處理方式</span>
                    <textarea className="min-h-[76px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" placeholder="例如：已更換配件、主管確認可出庫。" value={resolutionNote} onChange={(event) => setResolutionNote(event.target.value)} />
                  </label>
                </div>
              </div>
              <div className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-3">
                <div>
                  <p className="font-extrabold text-[#203d32]">揀貨 / 包裝 / 物流</p>
                  <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">出庫前保留揀貨與包裝檢核，物流資料可先填寫，後續列印與追蹤都會用到。</p>
                </div>
                <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
                  {[
                    ["Picked", "已完成揀貨", pickingChecklist, setPickingChecklist],
                    ["QuantityChecked", "數量核對完成", pickingChecklist, setPickingChecklist],
                    ["Packed", "已完成包裝", packingChecklist, setPackingChecklist],
                    ["DocumentIncluded", "單據已放入", packingChecklist, setPackingChecklist],
                  ].map(([key, label, state, setter]) => (
                    <ErpCheckboxCard checked={Boolean((state as Record<string, boolean>)[String(key)])} key={String(key)} label={String(label)} onChange={(checked) => (setter as typeof setPickingChecklist)((current) => ({ ...current, [String(key)]: checked }))} />
                  ))}
                </div>
                <div className="grid gap-3 md:grid-cols-3">
                  <LogisticsCarrierPicker label="物流公司" options={logisticsCarriers} value={logisticsCarrier} onChange={setLogisticsCarrier} />
                  <TextField label="物流單號" value={trackingNumber} onChange={setTrackingNumber} />
                  <TextField label="物流備註" value={logisticsNote} onChange={setLogisticsNote} />
                </div>
              </div>
              {selected?.Items?.length ? (
                <div className="grid gap-3 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
                  <div>
                    <p className="font-extrabold text-[#203d32]">出庫明細</p>
                    <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">出庫倉：{selectedWarehouse?.Name || "尚未選擇"}。序號商品請從可用庫存勾選；未指定序號的數量會以一般庫存扣帳。</p>
                  </div>
                  <div className="overflow-x-auto rounded-md border border-[#edf0ee] bg-white">
                    <table className="w-full min-w-[780px] table-fixed text-left text-sm">
                      <thead className="bg-[#f3f7f4] text-[#59665f]">
                        <tr>
                          <th className="w-[240px] px-3 py-2">商品</th>
                          <th className="w-[140px] px-3 py-2">SKU / 規格</th>
                          <th className="w-[90px] px-3 py-2 text-right">需求數</th>
                          <th className="w-[110px] px-3 py-2 text-right">已挑序號</th>
                          <th className="w-[120px] px-3 py-2 text-right">一般扣庫</th>
                          <th className="px-3 py-2">狀態</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-[#edf0ee]">
                        {selected.Items.map((item) => {
                          const chosenCount = (selectedStockUnits[item.StoreSalesOrderItemId] || []).length;
                          const remainingCount = Math.max(0, item.Quantity - chosenCount);
                          return (
                            <tr key={item.StoreSalesOrderItemId}>
                              <td className="px-3 py-3"><p className="break-words font-extrabold text-[#203d32]">{item.ProductName}</p></td>
                              <td className="px-3 py-3"><p className="break-all font-bold text-[#203d32]">{item.Sku || "-"}</p><p className="break-words text-xs font-bold text-[#7b877f]">{item.VariantLabel || "單一規格"}</p></td>
                              <td className="px-3 py-3 text-right font-extrabold">{item.Quantity}</td>
                              <td className="px-3 py-3 text-right font-extrabold">{chosenCount}</td>
                              <td className="px-3 py-3 text-right font-extrabold">{remainingCount}</td>
                              <td className="px-3 py-3"><StatusBadge tone={chosenCount < item.Quantity ? "warning" : "default"}>{chosenCount < item.Quantity ? "部分未指定序號" : "序號已指定"}</StatusBadge></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  {selected.Items.map((item) => {
                    const chosen = selectedStockUnits[item.StoreSalesOrderItemId] || [];
                    const units = item.AvailableStockUnits || [];
                    return (
                      <section className="rounded-md border border-[#edf0ee] bg-white p-3" key={item.StoreSalesOrderItemId}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div className="min-w-0">
                            <p className="break-words font-extrabold text-[#203d32]">{item.ProductName}</p>
                            <p className="mt-1 break-words text-sm font-semibold text-[#7b877f]">{item.VariantLabel || "單一規格"} / {item.Sku || "-"}</p>
                          </div>
                          <StatusBadge tone={chosen.length < item.Quantity ? "warning" : "default"}>已選 {chosen.length} / {item.Quantity}</StatusBadge>
                        </div>
                        {units.length > 0 ? (
                          <div className="mt-3 max-w-full overflow-x-auto">
                            <table className="w-full min-w-[680px] table-fixed text-left text-sm">
                              <thead className="bg-[#f3f7f4] text-[#59665f]">
                                <tr><th className="w-[74px] px-3 py-2">選取</th><th className="w-[180px] px-3 py-2">序號</th><th className="w-[90px] px-3 py-2">耳側</th><th className="w-[140px] px-3 py-2">入庫日</th><th className="w-[120px] px-3 py-2 text-right">成本</th><th className="px-3 py-2">備註</th></tr>
                              </thead>
                              <tbody className="divide-y divide-[#edf0ee]">
                                {units.map((unit) => (
                                  <tr key={unit.ErpStockUnitId}>
                                    <td className="px-3 py-3">
                                      <ErpCheckbox ariaLabel={`選取序號 ${unit.SerialNumber || "無序號"}`} checked={chosen.includes(unit.ErpStockUnitId)} onChange={() => toggleStockUnit(item.StoreSalesOrderItemId, unit.ErpStockUnitId, item.Quantity)} />
                                    </td>
                                    <td className="break-all px-3 py-3 font-bold text-[#203d32]">{unit.SerialNumber || "無序號"}</td>
                                    <td className="px-3 py-3 font-semibold text-[#59665f]">{unit.EarSideLabel}</td>
                                    <td className="px-3 py-3 text-[#59665f]">{unit.ReceivedAt || "-"}</td>
                                    <td className="px-3 py-3 text-right font-bold">{money(unit.UnitCost)}</td>
                                    <td className="break-words px-3 py-3 text-[#59665f]">{unit.Note || "-"}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="mt-3 rounded-md border border-[#f3d9a7] bg-[#fffaf0] px-3 py-2 text-sm font-bold leading-6 text-[#8a5b14]">目前沒有可直接挑選的 ERP 現貨序號；仍可建立出庫單，出庫時會依庫存數量扣帳。</div>
                        )}
                      </section>
                    );
                  })}
                </div>
              ) : null}
              <label>
                <span className="mb-2 block text-sm font-bold text-[#59665f]">出庫備註</span>
                <textarea className="min-h-[96px] w-full rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={note} onChange={(event) => setNote(event.target.value)} />
              </label>
              <button className="inline-flex min-h-11 w-full items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading || !selectedId} type="submit">
                <Send size={18} />
                建立出庫單
              </button>
            </form>
          </section>

          <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
            <div className="mb-3 flex items-center justify-between gap-2">
              <h2 className="text-base font-extrabold text-[#203d32]">待備貨 / 待出庫訂單</h2>
              <span className="text-sm font-bold text-[#7b877f]">{count(candidates.length)}</span>
            </div>
            <div className="grid gap-3">
              {candidates.length === 0 ? <EmptyState text="目前沒有待備貨或待出庫門市訂單。" /> : null}
              {candidates.map((row) => (
                <OutboundCandidateRow
                  active={selectedId === row.StoreSalesOrderId}
                  expanded={expandedCandidateId === row.StoreSalesOrderId}
                  key={row.StoreSalesOrderId}
                  row={row}
                  onSelect={() => selectOutboundOrderCandidate(row.StoreSalesOrderId)}
                  onToggle={() => setExpandedCandidateId((current) => current === row.StoreSalesOrderId ? "" : row.StoreSalesOrderId)}
                />
              ))}
            </div>
          </section>
        </aside> : null}
      </div>
    </div>
  );
}

const emptyWarehouseForm: ErpWarehousePayload = { Code: "", Name: "", WarehouseType: "Headquarters", LocationId: "", IsActive: true, SortOrder: 100 };
const emptySupplierForm: ErpSupplierPayload = {
  Code: "",
  Name: "",
  SupplierType: "Manufacturer",
  ShortName: "",
  ContactName: "",
  ContactTitle: "",
  Mobile: "",
  Phone: "",
  Fax: "",
  LineId: "",
  Email: "",
  Website: "",
  PurchaseEmail: "",
  FinanceEmail: "",
  Country: "Taiwan",
  TaxNumber: "",
  Address: "",
  InvoiceTitle: "",
  InvoiceTaxId: "",
  BankName: "",
  BankAccountName: "",
  BankAccountNumber: "",
  PaymentTerms: "",
  Currency: "TWD",
  MinimumOrderAmount: 0,
  ShippingMethod: "",
  TradeTerms: "",
  WarrantyPolicy: "",
  ReturnPolicy: "",
  QualityRating: 3,
  RiskLevel: "Normal",
  Tags: "",
  LeadTimeDays: 0,
  Note: "",
  IsActive: true,
  SortOrder: 100,
};
const emptySupplierPriceForm: ErpSupplierPricePayload = { ProductId: "", VariantId: null, SupplierSku: "", PurchaseUnit: "台", UnitCost: 0, Currency: "TWD", MinimumOrderQuantity: 1, LeadTimeDays: 0, IsDefault: false, EffectiveFrom: "", EffectiveTo: "", IsActive: true, Note: "" };

type PurchaseLine = {
  LineKey: string;
  StoreSalesOrderItemId?: string;
  ProductId: string;
  VariantId: string | null;
  ProductName: string;
  ProductCode?: string;
  Sku: string | null;
  VariantLabel: string | null;
  Quantity: number;
  UnitCost: number;
  SupplierSku: string;
  PurchaseUnit: string;
  Currency?: string;
  MinimumOrderQuantity?: number;
  LeadTimeDays?: number;
  SupplierPriceStatus?: "Matched" | "Missing" | "Manual";
  Note: string;
};

type ReceiveLine = {
  ErpPurchaseOrderItemId: string;
  ReceiveQuantity: number;
  AcceptedQuantity: number;
  UnitCost: number;
  SerialText: string;
  EarSide: string;
  ExceptionType: string;
  ResolutionAction: string;
  InspectionChecklist: Record<string, boolean>;
  Note: string;
};

type SerialRangeInput = {
  Prefix: string;
  Start: string;
  End: string;
};

function WarehouseView() {
  const [items, setItems] = useState<ErpWarehouse[]>([]);
  const [locations, setLocations] = useState<ErpLocationOption[]>([]);
  const [types, setTypes] = useState<AdminStoreSalesOrderOption[]>([]);
  const [editingId, setEditingId] = useState("");
  const [form, setForm] = useState<ErpWarehousePayload>(emptyWarehouseForm);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrors>({});

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpWarehouses();
      setItems(data.Items);
      setLocations(data.LocationOptions);
      setTypes(data.WarehouseTypeOptions);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "倉庫資料載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  function edit(item: ErpWarehouse) {
    setEditingId(item.ErpWarehouseId);
    setForm({
      Code: item.Code,
      Name: item.Name,
      WarehouseType: item.WarehouseType,
      LocationId: item.LocationId || "",
      IsActive: item.IsActive,
      SortOrder: item.SortOrder,
    });
    setFormErrors({});
  }

  function validateWarehouseForm() {
    const errors: FormErrors = {};
    if (!form.Code.trim()) errors.Code = "請輸入倉庫代碼。";
    if (!form.Name.trim()) errors.Name = "請輸入倉庫名稱。";
    if (!form.WarehouseType) errors.WarehouseType = "請選擇倉庫類型。";
    if (!Number.isFinite(form.SortOrder) || form.SortOrder < 0) errors.SortOrder = "排序需為 0 以上的數字。";
    return errors;
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validateWarehouseForm();
    if (Object.keys(validationErrors).length > 0) {
      applySubmitErrors(validationErrors, setFormErrors, setMessage);
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    setFormErrors({});
    try {
      const payload = { ...form, LocationId: form.LocationId || null };
      const result = editingId ? await updateErpWarehouse(editingId, payload) : await createErpWarehouse(payload);
      setSuccess(result.message || (editingId ? "倉庫已更新。" : "倉庫已建立。"));
      setEditingId("");
      setForm(emptyWarehouseForm);
      await load();
    } catch (error) {
      const errors = apiErrors(error);
      if (Object.keys(errors).length > 0) applySubmitErrors(errors, setFormErrors, setMessage);
      else setMessage(errorText(error, "倉庫儲存失敗。"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid gap-5">
      <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />
      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_440px]">
        <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
            <h2 className="text-base font-extrabold text-[#203d32]">倉庫列表</h2>
            <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(items.length)}</span>
          </div>
          <div className="overflow-x-auto px-4 pb-4">
            {items.length === 0 ? <EmptyState text="目前尚未建立倉庫。" /> : (
              <table className="w-full min-w-[920px] table-fixed text-left text-sm">
                <thead className="bg-[#f3f7f4] text-[#59665f]">
                  <tr>
                    <th className="w-[130px] px-4 py-3">代碼</th>
                    <th className="px-4 py-3">名稱</th>
                    <th className="w-[140px] px-4 py-3">類型</th>
                    <th className="w-[170px] px-4 py-3">對應門市</th>
                    <th className="w-[90px] px-4 py-3">狀態</th>
                    <th className="w-[100px] px-4 py-3 text-right">操作</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#edf0ee]">
                  {items.map((item) => (
                    <tr key={item.ErpWarehouseId}>
                      <td className="break-all px-4 py-4 font-extrabold text-[#203d32]">{item.Code}</td>
                      <td className="break-words px-4 py-4 font-bold text-[#203d32]">{item.Name}</td>
                      <td className="px-4 py-4">{item.WarehouseTypeLabel}</td>
                      <td className="break-words px-4 py-4 text-[#59665f]">{item.LocationName || "-"}</td>
                      <td className="px-4 py-4"><StatusBadge tone={item.IsActive ? "default" : "warning"}>{item.IsActive ? "啟用" : "停用"}</StatusBadge></td>
                      <td className="px-4 py-4 text-right"><button className="inline-flex min-w-[88px] items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 py-2 font-bold text-[#315746]" onClick={() => edit(item)} type="button">編輯</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <h2 className="text-base font-extrabold text-[#203d32]">{editingId ? "編輯倉庫" : "新增倉庫"}</h2>
          <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">總部倉、門市倉、維修倉與在途倉都在這裡維護。</p>
          <form className="mt-4 grid gap-3" onSubmit={submit}>
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField error={formErrors.Code} fieldName="Code" label="倉庫代碼" value={form.Code} onChange={(value) => { setForm((current) => ({ ...current, Code: value })); setFormErrors((current) => ({ ...current, Code: "" })); }} />
              <TextField error={formErrors.Name} fieldName="Name" label="倉庫名稱" value={form.Name} onChange={(value) => { setForm((current) => ({ ...current, Name: value })); setFormErrors((current) => ({ ...current, Name: "" })); }} />
            </div>
            <label data-field="WarehouseType">
              <span className="mb-2 block text-sm font-bold text-[#59665f]">倉庫類型</span>
              <select className={fieldClass(formErrors, "WarehouseType", "h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15")} value={form.WarehouseType} onChange={(event) => { setForm((current) => ({ ...current, WarehouseType: event.target.value })); setFormErrors((current) => ({ ...current, WarehouseType: "" })); }}>
                {types.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
              </select>
              <FieldError errors={formErrors} name="WarehouseType" />
            </label>
            <label>
              <span className="mb-2 block text-sm font-bold text-[#59665f]">對應門市</span>
              <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.LocationId || ""} onChange={(event) => setForm((current) => ({ ...current, LocationId: event.target.value }))}>
                <option value="">不綁定門市</option>
                {locations.map((location) => <option key={location.LocationId} value={location.LocationId}>{location.Name}{location.IsActive ? "" : "（停用）"}</option>)}
              </select>
            </label>
            <div className="grid gap-3 sm:grid-cols-2">
              <NumberField error={formErrors.SortOrder} fieldName="SortOrder" label="排序" value={form.SortOrder} onChange={(value) => { setForm((current) => ({ ...current, SortOrder: value })); setFormErrors((current) => ({ ...current, SortOrder: "" })); }} />
              <div className="sm:mt-7">
                <ErpSwitchCard checked={form.IsActive} label="啟用" onChange={(checked) => setForm((current) => ({ ...current, IsActive: checked }))} />
              </div>
            </div>
            <div className="rwd-action-grid">
              <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} type="submit">儲存</button>
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => { setEditingId(""); setForm(emptyWarehouseForm); setFormErrors({}); }} type="button">取消</button>
            </div>
          </form>
        </section>
      </div>

    </div>
  );
}

function LogisticsCarrierPicker({ label, onChange, options, value }: { label: string; onChange: (value: string) => void; options: ErpLogisticsCarrierOption[]; value: string }) {
  const hasOptions = options.length > 0;
  const matched = options.find((option) => option.Name === value || option.Code === value);
  const isManual = !hasOptions || (value !== "" && !matched);
  const selectValue = matched?.Name || (isManual ? "__manual__" : "");

  return (
    <div className="min-w-0">
      <span className="mb-2 block text-sm font-bold text-[#59665f]">{label}</span>
      {hasOptions ? (
        <div className="grid gap-2">
          <select
            className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] bg-white px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            value={selectValue}
            onChange={(event) => {
              const nextValue = event.target.value;
              if (nextValue === "__manual__") {
                onChange(matched ? "" : value);
                return;
              }
              onChange(nextValue);
            }}
          >
            <option value="">請選擇物流公司</option>
            {options.map((option) => (
              <option key={option.LogisticsCarrierId} value={option.Name}>
                {option.Name}{option.Code ? `（${option.Code}）` : ""}
              </option>
            ))}
            <option value="__manual__">其他 / 手動輸入</option>
          </select>
          {isManual ? (
            <input
              className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
              placeholder="手動輸入物流公司"
              value={value}
              onChange={(event) => onChange(event.target.value)}
            />
          ) : null}
        </div>
      ) : (
        <div className="grid gap-1">
          <input
            className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
            placeholder="總管理端尚未建立物流公司，請手動輸入"
            value={value}
            onChange={(event) => onChange(event.target.value)}
          />
          <p className="text-xs font-semibold text-[#8a968f]">若常用物流未出現，請先到總管理端「物流串接設定」建立物流公司主檔。</p>
        </div>
      )}
    </div>
  );
}

function TextField({ error, fieldName, label, onChange, placeholder, value }: { error?: string; fieldName?: string; label: string; onChange: (value: string) => void; placeholder?: string; value: string }) {
  const name = fieldName || label;
  return (
    <label className="min-w-0" data-field={name}>
      <span className="mb-2 block text-sm font-bold text-[#59665f]">{label}</span>
      <input className={fieldClass(error ? { [name]: error } : {}, name, "h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15")} placeholder={placeholder} value={value} onChange={(event) => onChange(event.target.value)} />
      {error ? <p className="mt-1 text-xs font-bold text-[#b45309]">{error}</p> : null}
    </label>
  );
}

function NumberField({ error, fieldName, label, onChange, value }: { error?: string; fieldName?: string; label: string; onChange: (value: number) => void; value: number }) {
  const name = fieldName || label;
  return (
    <label className="min-w-0" data-field={name}>
      <span className="mb-2 block text-sm font-bold text-[#59665f]">{label}</span>
      <input className={fieldClass(error ? { [name]: error } : {}, name, "h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15")} type="number" value={value} onChange={(event) => onChange(Number(event.target.value || 0))} />
      {error ? <p className="mt-1 text-xs font-bold text-[#b45309]">{error}</p> : null}
    </label>
  );
}

function SupplierView({ mode = "list", supplierIdParam = "" }: { mode?: "list" | "create" | "edit"; supplierIdParam?: string }) {
  const navigate = useNavigate();
  const isListMode = mode === "list";
  const isCreateMode = mode === "create";
  const isEditMode = mode === "edit";
  const [keyword, setKeyword] = useState("");
  const [items, setItems] = useState<ErpSupplier[]>([]);
  const [editingId, setEditingId] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState<ErpSupplierPayload>(emptySupplierForm);
  const [prices, setPrices] = useState<ErpSupplierProductPrice[]>([]);
  const [priceEditingId, setPriceEditingId] = useState("");
  const [priceForm, setPriceForm] = useState<ErpSupplierPricePayload>(emptySupplierPriceForm);
  const [priceProductName, setPriceProductName] = useState("");
  const [priceKeyword, setPriceKeyword] = useState("");
  const [priceResults, setPriceResults] = useState<ErpProductSearchItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [priceErrors, setPriceErrors] = useState<FormErrors>({});

  async function load(nextKeyword = keyword) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpSuppliers({ Keyword: nextKeyword.trim() });
      setItems(data.Items);
      if (isCreateMode) {
        startCreateSupplier();
      }
      if (isEditMode && supplierIdParam) {
        const target = data.Items.find((item) => item.ErpSupplierId === supplierIdParam);
        if (target) {
          edit(target);
        } else {
          setMessage("找不到要編輯的供應商。");
        }
      }
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "供應商資料載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function edit(item: ErpSupplier) {
    setEditingId(item.ErpSupplierId);
    setFormOpen(true);
    setForm({
      Code: item.Code,
      Name: item.Name,
      SupplierType: item.SupplierType || "Manufacturer",
      ShortName: item.ShortName || "",
      ContactName: item.ContactName || "",
      ContactTitle: item.ContactTitle || "",
      Mobile: item.Mobile || "",
      Phone: item.Phone || "",
      Fax: item.Fax || "",
      LineId: item.LineId || "",
      Email: item.Email || "",
      Website: item.Website || "",
      PurchaseEmail: item.PurchaseEmail || "",
      FinanceEmail: item.FinanceEmail || "",
      Country: item.Country || "",
      TaxNumber: item.TaxNumber || "",
      Address: item.Address || "",
      InvoiceTitle: item.InvoiceTitle || "",
      InvoiceTaxId: item.InvoiceTaxId || "",
      BankName: item.BankName || "",
      BankAccountName: item.BankAccountName || "",
      BankAccountNumber: item.BankAccountNumber || "",
      PaymentTerms: item.PaymentTerms || "",
      Currency: item.Currency || "TWD",
      MinimumOrderAmount: item.MinimumOrderAmount || 0,
      ShippingMethod: item.ShippingMethod || "",
      TradeTerms: item.TradeTerms || "",
      WarrantyPolicy: item.WarrantyPolicy || "",
      ReturnPolicy: item.ReturnPolicy || "",
      QualityRating: item.QualityRating || 3,
      RiskLevel: item.RiskLevel || "Normal",
      Tags: item.Tags || "",
      LeadTimeDays: item.LeadTimeDays,
      Note: item.Note || "",
      IsActive: item.IsActive,
      SortOrder: item.SortOrder,
    });
    setPriceEditingId("");
    setPriceForm(emptySupplierPriceForm);
    setPriceProductName("");
    setPriceResults([]);
    setFormErrors({});
    setPriceErrors({});
    void loadPrices(item.ErpSupplierId);
  }

  function resetSupplierForm() {
    setEditingId("");
    setFormOpen(false);
    setForm(emptySupplierForm);
    setPrices([]);
    setPriceEditingId("");
    setPriceForm(emptySupplierPriceForm);
    setPriceProductName("");
    setPriceResults([]);
    setFormErrors({});
    setPriceErrors({});
  }

  function startCreateSupplier() {
    setEditingId("");
    setFormOpen(true);
    setForm(emptySupplierForm);
    setPrices([]);
    setPriceEditingId("");
    setPriceForm(emptySupplierPriceForm);
    setPriceProductName("");
    setPriceKeyword("");
    setPriceResults([]);
    setFormErrors({});
    setPriceErrors({});
  }

  function validateSupplierForm() {
    const errors: FormErrors = {};
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!form.Code.trim()) errors.Code = "請輸入供應商代碼。";
    if (!form.Name.trim()) errors.Name = "請輸入供應商名稱。";
    if (!form.Currency?.trim()) errors.Currency = "請輸入採購幣別。";
    if (!Number.isFinite(form.LeadTimeDays) || form.LeadTimeDays < 0) errors.LeadTimeDays = "預估交期需為 0 以上天數。";
    if (!Number.isFinite(form.MinimumOrderAmount || 0) || (form.MinimumOrderAmount || 0) < 0) errors.MinimumOrderAmount = "最低採購金額不可小於 0。";
    if (!Number.isFinite(form.QualityRating || 0) || (form.QualityRating || 0) < 1 || (form.QualityRating || 0) > 5) errors.QualityRating = "供應商評分需介於 1 到 5。";
    if (!Number.isFinite(form.SortOrder) || form.SortOrder < 0) errors.SortOrder = "排序需為 0 以上的數字。";
    if (form.Email?.trim() && !emailPattern.test(form.Email.trim())) errors.Email = "主要 Email 格式不正確。";
    if (form.PurchaseEmail?.trim() && !emailPattern.test(form.PurchaseEmail.trim())) errors.PurchaseEmail = "採購 Email 格式不正確。";
    if (form.FinanceEmail?.trim() && !emailPattern.test(form.FinanceEmail.trim())) errors.FinanceEmail = "財務 Email 格式不正確。";
    return errors;
  }

  function validatePriceForm() {
    const errors: FormErrors = {};
    if (!editingId) errors.Supplier = "請先選擇一位供應商。";
    if (!priceForm.ProductId) errors.ProductId = "請搜尋並選擇商品。";
    if (!priceForm.PurchaseUnit.trim()) errors.PurchaseUnit = "請輸入採購單位。";
    if (!Number.isFinite(priceForm.UnitCost) || priceForm.UnitCost <= 0) errors.UnitCost = "採購單價需大於 0。";
    if (!priceForm.Currency.trim()) errors.PriceCurrency = "請輸入幣別。";
    if (!Number.isFinite(priceForm.MinimumOrderQuantity) || priceForm.MinimumOrderQuantity < 1) errors.MinimumOrderQuantity = "最低採購量需至少 1。";
    if (!Number.isFinite(priceForm.LeadTimeDays) || priceForm.LeadTimeDays < 0) errors.PriceLeadTimeDays = "交期天數不可小於 0。";
    if (priceForm.EffectiveFrom && priceForm.EffectiveTo && priceForm.EffectiveFrom > priceForm.EffectiveTo) errors.EffectiveTo = "生效迄日不可早於生效日起日。";
    return errors;
  }

  async function loadPrices(supplierId = editingId) {
    if (!supplierId) {
      setPrices([]);
      return;
    }
    setPriceLoading(true);
    try {
      const data = await listErpSupplierPrices(supplierId);
      setPrices(data.Items);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "供應商價格載入失敗。");
    } finally {
      setPriceLoading(false);
    }
  }

  async function searchPriceProducts() {
    if (priceKeyword.trim().length < 2) {
      setMessage("商品搜尋至少輸入 2 個字。");
      return;
    }
    setPriceLoading(true);
    setMessage("");
    try {
      const data = await searchErpProducts({ Keyword: priceKeyword.trim(), ErpSupplierId: editingId || undefined });
      setPriceResults(data.Items);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "商品搜尋失敗。");
    } finally {
      setPriceLoading(false);
    }
  }

  function selectPriceProduct(item: ErpProductSearchItem) {
    setPriceForm((current) => ({
      ...current,
      ProductId: item.ProductId,
      VariantId: item.VariantId,
      SupplierSku: item.SupplierPrice?.SupplierSku || current.SupplierSku || "",
      PurchaseUnit: item.SupplierPrice?.PurchaseUnit || current.PurchaseUnit || "台",
      UnitCost: item.SupplierPrice?.UnitCost || current.UnitCost || 0,
      Currency: item.SupplierPrice?.Currency || current.Currency || "TWD",
      MinimumOrderQuantity: item.SupplierPrice?.MinimumOrderQuantity || current.MinimumOrderQuantity || 1,
      LeadTimeDays: item.SupplierPrice?.LeadTimeDays || current.LeadTimeDays || 0,
    }));
    setPriceProductName(`${item.ProductName}${item.VariantLabel ? ` / ${item.VariantLabel}` : ""}`);
    setPriceResults([]);
    setPriceErrors((current) => ({ ...current, ProductId: "", UnitCost: "" }));
  }

  function editPrice(item: ErpSupplierProductPrice) {
    setPriceEditingId(item.ErpSupplierProductPriceId);
    setPriceProductName(`${item.ProductName}${item.VariantLabel ? ` / ${item.VariantLabel}` : ""}`);
    setPriceForm({
      ProductId: item.ProductId,
      VariantId: item.VariantId,
      SupplierSku: item.SupplierSku || "",
      PurchaseUnit: item.PurchaseUnit || "台",
      UnitCost: item.UnitCost,
      Currency: item.Currency || "TWD",
      MinimumOrderQuantity: item.MinimumOrderQuantity,
      LeadTimeDays: item.LeadTimeDays,
      IsDefault: item.IsDefault,
      EffectiveFrom: item.EffectiveFrom || "",
      EffectiveTo: item.EffectiveTo || "",
      IsActive: item.IsActive,
      Note: item.Note || "",
    });
    setPriceErrors({});
  }

  async function submitPrice(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validatePriceForm();
    if (Object.keys(validationErrors).length > 0) {
      applySubmitErrors(validationErrors, setPriceErrors, setMessage);
      return;
    }
    setPriceLoading(true);
    setMessage("");
    setSuccess("");
    setPriceErrors({});
    try {
      const result = priceEditingId ? await updateErpSupplierPrice(priceEditingId, priceForm) : await createErpSupplierPrice(editingId, priceForm);
      setPrices(result.Items);
      setSuccess(priceEditingId ? "供應商商品價格已更新。" : "供應商商品價格已建立。");
      setPriceEditingId("");
      setPriceForm(emptySupplierPriceForm);
      setPriceProductName("");
      setPriceKeyword("");
      setPriceResults([]);
    } catch (error) {
      const errors = apiErrors(error);
      if (Object.keys(errors).length > 0) applySubmitErrors(errors, setPriceErrors, setMessage);
      else setMessage(errorText(error, "供應商價格儲存失敗。"));
    } finally {
      setPriceLoading(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const validationErrors = validateSupplierForm();
    if (Object.keys(validationErrors).length > 0) {
      applySubmitErrors(validationErrors, setFormErrors, setMessage);
      return;
    }
    setLoading(true);
    setMessage("");
    setSuccess("");
    setFormErrors({});
    try {
      const result = editingId ? await updateErpSupplier(editingId, form) : await createErpSupplier(form);
      setSuccess(result.message || (editingId ? "供應商已更新。" : "供應商已建立。"));
      resetSupplierForm();
      await load();
      navigate("/erp/suppliers");
    } catch (error) {
      const errors = apiErrors(error);
      if (Object.keys(errors).length > 0) applySubmitErrors(errors, setFormErrors, setMessage);
      else setMessage(errorText(error, "供應商儲存失敗。"));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid gap-5">
      {isListMode ? <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(keyword); }}>
        <div className="grid gap-3 xl:grid-cols-[minmax(260px,1fr)_300px] xl:items-end">
          <TextField label="關鍵字" value={keyword} onChange={setKeyword} />
          <div className="rwd-filter-actions xl:col-span-2">
            <button className="inline-flex h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white" onClick={() => navigate("/erp/suppliers/new")} type="button">新增供應商</button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" type="submit"><Search size={18} />查詢</button>
            <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => { setKeyword(""); void load(""); }} type="button"><RefreshCw size={18} />重整</button>
          </div>
        </div>
      </form> : null}
      <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />
      {!isListMode ? <section className="order-1 rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-base font-extrabold text-[#203d32]">{editingId ? "編輯供應商主檔" : "新增供應商主檔"}</h2>
              {editingId ? <StatusBadge>編輯中</StatusBadge> : null}
            </div>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">{editingId ? "正在編輯既有供應商；下方可同步維護此供應商的商品價格。" : "先建立供應商主檔；儲存後回列表，再進入編輯頁維護商品價格。"}</p>
          </div>
          <div className="rwd-action-grid md:min-w-[280px]">
            <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/suppliers")} type="button">返回列表</button>
          </div>
        </div>
        {formOpen ? (
          <form className="grid gap-5 border-t border-[#edf0ee] p-4" onSubmit={submit}>
            <div className="grid gap-3 lg:grid-cols-3">
              <TextField error={formErrors.Code} fieldName="Code" label="供應商代碼" value={form.Code} onChange={(value) => { setForm((current) => ({ ...current, Code: value })); setFormErrors((current) => ({ ...current, Code: "" })); }} />
              <TextField error={formErrors.Name} fieldName="Name" label="供應商名稱" value={form.Name} onChange={(value) => { setForm((current) => ({ ...current, Name: value })); setFormErrors((current) => ({ ...current, Name: "" })); }} />
              <TextField label="簡稱" value={form.ShortName || ""} onChange={(value) => setForm((current) => ({ ...current, ShortName: value }))} />
              <label className="min-w-0">
                <span className="mb-2 block text-sm font-bold text-[#59665f]">供應商類型</span>
                <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.SupplierType || "Manufacturer"} onChange={(event) => setForm((current) => ({ ...current, SupplierType: event.target.value }))}>
                  <option value="Manufacturer">製造商 / 原廠</option>
                  <option value="Distributor">代理商 / 經銷</option>
                  <option value="Parts">維修零件</option>
                  <option value="Consumables">耗材</option>
                  <option value="Logistics">物流服務</option>
                  <option value="Service">外包服務</option>
                  <option value="Other">其他</option>
                </select>
              </label>
              <TextField label="國家 / 地區" value={form.Country || ""} onChange={(value) => setForm((current) => ({ ...current, Country: value }))} />
              <TextField label="網站" value={form.Website || ""} onChange={(value) => setForm((current) => ({ ...current, Website: value }))} />
            </div>

            <div className="rounded-md border border-[#e7ece8] p-4">
              <h3 className="text-sm font-extrabold text-[#203d32]">聯絡窗口</h3>
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <TextField label="聯絡人" value={form.ContactName || ""} onChange={(value) => setForm((current) => ({ ...current, ContactName: value }))} />
                <TextField label="職稱" value={form.ContactTitle || ""} onChange={(value) => setForm((current) => ({ ...current, ContactTitle: value }))} />
                <TextField label="電話" value={form.Phone || ""} onChange={(value) => setForm((current) => ({ ...current, Phone: value }))} />
                <TextField label="手機" value={form.Mobile || ""} onChange={(value) => setForm((current) => ({ ...current, Mobile: value }))} />
                <TextField label="傳真" value={form.Fax || ""} onChange={(value) => setForm((current) => ({ ...current, Fax: value }))} />
                <TextField label="LINE ID" value={form.LineId || ""} onChange={(value) => setForm((current) => ({ ...current, LineId: value }))} />
                <TextField error={formErrors.Email} fieldName="Email" label="主要 Email" value={form.Email || ""} onChange={(value) => { setForm((current) => ({ ...current, Email: value })); setFormErrors((current) => ({ ...current, Email: "" })); }} />
                <TextField error={formErrors.PurchaseEmail} fieldName="PurchaseEmail" label="採購 Email" value={form.PurchaseEmail || ""} onChange={(value) => { setForm((current) => ({ ...current, PurchaseEmail: value })); setFormErrors((current) => ({ ...current, PurchaseEmail: "" })); }} />
                <TextField error={formErrors.FinanceEmail} fieldName="FinanceEmail" label="財務 Email" value={form.FinanceEmail || ""} onChange={(value) => { setForm((current) => ({ ...current, FinanceEmail: value })); setFormErrors((current) => ({ ...current, FinanceEmail: "" })); }} />
                <div className="md:col-span-2 xl:col-span-3"><TextField label="地址" value={form.Address || ""} onChange={(value) => setForm((current) => ({ ...current, Address: value }))} /></div>
              </div>
            </div>

            <div className="rounded-md border border-[#e7ece8] p-4">
              <h3 className="text-sm font-extrabold text-[#203d32]">採購與財務條件</h3>
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <TextField label="付款條件" value={form.PaymentTerms || ""} onChange={(value) => setForm((current) => ({ ...current, PaymentTerms: value }))} />
                <NumberField error={formErrors.LeadTimeDays} fieldName="LeadTimeDays" label="預估交期天數" value={form.LeadTimeDays} onChange={(value) => { setForm((current) => ({ ...current, LeadTimeDays: value })); setFormErrors((current) => ({ ...current, LeadTimeDays: "" })); }} />
                <TextField error={formErrors.Currency} fieldName="Currency" label="幣別" value={form.Currency || "TWD"} onChange={(value) => { setForm((current) => ({ ...current, Currency: value.toUpperCase() })); setFormErrors((current) => ({ ...current, Currency: "" })); }} />
                <NumberField error={formErrors.MinimumOrderAmount} fieldName="MinimumOrderAmount" label="最低採購金額" value={form.MinimumOrderAmount || 0} onChange={(value) => { setForm((current) => ({ ...current, MinimumOrderAmount: value })); setFormErrors((current) => ({ ...current, MinimumOrderAmount: "" })); }} />
                <TextField label="出貨方式" value={form.ShippingMethod || ""} onChange={(value) => setForm((current) => ({ ...current, ShippingMethod: value }))} />
                <TextField label="交易條件" value={form.TradeTerms || ""} onChange={(value) => setForm((current) => ({ ...current, TradeTerms: value }))} />
                <TextField label="發票抬頭" value={form.InvoiceTitle || ""} onChange={(value) => setForm((current) => ({ ...current, InvoiceTitle: value }))} />
                <TextField label="統編 / 稅號" value={form.InvoiceTaxId || form.TaxNumber || ""} onChange={(value) => setForm((current) => ({ ...current, InvoiceTaxId: value, TaxNumber: value }))} />
                <TextField label="銀行名稱" value={form.BankName || ""} onChange={(value) => setForm((current) => ({ ...current, BankName: value }))} />
                <TextField label="戶名" value={form.BankAccountName || ""} onChange={(value) => setForm((current) => ({ ...current, BankAccountName: value }))} />
                <TextField label="帳號" value={form.BankAccountNumber || ""} onChange={(value) => setForm((current) => ({ ...current, BankAccountNumber: value }))} />
              </div>
            </div>

            <div className="rounded-md border border-[#e7ece8] p-4">
              <h3 className="text-sm font-extrabold text-[#203d32]">風險與備註</h3>
              <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                <NumberField error={formErrors.QualityRating} fieldName="QualityRating" label="供應商評分 1-5" value={form.QualityRating || 3} onChange={(value) => { setForm((current) => ({ ...current, QualityRating: value })); setFormErrors((current) => ({ ...current, QualityRating: "" })); }} />
                <label className="min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">風險等級</span>
                  <select className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.RiskLevel || "Normal"} onChange={(event) => setForm((current) => ({ ...current, RiskLevel: event.target.value }))}>
                    <option value="Low">低風險</option>
                    <option value="Normal">一般</option>
                    <option value="High">高風險</option>
                    <option value="Suspended">暫停往來</option>
                  </select>
                </label>
                <TextField label="標籤" value={form.Tags || ""} onChange={(value) => setForm((current) => ({ ...current, Tags: value }))} />
                <NumberField error={formErrors.SortOrder} fieldName="SortOrder" label="排序" value={form.SortOrder} onChange={(value) => { setForm((current) => ({ ...current, SortOrder: value })); setFormErrors((current) => ({ ...current, SortOrder: "" })); }} />
                <label className="md:col-span-2">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">保固政策</span>
                  <textarea className="min-h-[88px] w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.WarrantyPolicy || ""} onChange={(event) => setForm((current) => ({ ...current, WarrantyPolicy: event.target.value }))} />
                </label>
                <label className="md:col-span-2">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">退換貨政策</span>
                  <textarea className="min-h-[88px] w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.ReturnPolicy || ""} onChange={(event) => setForm((current) => ({ ...current, ReturnPolicy: event.target.value }))} />
                </label>
                <label className="md:col-span-2 xl:col-span-4">
                  <span className="mb-2 block text-sm font-bold text-[#59665f]">備註</span>
                  <textarea className="min-h-[96px] w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 py-2 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" value={form.Note || ""} onChange={(event) => setForm((current) => ({ ...current, Note: event.target.value }))} />
                </label>
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-[#edf0ee] pt-4 md:flex-row md:items-center md:justify-between">
              <div className="md:min-w-[220px]">
                <ErpSwitchCard checked={form.IsActive} label="啟用供應商" onChange={(checked) => setForm((current) => ({ ...current, IsActive: checked }))} />
              </div>
              <div className="rwd-action-grid md:min-w-[320px]">
                <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => navigate("/erp/suppliers")} type="button">取消</button>
                <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} type="submit">{editingId ? "儲存修改" : "建立供應商"}</button>
              </div>
            </div>
          </form>
        ) : null}
      </section> : null}

      {isListMode ? <section className="order-3 rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <h2 className="text-base font-extrabold text-[#203d32]">供應商列表</h2>
          <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(items.length)}</span>
        </div>
        <div className="erp-table-scroll border-t border-[#edf0ee]">
          {items.length === 0 ? <EmptyState text="目前沒有符合條件的供應商。" /> : (
            <table className="w-full min-w-[1280px] table-fixed text-left text-sm">
              <thead className="sticky top-0 z-40 bg-[#f3f7f4] text-[#59665f]">
                <tr>
                  <th className="w-[150px] whitespace-nowrap px-4 py-3">代碼</th>
                  <th className="w-[280px] whitespace-nowrap px-4 py-3">供應商</th>
                  <th className="w-[250px] whitespace-nowrap px-4 py-3">聯絡窗口</th>
                  <th className="w-[270px] whitespace-nowrap px-4 py-3">採購條件</th>
                  <th className="w-[200px] whitespace-nowrap px-4 py-3">風險 / 狀態</th>
                  <th className="erp-sticky-action-head sticky right-0 z-50 w-[130px] whitespace-nowrap bg-[#f3f7f4] px-4 py-3 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {items.map((item) => (
                  <tr className={editingId === item.ErpSupplierId ? "bg-[#f0faf4]" : undefined} key={item.ErpSupplierId}>
                    <td className="break-all px-4 py-4 font-extrabold text-[#203d32]">{item.Code}</td>
                    <td className="px-4 py-4">
                      <p className="break-words font-extrabold text-[#203d32]">{item.Name}</p>
                      <p className="mt-1 break-words text-[#7b877f]">{item.ShortName || item.SupplierTypeLabel || "-"}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="break-words font-bold text-[#203d32]">{item.ContactName || "-"}</p>
                      <p className="break-all text-[#7b877f]">{item.PurchaseEmail || item.Email || item.Phone || "-"}</p>
                    </td>
                    <td className="px-4 py-4">
                      <p className="break-words font-bold text-[#203d32]">{item.PaymentTerms || "未設定付款條件"}</p>
                      <p className="mt-1 text-[#7b877f]">交期 {item.LeadTimeDays} 天 · {item.Currency || "TWD"} · MOQ 金額 {money(item.MinimumOrderAmount)}</p>
                    </td>
                    <td className="px-4 py-4">
                      <div className="flex flex-wrap gap-2">
                        <StatusBadge tone={item.RiskLevel === "High" || item.RiskLevel === "Suspended" ? "warning" : "default"}>{item.RiskLevelLabel || "一般"}</StatusBadge>
                        <StatusBadge tone={item.IsActive ? "default" : "warning"}>{item.IsActive ? "啟用" : "停用"}</StatusBadge>
                      </div>
                      <p className="mt-2 text-xs font-bold text-[#7b877f]">評分 {item.QualityRating || 3} / 5</p>
                    </td>
                    <td className="erp-sticky-action-cell sticky right-0 z-10 bg-white px-4 py-4 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]"><button className="inline-flex min-w-[88px] items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 py-2 font-bold text-[#315746]" onClick={() => navigate(`/erp/suppliers/${item.ErpSupplierId}/edit`)} type="button">編輯</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section> : null}

      {isEditMode && editingId && formOpen ? <section className="order-2 rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-extrabold text-[#203d32]">目前供應商商品價格</h2>
            <p className="mt-1 text-sm font-semibold leading-6 text-[#59665f]">正在維護「{form.Name || "此供應商"}」的料號、採購單位、成本與交期；建立採購單時會優先帶入這裡的資料。</p>
          </div>
          {editingId ? <StatusBadge>{priceLoading ? "處理中" : count(prices.length)}</StatusBadge> : <StatusBadge tone="warning">先選供應商</StatusBadge>}
        </div>

        <form className="mt-4 grid gap-4 rounded-md border border-[#e7ece8] bg-[#fbfcfb] p-4" onSubmit={submitPrice}>
          <div data-field="ProductId">
            <ErpProductSearchPanel
              description="請從統一商品搜尋結果選擇要維護價格的商品；若已建價格會自動帶入供應商料號、成本、MOQ 與交期。"
              disabled={!editingId}
              error={priceErrors.ProductId}
              keyword={priceKeyword}
              loading={priceLoading}
              onKeywordChange={setPriceKeyword}
              onSearch={() => void searchPriceProducts()}
              onSelect={selectPriceProduct}
              placeholder="品名、商品編號、SKU"
              results={priceResults}
              secondaryAction={<button className="inline-flex min-h-11 w-full items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => { setPriceEditingId(""); setPriceForm(emptySupplierPriceForm); setPriceProductName(""); setPriceKeyword(""); setPriceResults([]); }} type="button">清空表單</button>}
              selectedPredicate={(item) => priceForm.ProductId === item.ProductId && (priceForm.VariantId || null) === (item.VariantId || null)}
              selectedLabel="已選"
              selectLabel="選擇"
              title="商品關鍵字"
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-[minmax(240px,1.4fr)_140px_130px_120px_120px_120px]">
            <label className="min-w-0" data-field="ProductId">
              <span className="mb-2 block text-sm font-bold text-[#59665f]">已選商品</span>
              <input className={fieldClass(priceErrors, "ProductId", "h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#203d32]")} disabled value={priceProductName || "請先搜尋並選擇商品"} />
              <FieldError errors={priceErrors} name="ProductId" />
            </label>
            <TextField label="供應商料號" value={priceForm.SupplierSku || ""} onChange={(value) => setPriceForm((current) => ({ ...current, SupplierSku: value }))} />
            <TextField error={priceErrors.PurchaseUnit} fieldName="PurchaseUnit" label="採購單位" value={priceForm.PurchaseUnit} onChange={(value) => { setPriceForm((current) => ({ ...current, PurchaseUnit: value })); setPriceErrors((current) => ({ ...current, PurchaseUnit: "" })); }} />
            <NumberField error={priceErrors.UnitCost} fieldName="UnitCost" label="採購單價" value={priceForm.UnitCost} onChange={(value) => { setPriceForm((current) => ({ ...current, UnitCost: value })); setPriceErrors((current) => ({ ...current, UnitCost: "" })); }} />
            <NumberField error={priceErrors.MinimumOrderQuantity} fieldName="MinimumOrderQuantity" label="最低採購量" value={priceForm.MinimumOrderQuantity} onChange={(value) => { setPriceForm((current) => ({ ...current, MinimumOrderQuantity: value })); setPriceErrors((current) => ({ ...current, MinimumOrderQuantity: "" })); }} />
            <NumberField error={priceErrors.PriceLeadTimeDays} fieldName="PriceLeadTimeDays" label="交期天數" value={priceForm.LeadTimeDays} onChange={(value) => { setPriceForm((current) => ({ ...current, LeadTimeDays: value })); setPriceErrors((current) => ({ ...current, PriceLeadTimeDays: "" })); }} />
          </div>

          <div className="grid gap-3 lg:grid-cols-[120px_160px_160px_minmax(220px,1fr)]">
            <TextField error={priceErrors.PriceCurrency} fieldName="PriceCurrency" label="幣別" value={priceForm.Currency} onChange={(value) => { setPriceForm((current) => ({ ...current, Currency: value.toUpperCase() })); setPriceErrors((current) => ({ ...current, PriceCurrency: "" })); }} />
            <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">生效日起</span><input className="h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" type="date" value={priceForm.EffectiveFrom || ""} onChange={(event) => setPriceForm((current) => ({ ...current, EffectiveFrom: event.target.value }))} /></label>
            <label className="min-w-0" data-field="EffectiveTo"><span className="mb-2 block text-sm font-bold text-[#59665f]">生效迄</span><input className={fieldClass(priceErrors, "EffectiveTo", "h-11 w-full min-w-0 rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15")} type="date" value={priceForm.EffectiveTo || ""} onChange={(event) => { setPriceForm((current) => ({ ...current, EffectiveTo: event.target.value })); setPriceErrors((current) => ({ ...current, EffectiveTo: "" })); }} /><FieldError errors={priceErrors} name="EffectiveTo" /></label>
            <TextField label="價格備註" value={priceForm.Note || ""} onChange={(value) => setPriceForm((current) => ({ ...current, Note: value }))} />
          </div>

          <div className="grid gap-3 md:grid-cols-[1fr_1fr_220px] md:items-center">
            <ErpSwitchCard checked={priceForm.IsDefault} label="設為預設價格" onChange={(checked) => setPriceForm((current) => ({ ...current, IsDefault: checked }))} />
            <ErpSwitchCard checked={priceForm.IsActive} label="啟用" onChange={(checked) => setPriceForm((current) => ({ ...current, IsActive: checked }))} />
            <button className="inline-flex min-h-11 w-full items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={!editingId || !priceForm.ProductId || priceLoading} type="submit">{priceEditingId ? "更新價格" : "新增價格"}</button>
          </div>
        </form>

        <div className="mt-4 overflow-x-auto">
          {prices.length === 0 ? <EmptyState text={editingId ? "尚未建立供應商商品價格。" : "請先從上方供應商列表選擇一位供應商。"} /> : (
            <table className="w-full min-w-[1080px] table-fixed text-left text-sm">
              <thead className="bg-[#f3f7f4] text-[#59665f]">
                <tr>
                  <th className="w-[260px] px-4 py-3">商品</th>
                  <th className="w-[140px] px-4 py-3">SKU / 料號</th>
                  <th className="w-[120px] px-4 py-3">採購單價</th>
                  <th className="w-[100px] px-4 py-3">單位</th>
                  <th className="w-[110px] px-4 py-3">MOQ</th>
                  <th className="w-[110px] px-4 py-3">交期</th>
                  <th className="w-[140px] px-4 py-3">狀態</th>
                  <th className="w-[110px] px-4 py-3 text-right">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {prices.map((item) => (
                  <tr key={item.ErpSupplierProductPriceId}>
                    <td className="px-4 py-4"><p className="break-words font-extrabold text-[#203d32]">{item.ProductName}</p><p className="break-words text-[#7b877f]">{item.VariantLabel || "-"}</p></td>
                    <td className="break-all px-4 py-4"><p>{item.Sku || "-"}</p><p className="text-[#7b877f]">{item.SupplierSku || "-"}</p></td>
                    <td className="px-4 py-4 font-extrabold text-[#203d32]">{item.Currency} {Number(item.UnitCost).toLocaleString("zh-TW")}</td>
                    <td className="px-4 py-4">{item.PurchaseUnit}</td>
                    <td className="px-4 py-4">{item.MinimumOrderQuantity}</td>
                    <td className="px-4 py-4">{item.LeadTimeDays} 天</td>
                    <td className="px-4 py-4"><div className="flex flex-wrap gap-2"><StatusBadge tone={item.IsActive ? "default" : "warning"}>{item.IsActive ? "啟用" : "停用"}</StatusBadge>{item.IsDefault ? <StatusBadge>預設</StatusBadge> : null}</div></td>
                    <td className="px-4 py-4 text-right"><button className="inline-flex min-w-[88px] items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 py-2 font-bold text-[#315746]" onClick={() => editPrice(item)} type="button">編輯</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section> : null}
    </div>
  );
}

function AttachmentsView() {
  const year = new Date().getFullYear();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formSectionRef = useRef<HTMLElement | null>(null);
  const initialRelatedType = searchParams.get("RelatedType") || "";
  const initialRelatedId = searchParams.get("RelatedId") || "";
  const initialDocumentCategory = searchParams.get("DocumentCategory") || "";
  const [keyword, setKeyword] = useState("");
  const [relatedType, setRelatedType] = useState(initialRelatedType);
  const [attachmentType, setAttachmentType] = useState("");
  const [documentCategory, setDocumentCategory] = useState(initialDocumentCategory);
  const [confidentialOnly, setConfidentialOnly] = useState(false);
  const [includeDisabled, setIncludeDisabled] = useState(false);
  const [dateFrom, setDateFrom] = useState(`${year}-01-01`);
  const [dateTo, setDateTo] = useState(`${year}-12-31`);
  const [items, setItems] = useState<ErpAttachment[]>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [relatedOptions, setRelatedOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [typeOptions, setTypeOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [canViewConfidential, setCanViewConfidential] = useState(true);
  const [form, setForm] = useState({ RelatedType: initialRelatedType || "General", RelatedId: initialRelatedId, AttachmentType: initialDocumentCategory === "Shipment" ? "DeliveryProof" : "Other", DocumentCategory: initialDocumentCategory || "General", ParentAttachmentId: "", VersionNo: "1", IsConfidential: false, FileName: "", FileUrl: "", Note: "" });
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [operationProgress, setOperationProgress] = useState<OperationProgressState | null>(null);
  const [disableAction, setDisableAction] = useState<{ item: ErpAttachment; reason: string; error: string } | null>(null);

  async function load(page = 1) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpAttachments({ Keyword: keyword.trim(), RelatedType: relatedType, AttachmentType: attachmentType, DocumentCategory: documentCategory, ConfidentialOnly: confidentialOnly, IncludeDisabled: includeDisabled, DateFrom: dateFrom, DateTo: dateTo, Page: page, PerPage: 20 });
      setItems(data.Items);
      setPagination(data.Pagination);
      setRelatedOptions(data.RelatedTypeOptions || []);
      setTypeOptions(data.AttachmentTypeOptions || []);
      setCategoryOptions(data.DocumentCategoryOptions || []);
      setCanViewConfidential(data.CanViewConfidential !== false);
    } catch (error) {
      setMessage(errorText(error, "附件中心載入失敗。"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!selectedFile && form.FileUrl.trim() === "") {
      setMessage("請選擇要上傳的檔案，或填寫外部檔案連結。");
      return;
    }
    if (!selectedFile && form.FileName.trim() === "") {
      setMessage("請輸入附件名稱，方便後續查找與稽核。");
      return;
    }
    if (selectedFile && selectedFile.size > 20 * 1024 * 1024) {
      setMessage("附件檔案大小不可超過 20MB。");
      return;
    }
    setLoading(true);
    setMessage("");
    setOperationProgress(selectedFile ? { label: "附件上傳中", detail: selectedFile.name, percent: 0 } : { label: "附件紀錄建立中", percent: null });
    try {
      const payload = { ...form, VersionNo: Number(form.VersionNo || 1) };
      if (selectedFile) {
        await uploadErpAttachment({ ...payload, FileName: form.FileName || selectedFile.name }, selectedFile, (progress: UploadProgressInfo) => {
          setOperationProgress({ label: "附件上傳中", detail: `${selectedFile.name} / ${formatBytes(progress.loaded)} of ${formatBytes(progress.total)}`, percent: progress.percent });
        });
        setOperationProgress({ label: "附件已上傳，正在建立治理紀錄", detail: selectedFile.name, percent: null });
      } else {
        await createErpAttachment(payload);
      }
      setForm({ RelatedType: initialRelatedType || "General", RelatedId: initialRelatedId, AttachmentType: initialDocumentCategory === "Shipment" ? "DeliveryProof" : "Other", DocumentCategory: initialDocumentCategory || "General", ParentAttachmentId: "", VersionNo: "1", IsConfidential: false, FileName: "", FileUrl: "", Note: "" });
      setSelectedFile(null);
      setMessage(selectedFile ? "附件已上傳並建立紀錄。" : "附件紀錄已建立。");
      await load(1);
    } catch (error) {
      setMessage(errorText(error, selectedFile ? "附件上傳失敗。" : "附件紀錄建立失敗。"));
    } finally {
      setOperationProgress(null);
      setLoading(false);
    }
  }

  async function downloadAttachment(item: ErpAttachment) {
    setLoading(true);
    setMessage("");
    try {
      const data = await downloadErpAttachment(item.ErpAttachmentId);
      window.open(data.DownloadUrl, "_blank", "noopener,noreferrer");
      await load(pagination.Page);
    } catch (error) {
      setMessage(errorText(error, "附件下載失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function confirmDisableAttachment() {
    if (!disableAction) return;
    if (disableAction.reason.trim() === "") {
      setDisableAction((current) => current ? { ...current, error: "請輸入停用附件原因。" } : current);
      return;
    }
    setLoading(true);
    try {
      await disableErpAttachment(disableAction.item.ErpAttachmentId, { DisabledReason: disableAction.reason.trim() });
      setDisableAction(null);
      setMessage("附件已停用並保留稽核紀錄。");
      await load(pagination.Page);
    } catch (error) {
      setDisableAction((current) => current ? { ...current, error: errorText(error, "附件停用失敗。") } : current);
    } finally {
      setLoading(false);
    }
  }

  function downloadAttachmentNamingTemplate() {
    downloadCsvFile("erp-attachment-naming-template.csv", [
      ["關聯類型", "關聯單號", "文件分類", "附件類型", "建議檔名", "是否機密", "備註"],
      ["採購單", "PO-20260824-0001", "採購文件", "報價單", "PO-20260824-0001-報價單-v1.pdf", "否", "供應商報價與採購依據"],
      ["採購入庫驗收", "PR-20260824-0001", "驗收文件", "驗收照片", "PR-20260824-0001-驗收照片-v1.jpg", "否", "到貨點收與外箱照片"],
      ["採購入庫驗收", "PR-20260824-0001", "異常 / RMA", "異常證明", "PR-20260824-0001-異常證明-v1.pdf", "否", "瑕疵、短缺、退換貨證明"],
      ["出庫單", "OB-20260824-0001", "物流 / 簽收", "物流 / 簽收", "OB-20260824-0001-簽收單-v1.pdf", "否", "物流與門市簽收文件"],
      ["供應商請款", "AP-20260824-0001", "財務 / 對帳", "銀行對帳單", "AP-20260824-0001-付款證明-v1.pdf", "是", "付款、對帳、合約類建議機密"],
    ]);
  }

  function prepareNewAttachmentVersion(item: ErpAttachment) {
    const latestVersion = Math.max(item.VersionNo || 1, ...(item.Versions || []).map((version) => version.VersionNo || 1));
    const nextVersion = latestVersion + 1;
    const nextFileName = /[-_]?v\d+(\.[^.]+)?$/i.test(item.FileName)
      ? item.FileName.replace(/[-_]?v\d+(\.[^.]+)?$/i, `-v${nextVersion}$1`)
      : item.FileName.includes(".")
        ? item.FileName.replace(/(\.[^.]+)$/, `-v${nextVersion}$1`)
        : `${item.FileName}-v${nextVersion}`;
    setForm({
      RelatedType: item.RelatedType,
      RelatedId: item.RelatedId || "",
      AttachmentType: item.AttachmentType,
      DocumentCategory: item.DocumentCategory || "General",
      ParentAttachmentId: item.RootAttachmentId || item.ParentAttachmentId || item.ErpAttachmentId,
      VersionNo: String(nextVersion),
      IsConfidential: item.IsConfidential,
      FileName: nextFileName,
      FileUrl: "",
      Note: item.Note || "",
    });
    setSelectedFile(null);
    setMessage(`已帶入「${item.FileName}」的新版本資料，請上傳新版檔案。`);
    formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function filterSameRelatedAttachments(item: ErpAttachment) {
    if (!item.RelatedId) return;
    setRelatedType(item.RelatedType);
    setKeyword("");
    setAttachmentType("");
    setDocumentCategory("");
    setConfidentialOnly(false);
    setMessage("已套用同單據附件篩選，請按查詢。");
  }

  return (
    <div className="grid gap-5">
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <ErpTextActionDialog
        open={Boolean(disableAction)}
        title="停用附件"
        description={disableAction ? `附件「${disableAction.item.FileName}」停用後不會出現在一般附件列表，但會保留稽核紀錄。` : ""}
        label="停用原因"
        placeholder="例如：上傳錯誤、版本已失效、文件內容不正確"
        value={disableAction?.reason || ""}
        required
        error={disableAction?.error || ""}
        tone="danger"
        loading={loading}
        confirmText="確認停用"
        onClose={() => setDisableAction(null)}
        onChange={(value) => setDisableAction((current) => current ? { ...current, reason: value, error: "" } : current)}
        onConfirm={confirmDisableAttachment}
      />
      <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(240px,1fr)_170px_170px_170px_150px_150px] xl:items-end">
          <TextField label="關鍵字" placeholder="附件名稱、網址、備註" value={keyword} onChange={setKeyword} />
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">關聯類型</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={relatedType} onChange={(event) => setRelatedType(event.target.value)}><option value="">全部</option>{relatedOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">附件類型</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={attachmentType} onChange={(event) => setAttachmentType(event.target.value)}><option value="">全部</option>{typeOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">文件分類</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={documentCategory} onChange={(event) => setDocumentCategory(event.target.value)}><option value="">全部分類</option>{categoryOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
        </div>
        <div className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(220px,1fr)_minmax(220px,1fr)_auto_auto] lg:items-center">
          <ErpSwitchCard checked={confidentialOnly} label="只看機密文件" onChange={setConfidentialOnly} />
          <ErpSwitchCard checked={includeDisabled} label="包含停用附件" onChange={setIncludeDisabled} />
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" disabled={loading} type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" onClick={() => { setKeyword(""); setRelatedType(""); setAttachmentType(""); setDocumentCategory(""); setConfidentialOnly(false); setIncludeDisabled(false); setDateFrom(`${year}-01-01`); setDateTo(`${year}-12-31`); }} type="button"><RefreshCw size={18} />重整</button>
        </div>
      </form>

      <section ref={formSectionRef} className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <h2 className="text-base font-extrabold text-[#203d32]">新增附件</h2>
        <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">可直接上傳驗收照片、發票、對帳單、簽收文件，也可保留外部雲端連結；上傳後系統會自動建立附件紀錄。</p>
        {!canViewConfidential ? <div className="mt-3 rounded-md border border-[#f2d09b] bg-[#fff8e9] px-3 py-2 text-sm font-bold leading-6 text-[#8a5a13]">此帳號尚未開通「ERP 機密附件檢視下載」權限；機密附件不會出現在列表，也無法下載。</div> : null}
        <div className="mt-3 overflow-hidden rounded-md border border-[#dfe6e1] bg-[#fbfcfb]">
          <div className="grid divide-y divide-[#edf0ee] text-sm md:grid-cols-3 md:divide-x md:divide-y-0">
            <div className="min-w-0 p-3">
              <div className="flex items-center gap-2 text-[#203d32]">
                <ClipboardList size={17} />
                <p className="font-extrabold">命名規則</p>
              </div>
              <p className="mt-2 break-words font-bold leading-6 text-[#59665f]">建議使用單據號、文件類型與版本，方便搜尋與稽核。</p>
              <p className="mt-2 rounded-md bg-white px-2 py-1 text-xs font-extrabold text-[#315746]">例：PO-20260824-0001-發票-v2.pdf</p>
            </div>
            <div className="min-w-0 p-3">
              <div className="flex items-center gap-2 text-[#203d32]">
                <RotateCcw size={17} />
                <p className="font-extrabold">版本管理</p>
              </div>
              <p className="mt-2 break-words font-bold leading-6 text-[#59665f]">新版本填母附件 ID，舊版以停用保留紀錄，不直接刪除。</p>
              <p className="mt-2 rounded-md bg-white px-2 py-1 text-xs font-extrabold text-[#7b877f]">適用：發票修正版、驗收照片補件</p>
            </div>
            <div className="min-w-0 p-3">
              <div className="flex items-center gap-2 text-[#203d32]">
                <Lock size={17} />
                <p className="font-extrabold">機密文件</p>
              </div>
              <p className="mt-2 break-words font-bold leading-6 text-[#59665f]">付款、合約、供應商報價請勾選機密，下載會寫入稽核紀錄。</p>
              <p className="mt-2 rounded-md bg-white px-2 py-1 text-xs font-extrabold text-[#8a5a13]">僅授權人員可檢視下載</p>
            </div>
          </div>
        </div>
        <div className="mt-3 flex flex-wrap justify-end gap-2">
          <button className="inline-flex min-h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={downloadAttachmentNamingTemplate} type="button"><Download size={17} />下載命名範本</button>
        </div>
        <form className="mt-4 grid gap-3" onSubmit={submit}>
          <OperationProgress progress={operationProgress} />
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label><span className="mb-2 block text-sm font-bold text-[#59665f]">關聯類型</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={form.RelatedType} onChange={(event) => setForm((current) => ({ ...current, RelatedType: event.target.value }))}>{relatedOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
            <TextField label="關聯 ID" placeholder="例如採購單 ID，可留空" value={form.RelatedId} onChange={(value) => setForm((current) => ({ ...current, RelatedId: value }))} />
            <label><span className="mb-2 block text-sm font-bold text-[#59665f]">附件類型</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={form.AttachmentType} onChange={(event) => setForm((current) => ({ ...current, AttachmentType: event.target.value }))}>{typeOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
            <TextField label="附件名稱" value={form.FileName} onChange={(value) => setForm((current) => ({ ...current, FileName: value }))} />
          </div>
          <div className="grid gap-3 md:grid-cols-[minmax(180px,1fr)_140px_minmax(180px,1fr)] md:items-end">
            <label><span className="mb-2 block text-sm font-bold text-[#59665f]">文件分類</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={form.DocumentCategory} onChange={(event) => setForm((current) => ({ ...current, DocumentCategory: event.target.value }))}>{categoryOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
            <TextField label="母附件 ID" placeholder="新版本才填" value={form.ParentAttachmentId} onChange={(value) => setForm((current) => ({ ...current, ParentAttachmentId: value }))} />
            <label className="min-w-0">
              <span className="mb-2 block text-sm font-bold text-[#59665f]">版本</span>
              <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3 outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15" min="1" type="number" value={form.VersionNo} onChange={(event) => setForm((current) => ({ ...current, VersionNo: event.target.value }))} />
            </label>
            <ErpSwitchCard checked={form.IsConfidential} label="機密文件，僅授權人員可檢視" onChange={(checked) => setForm((current) => ({ ...current, IsConfidential: checked }))} />
          </div>
          <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
            <label className="min-w-0 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
              <span className="mb-2 block text-sm font-bold text-[#59665f]">上傳檔案</span>
              <input
                className="block w-full min-w-0 text-sm font-bold text-[#59665f] file:mr-3 file:min-h-10 file:rounded-md file:border-0 file:bg-[#315746] file:px-4 file:font-bold file:text-white"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.gif,.doc,.docx,.xls,.xlsx,.csv,.txt,.zip"
                onChange={(event) => {
                  const file = event.target.files?.[0] || null;
                  setSelectedFile(file);
                  if (file && form.FileName.trim() === "") setForm((current) => ({ ...current, FileName: file.name }));
                }}
              />
              <p className="mt-2 break-words text-xs font-bold leading-5 text-[#7b877f]">{selectedFile ? `${selectedFile.name} / ${(selectedFile.size / 1024 / 1024).toFixed(2)} MB` : "支援 PDF、圖片、Office、CSV、TXT、ZIP，單檔上限 20MB。"}</p>
            </label>
            <TextField label="外部檔案連結" placeholder="若檔案在雲端硬碟或外部系統，可貼上連結；直接上傳時可留空" value={form.FileUrl} onChange={(value) => setForm((current) => ({ ...current, FileUrl: value }))} />
          </div>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">附件備註</span><textarea className="min-h-[88px] w-full rounded-md border border-[#cfd8d2] px-3 py-2" value={form.Note} onChange={(event) => setForm((current) => ({ ...current, Note: event.target.value }))} /></label>
          <div className="rwd-action-grid justify-end">
            <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" disabled={loading} onClick={() => { setSelectedFile(null); setForm((current) => ({ ...current, FileName: "", FileUrl: "" })); }} type="button">清除檔案</button>
            <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" aria-busy={loading ? "true" : "false"} disabled={loading} type="submit">{loading ? selectedFile ? "上傳中" : "建立中" : selectedFile ? "上傳附件" : "建立附件紀錄"}</button>
          </div>
        </form>
      </section>

      <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4"><h2 className="text-base font-extrabold text-[#203d32]">附件列表</h2><span className="text-sm font-bold text-[#7b877f]">{count(pagination.Total)}</span></div>
        {items.length === 0 ? <div className="px-4 pb-4"><EmptyState text="目前沒有符合條件的附件紀錄。" /></div> : (
          <div className="erp-table-scroll border-t border-[#edf0ee]">
            <table className="w-full min-w-[1180px] table-fixed text-left text-sm">
              <thead className="bg-[#f3f7f4] text-[#59665f]"><tr><th className="w-[230px] px-4 py-3">附件</th><th className="w-[180px] px-4 py-3">分類 / 類型</th><th className="w-[160px] px-4 py-3">關聯</th><th className="px-4 py-3">連結 / 備註</th><th className="w-[150px] px-4 py-3">建立</th><th className="w-[170px] px-4 py-3">治理</th></tr></thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {items.map((item) => (
                  <tr key={item.ErpAttachmentId}>
                    <td className="break-words px-4 py-4 align-top font-extrabold text-[#203d32]">
                      {item.FileName}
                      <p className="mt-1 text-xs font-bold text-[#7b877f]">
                        附件 ID {item.ErpAttachmentId} / 第 {item.VersionNo || 1} 版{item.ParentAttachmentId ? ` / 母件 ${item.ParentAttachmentId}` : ""}
                      </p>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {item.IsConfidential ? <StatusBadge tone="warning">機密</StatusBadge> : null}
                        {item.DisabledAt ? <StatusBadge tone="danger">已停用</StatusBadge> : <StatusBadge>啟用</StatusBadge>}
                      </div>
                      {item.Versions && item.Versions.length > 1 ? (
                        <div className="mt-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-2 text-xs font-bold leading-5 text-[#59665f]">
                          <p className="text-[#203d32]">版本紀錄</p>
                          {item.Versions.slice(0, 4).map((version) => (
                            <p key={version.ErpAttachmentId} className="mt-1 break-words">
                              v{version.VersionNo} / #{version.ErpAttachmentId} / {version.IsActive ? "啟用" : "停用"} / 下載 {version.DownloadCount} 次
                            </p>
                          ))}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4 align-top">
                      <StatusBadge>{item.DocumentCategoryLabel}</StatusBadge>
                      <p className="mt-2 text-xs font-bold text-[#59665f]">{item.AttachmentTypeLabel}</p>
                    </td>
                    <td className="break-words px-4 py-4 align-top">
                      <p className="font-extrabold text-[#203d32]">{item.RelatedSummary?.Title || item.RelatedTypeLabel}</p>
                      {item.RelatedSummary?.Subtitle ? <p className="mt-1 text-xs font-bold text-[#7b877f]">{item.RelatedSummary.Subtitle}</p> : null}
                      <p className="text-xs font-bold text-[#7b877f]">ID {item.RelatedId || "-"}</p>
                      <div className="mt-2 grid gap-2">
                        {item.RelatedSummary?.Path ? (
                          <button className="inline-flex min-h-9 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 text-xs font-bold text-[#315746]" onClick={() => navigate(item.RelatedSummary?.Path || erpAttachmentRelatedPath(item))} type="button">前往單據</button>
                        ) : null}
                        {item.RelatedId ? (
                          <button className="inline-flex min-h-9 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 text-xs font-bold text-[#315746]" onClick={() => filterSameRelatedAttachments(item)} type="button">查同單據附件</button>
                        ) : null}
                      </div>
                    </td>
                    <td className="break-words px-4 py-4 align-top">
                      {item.FileUrl ? (
                        <button className="max-w-full break-all text-left font-bold text-[#315746] underline" onClick={() => downloadAttachment(item)} type="button">{item.FileUrl}</button>
                      ) : "-"}
                      <p className="mt-1 text-xs font-bold leading-5 text-[#7b877f]">{item.DisabledReason ? `停用原因：${item.DisabledReason}` : item.Note || ""}</p>
                      {item.AccessLogs && item.AccessLogs.length > 0 ? (
                        <div className="mt-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-2 text-xs font-bold leading-5 text-[#59665f]">
                          <p className="text-[#203d32]">最近操作</p>
                          {item.AccessLogs.slice(0, 3).map((log, index) => (
                            <p key={`${item.ErpAttachmentId}-${log.Action}-${log.CreatedAt}-${index}`} className="mt-1">
                              {log.ActionLabel} / {log.AdminName || "-"} / {log.CreatedAt}
                            </p>
                          ))}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-4 py-4 align-top">
                      {item.CreatedAt}
                      <p className="text-xs font-bold text-[#7b877f]">{item.UploadedByAdminName || "-"}</p>
                    </td>
                    <td className="px-4 py-4 align-top">
                      <div className="rwd-action-grid">
                        <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" disabled={loading || !item.FileUrl || Boolean(item.DisabledAt)} onClick={() => downloadAttachment(item)} type="button">下載</button>
                        <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" disabled={loading || Boolean(item.DisabledAt)} onClick={() => prepareNewAttachmentVersion(item)} type="button">建立新版</button>
                        <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#efc8c6] bg-white px-3 font-bold text-[#a6332f]" disabled={loading || Boolean(item.DisabledAt)} onClick={() => setDisableAction({ item, reason: "", error: "" })} type="button">停用</button>
                      </div>
                      <p className="mt-2 text-xs font-bold text-[#7b877f]">下載 {item.DownloadCount || 0} 次</p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
      </section>
    </div>
  );
}

function erpAttachmentRelatedPath(item: ErpAttachment) {
  if (!item.RelatedId) return "未綁定單據";
  const paths: Record<string, string> = {
    PurchaseOrder: `/erp/purchase-orders?Keyword=${item.RelatedId}`,
    PurchaseReceipt: `/erp/purchase-receipts/${item.RelatedId}`,
    OutboundOrder: `/erp/outbound-orders?Keyword=${item.RelatedId}`,
    Supplier: `/erp/suppliers/${item.RelatedId}/edit`,
    Payable: `/erp/payables?Keyword=${item.RelatedId}`,
  };
  return paths[item.RelatedType] || `關聯 ID ${item.RelatedId}`;
}

function PayablesView() {
  const year = new Date().getFullYear();
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [dateFrom, setDateFrom] = useState(`${year}-01-01`);
  const [dateTo, setDateTo] = useState(`${year}-12-31`);
  const [items, setItems] = useState<ErpSupplierPayable[]>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [methodOptions, setMethodOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [suppliers, setSuppliers] = useState<ErpSupplierOption[]>([]);
  const [purchases, setPurchases] = useState<Array<{ ErpPurchaseOrderId: string; PurchaseNumber: string; SupplierName: string | null; PurchaseTotal: number }>>([]);
  const [form, setForm] = useState({ ErpSupplierId: "", ErpPurchaseOrderId: "", SupplierName: "", InvoiceNumber: "", InvoiceDate: "", DueDate: "", TotalAmount: "", TaxAmount: "0", Note: "" });
  const [formSupplierKeyword, setFormSupplierKeyword] = useState("");
  const [paymentForms, setPaymentForms] = useState<Record<string, { Amount: string; PaymentMethod: string; PaymentDate: string; ReferenceNumber: string; Note: string }>>({});
  const [paymentBatches, setPaymentBatches] = useState<Array<{ ErpPaymentBatchId: string; BatchNumber: string; BatchDate: string; PaymentMethodLabel: string; TotalAmount: number; PaymentCount: number; ReconciliationStatus: string; ReconciliationStatusLabel: string; StatementNumber: string | null }>>([]);
  const [selectedPaymentIds, setSelectedPaymentIds] = useState<string[]>([]);
  const [batchForm, setBatchForm] = useState({ BatchDate: new Date().toISOString().slice(0, 10), PaymentMethod: "BankTransfer", BankAccountName: "", BankAccountLast5: "", Note: "" });
  const [reconcileAction, setReconcileAction] = useState<{ batchId: string; batchNumber: string; statementNumber: string; statementDate: string; note: string; error: string } | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [voidPaymentAction, setVoidPaymentAction] = useState<{ paymentId: string; reason: string; error: string } | null>(null);

  async function load(page = 1) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpPayables({ Keyword: keyword.trim(), Status: status, ErpSupplierId: supplierId, DateFrom: dateFrom, DateTo: dateTo, Page: page, PerPage: 20 });
      setItems(data.Items);
      setCounts(data.Counts || {});
      setPagination(data.Pagination);
      setStatusOptions(data.StatusOptions || []);
      setMethodOptions(data.PaymentMethodOptions || []);
      setSuppliers(data.SupplierOptions || []);
      setPurchases(data.PurchaseOptions || []);
      const batches = await listErpPaymentBatches({ DateFrom: dateFrom, DateTo: dateTo, Page: 1, PerPage: 8 });
      setPaymentBatches(batches.Items || []);
    } catch (error) {
      setMessage(errorText(error, "請款付款資料載入失敗。"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const payableSupplierMatches = useMemo(() => {
    const term = formSupplierKeyword.trim().toLowerCase();
    const source = term
      ? suppliers.filter((supplier) => [supplier.Code, supplier.Name, supplier.PaymentTerms || "", supplier.Currency || ""].join(" ").toLowerCase().includes(term))
      : suppliers;
    return source.slice(0, 8);
  }, [formSupplierKeyword, suppliers]);

  function selectPayableSupplier(supplier: ErpSupplierOption | null) {
    if (!supplier) {
      setForm((current) => ({ ...current, ErpSupplierId: "" }));
      setFormSupplierKeyword("");
      return;
    }
    setForm((current) => ({ ...current, ErpSupplierId: supplier.ErpSupplierId, SupplierName: supplier.Name }));
    setFormSupplierKeyword(`${supplier.Code} ${supplier.Name}`);
  }

  async function submitPayable(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setMessage("");
    try {
      await createErpPayable({
        ErpSupplierId: form.ErpSupplierId || undefined,
        ErpPurchaseOrderId: form.ErpPurchaseOrderId || undefined,
        SupplierName: form.SupplierName || undefined,
        InvoiceNumber: form.InvoiceNumber,
        InvoiceDate: form.InvoiceDate,
        DueDate: form.DueDate,
        TotalAmount: Number(form.TotalAmount || 0),
        TaxAmount: Number(form.TaxAmount || 0),
        Note: form.Note,
      });
      setForm({ ErpSupplierId: "", ErpPurchaseOrderId: "", SupplierName: "", InvoiceNumber: "", InvoiceDate: "", DueDate: "", TotalAmount: "", TaxAmount: "0", Note: "" });
      setFormSupplierKeyword("");
      setMessage("供應商請款單已建立。");
      await load(1);
    } catch (error) {
      setMessage(errorText(error, "請款單建立失敗。"));
    } finally {
      setLoading(false);
    }
  }

  function setPaymentForm(id: string, patch: Partial<{ Amount: string; PaymentMethod: string; PaymentDate: string; ReferenceNumber: string; Note: string }>) {
    setPaymentForms((current) => ({ ...current, [id]: { Amount: "", PaymentMethod: "BankTransfer", PaymentDate: new Date().toISOString().slice(0, 10), ReferenceNumber: "", Note: "", ...(current[id] || {}), ...patch } }));
  }

  async function approve(id: string) {
    setLoading(true);
    setMessage("");
    try { await approveErpPayable(id); setMessage("請款單已核准。"); await load(pagination.Page); }
    catch (error) { setMessage(errorText(error, "核准請款單失敗。")); }
    finally { setLoading(false); }
  }

  async function pay(item: ErpSupplierPayable) {
    const payment = paymentForms[item.ErpSupplierPayableId] || { Amount: String(item.BalanceAmount), PaymentMethod: "BankTransfer", PaymentDate: new Date().toISOString().slice(0, 10), ReferenceNumber: "", Note: "" };
    setLoading(true);
    setMessage("");
    try {
      await createErpPayablePayment(item.ErpSupplierPayableId, { Amount: Number(payment.Amount || 0), PaymentMethod: payment.PaymentMethod, PaymentDate: payment.PaymentDate, ReferenceNumber: payment.ReferenceNumber, Note: payment.Note });
      setMessage("付款紀錄已建立。");
      await load(pagination.Page);
    } catch (error) { setMessage(errorText(error, "付款紀錄建立失敗。")); }
    finally { setLoading(false); }
  }

  function openVoidPayment(paymentId: string) {
    setVoidPaymentAction({ paymentId, reason: "", error: "" });
  }

  async function voidPayment() {
    if (!voidPaymentAction) return;
    const reason = voidPaymentAction.reason.trim();
    if (!reason) {
      setVoidPaymentAction((current) => current ? { ...current, error: "作廢付款需要填寫原因。" } : current);
      return;
    }
    setLoading(true);
    try { await voidErpPayablePayment(voidPaymentAction.paymentId, { VoidReason: reason }); setVoidPaymentAction(null); setMessage("付款紀錄已作廢。"); await load(pagination.Page); }
    catch (error) { setMessage(errorText(error, "作廢付款失敗。")); }
    finally { setLoading(false); }
  }

  const unreconciledPayments = useMemo(() => items.flatMap((item) => item.Payments
    .filter((payment) => payment.Status === "Paid" && !payment.ErpPaymentBatchId && payment.PaymentMethod === batchForm.PaymentMethod)
    .map((payment) => ({ ...payment, PayableNumber: item.PayableNumber, SupplierName: item.SupplierName }))), [items, batchForm.PaymentMethod]);

  async function createBatch() {
    if (selectedPaymentIds.length === 0) {
      setMessage("請先勾選要納入付款批次的付款紀錄。");
      return;
    }
    setLoading(true);
    setMessage("");
    try {
      await createErpPaymentBatch({ PaymentIds: selectedPaymentIds, BatchDate: batchForm.BatchDate, PaymentMethod: batchForm.PaymentMethod, BankAccountName: batchForm.BankAccountName, BankAccountLast5: batchForm.BankAccountLast5, Note: batchForm.Note });
      setSelectedPaymentIds([]);
      setMessage("付款批次已建立，可進行銀行對帳。");
      await load(pagination.Page);
    } catch (error) {
      setMessage(errorText(error, "付款批次建立失敗。"));
    } finally {
      setLoading(false);
    }
  }

  async function reconcileBatch() {
    if (!reconcileAction) return;
    if (reconcileAction.statementNumber.trim() === "") {
      setReconcileAction((current) => current ? { ...current, error: "請輸入銀行或付款對帳單號。" } : current);
      return;
    }
    setLoading(true);
    try {
      await reconcileErpPaymentBatch(reconcileAction.batchId, { StatementNumber: reconcileAction.statementNumber.trim(), StatementDate: reconcileAction.statementDate, Note: reconcileAction.note });
      setReconcileAction(null);
      setMessage("付款批次已完成對帳。");
      await load(pagination.Page);
    } catch (error) {
      setReconcileAction((current) => current ? { ...current, error: errorText(error, "付款批次對帳失敗。") } : current);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="grid gap-5">
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <ErpTextActionDialog
        open={Boolean(voidPaymentAction)}
        title="作廢付款紀錄"
        description="付款作廢會保留原付款流水與作廢原因，方便後續財務對帳與主管覆核。請確認這筆付款確實需要作廢。"
        label="作廢原因"
        placeholder="例如：付款帳號輸入錯誤、重複付款、銀行退匯。"
        value={voidPaymentAction?.reason || ""}
        required
        confirmText="確認作廢"
        tone="danger"
        loading={loading}
        error={voidPaymentAction?.error || ""}
        onChange={(value) => setVoidPaymentAction((current) => current ? { ...current, reason: value, error: "" } : current)}
        onClose={() => { if (!loading) setVoidPaymentAction(null); }}
        onConfirm={voidPayment}
      />
      <ErpTextActionDialog
        open={Boolean(reconcileAction)}
        title="完成付款批次對帳"
        description={reconcileAction ? `付款批次 ${reconcileAction.batchNumber} 完成銀行或付款平台對帳後，批次內付款會標記為已對帳。` : ""}
        label="銀行 / 付款平台對帳單號"
        placeholder="例如：BANK-202608-001、匯款批次單號、信用卡請款批號"
        value={reconcileAction?.statementNumber || ""}
        required
        error={reconcileAction?.error || ""}
        loading={loading}
        confirmText="完成對帳"
        onChange={(value) => setReconcileAction((current) => current ? { ...current, statementNumber: value, error: "" } : current)}
        onClose={() => setReconcileAction(null)}
        onConfirm={reconcileBatch}
      />
      <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(240px,1fr)_180px_190px_160px_160px] xl:items-end">
          <TextField label="關鍵字" placeholder="請款單、採購單、供應商、發票號碼" value={keyword} onChange={setKeyword} />
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">狀態</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={status} onChange={(event) => setStatus(event.target.value)}><option value="">全部</option>{statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">供應商</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={supplierId} onChange={(event) => setSupplierId(event.target.value)}><option value="">全部供應商</option>{suppliers.map((supplier) => <option key={supplier.ErpSupplierId} value={supplier.ErpSupplierId}>{supplier.Code} / {supplier.Name}</option>)}</select></label>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
        </div>
        <div className="rwd-filter-actions mt-3 justify-end"><button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white" disabled={loading} type="submit"><Search size={18} />查詢</button><button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" type="button" onClick={() => { setKeyword(""); setStatus(""); setSupplierId(""); setDateFrom(`${year}-01-01`); setDateTo(`${year}-12-31`); }}><RefreshCw size={18} />重整</button></div>
      </form>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-6">
        <MetricCard icon={<ClipboardList size={22} />} label="請款總數" value={count(counts.Total || 0)} />
        <MetricCard icon={<ShieldCheck size={22} />} label="待覆核" value={count(counts.PendingReview || 0)} tone={(counts.PendingReview || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<ClipboardList size={22} />} label="待付款" value={count(counts.Approved || 0)} />
        <MetricCard icon={<Package size={22} />} label="部分付款" value={count(counts.PartiallyPaid || 0)} tone={(counts.PartiallyPaid || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="逾期應付" value={count(counts.Overdue || 0)} tone={(counts.Overdue || 0) > 0 ? "danger" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="7日內到期" value={count(counts.DueSoon || 0)} tone={(counts.DueSoon || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<ClipboardList size={22} />} label="逾期 1-30 天" value={count(counts.Aging1To30 || 0)} tone={(counts.Aging1To30 || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<ClipboardList size={22} />} label="逾期 31-60 天" value={count(counts.Aging31To60 || 0)} tone={(counts.Aging31To60 || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="逾期 61-90 天" value={count(counts.Aging61To90 || 0)} tone={(counts.Aging61To90 || 0) > 0 ? "danger" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="逾期 90 天以上" value={count(counts.AgingOver90 || 0)} tone={(counts.AgingOver90 || 0) > 0 ? "danger" : "default"} />
        <MetricCard icon={<Package size={22} />} label="未付餘額" value={money(counts.BalanceAmount || 0)} tone={(counts.BalanceAmount || 0) > 0 ? "warning" : "default"} />
      </div>

      <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-extrabold text-[#203d32]">付款批次 / 銀行對帳</h2>
            <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">將已付款紀錄彙整成同一批次，再依銀行或付款平台對帳單完成對帳；已對帳付款不可直接作廢。</p>
          </div>
          <StatusBadge tone={unreconciledPayments.length > 0 ? "warning" : "default"}>{unreconciledPayments.length} 筆可建批次</StatusBadge>
        </div>
        <div className="mt-4 grid gap-2 md:grid-cols-3">
          {[
            ["1", "建立付款", "先在請款單下方建立付款紀錄，付款方式與金額會形成財務流水。"],
            ["2", "建立批次", "勾選尚未納入批次的付款，依付款方式彙整成同一批付款批次。"],
            ["3", "銀行對帳", "取得銀行或付款平台對帳單號後完成對帳；已對帳付款不可直接作廢。"],
          ].map(([step, title, description]) => (
            <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3" key={step}>
              <div className="flex items-start gap-3">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-[#e8f4ec] text-sm font-extrabold text-[#315746]">{step}</span>
                <span className="min-w-0">
                  <span className="block font-extrabold text-[#203d32]">{title}</span>
                  <span className="mt-1 block text-sm font-bold leading-6 text-[#7b877f]">{description}</span>
                </span>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1.1fr)_minmax(320px,.9fr)]">
          <div className="rounded-md border border-[#edf0ee] p-3">
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
              <label><span className="mb-2 block text-sm font-bold text-[#59665f]">付款方式</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={batchForm.PaymentMethod} onChange={(event) => { setBatchForm((current) => ({ ...current, PaymentMethod: event.target.value })); setSelectedPaymentIds([]); }}>{methodOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
              <label><span className="mb-2 block text-sm font-bold text-[#59665f]">批次日期</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={batchForm.BatchDate} onChange={(event) => setBatchForm((current) => ({ ...current, BatchDate: event.target.value }))} /></label>
              <TextField label="付款帳戶" placeholder="例如：公司台幣戶" value={batchForm.BankAccountName} onChange={(value) => setBatchForm((current) => ({ ...current, BankAccountName: value }))} />
              <TextField label="帳號末五碼" value={batchForm.BankAccountLast5} onChange={(value) => setBatchForm((current) => ({ ...current, BankAccountLast5: value }))} />
            </div>
            <div className="mt-3 max-h-[280px] overflow-auto rounded-md border border-[#edf0ee]">
              {unreconciledPayments.length === 0 ? <EmptyState text="目前沒有符合付款方式且尚未納入批次的付款紀錄。" /> : unreconciledPayments.map((payment) => (
                <div className="flex min-w-0 items-start gap-3 border-b border-[#edf0ee] p-3 last:border-b-0" key={payment.ErpSupplierPaymentId}>
                  <ErpCheckbox className="mt-1" ariaLabel={`選取付款 ${payment.PaymentNumber}`} checked={selectedPaymentIds.includes(payment.ErpSupplierPaymentId)} onChange={(checked) => setSelectedPaymentIds((current) => checked ? [...current, payment.ErpSupplierPaymentId] : current.filter((id) => id !== payment.ErpSupplierPaymentId))} />
                  <span className="min-w-0 flex-1">
                    <span className="block break-words font-extrabold text-[#203d32]">{payment.PaymentNumber} / {payment.PayableNumber}</span>
                    <span className="mt-1 block break-words text-sm font-bold text-[#7b877f]">{payment.SupplierName} / {payment.PaymentDate} / {payment.PaymentMethodLabel}</span>
                  </span>
                  <span className="shrink-0 font-extrabold text-[#203d32]">{money(payment.Amount)}</span>
                </div>
              ))}
            </div>
            <div className="rwd-action-grid mt-3 justify-end">
              <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white" disabled={loading || selectedPaymentIds.length === 0} onClick={createBatch} type="button">建立付款批次</button>
            </div>
          </div>
          <div className="rounded-md border border-[#edf0ee] p-3">
            <h3 className="font-extrabold text-[#203d32]">最近付款批次</h3>
            <div className="mt-3 grid gap-2">
              {paymentBatches.length === 0 ? <EmptyState text="目前沒有付款批次。" /> : paymentBatches.map((batch) => (
                <div className="rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3" key={batch.ErpPaymentBatchId}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="break-words font-extrabold text-[#203d32]">{batch.BatchNumber}</p>
                      <p className="mt-1 text-sm font-bold text-[#7b877f]">{batch.BatchDate} / {batch.PaymentMethodLabel} / {batch.PaymentCount} 筆</p>
                    </div>
                    <StatusBadge tone={batch.ReconciliationStatus === "Reconciled" ? "default" : "warning"}>{batch.ReconciliationStatusLabel}</StatusBadge>
                  </div>
                  <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    <p className="font-extrabold text-[#203d32]">{money(batch.TotalAmount)}</p>
                    {batch.ReconciliationStatus !== "Reconciled" ? <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746]" disabled={loading} onClick={() => setReconcileAction({ batchId: batch.ErpPaymentBatchId, batchNumber: batch.BatchNumber, statementNumber: batch.StatementNumber || "", statementDate: new Date().toISOString().slice(0, 10), note: "", error: "" })} type="button">對帳</button> : <span className="text-sm font-bold text-[#7b877f]">{batch.StatementNumber || "已對帳"}</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <h2 className="text-base font-extrabold text-[#203d32]">建立供應商請款</h2>
        <form className="mt-4 grid gap-3" onSubmit={submitPayable}>
          <div className="rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
            <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
              <div className="min-w-0">
                <h3 className="font-extrabold text-[#203d32]">供應商</h3>
                <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">可用代碼或名稱搜尋供應商主檔；若尚未建檔，仍可先手動輸入供應商名稱。</p>
              </div>
              <StatusBadge tone={form.ErpSupplierId ? "default" : "warning"}>{form.ErpSupplierId ? "已選主檔" : "手動模式"}</StatusBadge>
            </div>
            <div className="mt-3 grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(260px,.38fr)]">
              <label>
                <span className="mb-2 block text-sm font-bold text-[#59665f]">快速搜尋</span>
                <input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" placeholder="輸入供應商代碼、名稱、付款條件或幣別" value={formSupplierKeyword} onChange={(event) => setFormSupplierKeyword(event.target.value)} />
              </label>
              <label>
                <span className="mb-2 block text-sm font-bold text-[#59665f]">下拉選擇</span>
                <select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={form.ErpSupplierId} onChange={(event) => selectPayableSupplier(suppliers.find((item) => item.ErpSupplierId === event.target.value) || null)}>
                  <option value="">手動供應商</option>
                  {suppliers.map((supplier) => <option key={supplier.ErpSupplierId} value={supplier.ErpSupplierId}>{supplier.Code} / {supplier.Name}</option>)}
                </select>
              </label>
            </div>
            <div className="mt-3 max-h-[240px] overflow-auto rounded-md border border-[#edf0ee] bg-white">
              {payableSupplierMatches.length === 0 ? <EmptyState text="沒有符合條件的供應商，可先手動輸入名稱或到供應商主檔建立。" /> : payableSupplierMatches.map((supplier) => {
                const selected = form.ErpSupplierId === supplier.ErpSupplierId;
                return (
                  <button className={`grid w-full gap-2 border-b border-[#edf0ee] p-3 text-left last:border-b-0 md:grid-cols-[minmax(180px,1fr)_minmax(160px,.7fr)_minmax(110px,.35fr)_minmax(120px,.42fr)] ${selected ? "bg-[#eef8f1] ring-1 ring-[#8bcaa1]" : "bg-white hover:bg-[#fbfcfb]"}`} key={supplier.ErpSupplierId} onClick={() => selectPayableSupplier(supplier)} type="button">
                    <span className="min-w-0">
                      <span className="block break-words font-extrabold text-[#203d32]">{supplier.Name}</span>
                      <span className="mt-1 block break-all text-sm font-bold text-[#7b877f]">{supplier.Code}</span>
                    </span>
                    <span className="text-sm font-bold text-[#59665f]">
                      <span className="block text-xs text-[#7b877f]">付款條件</span>
                      {supplier.PaymentTerms || `交期 ${supplier.LeadTimeDays || 0} 天`}
                    </span>
                    <span className="text-sm font-bold text-[#59665f]">
                      <span className="block text-xs text-[#7b877f]">幣別</span>
                      {supplier.Currency || "TWD"}
                    </span>
                    <span className="text-sm font-bold text-[#59665f]">
                      <span className="block text-xs text-[#7b877f]">最低金額</span>
                      {money(supplier.MinimumOrderAmount || 0)}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
              <TextField label="供應商名稱" value={form.SupplierName} onChange={(value) => setForm((current) => ({ ...current, SupplierName: value }))} />
              <button className="inline-flex min-h-11 items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => selectPayableSupplier(null)} type="button">清除主檔</button>
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <label><span className="mb-2 block text-sm font-bold text-[#59665f]">採購單</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={form.ErpPurchaseOrderId} onChange={(event) => { const po = purchases.find((item) => item.ErpPurchaseOrderId === event.target.value); setForm((current) => ({ ...current, ErpPurchaseOrderId: event.target.value, SupplierName: po?.SupplierName || current.SupplierName, TotalAmount: po ? String(po.PurchaseTotal) : current.TotalAmount })); }}><option value="">不綁定採購單</option>{purchases.map((po) => <option key={po.ErpPurchaseOrderId} value={po.ErpPurchaseOrderId}>{po.PurchaseNumber} / {po.SupplierName || "-"} / {money(po.PurchaseTotal)}</option>)}</select></label>
            <TextField label="發票號碼" value={form.InvoiceNumber} onChange={(value) => setForm((current) => ({ ...current, InvoiceNumber: value }))} />
            <label><span className="mb-2 block text-sm font-bold text-[#59665f]">發票日期</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={form.InvoiceDate} onChange={(event) => setForm((current) => ({ ...current, InvoiceDate: event.target.value }))} /></label>
            <label><span className="mb-2 block text-sm font-bold text-[#59665f]">付款到期日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={form.DueDate} onChange={(event) => setForm((current) => ({ ...current, DueDate: event.target.value }))} /></label>
            <NumberField label="稅額" value={Number(form.TaxAmount || 0)} onChange={(value) => setForm((current) => ({ ...current, TaxAmount: String(value) }))} />
            <NumberField label="請款總額" value={Number(form.TotalAmount || 0)} onChange={(value) => setForm((current) => ({ ...current, TotalAmount: String(value) }))} />
          </div>
          <label><span className="mb-2 block text-sm font-bold text-[#59665f]">請款備註</span><textarea className="min-h-[88px] w-full rounded-md border border-[#cfd8d2] px-3 py-2" value={form.Note} onChange={(event) => setForm((current) => ({ ...current, Note: event.target.value }))} /></label>
          <div className="rwd-action-grid justify-end"><button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white" disabled={loading} type="submit">建立請款單</button></div>
        </form>
      </section>

      <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-col gap-3 px-4 py-4 md:flex-row md:items-center md:justify-between">
          <div className="min-w-0">
            <h2 className="text-base font-extrabold text-[#203d32]">請款付款列表</h2>
            <p className="mt-1 text-sm font-bold text-[#7b877f]">{count(pagination.Total)} / 未付餘額 {money(counts.BalanceAmount || 0)}</p>
          </div>
          <ErpActionBar className="md:min-w-[180px]">
            <ErpDocumentPrintMenu
              items={[{ label: "供應商對帳單", description: "列印查詢區間內的請款與付款摘要", disabled: items.length === 0, disabledReason: "目前沒有可列印資料", onClick: () => printPayableStatement(items, counts, dateFrom, dateTo) }]}
            />
          </ErpActionBar>
        </div>
        {items.length === 0 ? <div className="px-4 pb-4"><EmptyState text="目前沒有符合條件的請款單。" /></div> : (
          <div className="grid gap-3 px-4 pb-4">
            {items.map((item) => {
              const payment = paymentForms[item.ErpSupplierPayableId] || { Amount: String(item.BalanceAmount), PaymentMethod: "BankTransfer", PaymentDate: new Date().toISOString().slice(0, 10), ReferenceNumber: "", Note: "" };
              return <article className="rounded-md border border-[#dfe6e1] p-4" key={item.ErpSupplierPayableId}>
                <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between"><div><StatusBadge tone={item.Status === "PendingReview" ? "warning" : item.Status === "Paid" ? "default" : "warning"}>{item.StatusLabel}</StatusBadge><h3 className="mt-2 text-xl font-extrabold text-[#203d32]">{item.PayableNumber}</h3><p className="mt-1 break-words text-sm font-bold text-[#59665f]">{item.SupplierName} / {item.PurchaseNumber || "未綁定採購單"}</p></div><div className="grid gap-2 sm:grid-cols-3 xl:min-w-[520px]"><InfoBox label="請款總額" value={money(item.TotalAmount)} /><InfoBox label="已付" value={money(item.PaidAmount)} /><InfoBox label="未付" value={money(item.BalanceAmount)} /></div></div>
                <div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-5"><InfoBox label="發票號碼" value={item.InvoiceNumber || "-"} /><InfoBox label="發票日期" value={item.InvoiceDate || "-"} /><InfoBox label="付款到期日" value={item.DueDate || "-"} /><InfoBox label="帳齡" value={item.AgingBucketLabel || "未逾期"} /><InfoBox label="建立人" value={item.CreatedByAdminName || "-"} /></div>
                <div className="mt-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                  <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
                    <div className="min-w-0">
                      <p className="font-extrabold text-[#203d32]">對帳摘要</p>
                      <p className="mt-1 break-words text-sm font-semibold leading-6 text-[#59665f]">採購單：{item.PurchaseNumber || "未綁定"} / 驗收單：{item.ReceiptNumber || "未綁定"} / 發票：{item.InvoiceNumber || "未填"}</p>
                    </div>
                    <StatusBadge tone={item.AgingDays > 0 ? "danger" : item.BalanceAmount > 0 ? "warning" : "default"}>{item.AgingDays > 0 ? item.AgingBucketLabel : item.BalanceAmount > 0 ? "待付款" : "已結清"}</StatusBadge>
                  </div>
                </div>
                <div className="mt-3 rwd-action-grid">{item.Status === "PendingReview" ? <button className="inline-flex min-h-11 items-center justify-center rounded-md bg-[#315746] px-5 font-bold text-white" disabled={loading} onClick={() => approve(item.ErpSupplierPayableId)} type="button">核准請款</button> : null}</div>
                {["Approved", "PartiallyPaid"].includes(item.Status) ? <div className="mt-4 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3"><h4 className="font-extrabold text-[#203d32]">新增付款 / 付款批次</h4><div className="mt-3 grid gap-3 md:grid-cols-2 xl:grid-cols-5"><NumberField label="付款金額" value={Number(payment.Amount || 0)} onChange={(value) => setPaymentForm(item.ErpSupplierPayableId, { Amount: String(value) })} /><label><span className="mb-2 block text-sm font-bold text-[#59665f]">付款方式</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={payment.PaymentMethod} onChange={(event) => setPaymentForm(item.ErpSupplierPayableId, { PaymentMethod: event.target.value })}>{methodOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label><label><span className="mb-2 block text-sm font-bold text-[#59665f]">付款日期</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={payment.PaymentDate} onChange={(event) => setPaymentForm(item.ErpSupplierPayableId, { PaymentDate: event.target.value })} /></label><TextField label="付款批次 / 交易編號" value={payment.ReferenceNumber} onChange={(value) => setPaymentForm(item.ErpSupplierPayableId, { ReferenceNumber: value })} /><button className="inline-flex min-h-11 items-center justify-center self-end rounded-md bg-[#315746] px-5 font-bold text-white" disabled={loading} onClick={() => pay(item)} type="button">建立付款</button></div></div> : null}
                {item.Payments.length ? <div className="mt-4 overflow-x-auto"><table className="w-full min-w-[820px] table-fixed text-sm"><thead className="bg-[#f3f7f4] text-[#59665f]"><tr><th className="px-3 py-2 text-left">付款單</th><th className="px-3 py-2 text-left">日期 / 方式</th><th className="px-3 py-2 text-right">金額</th><th className="px-3 py-2 text-left">狀態 / 對帳</th><th className="px-3 py-2 text-right">操作</th></tr></thead><tbody className="divide-y divide-[#edf0ee]">{item.Payments.map((payRow) => <tr key={payRow.ErpSupplierPaymentId}><td className="break-words px-3 py-3 font-bold">{payRow.PaymentNumber}<p className="text-xs text-[#7b877f]">{payRow.ReferenceNumber || "-"}</p></td><td className="px-3 py-3">{payRow.PaymentDate}<p className="text-xs text-[#7b877f]">{payRow.PaymentMethodLabel}</p></td><td className="px-3 py-3 text-right font-bold">{money(payRow.Amount)}</td><td className="px-3 py-3"><div className="grid gap-1"><StatusBadge tone={payRow.Status === "Void" ? "danger" : "default"}>{payRow.Status === "Void" ? "已作廢" : "有效"}</StatusBadge><span className="text-xs font-bold text-[#7b877f]">{payRow.BatchNumber || "未進批次"} / {payRow.ReconciliationStatusLabel}</span></div></td><td className="px-3 py-3 text-right">{payRow.Status !== "Void" && payRow.ReconciliationStatus !== "Reconciled" ? <button className="inline-flex min-h-10 items-center justify-center rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#a33a2f]" onClick={() => openVoidPayment(payRow.ErpSupplierPaymentId)} type="button">作廢</button> : "-"}</td></tr>)}</tbody></table></div> : null}
              </article>;
            })}
          </div>
        )}
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
      </section>
    </div>
  );
}

function QualityInspectionsView() {
  const navigate = useNavigate();
  const year = new Date().getFullYear();
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [warehouseId, setWarehouseId] = useState("");
  const [locationId, setLocationId] = useState("");
  const [dateFrom, setDateFrom] = useState(`${year}-01-01`);
  const [dateTo, setDateTo] = useState(`${year}-12-31`);
  const [items, setItems] = useState<ErpQualityInspection[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [pagination, setPagination] = useState(emptyPagination);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [warehouses, setWarehouses] = useState<AdminErpWarehouse[]>([]);
  const [locations, setLocations] = useState<ErpLocationOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load(page = 1) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpQualityInspections({ Keyword: keyword.trim(), InspectionStatus: status, ErpWarehouseId: warehouseId, LocationId: locationId, DateFrom: dateFrom, DateTo: dateTo, Page: page, PerPage: 20 });
      setItems(data.Inspections);
      setCounts(data.Counts || {});
      setPagination(data.Pagination);
      setStatusOptions(data.InspectionStatusOptions || []);
      setWarehouses(data.Warehouses || []);
      setLocations(data.LocationOptions || []);
    } catch (error) {
      setMessage(errorText(error, "工程檢查資料載入失敗。"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reset() {
    setKeyword("");
    setStatus("");
    setWarehouseId("");
    setLocationId("");
    setDateFrom(`${year}-01-01`);
    setDateTo(`${year}-12-31`);
    void listErpQualityInspections({ DateFrom: `${year}-01-01`, DateTo: `${year}-12-31`, Page: 1, PerPage: 20 }).then((data) => {
      setItems(data.Inspections);
      setCounts(data.Counts || {});
      setPagination(data.Pagination);
      setStatusOptions(data.InspectionStatusOptions || []);
      setWarehouses(data.Warehouses || []);
      setLocations(data.LocationOptions || []);
    }).catch((error) => setMessage(errorText(error, "工程檢查資料載入失敗。")));
  }

  return (
    <div className="grid gap-5">
      <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(1); }}>
        <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-[minmax(260px,1fr)_180px_180px_180px_160px_160px] 2xl:items-end">
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">關鍵字</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" placeholder="出庫單、門市訂單、客戶、商品、序號" value={keyword} onChange={(event) => setKeyword(event.target.value)} /></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">QC 狀態</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={status} onChange={(event) => setStatus(event.target.value)}><option value="">全部</option>{statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">出庫倉</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}><option value="">全部倉庫</option>{warehouses.map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">目的門市</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={locationId} onChange={(event) => setLocationId(event.target.value)}><option value="">全部門市</option>{locations.map((location) => <option key={location.LocationId} value={location.LocationId}>{location.Name}</option>)}</select></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
        </div>
        <div className="rwd-filter-actions mt-3 justify-end">
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white disabled:opacity-60" disabled={loading} type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex min-h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746]" disabled={loading} onClick={reset} type="button"><RefreshCw size={18} />重整</button>
        </div>
      </form>
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard icon={<ShieldCheck size={22} />} label="全部檢查" value={count(counts.Total)} />
        <MetricCard icon={<ShieldCheck size={22} />} label="待檢查" value={count(counts.Pending)} tone={(counts.Pending || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<ShieldCheck size={22} />} label="檢查通過" value={count(counts.Passed)} />
        <MetricCard icon={<AlertTriangle size={22} />} label="檢查異常" value={count(counts.Abnormal)} tone={(counts.Abnormal || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="阻擋出庫" value={count(counts.Blocked)} tone={(counts.Blocked || 0) > 0 ? "danger" : "default"} />
      </div>
      <section className="rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
          <div><h2 className="text-base font-extrabold text-[#203d32]">工程檢查列表</h2><p className="mt-1 text-sm font-bold text-[#7b877f]">由出庫單建立時的工程檢查資料彙整，可快速追蹤異常與阻擋出庫。</p></div>
          <span className="text-sm font-bold text-[#7b877f]">{loading ? "載入中..." : count(pagination.Total)}</span>
        </div>
        {items.length === 0 && !loading ? <div className="px-4 pb-4"><EmptyState text="目前沒有符合條件的工程檢查資料。" /></div> : null}
        {items.length > 0 ? (
          <div className="erp-table-scroll border-t border-[#edf0ee]">
            <table className="w-full min-w-[1480px] table-fixed text-left text-sm">
              <thead className="sticky top-0 z-40 bg-[#f3f7f4] text-[#59665f]"><tr><th className="w-[150px] whitespace-nowrap px-4 py-3">QC 狀態</th><th className="w-[260px] whitespace-nowrap px-4 py-3">出庫單 / 門市訂單</th><th className="w-[250px] whitespace-nowrap px-4 py-3">倉庫 → 門市</th><th className="w-[190px] whitespace-nowrap px-4 py-3">客戶</th><th className="w-[170px] whitespace-nowrap px-4 py-3">商品數 / 數量</th><th className="w-[210px] whitespace-nowrap px-4 py-3">檢查時間</th><th className="w-[250px] whitespace-nowrap px-4 py-3">異常與處理</th><th className="erp-sticky-action-head sticky right-0 z-50 w-[150px] whitespace-nowrap bg-[#f3f7f4] px-4 py-3 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]">操作</th></tr></thead>
              <tbody className="divide-y divide-[#edf0ee]">
                {items.map((item) => (
                  <tr key={item.ErpOutboundOrderId}>
                    <td className="px-4 py-4"><StatusBadge tone={item.InspectionStatus === "Blocked" ? "danger" : item.InspectionStatus === "Abnormal" || item.InspectionStatus === "Pending" ? "warning" : "default"}>{item.InspectionStatusLabel}</StatusBadge></td>
                    <td className="break-words px-4 py-4"><p className="font-extrabold text-[#203d32]">{item.OutboundNumber}</p><p className="mt-1 text-[#7b877f]">{item.StoreOrderNumber}</p></td>
                    <td className="break-words px-4 py-4">{item.FromWarehouseName} → {item.ToLocationName}</td>
                    <td className="break-words px-4 py-4">{item.CustomerName}</td>
                    <td className="px-4 py-4">{item.ItemCount} 項 / {item.QuantityTotal} 件</td>
                    <td className="break-words px-4 py-4">{item.InspectedAt || item.CreatedAt}</td>
                    <td className="px-4 py-4"><p className="break-words font-bold text-[#203d32]">{item.AbnormalNote || "無異常紀錄"}</p>{item.ResolutionNote ? <p className="mt-1 break-words text-[#7b877f]">{item.ResolutionNote}</p> : null}</td>
                    <td className="erp-sticky-action-cell sticky right-0 z-10 bg-white px-4 py-4 text-right shadow-[-10px_0_18px_rgba(32,61,50,.08)]"><button className="inline-flex min-h-10 min-w-[96px] items-center justify-center rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746]" onClick={() => navigate(`/erp/outbound-orders?Keyword=${encodeURIComponent(item.OutboundNumber)}`)} type="button">查看出庫</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <Pagination disabled={loading} pagination={pagination} onChange={(page) => load(page)} />
      </section>
    </div>
  );
}

function ErpReportsView() {
  const year = new Date().getFullYear();
  const [dateFrom, setDateFrom] = useState(`${year}-01-01`);
  const [dateTo, setDateTo] = useState(`${year}-12-31`);
  const [warehouseId, setWarehouseId] = useState("");
  const [data, setData] = useState<ErpReportData | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load() {
    setLoading(true);
    setMessage("");
    try {
      setData(await getErpReports({ DateFrom: dateFrom, DateTo: dateTo, ErpWarehouseId: warehouseId }));
    } catch (error) {
      setMessage(errorText(error, "ERP 報表載入失敗。"));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleExport() {
    setMessage("");
    try {
      await exportErpReports({ DateFrom: dateFrom, DateTo: dateTo, ErpWarehouseId: warehouseId });
    } catch (error) {
      setMessage(errorText(error, "ERP 報表匯出失敗。"));
    }
  }

  const metrics = data?.Metrics;

  return (
    <div className="grid gap-5">
      <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={(event) => { event.preventDefault(); void load(); }}>
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[minmax(160px,1fr)_minmax(160px,1fr)_minmax(220px,1.2fr)_auto] xl:items-end">
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">起日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} /></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">迄日</span><input className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} /></label>
          <label className="min-w-0"><span className="mb-2 block text-sm font-bold text-[#59665f]">倉庫</span><select className="h-11 w-full rounded-md border border-[#cfd8d2] px-3" value={warehouseId} onChange={(event) => setWarehouseId(event.target.value)}><option value="">全部倉庫</option>{(data?.Warehouses || []).map((warehouse) => <option key={warehouse.ErpWarehouseId} value={warehouse.ErpWarehouseId}>{warehouse.Name}</option>)}</select></label>
          <div className="grid grid-cols-2 gap-2 md:col-span-2 xl:col-span-1 xl:flex xl:justify-end">
            <button className="inline-flex min-h-11 min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-[#315746] px-4 font-bold text-white disabled:opacity-60 xl:min-w-[112px]" disabled={loading} type="submit"><Search size={18} />查詢</button>
            <button className="inline-flex min-h-11 min-w-0 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] disabled:opacity-60 xl:min-w-[136px]" disabled={loading || !data} onClick={handleExport} type="button"><Download size={18} />匯出 Excel</button>
          </div>
        </div>
      </form>
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <MetricCard icon={<Package size={22} />} label="庫存品項" value={count(metrics?.InventoryItems)} />
        <MetricCard icon={<Warehouse size={22} />} label="可用庫存" value={count(metrics?.AvailableStock)} />
        <MetricCard icon={<AlertTriangle size={22} />} label="需補貨" value={count(metrics?.NeedsReorder)} tone={(metrics?.NeedsReorder || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<ClipboardList size={22} />} label="採購金額" value={money(metrics?.PurchaseTotal)} />
        <MetricCard icon={<AlertTriangle size={22} />} label="驗收異常" value={count(metrics?.ReceiptExceptions)} tone={(metrics?.ReceiptExceptions || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<Send size={22} />} label="出庫單數" value={count(metrics?.OutboundOrders)} />
        <MetricCard icon={<BarChart3 size={22} />} label="庫存異動" value={count(metrics?.StockMovements)} />
        <MetricCard icon={<Warehouse size={22} />} label="庫存金額" value={money(metrics?.StockValue)} />
        <MetricCard icon={<BarChart3 size={22} />} label="平均週轉率" value={`${metrics?.InventoryTurnoverRate ?? 0}`} />
        <MetricCard icon={<AlertTriangle size={22} />} label="滯銷 / 長庫齡" value={count(metrics?.SlowMovingItems)} tone={(metrics?.SlowMovingItems || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<ClipboardList size={22} />} label="交期達成率" value={`${metrics?.PurchaseLeadTimeRate ?? 0}%`} />
        <MetricCard icon={<AlertTriangle size={22} />} label="供應商異常率" value={`${metrics?.SupplierExceptionRate ?? 0}%`} tone={(metrics?.SupplierExceptionRate || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<AlertTriangle size={22} />} label="驗收不良率" value={`${metrics?.ReceiptDefectRate ?? 0}%`} tone={(metrics?.ReceiptDefectRate || 0) > 0 ? "warning" : "default"} />
        <MetricCard icon={<ClipboardList size={22} />} label="應付未付" value={money(metrics?.PayableBalance)} tone={(metrics?.PayableBalance || 0) > 0 ? "warning" : "default"} />
      </div>
      <div className="grid gap-5 2xl:grid-cols-2">
        <ReportTable title="補貨建議" empty="目前沒有需補貨品項。" rows={data?.ReorderItems || []} columns={["商品", "SKU", "倉庫", "可用", "補貨點", "建議採購"]} keys={["ProductName", "Sku", "WarehouseName", "AvailableStock", "ReorderPoint", "SuggestedPurchaseQuantity"]} />
        <ReportTable title="採購入庫驗收" empty="目前沒有驗收資料。" rows={data?.ReceiptRows || []} columns={["驗收單", "採購單", "供應商", "狀態", "異常"]} keys={["ReceiptNumber", "PurchaseNumber", "SupplierName", "InspectionStatusLabel", "RejectedQuantity"]} />
        <ReportTable title="出庫與工程檢查" empty="目前沒有出庫檢查資料。" rows={data?.QualityRows || []} columns={["出庫單", "門市訂單", "門市", "QC", "數量"]} keys={["OutboundNumber", "StoreOrderNumber", "ToLocationName", "InspectionStatusLabel", "QuantityTotal"]} />
        <ReportTable title="庫存異動" empty="目前沒有庫存異動。" rows={data?.MovementRows || []} columns={["時間", "類型", "商品", "倉庫", "數量"]} keys={["CreatedAt", "MovementTypeLabel", "ProductName", "WarehouseName", "Quantity"]} />
        <ReportTable title="庫存週轉率" empty="目前沒有週轉資料。" rows={data?.TurnoverRows || []} columns={["商品", "SKU", "倉庫", "期間出庫", "可用", "週轉率"]} keys={["ProductName", "Sku", "WarehouseName", "OutboundQuantity", "AvailableStock", "TurnoverRate"]} />
        <ReportTable title="滯銷 / 長庫齡" empty="目前沒有長庫齡品項。" rows={data?.SlowMovingRows || []} columns={["商品", "SKU", "倉庫", "數量", "庫齡", "金額"]} keys={["ProductName", "Sku", "WarehouseName", "Quantity", "AgeDays", "StockValue"]} />
        <ReportTable title="採購交期達成率" empty="目前沒有交期資料。" rows={data?.PurchaseLeadTimeRows || []} columns={["供應商", "採購單", "準時", "逾期", "達成率"]} keys={["SupplierName", "PurchaseOrders", "OnTimeCount", "LateCount", "OnTimeRateLabel"]} />
        <ReportTable title="供應商異常率" empty="目前沒有供應商異常資料。" rows={data?.SupplierExceptionRows || []} columns={["供應商", "驗收單", "異常", "異常率"]} keys={["SupplierName", "ReceiptCount", "ExceptionCount", "ExceptionRateLabel"]} />
        <ReportTable title="驗收不良率" empty="目前沒有驗收不良資料。" rows={data?.ReceiptDefectRows || []} columns={["商品", "SKU", "到貨", "不良", "不良率"]} keys={["ProductName", "Sku", "ArrivedQuantity", "RejectedQuantity", "DefectRateLabel"]} />
        <ReportTable title="庫存金額明細" empty="目前沒有庫存金額資料。" rows={data?.StockValueRows || []} columns={["商品", "SKU", "倉庫", "現貨", "平均成本", "金額"]} keys={["ProductName", "Sku", "WarehouseName", "InStock", "AverageCost", "StockValue"]} />
        <ReportTable title="應付帳齡分析" empty="目前沒有應付未付資料。" rows={data?.PayableAgingRows || []} columns={["供應商", "未付", "未到期", "1-30", "31-60", "90+"]} keys={["SupplierName", "BalanceAmount", "Current", "Days1To30", "Days31To60", "Over90"]} />
      </div>
    </div>
  );
}

function AuditLogsView() {
  const currentYear = new Date().getFullYear();
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("Keyword") || "");
  const [module, setModule] = useState(searchParams.get("Module") || "");
  const [action, setAction] = useState(searchParams.get("Action") || "");
  const [adminUserId, setAdminUserId] = useState(searchParams.get("AdminUserId") || "");
  const [dateFrom, setDateFrom] = useState(searchParams.get("DateFrom") || `${currentYear}-01-01`);
  const [dateTo, setDateTo] = useState(searchParams.get("DateTo") || `${currentYear}-12-31`);
  const [items, setItems] = useState<ErpAuditLog[]>([]);
  const [selected, setSelected] = useState<ErpAuditLog | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [moduleOptions, setModuleOptions] = useState<Array<{ Key: string; Label: string }>>([]);
  const [actionOptions, setActionOptions] = useState<Array<{ Key: string; Label: string }>>([]);
  const [adminUsers, setAdminUsers] = useState<Array<{ AdminUserId: string; Name: string }>>([]);
  const [pagination, setPagination] = useState(emptyPagination);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  async function load(page = 1) {
    setLoading(true);
    setMessage("");
    try {
      const data = await listErpAuditLogs({ Keyword: keyword, Module: module, Action: action, AdminUserId: adminUserId, DateFrom: dateFrom, DateTo: dateTo, Page: page, PerPage: pagination.PerPage || 20 });
      setItems(data.Items || []);
      setCounts(data.Counts || {});
      setPagination(data.Pagination || emptyPagination);
      setModuleOptions(data.ModuleOptions || []);
      setActionOptions(data.ActionOptions || []);
      setAdminUsers(data.AdminUsers || []);
      setSelected((current) => {
        if (current && data.Items.some((item) => item.ErpAuditLogId === current.ErpAuditLogId)) return current;
        return data.Items[0] || null;
      });
      const next = new URLSearchParams();
      if (keyword) next.set("Keyword", keyword);
      if (module) next.set("Module", module);
      if (action) next.set("Action", action);
      if (adminUserId) next.set("AdminUserId", adminUserId);
      if (dateFrom) next.set("DateFrom", dateFrom);
      if (dateTo) next.set("DateTo", dateTo);
      if (page > 1) next.set("Page", String(page));
      setSearchParams(next, { replace: true });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "操作稽核紀錄載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(Number(searchParams.get("Page") || "1"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function reset() {
    setKeyword("");
    setModule("");
    setAction("");
    setAdminUserId("");
    setDateFrom(`${currentYear}-01-01`);
    setDateTo(`${currentYear}-12-31`);
    setSearchParams({}, { replace: true });
    window.setTimeout(() => void load(1), 0);
  }

  function handleSearch(event: FormEvent) {
    event.preventDefault();
    void load(1);
  }

  return (
    <div className="grid gap-5">
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <form className="min-w-0 rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={handleSearch}>
        <div className="grid min-w-0 gap-3 md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-[minmax(280px,1.4fr)_minmax(150px,.7fr)_minmax(150px,.7fr)_minmax(150px,.7fr)_minmax(150px,.7fr)_minmax(150px,.7fr)]">
          <label className="grid min-w-0 gap-2 text-sm font-extrabold text-[#59665f]">
            關鍵字
            <input className="h-12 min-w-0 rounded-md border border-[#ccd8d1] px-4 text-base font-bold text-[#203d32] outline-none focus:border-[#315746]" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="單號、摘要、操作人、IP" />
          </label>
          <label className="grid min-w-0 gap-2 text-sm font-extrabold text-[#59665f]">
            模組
            <select className="h-12 min-w-0 rounded-md border border-[#ccd8d1] px-4 text-base font-bold text-[#203d32] outline-none focus:border-[#315746]" value={module} onChange={(event) => setModule(event.target.value)}>
              <option value="">全部模組</option>
              {moduleOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="grid min-w-0 gap-2 text-sm font-extrabold text-[#59665f]">
            動作
            <select className="h-12 min-w-0 rounded-md border border-[#ccd8d1] px-4 text-base font-bold text-[#203d32] outline-none focus:border-[#315746]" value={action} onChange={(event) => setAction(event.target.value)}>
              <option value="">全部動作</option>
              {actionOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}
            </select>
          </label>
          <label className="grid min-w-0 gap-2 text-sm font-extrabold text-[#59665f]">
            操作人
            <select className="h-12 min-w-0 rounded-md border border-[#ccd8d1] px-4 text-base font-bold text-[#203d32] outline-none focus:border-[#315746]" value={adminUserId} onChange={(event) => setAdminUserId(event.target.value)}>
              <option value="">全部人員</option>
              {adminUsers.map((option) => <option key={option.AdminUserId} value={option.AdminUserId}>{option.Name}</option>)}
            </select>
          </label>
          <label className="grid min-w-0 gap-2 text-sm font-extrabold text-[#59665f]">
            起日
            <input className="h-12 min-w-0 rounded-md border border-[#ccd8d1] px-4 text-base font-bold text-[#203d32] outline-none focus:border-[#315746]" type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} />
          </label>
          <label className="grid min-w-0 gap-2 text-sm font-extrabold text-[#59665f]">
            迄日
            <input className="h-12 min-w-0 rounded-md border border-[#ccd8d1] px-4 text-base font-bold text-[#203d32] outline-none focus:border-[#315746]" type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} />
          </label>
        </div>
        <div className="rwd-filter-actions mt-4 justify-end">
          <span className="hidden sm:block" />
          <button className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 text-base font-extrabold text-white disabled:opacity-60" disabled={loading} type="submit"><Search size={18} />查詢</button>
          <button className="inline-flex min-h-12 items-center justify-center gap-2 rounded-md border border-[#ccd8d1] bg-white px-5 text-base font-extrabold text-[#315746]" type="button" onClick={reset}><RefreshCw size={18} />重整</button>
        </div>
      </form>

      <div className="erp-metric-grid">
        <MetricCard icon={<ClipboardList size={22} />} label="全部紀錄" value={count(counts.Total)} />
        <MetricCard icon={<HistoryIcon />} label="今日操作" value={count(counts.Today)} />
        <MetricCard icon={<ClipboardList size={22} />} label="採購 / 驗收" value={count(counts.Purchase)} />
        <MetricCard icon={<Package size={22} />} label="庫存 / 出庫" value={count(counts.Inventory)} />
        <MetricCard icon={<AlertTriangle size={22} />} label="異常 / 作廢" value={count(counts.Exception)} tone={(counts.Exception || 0) > 0 ? "warning" : "default"} />
      </div>

      <div className="grid min-w-0 items-start gap-5 2xl:grid-cols-[minmax(0,1.35fr)_minmax(0,.65fr)]">
        <section className="min-w-0 overflow-hidden rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
            <h2 className="text-lg font-extrabold text-[#203d32]">稽核紀錄列表</h2>
            <span className="text-sm font-bold text-[#7b877f]">{count(pagination.Total)}</span>
          </div>
          {items.length === 0 ? <div className="px-4 pb-4"><EmptyState text="目前沒有符合條件的 ERP 操作紀錄。" /></div> : (
            <div className="erp-table-scroll border-y border-[#edf0ee]">
              <table className="w-full min-w-[920px] table-fixed text-left text-sm">
                <thead className="bg-[#f3f7f4] text-[#59665f]">
                  <tr>
                    <th className="w-[160px] px-4 py-3">時間</th>
                    <th className="w-[150px] px-4 py-3">模組 / 動作</th>
                    <th className="w-[180px] px-4 py-3">單據</th>
                    <th className="px-4 py-3">摘要</th>
                    <th className="w-[150px] px-4 py-3">操作人</th>
                    <th className="w-[130px] px-4 py-3">IP</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#edf0ee]">
                  {items.map((item) => (
                    <tr key={item.ErpAuditLogId} className={`cursor-pointer align-top hover:bg-[#f8fbf8] ${selected?.ErpAuditLogId === item.ErpAuditLogId ? "bg-[#edf7f0]" : ""}`} onClick={() => setSelected(item)}>
                      <td className="px-4 py-4 font-bold text-[#203d32]">{item.CreatedAt || "-"}</td>
                      <td className="space-y-2 px-4 py-4">
                        <StatusBadge tone={["Cancel", "Void", "Reject", "ResolveException"].includes(item.Action) ? "warning" : "default"}>{item.ModuleLabel}</StatusBadge>
                        <div className="font-extrabold text-[#203d32]">{item.ActionLabel}</div>
                      </td>
                      <td className="break-words px-4 py-4 font-extrabold text-[#203d32]">{item.EntityNumber || `${item.EntityType} #${item.EntityId || "-"}`}</td>
                      <td className="break-words px-4 py-4 font-bold text-[#4d5b54]">{item.Summary}</td>
                      <td className="break-words px-4 py-4 font-bold text-[#203d32]">{item.AdminDisplayName || item.AdminUsername || "-"}</td>
                      <td className="break-words px-4 py-4 font-bold text-[#59665f]">{item.IpAddress || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <Pagination disabled={loading} pagination={pagination} onChange={(page) => void load(page)} />
        </section>

        <section className="min-w-0 overflow-hidden rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
          <h2 className="text-lg font-extrabold text-[#203d32]">操作詳情</h2>
          {!selected ? <div className="mt-4"><EmptyState text="選擇一筆稽核紀錄查看完整內容。" /></div> : (
            <div className="mt-4 grid gap-4">
              <div className="grid gap-3 rounded-md bg-[#f8fbf8] p-4">
                <div><span className="text-sm font-bold text-[#7b877f]">摘要</span><p className="mt-1 break-words text-base font-extrabold text-[#203d32]">{selected.Summary}</p></div>
                <div className="grid gap-3 min-[1680px]:grid-cols-2">
                  <InfoBlock label="單據" value={selected.EntityNumber || `${selected.EntityType} #${selected.EntityId || "-"}`} />
                  <InfoBlock label="操作人" value={selected.AdminDisplayName || selected.AdminUsername || "-"} />
                  <InfoBlock label="操作時間" value={selected.CreatedAt || "-"} />
                  <InfoBlock label="IP" value={selected.IpAddress || "-"} />
                </div>
              </div>
              <AuditJsonBlock title="異動前" data={selected.BeforeData} />
              <AuditJsonBlock title="異動後" data={selected.AfterData} />
              <div>
                <p className="text-sm font-extrabold text-[#59665f]">瀏覽器 / 裝置</p>
                <p className="mt-2 max-h-24 overflow-auto break-all rounded-md border border-[#dfe6e1] bg-[#f9fbfa] p-3 text-sm font-bold text-[#59665f]">{selected.UserAgent || "-"}</p>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

function HistoryIcon() {
  return <RefreshCw size={22} />;
}

function InfoBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 rounded-md border border-[#dfe6e1] bg-white p-3">
      <div className="text-xs font-extrabold text-[#7b877f]">{label}</div>
      <div className="mt-1 break-words text-sm font-extrabold text-[#203d32]">{value}</div>
    </div>
  );
}

function AuditJsonBlock({ title, data }: { title: string; data: Record<string, unknown> | null }) {
  return (
    <div>
      <p className="text-sm font-extrabold text-[#59665f]">{title}</p>
      <pre className="mt-2 max-h-56 overflow-auto whitespace-pre-wrap break-all rounded-md border border-[#dfe6e1] bg-[#f9fbfa] p-3 text-xs font-bold leading-6 text-[#203d32]">
        {data ? JSON.stringify(data, null, 2) : "-"}
      </pre>
    </div>
  );
}

type ReportColumn = {
  label: string;
  keys: string[];
  kind?: "product";
};

function buildReportColumns(columns: string[], keys: string[]): ReportColumn[] {
  const result: ReportColumn[] = [];
  for (let index = 0; index < keys.length; index += 1) {
    if (keys[index] === "ProductName" && keys[index + 1] === "Sku") {
      result.push({
        label: "商品資訊",
        keys: ["ProductName", "Sku"],
        kind: "product",
      });
      index += 1;
      continue;
    }

    result.push({
      label: columns[index] || keys[index],
      keys: [keys[index]],
    });
  }
  return result;
}

function ReportTable({ title, empty, rows, columns, keys }: { title: string; empty: string; rows: Array<Record<string, unknown>>; columns: string[]; keys: string[] }) {
  const reportColumns = buildReportColumns(columns, keys);
  const productColumn = reportColumns.find((column) => column.kind === "product");
  const primaryColumn = productColumn || reportColumns[0];
  const secondaryColumn = productColumn ? null : reportColumns[1] || null;
  const detailColumns = reportColumns.filter((column) => column !== primaryColumn && column !== secondaryColumn);

  function cellText(value: unknown) {
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
  }

  function columnText(row: Record<string, unknown>, column: ReportColumn) {
    return cellText(row[column.keys[0]]);
  }

  return (
    <section className="flex h-[420px] min-w-0 flex-col overflow-hidden rounded-md border border-[#dfe6e1] bg-white shadow-[0_8px_22px_rgba(32,61,50,.04)]">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-4">
        <h2 className="text-base font-extrabold text-[#203d32]">{title}</h2>
        <span className="text-sm font-bold text-[#7b877f]">{count(rows.length)}</span>
      </div>
      {rows.length === 0 ? <div className="flex-1 px-4 pb-4"><EmptyState text={empty} /></div> : (
        <div className="min-h-0 flex-1 overflow-auto px-4 pb-4">
          <div className="grid gap-3">
            {rows.map((row, rowIndex) => {
              const productName = productColumn ? cellText(row.ProductName) : "";
              const sku = productColumn ? cellText(row.Sku) : "";
              const primaryText = productColumn ? productName : columnText(row, primaryColumn);
              const secondaryText = secondaryColumn ? columnText(row, secondaryColumn) : "";

              return (
                <article className="rounded-md border border-[#edf0ee] bg-white p-3 shadow-[0_4px_12px_rgba(32,61,50,.035)]" key={rowIndex}>
                  <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="break-words text-sm font-extrabold leading-6 text-[#203d32]">{primaryText}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {productColumn ? <span className="rounded-md bg-[#f3f7f4] px-2 py-1 text-xs font-bold text-[#59665f]">SKU {sku}</span> : null}
                        {secondaryColumn ? <span className="rounded-md bg-[#f3f7f4] px-2 py-1 text-xs font-bold text-[#59665f]">{secondaryColumn.label} {secondaryText}</span> : null}
                      </div>
                    </div>
                  </div>
                  {detailColumns.length > 0 ? (
                    <div className="mt-3 grid gap-2 sm:grid-cols-2">
                      {detailColumns.map((column) => (
                        <div className="min-w-0 rounded-md bg-[#f8fbf8] px-3 py-2" key={`${column.label}-${column.keys.join("-")}`}>
                          <p className="whitespace-nowrap text-xs font-extrabold text-[#7b877f]">{column.label}</p>
                          <p className="mt-1 break-words text-sm font-extrabold leading-5 text-[#203d32]">{columnText(row, column)}</p>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        </div>
      )}
    </section>
  );
}

function FilterPanel({ children, onSubmit }: { children: ReactNode; onSubmit: (event: FormEvent<HTMLFormElement>) => void }) {
  return (
    <form className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]" onSubmit={onSubmit}>
      <div className="grid min-w-0 gap-4 md:grid-cols-2 xl:grid-cols-4">{children}</div>
    </form>
  );
}

function Field({ children, label }: { children: ReactNode; label: string }) {
  return (
    <label className="grid min-w-0 gap-2 text-sm font-extrabold text-[#59665f]">
      <span>{label}</span>
      {children}
    </label>
  );
}

function AccountingPeriodsView() {
  const [items, setItems] = useState<ErpAccountingPeriod[]>([]);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({ PeriodCode: "", PeriodName: "", DateFrom: `${new Date().getFullYear()}-01-01`, DateTo: `${new Date().getFullYear()}-12-31`, LockInventory: true, LockPurchase: true, LockPayment: true, LockReason: "" });
  const [periodAction, setPeriodAction] = useState<{ type: "close" | "reopen" | "void"; item: ErpAccountingPeriod; note: string; error: string } | null>(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const data = await listErpAccountingPeriods({ Keyword: keyword, Status: status });
      setItems(data.Items || []);
      setCounts(data.Counts || {});
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "會計期間載入失敗。");
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    try {
      await createErpAccountingPeriod(form);
      setMessage("會計期間已建立。");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "建立失敗。");
    } finally {
      setLoading(false);
    }
  }

  async function action() {
    if (!periodAction) return;
    const note = periodAction.note.trim();
    if (periodAction.type === "void" && note === "") {
      setPeriodAction((current) => current ? { ...current, error: "請輸入作廢原因。" } : current);
      return;
    }
    setLoading(true);
    try {
      if (periodAction.type === "close") await closeErpAccountingPeriod(periodAction.item.ErpAccountingPeriodId, { LockInventory: true, LockPurchase: true, LockPayment: true, LockReason: note || "期間結帳鎖定" });
      else if (periodAction.type === "reopen") await reopenErpAccountingPeriod(periodAction.item.ErpAccountingPeriodId, { ReopenReason: note });
      else await voidErpAccountingPeriod(periodAction.item.ErpAccountingPeriodId, { VoidReason: note });
      setPeriodAction(null);
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "操作失敗。");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="erp-page-shell grid gap-5">
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <FilterPanel onSubmit={(event) => { event.preventDefault(); void load(); }}>
        <Field label="關鍵字"><input className="erp-input" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="期間代碼、名稱、原因" /></Field>
        <Field label="狀態"><select className="erp-input" value={status} onChange={(event) => setStatus(event.target.value)}><option value="">全部</option><option value="Open">開放中</option><option value="Closed">已鎖定</option><option value="Voided">已作廢</option></select></Field>
        <div className="erp-filter-actions"><button className="erp-primary-btn" disabled={loading} type="submit"><Search size={18} />查詢</button></div>
      </FilterPanel>
      <div className="erp-metric-grid"><MetricCard icon={<Lock size={20} />} label="期間總數" value={count(counts.Total)} /><MetricCard icon={<Lock size={20} />} label="開放中" value={count(counts.Open)} /><MetricCard icon={<Lock size={20} />} label="已鎖定" value={count(counts.Closed)} tone="warning" /><MetricCard icon={<AlertTriangle size={20} />} label="已作廢" value={count(counts.Voided)} tone={(counts.Voided || 0) > 0 ? "danger" : "default"} /></div>
      <ErpTextActionDialog
        open={Boolean(periodAction)}
        title={periodAction?.type === "void" ? "作廢會計期間" : periodAction?.type === "reopen" ? "重開會計期間" : "鎖定會計期間"}
        description={periodAction ? `${periodAction.type === "void" ? "作廢" : periodAction.type === "reopen" ? "重開" : "鎖定"}會計期間 ${periodAction.item.PeriodCode} / ${periodAction.item.PeriodName}。${periodAction.type === "void" ? "作廢後不會刪除資料，但不能再作為正式鎖定期間使用，請留下原因。" : periodAction.type === "reopen" ? "重開後此日期範圍可再次進行對應異動，建議留下主管核准或財務原因。" : "鎖定後符合範圍的庫存、採購、付款異動會被管制。"}` : ""}
        label={periodAction?.type === "void" ? "作廢原因" : periodAction?.type === "reopen" ? "重開原因" : "鎖定原因"}
        placeholder={periodAction?.type === "void" ? "例如：期間建立錯誤、日期範圍重複、測試資料。" : periodAction?.type === "reopen" ? "例如：主管核准補登 8 月供應商付款。" : "例如：月結完成，禁止回補異動。"}
        value={periodAction?.note || ""}
        required={periodAction?.type === "void"}
        confirmText={periodAction?.type === "void" ? "確認作廢" : periodAction?.type === "reopen" ? "確認重開" : "確認鎖定"}
        tone={periodAction?.type === "void" ? "danger" : "default"}
        loading={loading}
        error={periodAction?.error || ""}
        onChange={(value) => setPeriodAction((current) => current ? { ...current, note: value, error: "" } : current)}
        onClose={() => !loading && setPeriodAction(null)}
        onConfirm={() => void action()}
      />
      <section className="rounded-md border border-[#dfe6e1] bg-white p-5">
        <h2 className="text-lg font-extrabold text-[#203d32]">建立會計期間</h2>
        <form className="mt-4 grid gap-4 lg:grid-cols-4" onSubmit={submit}>
          <Field label="期間代碼"><input className="erp-input" value={form.PeriodCode} onChange={(event) => setForm((prev) => ({ ...prev, PeriodCode: event.target.value }))} placeholder="例如 2026-08" /></Field>
          <Field label="期間名稱"><input className="erp-input" value={form.PeriodName} onChange={(event) => setForm((prev) => ({ ...prev, PeriodName: event.target.value }))} placeholder="例如 2026 年 8 月" /></Field>
          <Field label="起日"><input className="erp-input" type="date" value={form.DateFrom} onChange={(event) => setForm((prev) => ({ ...prev, DateFrom: event.target.value }))} /></Field>
          <Field label="迄日"><input className="erp-input" type="date" value={form.DateTo} onChange={(event) => setForm((prev) => ({ ...prev, DateTo: event.target.value }))} /></Field>
          <ErpSwitchCard checked={form.LockInventory} label="鎖庫存" onChange={(checked) => setForm((prev) => ({ ...prev, LockInventory: checked }))} />
          <ErpSwitchCard checked={form.LockPurchase} label="鎖採購" onChange={(checked) => setForm((prev) => ({ ...prev, LockPurchase: checked }))} />
          <ErpSwitchCard checked={form.LockPayment} label="鎖付款" onChange={(checked) => setForm((prev) => ({ ...prev, LockPayment: checked }))} />
          <div className="flex items-end"><button className="erp-primary-btn w-full" disabled={loading} type="submit">建立期間</button></div>
          <Field label="鎖定原因"><textarea className="erp-input min-h-[92px] lg:col-span-4" value={form.LockReason} onChange={(event) => setForm((prev) => ({ ...prev, LockReason: event.target.value }))} placeholder="例如：月結完成，禁止回補異動。" /></Field>
        </form>
      </section>
      <SimpleResponsiveList title="會計期間列表" countText={count(items.length)} empty="目前沒有會計期間。">
        {items.map((item) => (
          <div className="rounded-md border border-[#dfe6e1] bg-white p-4" key={item.ErpAccountingPeriodId}>
            <div className="flex flex-wrap items-start justify-between gap-3"><div><p className="text-xl font-extrabold">{item.PeriodCode}</p><p className="font-bold text-[#59665f]">{item.PeriodName} / {item.DateFrom} ~ {item.DateTo}</p></div><StatusBadge tone={item.Status === "Voided" ? "danger" : item.Status === "Closed" ? "warning" : "default"}>{item.StatusLabel}</StatusBadge></div>
            <div className="mt-4 grid gap-3 sm:grid-cols-3"><InfoBox label="庫存鎖定" value={item.LockInventory ? "是" : "否"} /><InfoBox label="採購鎖定" value={item.LockPurchase ? "是" : "否"} /><InfoBox label="付款鎖定" value={item.LockPayment ? "是" : "否"} /></div>
            {item.LockReason ? <p className="mt-3 break-words rounded-md bg-[#f8fbf8] px-3 py-2 text-sm font-bold leading-6 text-[#59665f]">{item.LockReason}</p> : null}
            <div className="mt-4 flex flex-wrap gap-2">
              {item.Status === "Closed" ? <button className="erp-secondary-btn" disabled={loading} onClick={() => setPeriodAction({ type: "reopen", item, note: "", error: "" })} type="button">重開期間</button> : null}
              {item.Status === "Open" ? <button className="erp-primary-btn" disabled={loading} onClick={() => setPeriodAction({ type: "close", item, note: item.LockReason || "期間結帳鎖定", error: "" })} type="button">鎖定期間</button> : null}
              {item.Status !== "Voided" ? <button className="erp-secondary-btn border-[#f2b8b5] text-[#a33a2f]" disabled={loading} onClick={() => setPeriodAction({ type: "void", item, note: "", error: "" })} type="button">作廢期間</button> : null}
            </div>
          </div>
        ))}
      </SimpleResponsiveList>
    </div>
  );
}

function RmaOrdersView() {
  const [items, setItems] = useState<ErpRmaOrder[]>([]);
  const [suppliers, setSuppliers] = useState<ErpSupplierOption[]>([]);
  const [logisticsCarriers, setLogisticsCarriers] = useState<ErpLogisticsCarrierOption[]>([]);
  const [statusOptions, setStatusOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [typeOptions, setTypeOptions] = useState<AdminStoreSalesOrderOption[]>([]);
  const [keyword, setKeyword] = useState("");
  const [status, setStatus] = useState("");
  const [form, setForm] = useState({ RmaType: "Return", ErpSupplierId: "", SupplierName: "", RmaNumber: "", ReturnTrackingNumber: "", ReplacementExpectedDate: "", CreditAmount: "", IssueSummary: "", ResolutionNote: "" });
  const [stageForms, setStageForms] = useState<Record<string, { Status: string; ReturnCarrier: string; ReturnTrackingNumber: string; ReplacementReceivedAt: string; CreditAmount: string; WriteOffAmount: string; StageNote: string }>>({});
  const [message, setMessage] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);
  async function load() {
    setLoading(true);
    try {
      const data = await listErpRmaOrders({ Keyword: keyword, Status: status });
      setItems(data.Items || []);
      setSuppliers(data.SupplierOptions || []);
      setLogisticsCarriers(data.LogisticsCarrierOptions || []);
      setStatusOptions(data.StatusOptions || []);
      setTypeOptions(data.TypeOptions || []);
      setStageForms((current) => {
        const next = { ...current };
        for (const item of data.Items || []) {
          if (!next[item.ErpRmaOrderId]) {
            next[item.ErpRmaOrderId] = {
              Status: nextRmaStage(item.Status),
              ReturnCarrier: item.ReturnCarrier || "",
              ReturnTrackingNumber: item.ReturnTrackingNumber || "",
              ReplacementReceivedAt: item.ReplacementReceivedAt?.slice(0, 10) || "",
              CreditAmount: item.CreditAmount ? String(item.CreditAmount) : "",
              WriteOffAmount: item.WriteOffAmount ? String(item.WriteOffAmount) : "",
              StageNote: item.StageNote || item.ResolutionNote || "",
            };
          }
        }
        return next;
      });
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "RMA 載入失敗。");
    } finally {
      setLoading(false);
    }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, []);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = await createErpRmaOrder({ ...form, CreditAmount: Number(form.CreditAmount || 0) });
      setSuccess(result.message || "RMA 單已建立。");
      setForm((prev) => ({ ...prev, RmaNumber: "", ReturnTrackingNumber: "", ReplacementExpectedDate: "", CreditAmount: "", IssueSummary: "", ResolutionNote: "" }));
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "建立失敗。");
    } finally {
      setLoading(false);
    }
  }

  function patchStage(id: string, patch: Partial<{ Status: string; ReturnCarrier: string; ReturnTrackingNumber: string; ReplacementReceivedAt: string; CreditAmount: string; WriteOffAmount: string; StageNote: string }>) {
    setStageForms((current) => ({ ...current, [id]: { Status: "ReturnShipped", ReturnCarrier: "", ReturnTrackingNumber: "", ReplacementReceivedAt: "", CreditAmount: "", WriteOffAmount: "", StageNote: "", ...(current[id] || {}), ...patch } }));
  }

  async function updateStage(item: ErpRmaOrder) {
    const stage = stageForms[item.ErpRmaOrderId];
    if (!stage) return;
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = await updateErpRmaOrderStage(item.ErpRmaOrderId, {
        Status: stage.Status,
        ReturnCarrier: stage.ReturnCarrier,
        ReturnTrackingNumber: stage.ReturnTrackingNumber,
        ReplacementReceivedAt: stage.ReplacementReceivedAt,
        CreditAmount: Number(stage.CreditAmount || 0),
        WriteOffAmount: Number(stage.WriteOffAmount || 0),
        StageNote: stage.StageNote,
      });
      setSuccess(result.message || "RMA 階段已更新。");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "RMA 階段更新失敗。");
    } finally {
      setLoading(false);
    }
  }

  async function closeRma(item: ErpRmaOrder) {
    setLoading(true);
    setMessage("");
    setSuccess("");
    try {
      const result = await closeErpRmaOrder(item.ErpRmaOrderId, { ResolutionNote: stageForms[item.ErpRmaOrderId]?.StageNote || "已完成退換貨追蹤。" });
      setSuccess(result.message || "RMA 單已結案。");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "結案失敗。");
    } finally {
      setLoading(false);
    }
  }

  const activeCount = items.filter((item) => item.Status !== "Closed").length;
  return (
    <div className="erp-page-shell grid gap-5">
      <ToastAlerts message={message} success={success} onClearMessage={() => setMessage("")} onClearSuccess={() => setSuccess("")} />
      <FilterPanel onSubmit={(event) => { event.preventDefault(); void load(); }}>
        <Field label="關鍵字"><input className="erp-input" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="RMA 單號、供應商、異常摘要" /></Field>
        <Field label="狀態"><select className="erp-input" value={status} onChange={(event) => setStatus(event.target.value)}><option value="">全部</option>{statusOptions.map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></Field>
        <div className="erp-filter-actions"><button className="erp-primary-btn" type="submit"><Search size={18} />查詢</button></div>
      </FilterPanel>
      <section className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-5">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-extrabold text-[#203d32]">建立 RMA 單</h2>
            <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">用於驗收異常、退供應商、換貨補件、折讓請款與報廢追蹤。後續每個階段都會留下稽核紀錄。</p>
          </div>
          <StatusBadge tone="warning">未結案 {activeCount} 筆</StatusBadge>
        </div>
        <form className="grid gap-4" onSubmit={submit}>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <Field label="類型"><select className="erp-input" value={form.RmaType} onChange={(event) => setForm((prev) => ({ ...prev, RmaType: event.target.value }))}>{(typeOptions.length ? typeOptions : [{ Key: "Return", Label: "退供應商" }, { Key: "Exchange", Label: "換貨" }, { Key: "Supplement", Label: "補件" }, { Key: "Credit", Label: "折讓" }, { Key: "WriteOff", Label: "報廢追蹤" }]).map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></Field>
            <Field label="供應商"><select className="erp-input" value={form.ErpSupplierId} onChange={(event) => setForm((prev) => ({ ...prev, ErpSupplierId: event.target.value }))}><option value="">手動輸入</option>{suppliers.map((supplier) => <option key={supplier.ErpSupplierId} value={supplier.ErpSupplierId}>{supplier.Name}</option>)}</select></Field>
            <Field label="手動供應商"><input className="erp-input" value={form.SupplierName} onChange={(event) => setForm((prev) => ({ ...prev, SupplierName: event.target.value }))} /></Field>
            <Field label="供應商 RMA 編號"><input className="erp-input" value={form.RmaNumber} onChange={(event) => setForm((prev) => ({ ...prev, RmaNumber: event.target.value }))} /></Field>
            <Field label="退回物流單號"><input className="erp-input" value={form.ReturnTrackingNumber} onChange={(event) => setForm((prev) => ({ ...prev, ReturnTrackingNumber: event.target.value }))} /></Field>
            <Field label="預計換貨/補件日"><input className="erp-input" type="date" value={form.ReplacementExpectedDate} onChange={(event) => setForm((prev) => ({ ...prev, ReplacementExpectedDate: event.target.value }))} /></Field>
            <Field label="預估折讓金額"><input className="erp-input" min="0" type="number" value={form.CreditAmount} onChange={(event) => setForm((prev) => ({ ...prev, CreditAmount: event.target.value }))} /></Field>
            <div className="flex items-end"><button className="erp-primary-btn w-full" disabled={loading} type="submit"><RotateCcw size={18} />建立 RMA</button></div>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="異常摘要"><textarea className="erp-input min-h-[96px]" value={form.IssueSummary} onChange={(event) => setForm((prev) => ({ ...prev, IssueSummary: event.target.value }))} /></Field>
            <Field label="處理備註"><textarea className="erp-input min-h-[96px]" value={form.ResolutionNote} onChange={(event) => setForm((prev) => ({ ...prev, ResolutionNote: event.target.value }))} /></Field>
          </div>
        </form>
      </section>
      <SimpleResponsiveList title="RMA 列表" countText={count(items.length)} empty="目前沒有 RMA 單。">
        {items.map((item) => {
          const stage = stageForms[item.ErpRmaOrderId] || { Status: nextRmaStage(item.Status), ReturnCarrier: item.ReturnCarrier || "", ReturnTrackingNumber: item.ReturnTrackingNumber || "", ReplacementReceivedAt: item.ReplacementReceivedAt?.slice(0, 10) || "", CreditAmount: item.CreditAmount ? String(item.CreditAmount) : "", WriteOffAmount: item.WriteOffAmount ? String(item.WriteOffAmount) : "", StageNote: item.StageNote || "" };
          return (
            <article className="rounded-md border border-[#dfe6e1] bg-white p-4" key={item.ErpRmaOrderId}>
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="break-all text-xl font-extrabold text-[#203d32]">{item.RmaOrderNumber}</p>
                    <StatusBadge tone={item.Status === "Closed" ? "default" : "warning"}>{item.StatusLabel}</StatusBadge>
                  </div>
                  <p className="mt-1 break-words font-bold text-[#59665f]">{item.SupplierName || "-"} / {item.RmaTypeLabel} / 供應商 RMA：{item.RmaNumber || "-"}</p>
                </div>
                <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4 lg:min-w-[520px]">
                  <InfoBox label="退回物流" value={item.ReturnTrackingNumber ? `${item.ReturnCarrier || "未填物流"} / ${item.ReturnTrackingNumber}` : "尚未退回"} />
                  <InfoBox label="換貨到貨" value={item.ReplacementReceivedAt || item.ReplacementExpectedDate || "-"} />
                  <InfoBox label="折讓請款" value={item.CreditRequestedAt ? money(item.CreditAmount) : item.CreditAmount ? `預估 ${money(item.CreditAmount)}` : "-"} />
                  <InfoBox label="報廢入帳" value={item.WriteOffAt ? money(item.WriteOffAmount || 0) : "-"} />
                </div>
              </div>
              <p className="mt-3 break-words rounded-md bg-[#fbfcfb] px-3 py-2 text-sm font-bold leading-6 text-[#59665f]">異常摘要：{item.IssueSummary || "-"}</p>
              <div className="mt-3 grid gap-2 md:grid-cols-5">
                {[
                  ["Open", "建立/處理"],
                  ["ReturnShipped", "退回供應商"],
                  ["ReplacementReceived", "換貨到貨"],
                  ["CreditRequested", "折讓請款"],
                  ["WriteOffPosted", "報廢入帳"],
                ].map(([key, label]) => <div className={`rounded-md border px-3 py-2 text-sm font-extrabold ${rmaStageReached(item, key) ? "border-[#acd7bb] bg-[#eff8f1] text-[#245438]" : "border-[#edf0ee] bg-white text-[#8c9790]"}`} key={key}>{label}</div>)}
              </div>
              {item.Status !== "Closed" ? (
                <div className="mt-4 grid gap-3 rounded-md border border-[#edf0ee] bg-[#fbfcfb] p-3">
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                    <Field label="下一階段"><select className="erp-input" value={stage.Status} onChange={(event) => patchStage(item.ErpRmaOrderId, { Status: event.target.value })}>{rmaStageOptions(statusOptions).map((option) => <option key={option.Key} value={option.Key}>{option.Label}</option>)}</select></Field>
                    <LogisticsCarrierPicker label="退回物流公司" options={logisticsCarriers} value={stage.ReturnCarrier} onChange={(value) => patchStage(item.ErpRmaOrderId, { ReturnCarrier: value })} />
                    <Field label="退回物流單號"><input className="erp-input" value={stage.ReturnTrackingNumber} onChange={(event) => patchStage(item.ErpRmaOrderId, { ReturnTrackingNumber: event.target.value })} /></Field>
                    <Field label="換貨到貨日"><input className="erp-input" type="date" value={stage.ReplacementReceivedAt} onChange={(event) => patchStage(item.ErpRmaOrderId, { ReplacementReceivedAt: event.target.value })} /></Field>
                    <Field label="折讓金額"><input className="erp-input" min="0" type="number" value={stage.CreditAmount} onChange={(event) => patchStage(item.ErpRmaOrderId, { CreditAmount: event.target.value })} /></Field>
                    <Field label="報廢入帳金額"><input className="erp-input" min="0" type="number" value={stage.WriteOffAmount} onChange={(event) => patchStage(item.ErpRmaOrderId, { WriteOffAmount: event.target.value })} /></Field>
                    <div className="md:col-span-2">
                      <Field label="階段備註"><input className="erp-input" value={stage.StageNote} onChange={(event) => patchStage(item.ErpRmaOrderId, { StageNote: event.target.value })} placeholder="例如：已寄回供應商、等待換貨、折讓請款單號、報廢原因" /></Field>
                    </div>
                  </div>
                  <div className="rwd-action-grid justify-end">
                    <button className="erp-secondary-btn" disabled={loading} onClick={() => void updateStage(item)} type="button">更新階段</button>
                    <button className="erp-primary-btn" disabled={loading} onClick={() => void closeRma(item)} type="button">結案 RMA</button>
                  </div>
                </div>
              ) : <p className="mt-3 text-sm font-bold text-[#7b877f]">結案時間：{item.ClosedAt || "-"}</p>}
            </article>
          );
        })}
      </SimpleResponsiveList>
    </div>
  );
}

function nextRmaStage(status: string) {
  if (status === "ReturnShipped") return "ReplacementReceived";
  if (status === "ReplacementWaiting") return "ReplacementReceived";
  if (status === "ReplacementReceived") return "CreditRequested";
  if (status === "CreditWaiting") return "CreditRequested";
  if (status === "CreditRequested") return "WriteOffPosted";
  return "ReturnShipped";
}

function rmaStageOptions(statusOptions: AdminStoreSalesOrderOption[]) {
  const options = statusOptions.filter((option) => !["Open", "Closed"].includes(option.Key));
  return options.length > 0 ? options : [
    { Key: "ReturnShipped", Label: "已退回供應商" },
    { Key: "ReplacementReceived", Label: "換貨/補件到貨" },
    { Key: "CreditRequested", Label: "折讓請款" },
    { Key: "WriteOffPosted", Label: "報廢入帳" },
  ];
}

function rmaStageReached(item: ErpRmaOrder, stage: string) {
  if (stage === "Open") return true;
  if (stage === "ReturnShipped") return Boolean(item.ReturnedAt || item.ReturnTrackingNumber || ["ReturnShipped", "ReplacementReceived", "CreditRequested", "WriteOffPosted", "Closed"].includes(item.Status));
  if (stage === "ReplacementReceived") return Boolean(item.ReplacementReceivedAt || ["ReplacementReceived", "CreditRequested", "WriteOffPosted", "Closed"].includes(item.Status));
  if (stage === "CreditRequested") return Boolean(item.CreditRequestedAt || ["CreditRequested", "WriteOffPosted", "Closed"].includes(item.Status));
  if (stage === "WriteOffPosted") return Boolean(item.WriteOffAt || ["WriteOffPosted", "Closed"].includes(item.Status));
  return false;
}

function ImportBatchesView() {
  const [items, setItems] = useState<ErpImportBatch[]>([]);
  const [form, setForm] = useState({ ImportType: "StockUnits", FileName: "", Content: "", Note: "" });
  const [selectedFileName, setSelectedFileName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [operationProgress, setOperationProgress] = useState<OperationProgressState | null>(null);
  const importTemplateRows: Record<string, Array<Array<string | number>>> = {
    StockUnits: [
      ["商品編號", "SKU", "序號", "倉庫代碼", "耳側", "單位成本", "備註"],
      ["P000001", "P000001-L", "SN202608240001", "HQ", "左耳", 21000, "國外到貨入庫"],
      ["P000001", "P000001-R", "SN202608240002", "HQ", "右耳", 21000, "國外到貨入庫"],
    ],
    SupplierPrices: [
      ["供應商代碼", "商品編號", "SKU", "供應商料號", "採購單位", "採購單價", "最低採購量", "交期天數", "幣別", "生效日起", "生效迄", "備註"],
      ["HA-INTL-001", "P000001", "P000001-L", "HA-PRO3-L", "台", 21000, 1, 28, "TWD", "2026-08-24", "", "新版採購價格"],
      ["HA-INTL-001", "P000001", "P000001-R", "HA-PRO3-R", "台", 21000, 1, 28, "TWD", "2026-08-24", "", "新版採購價格"],
    ],
    InventoryCount: [
      ["商品編號", "SKU", "盤點數量", "倉庫代碼", "耳側", "序號清單", "單位成本", "差異原因", "備註"],
      ["P000001", "P000001-L", 2, "HQ", "左耳", "SN202608240001;SN202608240003", 21000, "盤盈", "主管覆核前預檢"],
      ["P000001", "P000001-R", 1, "HQ", "右耳", "SN202608240002", 21000, "帳實相符", ""],
    ],
  };
  const importTemplateNames: Record<string, string> = {
    StockUnits: "erp-import-template-stock-units.csv",
    SupplierPrices: "erp-import-template-supplier-prices.csv",
    InventoryCount: "erp-import-template-inventory-count.csv",
  };
  const importTemplateHints: Record<string, string> = {
    StockUnits: "一列一筆序號；若同商品多序號，請分列填寫，倉庫代碼需與 ERP 倉庫主檔一致。",
    SupplierPrices: "用於維護供應商商品價格；供應商代碼、商品編號、SKU、供應商料號與採購單價請務必填寫。",
    InventoryCount: "用於盤點明細預檢；序號清單可用分號分隔，正式盤點仍需由盤點單流程覆核結案。",
  };
  async function load() { setLoading(true); try { const data = await listErpImportBatches(); setItems(data.Items || []); } catch (error) { setMessage(error instanceof Error ? error.message : "匯入批次載入失敗。"); } finally { setLoading(false); } }
  useEffect(() => { void load(); }, []);
  function downloadTemplate() {
    const rows = importTemplateRows[form.ImportType] || importTemplateRows.StockUnits;
    downloadCsvFile(importTemplateNames[form.ImportType] || "erp-import-template.csv", rows);
    setMessage("範例格式已下載，請保留第一列欄位名稱後再填入資料。");
  }
  async function readImportFile(file: File) {
    if (/\.(xlsx|xls)$/i.test(file.name)) {
      setMessage("目前匯入預檢支援 CSV / TXT / TSV，請先將 Excel 另存為 CSV 後再上傳。");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setMessage("匯入檔案不可超過 5MB。");
      return;
    }
    setOperationProgress({ label: "匯入檔案讀取中", detail: file.name, percent: 0 });
    try {
      const content = await readFileAsTextWithProgress(file, (progress) => {
        setOperationProgress({ label: "匯入檔案讀取中", detail: `${file.name} / ${formatBytes(progress.loaded)} of ${formatBytes(progress.total)}`, percent: progress.percent });
      });
      setSelectedFileName(file.name);
      setForm((prev) => ({ ...prev, FileName: prev.FileName || file.name, Content: content }));
      setMessage("檔案內容已載入，可先檢查後送出預檢。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "檔案讀取失敗。");
    } finally {
      setOperationProgress(null);
    }
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    if (form.Content.trim() === "") {
      setMessage("請上傳 CSV / TXT 檔案，或貼上匯入內容。");
      return;
    }
    setLoading(true);
    setOperationProgress({ label: "匯入預檢中", detail: "系統正在驗證欄位、主檔、序號與資料格式。", percent: null });
    try {
      await validateErpImportBatch(form);
      setMessage("匯入預檢完成。");
      setSelectedFileName("");
      setForm((prev) => ({ ...prev, Content: "", Note: "" }));
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "預檢失敗。");
    } finally {
      setOperationProgress(null);
      setLoading(false);
    }
  }

  async function applyBatch(item: ErpImportBatch) {
    setLoading(true);
    setOperationProgress({ label: "匯入批次套用中", detail: `${item.ImportNumber} 正在寫入正式資料，請勿關閉頁面或重複點擊。`, percent: null });
    try {
      await applyErpImportBatch(item.ErpImportBatchId);
      setMessage("匯入批次已套用完成。");
      await load();
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "套用失敗。");
    } finally {
      setOperationProgress(null);
      setLoading(false);
    }
  }
  return (
    <div className="erp-page-shell grid gap-5">
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <section className="rounded-md border border-[#dfe6e1] bg-white p-5">
        <div className="flex flex-col gap-2 md:flex-row md:items-start md:justify-between">
          <div>
            <h2 className="text-lg font-extrabold text-[#203d32]">匯入預檢</h2>
            <p className="mt-1 text-sm font-bold leading-6 text-[#7b877f]">先上傳或貼上 CSV 內容，系統會建立預檢批次；無錯誤後才可套用，避免直接污染庫存與供應商價格資料。</p>
          </div>
          <StatusBadge>{items.length} 批次</StatusBadge>
        </div>
        <form className="mt-4 grid gap-4" onSubmit={submit}>
          <OperationProgress progress={operationProgress} />
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-[220px_minmax(220px,1fr)_220px]">
            <Field label="匯入類型"><select className="erp-input" value={form.ImportType} onChange={(event) => setForm((prev) => ({ ...prev, ImportType: event.target.value }))}><option value="StockUnits">庫存序號</option><option value="SupplierPrices">供應商價格</option><option value="InventoryCount">盤點明細</option></select></Field>
            <Field label="檔名"><input className="erp-input" value={form.FileName} onChange={(event) => setForm((prev) => ({ ...prev, FileName: event.target.value }))} placeholder="例如 supplier-price.csv" /></Field>
            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
              <button className="erp-secondary-btn w-full self-end" onClick={downloadTemplate} type="button"><Download size={18} />下載範例格式</button>
              <button className="erp-primary-btn w-full self-end" aria-busy={loading && Boolean(operationProgress) ? "true" : "false"} disabled={loading} type="submit"><Upload size={18} />{loading && operationProgress ? "預檢中" : "預檢匯入"}</button>
            </div>
          </div>
          <div className="rounded-md border border-[#dfe6e1] bg-[#f7faf8] p-3 text-sm font-bold leading-6 text-[#59665f]">
            <p>目前範例：{form.ImportType === "StockUnits" ? "庫存序號" : form.ImportType === "SupplierPrices" ? "供應商價格" : "盤點明細"}</p>
            <p>{importTemplateHints[form.ImportType]}</p>
          </div>
          <label className="min-w-0 rounded-md border border-[#dfe6e1] bg-[#fbfcfb] p-3">
            <span className="mb-2 block text-sm font-bold text-[#59665f]">上傳 CSV / TXT / TSV</span>
            <input
              className="block w-full min-w-0 text-sm font-bold text-[#59665f] file:mr-3 file:min-h-10 file:rounded-md file:border-0 file:bg-[#315746] file:px-4 file:font-bold file:text-white"
              type="file"
              accept=".csv,.txt,.tsv"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) void readImportFile(file);
              }}
            />
            <p className="mt-2 break-words text-xs font-bold leading-5 text-[#7b877f]">{selectedFileName ? `已載入：${selectedFileName}` : "正式 Excel 檔請先另存 CSV；第一列需為欄位名稱，後續一列一筆資料。"}</p>
          </label>
          <Field label="CSV 文字內容"><textarea className="erp-input min-h-[220px] font-mono text-sm" value={form.Content} onChange={(event) => setForm((prev) => ({ ...prev, Content: event.target.value }))} placeholder={"第一列請放欄位名稱，後續一列一筆資料。"} /></Field>
          <Field label="匯入備註"><textarea className="erp-input min-h-[88px]" value={form.Note} onChange={(event) => setForm((prev) => ({ ...prev, Note: event.target.value }))} placeholder="例如：供應商 8 月新版價格、國外到貨序號清單。" /></Field>
        </form>
      </section>
      <SimpleResponsiveList title="匯入批次列表" countText={count(items.length)} empty="目前沒有匯入批次。">{items.map((item) => <div className="rounded-md border border-[#dfe6e1] bg-white p-4" key={item.ErpImportBatchId}><div className="flex flex-wrap items-start justify-between gap-3"><div><p className="text-xl font-extrabold">{item.ImportNumber}</p><p className="font-bold text-[#59665f]">{item.ImportTypeLabel} / {item.FileName || "-"}</p></div><StatusBadge tone={item.ErrorRows > 0 ? "danger" : item.Status === "Applied" ? "default" : "warning"}>{item.StatusLabel}</StatusBadge></div><div className="mt-4 grid gap-3 sm:grid-cols-4"><InfoBox label="總列數" value={count(item.TotalRows)} /><InfoBox label="有效列" value={count(item.ValidRows)} /><InfoBox label="錯誤列" value={count(item.ErrorRows)} /><InfoBox label="已套用" value={count(item.AppliedRows)} /></div>{item.ErrorSummary?.length ? <div className="mt-3 rounded-md border border-[#f4d7a1] bg-[#fffaf0] p-3 text-sm font-bold leading-6 text-[#8a5b14]">{item.ErrorSummary.slice(0, 5).map((error, index) => <p key={`${item.ErpImportBatchId}-${index}`}>第 {error.Row || "-"} 列：{error.Message || "格式錯誤"}</p>)}</div> : null}{item.Status !== "Applied" && item.ErrorRows === 0 ? <button className="erp-primary-btn mt-4" aria-busy={loading && operationProgress?.label === "匯入批次套用中" ? "true" : "false"} disabled={loading} onClick={() => void applyBatch(item)} type="button">{loading && operationProgress?.label === "匯入批次套用中" ? "套用中" : "套用批次"}</button> : null}</div>)}</SimpleResponsiveList>
    </div>
  );
}

function TasksView() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ErpNotificationTask[]>([]);
  const [keyword, setKeyword] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  async function load() { setLoading(true); try { const data = await listErpTasks({ Keyword: keyword, Status: "Open" }); setItems(data.Items || []); } catch (error) { setMessage(error instanceof Error ? error.message : "待辦載入失敗。"); } finally { setLoading(false); } }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, []);
  function relatedPath(item: ErpNotificationTask) {
    if (item.RelatedType === "PurchaseOrder") return "/erp/purchase-orders";
    if (item.RelatedType === "PurchaseReceipt" && item.RelatedId) return `/erp/purchase-receipts/${item.RelatedId}`;
    if (item.RelatedType === "Payable") return "/erp/payables";
    if (item.RelatedType === "RmaOrder") return "/erp/rma-orders";
    if (item.RelatedType === "OutboundOrder") return "/erp/outbound-orders";
    if (item.RelatedType === "ImportBatch") return "/erp/import-batches";
    return "";
  }
  return (
    <div className="erp-page-shell grid gap-5">
      <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
      <section className="rounded-md border border-[#dfe6e1] bg-white p-4 shadow-[0_8px_22px_rgba(32,61,50,.04)]">
        <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(280px,420px)]">
          <div>
            <h2 className="text-lg font-extrabold text-[#203d32]">ERP 通知待辦怎麼來</h2>
            <p className="mt-2 text-sm font-bold leading-6 text-[#59665f]">資料來源包含系統自動產生的即時待辦。自動待辦目前會追蹤採購逾期、採購入庫驗收待處理與驗收異常；正式待辦可由後續流程寫入，完成後會留下完成紀錄。</p>
          </div>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-1">
            <InfoBox label="自動來源" value="採購逾期、驗收待處理、驗收異常" />
          </div>
        </div>
      </section>
      <FilterPanel onSubmit={(event) => { event.preventDefault(); void load(); }}>
        <Field label="關鍵字"><input className="erp-input" value={keyword} onChange={(event) => setKeyword(event.target.value)} placeholder="待辦標題、單號、內容" /></Field>
        <div className="erp-filter-actions"><button className="erp-primary-btn" type="submit"><Search size={18} />查詢</button></div>
      </FilterPanel>
      <div className="erp-metric-grid"><MetricCard icon={<Bell size={20} />} label="待辦總數" value={count(items.length)} /><MetricCard icon={<AlertTriangle size={20} />} label="高優先" value={count(items.filter((item) => item.Priority === "High").length)} tone="warning" /></div>
      <SimpleResponsiveList title="ERP 通知待辦" countText={count(items.length)} empty="目前沒有待處理事項。">{items.map((item) => {
        const path = relatedPath(item);
        const isFormalTask = /^\d+$/.test(item.ErpNotificationTaskId);
        return (
          <div className="rounded-md border border-[#dfe6e1] bg-white p-4" key={item.ErpNotificationTaskId}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="break-words text-xl font-extrabold text-[#203d32]">{item.Title}</p>
                <p className="mt-1 break-words font-bold text-[#59665f]">{item.RelatedNumber || "系統待辦"} {item.DueDate ? `/ 到期 ${item.DueDate}` : ""}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <StatusBadge tone={item.Priority === "High" ? "danger" : "default"}>{item.PriorityLabel}</StatusBadge>
                <StatusBadge>{isFormalTask ? "正式待辦" : "系統即時"}</StatusBadge>
              </div>
            </div>
            <p className="mt-3 break-words font-bold leading-7 text-[#59665f]">{item.Message || "-"}</p>
            <div className="rwd-action-grid mt-4">
              {path ? <button className="erp-secondary-btn" onClick={() => navigate(path)} type="button">前往處理</button> : null}
              {isFormalTask ? <button className="erp-secondary-btn" disabled={loading} onClick={async () => { setLoading(true); try { await completeErpTask(item.ErpNotificationTaskId, { CompletionNote: "已於 ERP 待辦中心完成。" }); await load(); } catch (error) { setMessage(error instanceof Error ? error.message : "完成待辦失敗。"); } finally { setLoading(false); } }} type="button">完成待辦</button> : null}
            </div>
          </div>
        );
      })}</SimpleResponsiveList>
    </div>
  );
}

function SimpleResponsiveList({ title, countText, empty, children }: { title: string; countText: string; empty: string; children: ReactNode }) {
  const hasItems = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return <section className="rounded-md border border-[#dfe6e1] bg-white p-4"><div className="mb-4 flex flex-wrap items-center justify-between gap-2"><h2 className="text-lg font-extrabold text-[#203d32]">{title}</h2><span className="text-sm font-bold text-[#7b877f]">{countText}</span></div>{hasItems ? <div className="grid gap-3">{children}</div> : <EmptyState text={empty} />}</section>;
}

export default function ErpOperations({ view }: { view: ErpView }) {
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [user, setUser] = useState<AdminUser | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    getCurrentErpUser()
      .then((data) => setUser(data.User))
      .catch((error) => {
        setMessage(error instanceof Error ? error.message : "ERP 模組載入失敗。");
        if (error instanceof ErpApiError && error.status === 401) navigate("/erp-login", { replace: true });
      });
  }, [navigate]);

  async function handleLogout() {
    await logoutErp().catch(() => undefined);
    navigate("/erp-login", { replace: true });
  }

  return (
    <ErpLayout title={pageTitle(view)} user={user} onLogout={handleLogout}>
      <div className="grid w-full max-w-none gap-5">
        <ToastAlerts message={message} onClearMessage={() => setMessage("")} />
        {view === "inventory" ? <InventoryView /> : null}
        {view === "inventory-detail" ? <InventoryView mode="detail" /> : null}
        {view === "units" ? <StockUnitsView /> : null}
        {view === "movements" ? <StockMovementsView /> : null}
        {view === "inventory-counts" ? <StockAdjustmentsView inventoryCountMode /> : null}
        {view === "inventory-count-detail" ? <StockAdjustmentsView mode="detail" inventoryCountMode /> : null}
        {view === "inventory-count-create" ? <StockAdjustmentsView mode="create" inventoryCountMode /> : null}
        {view === "adjustments" ? <StockAdjustmentsView /> : null}
        {view === "adjustment-detail" ? <StockAdjustmentsView mode="detail" /> : null}
        {view === "adjustment-create" ? <StockAdjustmentsView mode="create" /> : null}
        {view === "transfers" ? <StockTransfersView /> : null}
        {view === "transfer-create" ? <StockTransfersView mode="create" /> : null}
        {view === "purchase" ? <PurchaseView /> : null}
        {view === "purchase-suggestions" ? <PurchaseView mode="suggestions" /> : null}
        {view === "purchase-create" ? <PurchaseView mode="create" /> : null}
        {view === "purchase-receipts" ? <PurchaseReceiptsView /> : null}
        {view === "purchase-receipt-detail" ? <PurchaseReceiptsView mode="detail" /> : null}
        {view === "outbound" ? <OutboundView /> : null}
        {view === "outbound-create" ? <OutboundView mode="create" /> : null}
        {view === "quality-inspections" ? <QualityInspectionsView /> : null}
        {view === "attachments" ? <AttachmentsView /> : null}
        {view === "payables" ? <PayablesView /> : null}
        {view === "reports" ? <ErpReportsView /> : null}
        {view === "audit-logs" ? <AuditLogsView /> : null}
        {view === "accounting-periods" ? <AccountingPeriodsView /> : null}
        {view === "rma-orders" ? <RmaOrdersView /> : null}
        {view === "import-batches" ? <ImportBatchesView /> : null}
        {view === "tasks" ? <TasksView /> : null}
        {view === "warehouses" ? <WarehouseView /> : null}
        {view === "suppliers" ? <SupplierView /> : null}
        {view === "supplier-create" ? <SupplierView mode="create" /> : null}
        {view === "supplier-edit" ? <SupplierView mode="edit" supplierIdParam={supplierId || ""} /> : null}
      </div>
    </ErpLayout>
  );
}
